import { useState, useEffect, useMemo, useRef } from "react";
import { API_URL } from "./data/config";
import {
  Search,
  Navigation,
  X,
  Bell,
  Menu,
  Phone,
  Unlock,
  Mail,
  RefreshCw,
  ChevronRight,
  FileText,
  BarChart2,
  Compass,
  GitMerge,
  Calculator,
  Settings,
  ArrowUpRight,
  Check,
  Plus,
  ChevronDown,
  Sparkles,
  Lock,
  Eye,
  EyeOff,
  LogOut,
  ArrowLeft,
  ArrowRight,
  User,
  Zap,
  Target,
  Radio,
  TrendingUp,
  Battery,
  Wrench,
  DollarSign,
  Home,
  Info,
  MapPin,
  Clock,
  Building2,
  MessageCircle,
  Send,
  Sunrise,
} from "lucide-react";
import SolarLeadInsights from "./components/SolarLeadInsights";
import IntelligenceBriefing from "./components/IntelligenceBriefing";
import FirstRunSetup from "./components/FirstRunSetup";
import LifecycleBoard from "./components/LifecycleBoard";
import NearbyMap from "./components/NearbyMap";
import LegalPages from "./components/LegalPages";

// ─── DESIGN TOKENS - Monochrome with orange signal (locked May 24) ───────────
// Philosophy: no greys. All "muted" text/borders are alpha-on-ink instead.
// Orange is reserved for signal and CTA, never decoration.
const T = {
  // Surfaces
  bg: "#FFFFFF",
  bgOff: "#FAFAF6",        // raised surfaces, hover states
  bgSub: "#F4F4EE",        // subtle fills, tag backgrounds
  ink: "#0A0A0A",          // pure ink - the new canonical text color

  // Borders & dividers - all alpha-on-ink so they recede properly
  border: "rgba(10,10,10,0.10)",
  borderMid: "rgba(10,10,10,0.18)",
  borderStrong: "rgba(10,10,10,0.32)",

  // Text - solid inks only. #262B37 is the floor; nothing lighter ships.
  text: "#0A0A0A",
  textMid: "#181C26",
  textSub: "#262B37",
  textFaint: "#262B37",

  // Dark rail / sidebar
  sidebar: "#0E0E12",
  sidebarBorder: "rgba(255,255,255,0.08)",
  sidebarText: "#FFFFFF",
  sidebarTextActive: "#FFFFFF",
  sidebarActive: "rgba(255,255,255,0.06)",

  // Signal - orange is the ONLY accent in functional UI
  primary: "#FF8800",
  primaryLight: "#FFF3E5",
  primaryDark: "#E67700",
  primaryHot: "#FF5500",   // for hotter alerts

  // Legacy aliases - kept so existing call sites don't break
  accent: "#FF8800",
  accentLight: "#FFF3E5",
  accentDark: "#E67700",

  // Status
  success: "#16A34A",
  successLight: "#ECFDF3",
  successBorder: "#BBF7D0",
  danger: "#B91C1C",
  dangerLight: "#FEE2E2",
};

// ─── USER DEFAULTS ─────────────────────────────────────────────────────────────
const USER_DEFAULTS = {
  firstName: "",
  lastName: "",
  accountOwner: "",
  companyName: "",
  email: "",
  plan: "Pro",
  creditsRemaining: 50,
  isAdmin: false,
  // Timestamp of one-time skip-trace compliance acknowledgment (ISO string).
  // null until the user accepts the gate; once set, the gate never re-prompts.
  skiptraceAckAt: null as string | null,
  // Trial tier fields. Mirror /api/auth/me + /api/auth/start-trial. These MUST
  // stay in sync with the backend user response or reads below fail the build.
  tier: "pro" as string,
  trialEndsAt: null as string | null,
  trialDaysLeft: null as number | null,
  askUsed: 0,
  caps: null as null | { mapResults: number | null; nearbyResults: number | null; focusLeads: number | null; askQuestions: number | null; skipTrace: boolean },
};

// ─── SEGMENTS ──────────────────────────────────────────────────────────────────
// Territory display name (as shown in the picker / returned by
// /api/territories/list) -> province CODE the leads + segments endpoints filter
// on. Without this, picking "California" would query state="California" against
// a province column that stores "CA", returning nothing.
const TERRITORY_NAME_TO_CODE: Record<string, string> = {
  "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA", "Colorado": "CO",
  "Connecticut": "CT", "Delaware": "DE", "Florida": "FL", "Georgia": "GA", "Hawaii": "HI", "Idaho": "ID",
  "Illinois": "IL", "Indiana": "IN", "Iowa": "IA", "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA",
  "Maine": "ME", "Maryland": "MD", "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS",
  "Missouri": "MO", "Montana": "MT", "Nebraska": "NE", "Nevada": "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH",
  "Oklahoma": "OK", "Oregon": "OR", "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX", "Utah": "UT", "Vermont": "VT", "Virginia": "VA",
  "Washington": "WA", "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY", "Washington DC": "DC",
  "Alberta": "AB", "British Columbia": "BC", "Manitoba": "MB", "New Brunswick": "NB",
  "Newfoundland and Labrador": "NL", "Nova Scotia": "NS", "Ontario": "ON", "Prince Edward Island": "PE",
  "Quebec": "QC", "Saskatchewan": "SK", "Yukon": "YT", "Northwest Territories": "NT", "Nunavut": "NU",
};

// Resolve a territory display name into the { city, region } scope the leads +
// segments fetches expect. Returns "" / "" for All Markets (national) or for any
// name we cannot confidently map (e.g. a custom multi-city territory), so we
// never apply a filter that would silently return zero rows.
function territoryToScope(name: string): { city: string; region: string } {
  if (!name || name === "All Markets") return { city: "", region: "" };
  const m = name.match(/^(.+),\s*([A-Za-z]{2})$/);
  if (m) return { city: m[1].trim(), region: m[2].toUpperCase() };
  const code = TERRITORY_NAME_TO_CODE[name];
  if (code) return { city: "", region: code };
  return { city: "", region: "" };
}

// Region list for the first-run setup step. Plain { name, country }, no counts
// or rankings (first-run must never editorialize a market). Built from the
// name->code map so there is one source of truth.
const FIRST_RUN_REGIONS: { name: string; country: "US" | "CA" }[] = Object.entries(TERRITORY_NAME_TO_CODE).map(([name, code]) => ({
  name,
  country: (["AB", "BC", "MB", "NB", "NL", "NS", "ON", "PE", "QC", "SK", "YT", "NT", "NU"].indexOf(code) >= 0 ? "CA" : "US") as "US" | "CA",
}));

// Reverse of TERRITORY_NAME_TO_CODE (code -> display name). Labels a region-only
// selection coming back from the older picker modal so the territory name reads
// cleanly instead of as a bare code.
const CODE_TO_TERRITORY_NAME: Record<string, string> = {};
Object.keys(TERRITORY_NAME_TO_CODE).forEach((n) => { CODE_TO_TERRITORY_NAME[TERRITORY_NAME_TO_CODE[n]] = n; });

const SEGMENTS = {
  installers: {
    key: "installers",
    color: "#FF8800",
    label: "New Install",
    emoji: "⚡",
    days: "0-30 days",
    cta: "Recently filed. Original installer engaged. Brand awareness play only.",
  },
  cleaning: {
    key: "cleaning",
    color: "#0B8A87",
    label: "Maintenance",
    emoji: "🧹",
    days: "1-2 years",
    cta: "First service window. No one is calling them. Avg job $200-400.",
  },
  battery: {
    key: "battery",
    color: "#2970FF",
    label: "Battery Storage",
    emoji: "🔋",
    days: "2-5 years",
    cta: "Peak upsell window. System aged 2-5 years, prime time for battery storage add-on. Avg deal: $8K-$15K.",
  },
  takeover: {
    key: "takeover",
    color: "#F43F7E",
    label: "System Upgrade",
    emoji: "🔄",
    days: "5-10 years",
    cta: "System aging. Warranty expiring. Upgrade avg $12K-20K.",
  },
  loanpayoff: {
    key: "loanpayoff",
    color: "#7C5CFC",
    label: "Loan Payoff",
    emoji: "💳",
    days: "10-15 years",
    cta: "Loan likely paid off. Full replacement avg $20K-30K. Highest deal value.",
  },
  longterm: {
    key: "longterm",
    color: "#12B76A",
    label: "Replacement",
    emoji: "🔁",
    days: "15-20 years",
    cta: "System aged 15-20 years, warranty lapsing and loan long paid off. Your highest-value replacement and repower opportunity.",
  },
};

const STAGES = [
  { key: "all", label: "All" },
  { key: "unlocked", label: "Unlocked" },
  { key: "ai", label: "AI Sourced" },
  { key: "new", label: "New" },
  { key: "called", label: "Called" },
  { key: "interested", label: "Interested" },
  { key: "quoted", label: "Quoted" },
  { key: "closed", label: "Closed" },
];

const CALL_OUTCOMES = [
  { key: "no_answer", label: "No Answer", emoji: "📵" },
  { key: "voicemail", label: "Voicemail", emoji: "📬" },
  { key: "callback", label: "Callback", emoji: "🔄" },
  { key: "interested", label: "Interested", emoji: "✅" },
  { key: "not_interested", label: "Not Interested", emoji: "❌" },
  { key: "appointment", label: "Appt Set", emoji: "📅" },
];

// ─── PULSE FEED ────────────────────────────────────────────────────────────────
const PULSE = [
  {
    id: 1,
    tag: "Policy",
    state: "CA",
    hot: true,
    time: "2h ago",
    headline: "CA NEM 3.0 export rates cut",
    detail: "Lower export rates for new solar in PG&E territory.",
    action: "Push battery storage - pairs offset the cut",
    impact: "Battery demand up ~23% in affected areas",
  },
  {
    id: 2,
    tag: "Market",
    state: "TX",
    hot: true,
    time: "4h ago",
    headline: "Austin permits up 34% this week",
    detail: "New Installs volume spike - highest in 90 days.",
    action: "Work New Installs segment now, before competitors",
    impact: "108 fresh permits filed in the last 7 days",
  },
  {
    id: 7,
    tag: "Market",
    state: "AZ",
    hot: true,
    time: "6h ago",
    headline: "Phoenix Metro surging - summer starting early",
    detail: "Permit velocity 34% above 90-day avg.",
    action: "Book appointments now before peak season pricing kicks in",
    impact: "Phoenix is the #1 growth market this quarter",
  },
  {
    id: 8,
    tag: "Policy",
    state: "CO",
    hot: true,
    time: "1d ago",
    headline: "Colorado battery incentive doubles to $5K",
    detail: "Xcel Energy expanding storage rebate program.",
    action: "Lead every pitch with storage savings - $5K back on day one",
    impact: "Denver Metro storage attach rate expected to triple",
  },
  {
    id: 3,
    tag: "Incentive",
    state: "NY",
    hot: true,
    time: "1d ago",
    headline: "NY-Sun incentive raised to $0.35/W",
    detail: "Limited Q1 2026 Megawatt Block allocation - filling fast.",
    action: "Lead with incentive expiry urgency in your pitch",
    impact: "~$3,500 extra savings on average 10kW system",
  },
  {
    id: 4,
    tag: "Incentive",
    state: "AB",
    hot: false,
    time: "1d ago",
    headline: "Alberta $0.90/W rebate extended to June",
    detail: "ERA rebate program confirmed through June 2026.",
    action: "Remind prospects - urgency drives decisions",
    impact: "Up to $9,000 back on a 10kW system",
  },
  {
    id: 5,
    tag: "Market",
    state: "FL",
    hot: false,
    time: "2d ago",
    headline: "Florida filings up 18% year-over-year",
    detail: "Duke Energy territory leading growth, especially Tampa.",
    action: "Target Battery Storage upsells - high sun, high demand",
    impact: "Avg system value $28K-$36K in this market",
  },
  {
    id: 6,
    tag: "Incentive",
    state: "MA",
    hot: false,
    time: "3d ago",
    headline: "MA SMART adder rates updated",
    detail: "Community solar blocks selling quickly this quarter.",
    action: "Close fence-sitters - rates won't hold",
    impact: "$0.03-0.05/kWh extra in qualified areas",
  },
];

// ─── CREDIT BUNDLES ────────────────────────────────────────────────────────────
const CREDIT_BUNDLES = [
  { credits: 10, price: 9, label: "Starter", perCredit: "$0.90" },
  { credits: 50, price: 39, label: "Pro", perCredit: "$0.78", popular: true },
  { credits: 200, price: 129, label: "Team", perCredit: "$0.65" },
];

// ─── CALCULATOR DATA ──────────────────────────────────────────────────────────
const CALC_REGIONS = {
  CA: {
    label: "California",
    country: "US",
    avgWatt: 2.9,
    utilityRate: 0.29,
    sun: 5.5,
    state: {
      name: "CA SGIP Battery Rebate",
      amount: 200,
      type: "per_kw",
      note: "Self-Generation Incentive Program (battery focus)",
    },
    local: "NEM 3.0 net metering. LADWP & PG&E territory incentives vary.",
  },
  TX: {
    label: "Texas",
    country: "US",
    avgWatt: 2.65,
    utilityRate: 0.12,
    sun: 5.2,
    state: {
      name: "No state income tax credit",
      amount: 0,
      type: "flat",
      note: "Property tax exemption on added value",
    },
    local:
      "Austin Energy rebate $2,500 available. Some co-ops offer bill credits.",
  },
  FL: {
    label: "Florida",
    country: "US",
    avgWatt: 2.55,
    utilityRate: 0.13,
    sun: 5.6,
    state: {
      name: "FL Property Tax Exemption",
      amount: 0,
      type: "exemption",
      note: "100% property tax exemption on solar added value",
    },
    local:
      "Duke Energy & FPL net metering available. Sales tax exemption on equipment.",
  },
  AZ: {
    label: "Arizona",
    country: "US",
    avgWatt: 2.5,
    utilityRate: 0.12,
    sun: 6.0,
    state: {
      name: "AZ Residential Solar Credit",
      amount: 1000,
      type: "flat",
      note: "25% of cost up to $1,000 state tax credit",
    },
    local: "APS & SRP net metering. Maricopa County property tax exemption.",
  },
  NV: {
    label: "Nevada",
    country: "US",
    avgWatt: 2.6,
    utilityRate: 0.11,
    sun: 6.1,
    state: {
      name: "NV Sales Tax Exemption",
      amount: 0,
      type: "exemption",
      note: "No sales tax on solar equipment",
    },
    local: "NV Energy net metering. Property tax abatement available.",
  },
  CO: {
    label: "Colorado",
    country: "US",
    avgWatt: 2.75,
    utilityRate: 0.13,
    sun: 5.4,
    state: {
      name: "No state tax credit",
      amount: 0,
      type: "flat",
      note: "Property & sales tax exemptions available",
    },
    local:
      "Xcel Energy REC purchase program. Some utilities offer $500-1,500 rebates.",
  },
  NY: {
    label: "New York",
    country: "US",
    avgWatt: 3.1,
    utilityRate: 0.21,
    sun: 4.2,
    state: {
      name: "NY Solar Tax Credit",
      amount: 5000,
      type: "flat",
      note: "25% of cost up to $5,000 state tax credit",
    },
    local:
      "NY-Sun Megawatt Block incentive up to $0.30/W. ConEd varies by territory.",
  },
  MA: {
    label: "Massachusetts",
    country: "US",
    avgWatt: 3.15,
    utilityRate: 0.24,
    sun: 4.1,
    state: {
      name: "MA SMART Program",
      amount: 0.06,
      type: "per_kwh",
      note: "6¢/kWh production incentive for 10 years",
    },
    local:
      "MA Clean Energy Center adder programs. Municipal light plants vary.",
  },
  WA: {
    label: "Washington",
    country: "US",
    avgWatt: 2.85,
    utilityRate: 0.1,
    sun: 3.8,
    state: {
      name: "WA Sales Tax Exemption",
      amount: 0,
      type: "exemption",
      note: "Full sales tax exemption on solar equipment",
    },
    local:
      "PSE & Seattle City Light net metering. Some utility rebates available.",
  },
  OR: {
    label: "Oregon",
    country: "US",
    avgWatt: 2.8,
    utilityRate: 0.11,
    sun: 4.2,
    state: {
      name: "OR Residential Energy Tax Credit",
      amount: 0,
      type: "flat",
      note: "Program closed - check for updates",
    },
    local:
      "Energy Trust of Oregon cash incentives up to $1,800. PGE/Pacific Power.",
  },
  IL: {
    label: "Illinois",
    country: "US",
    avgWatt: 2.95,
    utilityRate: 0.14,
    sun: 4.4,
    state: {
      name: "IL Shines / REC Program",
      amount: 0,
      type: "rec",
      note: "REC sales generate $40-90/REC over 15 years",
    },
    local:
      "ComEd Smart Inverter Rebate $300. Ameren Illinois programs available.",
  },
  AB: {
    label: "Alberta",
    country: "CA",
    avgWatt: 3.2,
    utilityRate: 0.17,
    sun: 4.8,
    state: {
      name: "AB Residential Solar Rebate",
      amount: 0.9,
      type: "per_watt",
      note: "$0.90/W up to 10kW via Emissions Reduction Alberta",
    },
    local:
      "ENMAX & ATCO net metering. Some municipalities offer additional grants.",
  },
  BC: {
    label: "British Columbia",
    country: "CA",
    avgWatt: 3.4,
    utilityRate: 0.13,
    sun: 3.9,
    state: {
      name: "BC Hydro Net Metering",
      amount: 0,
      type: "net_meter",
      note: "Net metering at full retail rate - strong program",
    },
    local: "FortisBC net metering available. CleanBC incentives may apply.",
  },
  ON: {
    label: "Ontario",
    country: "CA",
    avgWatt: 3.3,
    utilityRate: 0.16,
    sun: 4.1,
    state: {
      name: "No provincial solar grant",
      amount: 0,
      type: "flat",
      note: "Net metering via Independent Electricity System Operator",
    },
    local:
      "Some utility-specific programs. Toronto offers property tax incentive.",
  },
  MB: {
    label: "Manitoba",
    country: "CA",
    avgWatt: 3.1,
    utilityRate: 0.1,
    sun: 4.5,
    state: {
      name: "Manitoba Hydro Net Metering",
      amount: 0,
      type: "net_meter",
      note: "Net metering at avoided cost rate",
    },
    local: "Limited provincial incentives. Federal Greener Homes applies.",
  },
  NS: {
    label: "Nova Scotia",
    country: "CA",
    avgWatt: 3.5,
    utilityRate: 0.18,
    sun: 3.8,
    state: {
      name: "NS Solar Homes Program",
      amount: 0.6,
      type: "per_watt",
      note: "$0.60/W rebate up to $6,000 via EfficiencyOne",
    },
    local: "NSP net metering. $1,000 PACE financing available in some areas.",
  },
};

const FEDERAL = {
  US: {
    name: "Federal ITC",
    rate: 0.3,
    note: "30% Investment Tax Credit - applies to full system + battery cost. Claim on federal tax return (Form 5695).",
  },
  CA: {
    name: "Canada Greener Homes Grant",
    amount: 5000,
    note: "Up to $5,000 federal grant for solar + battery retrofits. Apply through NRCan.",
  },
};

// ─── HELPERS ───────────────────────────────────────────────────────────────────
// Signal Score (0-10), computed server-side and written on every scored lead.
// Read it directly - never the legacy 0-100 lead_score (mixing the two scales
// was the bug this cutover fixes). Returns null when a lead hasn't been scored
// yet (a brand-new permit between the scrape and the nightly run) so the UI can
// show "New" instead of a misleading 0. Reads camelCase (mapLead / mapServerLead)
// or snake_case (raw RPC rows).
const getScore = (l: any) => {
  const s = l?.signalScore ?? l?.signal_score;
  if (s === null || s === undefined || s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};
const getSignalFactors = (l: any) => l?.signalFactors ?? l?.signal_factors ?? null;
const getDays = (l: any) => l.daysOld ?? l.days_old ?? 0;
const getVal = (l: any) => l.permitValue || l.permit_value || 0;
const getCity = (l: any) => (l.city || "").toLowerCase().trim();
const getState = (l: any) => (l.province || l.state || "").trim();
const getCountry = (l: any) => {
  if (l.country) return l.country.toUpperCase();
  const st = getState(l);
  return [
    "TX",
    "NV",
    "UT",
    "MA",
    "FL",
    "AZ",
    "CO",
    "WA",
    "CA",
    "OR",
    "IL",
    "NY",
  ].includes(st)
    ? "US"
    : "CA";
};
const getSegment = (l: any) => {
  const d = getDays(l);
  if (d <= 30)   return "installers";
  if (d <= 730)  return "cleaning";
  if (d <= 1825) return "battery";
  if (d <= 3650) return "takeover";
  if (d <= 5475) return "loanpayoff";
  return "longterm";
};
// getSmartScore is kept as the call-site name throughout the app, but it is now
// just the server-side signal score - the per-segment recompute that used to live
// here (capping New Installs at 75 and flattening the fresh feed) is gone; the
// backend scores everything.
const getSmartScore = (l: any) => getScore(l);

// ─── SIGNAL SCORE: one scale, one ladder, one formatter ──────────────────────
// HOT ≥ 8 · WARM ≥ 5 · COOL < 5 · unscored → "New". Every score threshold,
// label, decimal, and colour in the app routes through these four helpers so the
// bands can never drift apart again.
const SCORE_HOT = 8;
const SCORE_WARM = 5;
// Logic key. null/undefined (unscored) → "new". Comparisons against null are
// safe (null >= 8 is false), but we branch on null explicitly for clarity.
const scoreTier = (s: any): "hot" | "warm" | "cool" | "new" =>
  s == null ? "new" : s >= SCORE_HOT ? "hot" : s >= SCORE_WARM ? "warm" : "cool";
// Display label for a tier - uppercase bands, sentence-case "New".
const TIER_LABEL: Record<string, string> = { hot: "HOT", warm: "WARM", cool: "COOL", new: "New" };
const scoreLabelFor = (s: any) => TIER_LABEL[scoreTier(s)];
// One decimal everywhere: 8.4 - not 84, not 8.40. "New" for unscored leads.
const fmtScore = (s: any) => (s == null ? "New" : (Math.round(Number(s) * 10) / 10).toFixed(1));
// Locked palette: orange HOT, amber WARM, slate COOL/new.
const scoreHex = (s: any) =>
  s == null ? "#54546A" : s >= SCORE_HOT ? "#FF8800" : s >= SCORE_WARM ? "#D97706" : "#54546A";
const fmtVal = (v: any) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`);
// Format a North-American 10-digit number as (XXX) XXX-XXXX. Drops a leading
// country-code 1; anything that isn't a clean 10 digits is returned untouched
// (international numbers, extensions, partials) so we never mangle real data.
const fmtPhone = (raw: any) => {
  const d = String(raw || "").replace(/\D/g, "");
  const ten = d.length === 11 && d[0] === "1" ? d.slice(1) : d;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : String(raw || "");
};
const fmtAge = (d: any) =>
  d === 0 ? "Today" : d < 365 ? `${d}d old` : `${Math.round(d / 365)}yr old`;
const fmtCurrency = (n: any) =>
  n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${Math.round(n)}`;
const titleCase = (s: string) => {
  if (!s) return s;
  return s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()).replace(/\b(Dr|St|Rd|Blvd|Ave|Ln|Ct|Pl|Nw|Nw|Ne|Se|Sw|Pv)\b/gi, m => m.toUpperCase());
};
const cleanAddress = (addr: string) => {
  if (!addr) return addr;
  const cleaned = addr === addr.toUpperCase() ? titleCase(addr) : addr;
  return cleaned;
};

// Extract system details from permit description/type
const parsePermitDetails = (lead: any) => {
  const text = `${lead.permitType || ''} ${lead.description || ''}`.toLowerCase();
  const details: { panels?: number; kw?: number; battery?: boolean; systemType?: string } = {};

  // Panel count: "24 Solar PhotoVoltaic (PV) Panel(s)" or "24 panels"
  const panelMatch = text.match(/(\d+)\s*(?:solar\s*)?(?:photo\s*voltaic|pv|solar)?\s*(?:\(pv\))?\s*panel/i)
    || text.match(/(\d+)\s*(?:solar[\s-]*electric|pv)\s*(?:\(pv\))?\s*pa/i);
  if (panelMatch) details.panels = parseInt(panelMatch[1]);

  // System size in kW: "4.94kw" or "4.94 kw" or "4940w"
  const kwMatch = text.match(/([\d.]+)\s*kw(?:p|dc)?(?:\s|,|$)/i);
  if (kwMatch) details.kw = parseFloat(kwMatch[1]);
  if (!details.kw) {
    const wMatch = text.match(/([\d,]+)\s*w(?:att)?(?:s)?(?:\s|,|$)/i);
    if (wMatch) { const w = parseFloat(wMatch[1].replace(',', '')); if (w >= 500) details.kw = Math.round(w / 100) / 10; }
  }

  // Battery/storage mention
  if (text.includes('storage') || text.includes('battery') || text.includes('powerwall') || text.includes('encharge'))
    details.battery = true;
  const storageKwMatch = text.match(/([\d.]+)\s*kw.*(?:storage|battery)/i);
  if (storageKwMatch) details.battery = true;

  // System type
  if (text.includes('roof mount') || text.includes('rooftop') || text.includes('on the roof') || text.includes('roof-mount'))
    details.systemType = 'Roof mount';
  else if (text.includes('ground mount') || text.includes('ground-mount'))
    details.systemType = 'Ground mount';
  else if (text.includes('carport') || text.includes('canop'))
    details.systemType = 'Carport/Canopy';

  return Object.keys(details).length > 0 ? details : null;
};
const getInitials = (user: any) => {
  const full = user.firstName
    ? `${user.firstName} ${user.lastName || ""}`.trim()
    : user.accountOwner || user.name || "";
  const p = full.trim().split(" ").filter(Boolean);
  return p.length >= 2
    ? (p[0][0] + p[p.length - 1][0]).toUpperCase()
    : full.substring(0, 2).toUpperCase() || "?";
};

// ─── WEATHER ──────────────────────────────────────────────────────────────────
const WMO: Record<number, { icon: string; label: string }> = {
  0: { icon: "☀️", label: "Clear" },
  1: { icon: "🌤", label: "Mostly clear" },
  2: { icon: "⛅", label: "Partly cloudy" },
  3: { icon: "☁️", label: "Overcast" },
  45: { icon: "🌫", label: "Fog" },
  51: { icon: "🌦", label: "Light drizzle" },
  61: { icon: "🌧", label: "Light rain" },
  63: { icon: "🌧", label: "Rain" },
  71: { icon: "🌨", label: "Light snow" },
  80: { icon: "🌦", label: "Rain showers" },
  95: { icon: "⛈", label: "Thunderstorm" },
};

const TERRITORY_COORDS: Record<string, { lat: number; lng: number }> = {
  "Edmonton, AB": { lat: 53.5461, lng: -113.4938 },
  "Calgary, AB": { lat: 51.0447, lng: -114.0719 },
  "Vancouver, BC": { lat: 49.2827, lng: -123.1207 },
  "Toronto, ON": { lat: 43.6532, lng: -79.3832 },
  "Phoenix, AZ": { lat: 33.4484, lng: -112.074 },
  "Austin, TX": { lat: 30.2672, lng: -97.7431 },
  "Chicago, IL": { lat: 41.8781, lng: -87.6298 },
  "Las Vegas, NV": { lat: 36.1699, lng: -115.1398 },
  "Boston, MA": { lat: 42.3601, lng: -71.0589 },
  "Mississauga, ON": { lat: 43.589, lng: -79.6441 },
};

function useWeather(territory: string | null, country: string | null) {
  const [weather, setWeather] = useState<any>(null);

    useEffect(() => {
    if (!territory) return;
    const coords =
      TERRITORY_COORDS[territory] ||
      Object.entries(TERRITORY_COORDS).find(([k]) =>
        territory.includes(k.split(",")[0])
      )?.[1];
    if (!coords) return;
    fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lng}&current=temperature_2m,weathercode&temperature_unit=celsius&timezone=auto`
    )
      .then((r: any) => r.json())
      .then((data: any) => {
        const tempC = Math.round(data.current.temperature_2m);
        const tempF = Math.round((tempC * 9) / 5 + 32);
        const code = data.current.weathercode;
        const wmo = WMO[code] || { icon: "🌡", label: "Unknown" };
        setWeather({ tempC, tempF, code, isUS: country === "US", ...wmo });
      })
      .catch((_: any) =>
        setWeather({
          tempC: null,
          tempF: null,
          icon: "🌡",
          label: "Unavailable",
          code: -1,
          isUS: country === "US",
        })
      );
  }, [territory, country]);
  return weather;
}

// ─── TERRITORY PICKER ──────────────────────────────────────────────────────────
// ─── TERRITORY OPTIONS - grouped by country/province/state ───────────────────
const TERRITORY_GROUPS = [
  {
    country: "US", countryLabel: "United States", flag: "🇺🇸",
    regions: [
      { region: "HI", label: "Hawaii",           cities: ["Honolulu"] },
      { region: "NY", label: "New York",          cities: ["New York"] },
      { region: "TX", label: "Texas",             cities: ["Austin","San Antonio","Dallas","McKinney","Frisco","Allen","Pflugerville","Galveston County","Hutto"] },
      { region: "DC", label: "Washington DC",     cities: ["Washington"] },
      { region: "CA", label: "California",        cities: ["Los Angeles County","San Diego","Sacramento","San Francisco","San Jose","Long Beach","Anaheim","Orange County","Escondido","El Cajon","Fallbrook","Spring Valley","Ramona","Lakeside","Valley Center","Vista","Alpine","La Mesa","Rancho Santa Fe","West Sacramento","Elk Grove","Pico Rivera","Pasadena"] },
      { region: "IL", label: "Illinois",          cities: ["Chicago","Des Plaines","Berwyn","Evanston","Oak Park","Skokie"] },
      { region: "LA", label: "Louisiana",         cities: ["New Orleans","Baton Rouge"] },
      { region: "MA", label: "Massachusetts",     cities: ["Boston","Cambridge"] },
      { region: "AZ", label: "Arizona",           cities: ["Scottsdale","Maricopa County","Mesa","Tempe","Peoria"] },
      { region: "MD", label: "Maryland",          cities: ["Silver Spring","Gaithersburg","Rockville","Germantown","Bethesda","Potomac"] },
      { region: "NV", label: "Nevada",            cities: ["Las Vegas"] },
      { region: "FL", label: "Florida",           cities: ["Orlando","Miami","Fort Lauderdale"] },
      { region: "NC", label: "North Carolina",    cities: ["Raleigh","Cary","New Hanover County","Fayetteville"] },
      { region: "VA", label: "Virginia",          cities: ["Virginia Beach","Fairfax County"] },
      { region: "PA", label: "Pennsylvania",      cities: ["Pittsburgh"] },
      { region: "UT", label: "Utah",              cities: ["Salt Lake City"] },
      { region: "OH", label: "Ohio",              cities: ["Cleveland"] },
      { region: "CO", label: "Colorado",          cities: ["Aurora","Fort Collins","Boulder County","Westminster","Timnath"] },
      { region: "WA", label: "Washington",        cities: ["Seattle","Tacoma","Pierce County","Thurston County"] },
      { region: "TN", label: "Tennessee",         cities: ["Nashville","Memphis","Hamilton County"] },
      { region: "NM", label: "New Mexico",        cities: ["Albuquerque","Las Cruces"] },
      { region: "SD", label: "South Dakota",      cities: ["Sioux Falls"] },
      { region: "KS", label: "Kansas",            cities: ["Butler County"] },
      { region: "MN", label: "Minnesota",         cities: ["Saint Paul"] },
      { region: "GA", label: "Georgia",           cities: ["Atlanta","Johns Creek","Forsyth County","Macon","Columbus","McDonough","St. Marys"] },
      { region: "MI", label: "Michigan",          cities: ["Detroit","Genesee County"] },
      { region: "MT", label: "Montana",           cities: ["Kalispell"] },
      { region: "ME", label: "Maine",             cities: ["Searsmont"] },
      { region: "OR", label: "Oregon",            cities: ["Salem"] },
      { region: "IN", label: "Indiana",           cities: ["DeMotte"] },
    ],
  },
  {
    country: "CA", countryLabel: "Canada", flag: "🇨🇦",
    regions: [
      { region: "AB", label: "Alberta",          cities: ["Edmonton","Red Deer","Calgary","Strathcona County","Clearwater County"] },
      { region: "ON", label: "Ontario",           cities: ["Toronto","Kingston","Waterloo","Burlington","Brampton","Niagara Falls","Saugeen Shores","Thunder Bay"] },
      { region: "QC", label: "Quebec",            cities: ["Montreal"] },
      { region: "BC", label: "British Columbia",  cities: ["Vancouver","Victoria","Maple Ridge","Squamish","Columbia Shuswap","Pemberton","Delta"] },
      { region: "NS", label: "Nova Scotia",       cities: ["Halifax", "Dartmouth", "Bedford", "Cole Harbour", "Lower Sackville"] },
    ],
  },
];

// Flat list for backward compat
const CITY_OPTIONS = [
  { label: "All territories", city: null, region: null, flag: "🌎", isAll: true },
  ...TERRITORY_GROUPS.flatMap(cg =>
    cg.regions.flatMap(rg =>
      rg.cities.map(city => ({
        label: `${city}, ${rg.region}`,
        city,
        region: rg.region,
        flag: cg.flag,
        country: cg.country,
        regionLabel: rg.label,
        countryLabel: cg.countryLabel,
      }))
    )
  ),
];

function TerritoryPickerModal({ currentCity, currentRegion, onConfirm, onClose, liveGroups }: { currentCity: any, currentRegion: any, onConfirm: any, onClose: any, liveGroups?: any[] | null }) {
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [selectedRegion, setSelectedRegion] = useState<any>(null);
  const [pickedCity, setPickedCity] = useState<any>(currentCity || null);
  const [pickedRegion, setPickedRegion] = useState<any>(currentRegion || null);
  const [search, setSearch] = useState("");

  // Use live groups from backend if available, else fall back to hardcoded
  const GROUPS = (liveGroups && liveGroups.length > 0) ? liveGroups : TERRITORY_GROUPS;
  const OPTIONS = useMemo(() => [
    { label: "All territories", city: null, region: null, flag: "🌎", isAll: true },
    ...GROUPS.flatMap((cg: any) =>
      cg.regions.flatMap((rg: any) =>
        rg.cities.map((city: any) => ({
          label: `${city}, ${rg.region}`,
          city, region: rg.region, flag: cg.flag,
          country: cg.country, regionLabel: rg.label, countryLabel: cg.countryLabel,
        }))
      )
    ),
  ], [GROUPS]);

  const activeCountry = selectedCountry ? GROUPS.find((g: any) => g.country === selectedCountry) : null;
  const activeRegion = activeCountry && selectedRegion ? activeCountry.regions.find((r: any) => r.region === selectedRegion) : null;

  const searchLower = search.toLowerCase();
  const searchResults = search.length > 1
    ? OPTIONS.filter((o: any) => !o.isAll && (o.label.toLowerCase().includes(searchLower) || o.regionLabel.toLowerCase().includes(searchLower))).slice(0, 12)
    : null;

  const handleConfirm = () => {
    onConfirm(pickedCity, pickedRegion, !pickedCity && !pickedRegion);
    onClose();
  };

  // Breadcrumb navigation
  const goBack = () => {
    if (selectedRegion) setSelectedRegion(null);
    else if (selectedCountry) setSelectedCountry(null);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e: any) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: 440, maxWidth: "100%", boxShadow: "0 24px 64px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", maxHeight: "85vh", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "20px 22px 16px", borderBottom: "1px solid #E5E5EA", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {(selectedCountry || selectedRegion) && (
              <button onClick={goBack} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 16, color: "#FF8800", fontWeight: 700 }}>←</button>
            )}
            <div>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#0F0F1A", fontFamily: "'Outfit',sans-serif" }}>
                {selectedRegion && activeRegion ? activeRegion.label : selectedCountry && activeCountry ? activeCountry.countryLabel : "Select territory"}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "#F0F0F0", border: "none", borderRadius: 8, width: 30, height: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={13} color="#0F0F1A" />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: "12px 18px", borderBottom: "1px solid #E5E5EA", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#F5F5F5", borderRadius: 10, padding: "10px 14px" }}>
            <Search size={14} color="#0F0F1A" />
            <input value={search} onChange={(e: any) => setSearch(e.target.value)} placeholder="Search cities or states..." style={{ flex: 1, border: "none", background: "transparent", fontSize: 14, fontWeight: 500, color: "#0F0F1A", outline: "none" }} />
            {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}><X size={12} color="#0F0F1A" /></button>}
          </div>
        </div>

        {/* List */}
        <div style={{ overflowY: "auto", flex: 1, padding: "8px 14px 14px" }}>

          {/* Search results */}
          {searchResults && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {searchResults.length === 0 && <div style={{ padding: "24px", fontSize: 14, color: "#0F0F1A", textAlign: "center" }}>No results found</div>}
              {searchResults.map((opt: any) => {
                const isSel = pickedCity === opt.city && pickedRegion === opt.region;
                return (
                  <button key={opt.label} onClick={() => { setPickedCity(opt.city); setPickedRegion(opt.region); }}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: isSel ? "2px solid #FF8800" : "2px solid transparent", background: isSel ? "#FFF7ED" : "transparent", cursor: "pointer", textAlign: "left", width: "100%", transition: "background 0.1s" }}
                    onMouseEnter={(e: any) => { if (!isSel) e.currentTarget.style.background = "#F5F5F5"; }}
                    onMouseLeave={(e: any) => { if (!isSel) e.currentTarget.style.background = "transparent"; }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#0F0F1A" }}>{opt.city}</div>
                      <div style={{ fontSize: 13, color: "#54546A" }}>{opt.regionLabel}</div>
                    </div>
                    {isSel && <Check size={16} color="#FF8800" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* Level 1: All territories + Countries */}
          {!searchResults && !selectedCountry && (
            <>
              <button onClick={() => { setPickedCity(null); setPickedRegion(null); }}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 14px", borderRadius: 10, border: !pickedCity && !pickedRegion ? "2px solid #FF8800" : "2px solid #E5E5EA", background: !pickedCity && !pickedRegion ? "#FFF7ED" : "#fff", cursor: "pointer", textAlign: "left", width: "100%", marginBottom: 10 }}>
                <span style={{ fontSize: 22 }}>🌎</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#0F0F1A" }}>All territories</div>
                  <div style={{ fontSize: 13, color: "#54546A" }}>Every permit across the US and Canada</div>
                </div>
                {!pickedCity && !pickedRegion && <Check size={16} color="#FF8800" />}
              </button>

              {GROUPS.map((cg: any) => (
                <button key={cg.country} onClick={() => setSelectedCountry(cg.country)}
                  style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 14px", borderRadius: 12, border: "2px solid #E5E5EA", background: "#fff", cursor: "pointer", textAlign: "left", width: "100%", marginBottom: 8, transition: "all 0.15s" }}
                  onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = "#FF8800"; }}
                  onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = "#E5E5EA"; }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: "#0F0F1A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 900, color: "#fff" }}>{cg.country === "US" ? "US" : "CA"}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: "#0F0F1A", fontFamily: "'Outfit',sans-serif" }}>{cg.countryLabel}</div>
                    <div style={{ fontSize: 13, color: "#54546A" }}>{cg.regions.length} {cg.country === "US" ? "states" : "provinces"} available</div>
                  </div>
                  <ChevronRight size={16} color="#0F0F1A" />
                </button>
              ))}
            </>
          )}

          {/* Level 2: States/Provinces within a country */}
          {!searchResults && selectedCountry && !selectedRegion && activeCountry && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {activeCountry.regions.map((rg: any) => {
                const isSelected = !pickedCity && pickedRegion === rg.region;
                return (
                  <button key={rg.region} onClick={() => setSelectedRegion(rg.region)}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", borderRadius: 10, border: isSelected ? "2px solid #FF8800" : "2px solid transparent", background: isSelected ? "#FFF7ED" : "transparent", cursor: "pointer", textAlign: "left", width: "100%", transition: "background 0.1s" }}
                    onMouseEnter={(e: any) => { if (!isSelected) e.currentTarget.style.background = "#F5F5F5"; }}
                    onMouseLeave={(e: any) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#0F0F1A" }}>{rg.label}</div>
                      <div style={{ fontSize: 13, color: "#54546A" }}>{rg.cities.length} {rg.cities.length === 1 ? "city" : "cities"}</div>
                    </div>
                    <ChevronRight size={14} color="#0F0F1A" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Level 3: Cities within a state */}
          {!searchResults && selectedRegion && activeRegion && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* All of state option */}
              <button onClick={() => { setPickedCity(null); setPickedRegion(activeRegion.region); }}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", borderRadius: 10, border: !pickedCity && pickedRegion === activeRegion.region ? "2px solid #FF8800" : "2px solid #E5E5EA", background: !pickedCity && pickedRegion === activeRegion.region ? "#FFF7ED" : "#fff", cursor: "pointer", textAlign: "left", width: "100%", marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#FF8800" }}>All of {activeRegion.label}</div>
                  <div style={{ fontSize: 13, color: "#54546A" }}>Every city in this {activeCountry?.country === "US" ? "state" : "province"}</div>
                </div>
                {!pickedCity && pickedRegion === activeRegion.region && <Check size={14} color="#FF8800" />}
              </button>
              {activeRegion.cities.map((city: any) => {
                const isSel = pickedCity === city && pickedRegion === activeRegion.region;
                return (
                  <button key={city} onClick={() => { setPickedCity(city); setPickedRegion(activeRegion.region); }}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: isSel ? "2px solid #FF8800" : "2px solid transparent", background: isSel ? "#FFF7ED" : "transparent", cursor: "pointer", textAlign: "left", width: "100%", transition: "background 0.1s" }}
                    onMouseEnter={(e: any) => { if (!isSel) e.currentTarget.style.background = "#F5F5F5"; }}
                    onMouseLeave={(e: any) => { if (!isSel) e.currentTarget.style.background = "transparent"; }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#0F0F1A", flex: 1 }}>{city}</div>
                    {isSel && <Check size={14} color="#FF8800" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 18px", borderTop: "1px solid #E5E5EA", display: "flex", gap: 8, flexShrink: 0 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "12px", background: "#F0F0F0", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, color: "#0F0F1A", cursor: "pointer" }}>Cancel</button>
          <button onClick={handleConfirm} style={{ flex: 2, padding: "12px", background: "#FF8800", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>
            {pickedCity ? `Select ${pickedCity}` : pickedRegion ? `All of ${GROUPS.flatMap((c: any) => c.regions).find((r: any) => r.region === pickedRegion)?.label || pickedRegion}` : "All territories"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── SCORE RING ────────────────────────────────────────────────────────────────
const ScoreRing = ({ score, size = 48 }: { score: any, size?: any }) => {
  const r = (size - 6) / 2,
    circ = 2 * Math.PI * r,
    dash = ((Number(score) || 0) / 10) * circ;
  const tier = scoreTier(score);
  const color = tier === "hot" ? T.danger : tier === "warm" ? T.accent : T.primary;
  return (
    <div
      style={{ position: "relative", width: size, height: size, flexShrink: 0 }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: "rotate(-90deg)" }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={T.border}
          strokeWidth="3"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            fontFamily: "'Outfit',sans-serif",
            fontSize: size >= 48 ? 13 : 11,
            fontWeight: 800,
            color: T.text,
            lineHeight: 1,
          }}
        >
          {fmtScore(score)}
        </div>
        {size >= 40 && (
          <div
            style={{
              fontSize: 7,
              fontWeight: 700,
              marginTop: 1,
              color,
              textTransform: "uppercase",
            }}
          >
            {scoreLabelFor(score)}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── LEAD CARD ─────────────────────────────────────────────────────────────────
const LeadCard = ({ lead, onClick, compact = false, inPipeline = false, onAddToPipeline = null, focusLists = null, onAddToFocus = null }: { lead: any, onClick: any, compact?: any, callCount?: any, inPipeline?: any, onAddToPipeline?: any, focusLists?: any, onAddToFocus?: any }) => {
  const [focusPickerOpen, setFocusPickerOpen] = useState(false);
  const score = getSmartScore(lead),
    seg = SEGMENTS[getSegment(lead)],
    days = getDays(lead),
    val = getVal(lead),
    details = parsePermitDetails(lead);

  // Segment color is the spine of the row: the accent rail, score badge, segment
  // chip and storage pill all draw from it so each lane reads consistently and
  // matches the map door card. (seg.color is canonical in SEGMENTS.)
  const segColor = seg?.color || T.primary;
  const tier = scoreTier(score);
  const isHot = tier === "hot";
  const isWarm = tier === "warm";
  const tierLabel = scoreLabelFor(score); // HOT / WARM / COOL / New
  const scored = score != null;
  const badge = isHot
    ? { bg: segColor, fg: "#fff", sub: "rgba(255,255,255,0.9)" }
    : isWarm
    ? { bg: segColor + "16", fg: segColor, sub: "#1A1A1A" }
    : { bg: "#F1F1EF", fg: "#1A1A1A", sub: "#1A1A1A" };

  // The "why" - first clause of the segment's own cta copy, elevated as the
  // loudest line on the card. Orange when the signal is strong (warm/hot).
  const why = (seg?.cta || "").split(/[.\u2014]/)[0].trim();
  const whyStrong = isHot || isWarm;
  const isFresh = days <= 7;

  const tag: any = { fontSize: 10.5, fontWeight: 700, color: "#1A1A1A", background: "#F1F1EF", padding: "4px 9px", borderRadius: 7, border: "none", whiteSpace: "nowrap" };
  const tagNum: any = { ...tag, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", color: T.text };

  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 15,
        padding: compact ? "12px 20px 12px 16px" : "14px 20px 14px 16px",
        borderBottom: `1px solid ${T.border}`,
        borderLeft: `4px solid ${segColor}`,
        cursor: "pointer", background: T.bg, position: "relative",
        transition: "background 0.1s",
      }}
      onMouseEnter={(e: any) => (e.currentTarget.style.background = T.bgOff)}
      onMouseLeave={(e: any) => (e.currentTarget.style.background = T.bg)}
    >
      {/* Score badge - square, mono number */}
      <div style={{ flexShrink: 0, width: 50, height: 50, borderRadius: 12, background: badge.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: scored ? 18 : 12.5, fontWeight: scored ? 700 : 800, letterSpacing: scored ? 0 : "0.03em", color: badge.fg, lineHeight: 1 }}>{fmtScore(score)}</div>
        {scored && <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 8.5, fontWeight: 800, letterSpacing: "0.08em", color: badge.sub, marginTop: 3 }}>{tierLabel}</div>}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: "-0.01em" }}>
          {cleanAddress(lead.address)}
        </div>
        <div style={{ fontSize: 12.5, color: "#1A1A1A", marginTop: 3, fontWeight: 600, fontFamily: "ui-monospace, 'JetBrains Mono', monospace" }}>
          {lead.city}{lead.city && getState(lead) ? ", " : ""}{getState(lead)}
          <span style={{ color: "#1A1A1A" }}> · </span>
          <span style={{ fontSize: 11.5, color: segColor, fontWeight: 800 }}>{fmtAge(days)}</span>
        </div>

        {why && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 13, fontWeight: 700, color: "#0A0A0A" }}>
            <Zap size={13} color={segColor} fill={segColor} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{why}</span>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 7 }}>
          {seg?.label && <span style={{ fontSize: 10.5, fontWeight: 800, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", color: segColor, background: segColor + "12", border: `1px solid ${segColor}59`, padding: "4px 9px", borderRadius: 7, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: segColor }} />{seg.label}</span>}
          {details?.kw && <span style={tagNum}>{details.kw} kW</span>}
          {details?.panels && <span style={tag}>{details.panels} panels</span>}
          {details?.battery && <span style={{ fontSize: 10.5, fontWeight: 800, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", color: "#fff", background: segColor, padding: "4px 9px", borderRadius: 7, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 5 }}><Battery size={11} color="#fff" strokeWidth={2.4} />Storage</span>}
          {val > 0 && <span style={tagNum}>{fmtVal(val)}</span>}
          {lead.contractorName && (
            <span style={{ fontSize: 10.5, fontWeight: 700, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", color: "#1A1A1A", background: "#fff", border: "1px dashed rgba(10,10,10,0.22)", padding: "4px 9px", borderRadius: 7, display: "inline-flex", alignItems: "center", gap: 5, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <Wrench size={10} color="#1A1A1A" /> vs {lead.contractorName}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 12 }}>
        {(onAddToFocus || onAddToPipeline) && (
          <div style={{ position: "relative" }}>
            <button
              onClick={(e: any) => { e.stopPropagation(); if (inPipeline) return; if (onAddToFocus) setFocusPickerOpen((o) => !o); else if (onAddToPipeline) onAddToPipeline(); }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "8px 13px", border: `1.5px solid ${inPipeline || focusPickerOpen ? T.primary : T.border}`,
                background: inPipeline ? T.primary : T.bg, color: inPipeline ? "#fff" : T.text,
                fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 12.5,
                cursor: inPipeline ? "default" : "pointer", whiteSpace: "nowrap", transition: "all 120ms",
              }}
              onMouseEnter={(e: any) => { if (!inPipeline && !focusPickerOpen) e.currentTarget.style.borderColor = T.ink; }}
              onMouseLeave={(e: any) => { if (!inPipeline && !focusPickerOpen) e.currentTarget.style.borderColor = T.border; }}
            >
              {inPipeline ? <><Check size={13} /> In focus</> : <><Plus size={13} /> Add to focus</>}
            </button>
            {onAddToFocus && focusPickerOpen && !inPipeline && (
              <>
                <div onClick={(e: any) => { e.stopPropagation(); setFocusPickerOpen(false); }} style={{ position: "fixed", inset: 0, zIndex: 59 }} />
                <div onClick={(e: any) => e.stopPropagation()} style={{ position: "absolute", right: 0, top: "100%", marginTop: 6, background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 10, boxShadow: "0 10px 30px rgba(0,0,0,0.16)", zIndex: 60, minWidth: 230, overflow: "hidden", maxHeight: 320, overflowY: "auto" }}>
                  <div style={{ padding: "9px 13px", fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: T.textSub, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", borderBottom: `1px solid ${T.border}` }}>Add to which focus?</div>
                  {(focusLists || []).filter((f: any) => f.status !== "archived").map((f: any) => (
                    <button key={f.focus_list_id} onClick={(e: any) => { e.stopPropagation(); setFocusPickerOpen(false); onAddToFocus(f.focus_list_id); }}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 13px", background: "#fff", border: "none", borderBottom: `1px solid ${T.bgSub}`, fontSize: 13, fontWeight: 700, color: T.text, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}
                      onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgSub}
                      onMouseLeave={(e: any) => e.currentTarget.style.background = "#fff"}>
                      {f.title}
                    </button>
                  ))}
                  <button onClick={(e: any) => { e.stopPropagation(); setFocusPickerOpen(false); onAddToFocus(null); }}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 13px", background: "#fff", border: "none", fontSize: 13, fontWeight: 700, color: "#262B37", cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}
                    onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgSub}
                    onMouseLeave={(e: any) => e.currentTarget.style.background = "#fff"}>
                    Unfiled (no focus)
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        <ChevronRight size={16} color={T.textSub} strokeWidth={1.75} style={{ flexShrink: 0 }} />
      </div>
    </div>
  );
};


// ─── LOGO MARK - Concept A (Helios, ours, B&W) - locked May 24 ───────────────
// Structure: thin outer ring + 12 flame rays + solid central disc.
// Monochrome by design. `color` prop maps to actual CSS colors for backward
// compat with existing call sites. Pass any hex/CSS color to override.
const SolarMark = ({ size = 28, color = "white" }: { size?: any; color?: any }) => {
  const resolved =
    color === "white" ? "#FFFFFF" :
    color === "brand" ? "#0A0A0A" :
    color === "orange" ? "#FF8800" :   // legacy - generally use "brand" instead
    color === "black" ? "#0A0A0A" :
    color || "currentColor";
  const rays = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" fill="none" style={{ flexShrink: 0 }} aria-label="SolarLead">
      <circle cx="30" cy="30" r="27" fill="none" stroke={resolved} strokeWidth="1.5" />
      <g fill={resolved}>
        {rays.map(a => (
          <path key={a} d="M 28 19 Q 27 13, 30 7 Q 33 13, 32 19 Q 30 20, 28 19 Z" transform={`rotate(${a} 30 30)`} />
        ))}
      </g>
      <circle cx="30" cy="30" r="9" fill={resolved} />
    </svg>
  );
};

// ─── AUTH HELPERS ──────────────────────────────────────────────────────────────
function getToken() { return localStorage.getItem("sl_token"); }
function setToken(t: string) { localStorage.setItem("sl_token", t); }
function clearToken() { localStorage.removeItem("sl_token"); localStorage.removeItem("siq_entered"); }
function authHeaders(): Record<string, string> { const t = getToken(); return t ? { Authorization: `Bearer ${t}` } : {}; }

// ─── FETCH INTERCEPTOR - auto-attach Bearer token to backend requests ─────────
// Runs once at module load. Wraps window.fetch so EVERY call to API_URL gets
// the auth header without each fetch site needing to remember it. After the
// P0 security lockdown, backend endpoints (/api/leads, /api/markets, /api/ask
// etc.) require a valid JWT - this ensures the client always sends one.
//
// Behavior:
//   • Requests to API_URL → auto-inject Authorization header (unless caller
//     explicitly set one, in which case caller wins).
//   • Other origins (third-party APIs, CDN, etc.) → pass through unchanged.
//   • 401 response → token is dead (expired or revoked). Clear it and reload
//     so the user lands on the splash/login page instead of seeing broken
//     loading states.
//   • 403 with PLAN_REQUIRED → subscription lapsed mid-session. Re-fetch
//     /api/auth/me so the App's existing paywall gate catches it on next
//     render. (Hard-reload would lose user's in-progress work.)
if (typeof window !== "undefined" && !(window as any).__slFetchPatched) {
  const _origFetch = window.fetch.bind(window);
  (window as any).__slFetchPatched = true;
  window.fetch = async (input: any, init: any = {}) => {
    const url = typeof input === "string" ? input : (input?.url || "");
    const isApiCall = typeof API_URL === "string" && url.startsWith(API_URL);
    if (isApiCall) {
      const token = getToken();
      if (token) {
        // Spread caller's headers AFTER ours so explicit auth wins
        init = { ...init, headers: { Authorization: `Bearer ${token}`, ...(init.headers || {}) } };
      }
    }
    const res = await _origFetch(input, init);
    if (isApiCall && res.status === 401) {
      // Token is dead (or never existed - incognito / logged-out user). Don't
      // surface broken UI, but ALSO don't reload-loop. The original guard
      // checked window.location.pathname for "/auth", but the auth views live
      // at "/" with query params (?login=1, ?signup=1, ?reset=TOKEN), so the
      // guard never fired and any unauthenticated API call triggered an
      // infinite reload (background dashboard fetches → 401 → reload → repeat).
      //
      // New guard: if there's no token at all, the user is already in the
      // logged-out state - clearing again and reloading does nothing useful.
      // Only reload when we HAD a token (mid-session expiry) so the UI can
      // bounce them back to login cleanly.
      const hadToken = !!getToken();
      clearToken();
      if (hadToken) {
        try { window.location.reload(); } catch {}
      }
    }
    return res;
  };
}

// ─── SHARED STYLE BLOCK (injected once per page) ─────────────────────────────
const SL_GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
  html { width: 100%; height: 100%; overflow: hidden; overscroll-behavior: none; background: #09090F; }
  body { font-family: 'DM Sans', sans-serif; background: #fff; -webkit-font-smoothing: antialiased; margin: 0; padding: 0; width: 100%; height: 100%; height: 100dvh; overflow: hidden; overscroll-behavior: none; position: fixed; inset: 0; }
  #root { width: 100%; height: 100%; height: 100dvh; overflow: hidden; overscroll-behavior: none; }
  * { box-sizing: border-box; }
  /* Inner scrollables contain their own bounce - no propagation to body */
  *[style*="overflow-y: auto"], *[style*="overflowY: auto"], *[style*="overflow: auto"] { overscroll-behavior: contain; -webkit-overflow-scrolling: touch; }
  ::selection { background: rgba(255,136,0,0.2); }
  @keyframes slDrawerFade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slDrawerSlide { from { transform: translateX(32px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  @keyframes slFade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slModalPop { from { transform: scale(0.96) translateY(8px); opacity: 0; } to { transform: scale(1) translateY(0); opacity: 1; } }
`;

// ─── BRAND BAR (reusable logo row) ───────────────────────────────────────────
// Monochrome lockup - matches marketing solarlead.io Nav. Mark + wordmark in
// a single color, weight contrast (700 vs 900) carries "SolarLead" not color.
const BrandLogo = ({ dark = false, size = "default" }: { dark?: boolean; size?: string }) => {
  const color = dark ? "#FFFFFF" : "#0A0A0A";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: size === "large" ? 12 : 10 }}>
      <SolarMark size={size === "large" ? 32 : 26} color={color} />
      <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: size === "large" ? 24 : 18, fontWeight: 700, color, letterSpacing: "-0.02em" }}>
        Solar<span style={{ fontWeight: 900 }}>Lead</span>
      </span>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// ║  AUTH PAGES - Light split-panel design matching the 2026 brand              ║
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// ║  REDESIGNED AUTH PAGES - Light theme matching marketing site                ║
// ║  LoginPage, SignupPage, ForgotPasswordPage, ResetPasswordPage               ║
// ║  share AUTH_STYLES + AuthBrandPanel. Logic identical to previous version.   ║
// ═══════════════════════════════════════════════════════════════════════════════

// ─── SHARED AUTH STYLES (inject once) ─────────────────────────────────────────
const AUTH_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@600;700;800;900&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600;700&display=swap');

  /* Auth pages: undo body scroll-lock from SL_GLOBAL_STYLES (which exists for the main app's iOS rubber-band fix). */
  html { overflow: auto !important; height: auto !important; position: static !important; }
  body { overflow: auto !important; overflow-x: hidden !important; height: auto !important; min-height: 100vh; position: static !important; inset: auto !important; }
  #root { overflow: visible !important; height: auto !important; min-height: 100vh; }

  @keyframes authFadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  .auth-fade { animation: authFadeUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) both; }
  .auth-fade-2 { animation: authFadeUp 0.6s 0.1s cubic-bezier(0.22, 1, 0.36, 1) both; }
  .auth-fade-3 { animation: authFadeUp 0.6s 0.2s cubic-bezier(0.22, 1, 0.36, 1) both; }

  .auth-display {
    font-family: 'Outfit', system-ui, sans-serif;
    font-weight: 900;
    letter-spacing: -0.03em;
    -webkit-text-stroke: 0.02em currentColor;
  }
  .auth-body { font-family: 'Inter', system-ui, sans-serif; color: #262B37; }

  .auth-eyebrow {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #FF8800;
  }

  .auth-input {
    width: 100%; padding: 14px 16px; border-radius: 11px;
    border: 1.5px solid #D4D8DF; font-size: 15px;
    background: #FFFFFF; color: #0A0A0B;
    font-family: 'Inter', system-ui, sans-serif; font-weight: 500;
    transition: all 0.15s;
  }
  .auth-input::placeholder { color: #54546A; font-weight: 400; }
  .auth-input:focus { outline: none; border-color: #FF8800; box-shadow: 0 0 0 3px rgba(255,136,0,0.18); background: #FFFFFF; }

  .auth-label { display: block; font-size: 12px; font-weight: 800; color: #0A0A0B; margin-bottom: 8px; font-family: 'Inter', sans-serif; letter-spacing: 0.02em; }

  .auth-btn {
    width: 100%; padding: 16px; border-radius: 11px; background: #FF8800;
    color: #FFFFFF; border: 2px solid #FF8800; font-size: 15px; font-weight: 800;
    cursor: pointer; font-family: 'Outfit', system-ui, sans-serif;
    transition: all 0.18s cubic-bezier(0.22, 1, 0.36, 1); letter-spacing: -0.01em;
    display: flex; align-items: center; justify-content: center; gap: 10px;
  }
  .auth-btn:hover:not(:disabled) { background: #FF5500; border-color: #FF5500; transform: translateY(-1px); box-shadow: 0 8px 28px rgba(255,136,0,0.35); }
  .auth-btn:disabled { opacity: 0.55; cursor: wait; }

  .auth-link { background: none; border: none; cursor: pointer; color: #FF8800; font-weight: 800; font-size: 14px; font-family: 'Inter', sans-serif; padding: 0; }
  .auth-link:hover { color: #FF5500; text-decoration: underline; }

  .auth-back { display: inline-flex; align-items: center; gap: 9px; background: none; border: none; cursor: pointer; font-size: 15px; font-weight: 700; color: #0A0A0B; font-family: 'Inter', sans-serif; transition: color 0.15s; padding: 0; letter-spacing: 0.01em; text-decoration: none; }
  .auth-back:hover { color: #FF8800; }

  .auth-checkbox { width: 20px; height: 20px; border-radius: 6px; border: 2px solid #D4D8DF; background: #FFFFFF; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px; cursor: pointer; transition: all 0.15s; }
  .auth-checkbox.checked { background: #FF8800; border-color: #FF8800; }

  @media (max-width: 960px) {
    .auth-split { flex-direction: column !important; }
    .auth-brand-panel { display: none !important; }
    .auth-form-panel { padding: 32px 20px 48px !important; }
  }
  @media (max-width: 390px) {
    .auth-form-panel { padding: 24px 16px 40px !important; }
  }
`;

// ─── SHARED LEFT BRAND PANEL ───────────────────────────────────────────────────
// One panel for all four auth views: same headline system as solarlead.io so the
// hand-off from marketing to app is seamless. Light, no grids, no pills.
function AuthBrandPanel() {
  return (
    <div className="auth-brand-panel" style={{
      minWidth: 460, width: "44%", background: "#F6F7F9",
      display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
      padding: "48px 56px", position: "relative", overflow: "hidden",
      borderRight: "1px solid #E6E8EC",
    }}>
      <div style={{
        position: "absolute", top: -180, right: -180, width: 560, height: 560,
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(circle, rgba(255,136,0,0.10) 0%, transparent 60%)",
      }} />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 460, width: "100%" }}>
        <div style={{ marginBottom: 36 }}>
          <BrandLogo size="large" />
        </div>

        <h1 className="auth-display auth-fade" style={{ fontSize: 46, lineHeight: 1.04, color: "#0A0A0B", marginBottom: 18 }}>
          Stop buying leads.<br />
          <span style={{ color: "#FF8800" }}>Start working permits.</span>
        </h1>

        <p className="auth-fade-2" style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 21, color: "#0A0A0B", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.015em", marginBottom: 16 }}>
          Not a lead list. <span style={{ color: "#FF8800" }}>The buyers themselves.</span>
        </p>

        <p className="auth-fade-2" style={{ fontSize: 15, color: "#262B37", fontWeight: 600, lineHeight: 1.55, marginBottom: 30 }}>
          Every new solar permit the day it&apos;s filed, plus AI that reads your entire market.
        </p>

        <div className="auth-fade-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 28 }}>
          <div>
            <div className="auth-display" style={{ fontSize: 27, color: "#0A0A0B", lineHeight: 1, marginBottom: 5 }}>1M+</div>
            <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, color: "#FF8800", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>Permits indexed</div>
          </div>
          <div>
            <div className="auth-display" style={{ fontSize: 27, color: "#0A0A0B", lineHeight: 1, marginBottom: 5 }}>3,690+</div>
            <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, color: "#FF8800", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>Cities live</div>
          </div>
          <div>
            <div className="auth-display" style={{ fontSize: 27, color: "#0A0A0B", lineHeight: 1, marginBottom: 5 }}>4,300+</div>
            <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, color: "#FF8800", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>Contractors tracked</div>
          </div>
          <div>
            <div className="auth-display" style={{ fontSize: 27, color: "#FF8800", lineHeight: 1, marginBottom: 5 }}>$599/mo</div>
            <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, color: "#FF8800", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>One flat price</div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 18 }}>
          <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, color: "#FF8800", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", lineHeight: 1.7 }}>
            7-day money-back guarantee · cancel anytime · no setup fee
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ║  LOGIN PAGE                                                                ║
// ═══════════════════════════════════════════════════════════════════════════════
function LoginPage({ onLogin, onGoSignup, onBack, onForgot }: { onLogin: (token: string, user: any) => void; onGoSignup: () => void; onBack: () => void; onForgot: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!email || !email.includes("@")) { setError("Enter a valid email"); return; }
    if (!password) { setError("Enter your password"); return; }
    setError(""); setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (!d.success) { setError(d.error || "Login failed"); setLoading(false); return; }
      if (!d.user.isAdmin && (!d.user.plan || d.user.plan === "free")) {
        setError("No active subscription. Please sign up for a plan.");
        setLoading(false); return;
      }
      onLogin(d.token, d.user);
    } catch { setError("Connection error. Try again."); setLoading(false); }
  };

  return (
    <>
      <style>{SL_GLOBAL_STYLES}{AUTH_STYLES}</style>
      <div className="auth-split auth-body" style={{ minHeight: "100vh", display: "flex", background: "#FFFFFF" }}>

        <AuthBrandPanel />

        {/* ── RIGHT: Form ── */}
        <div className="auth-form-panel" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 56px", background: "#FFFFFF" }}>
          <div style={{ width: "100%", maxWidth: 420 }}>
            <a href="https://solarlead.io" className="auth-back auth-fade">
              <ArrowLeft size={16} /> Back to home
            </a>

            <div className="auth-fade" style={{ marginTop: 40 }}>
              <div className="auth-eyebrow" style={{ marginBottom: 14 }}>Account access</div>
              <h2 className="auth-display" style={{ fontSize: 40, color: "#0A0A0B", lineHeight: 1.04, marginBottom: 12 }}>Welcome back.</h2>
              <p style={{ fontSize: 16, color: "#262B37", fontWeight: 600, marginBottom: 36 }}>
                Sign in to your SolarLead account.
              </p>
            </div>

            <div className="auth-fade-2">
              {error && <div style={{ background: "#FEF3F2", border: "1px solid #FECDCA", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#D92D20", fontWeight: 600, marginBottom: 20 }}>{error}</div>}

              <label className="auth-label">Email</label>
              <input className="auth-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" type="email"
                style={{ marginBottom: 22 }}
                onKeyDown={e => e.key === "Enter" && submit()} />

              <label className="auth-label">Password</label>
              <div style={{ position: "relative", marginBottom: 10 }}>
                <input className="auth-input" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" type={showPw ? "text" : "password"}
                  style={{ paddingRight: 44 }}
                  onKeyDown={e => e.key === "Enter" && submit()} />
                <button onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#54546A" }}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 24 }}>
                <button onClick={onForgot} className="auth-link" style={{ fontSize: 13 }}>Forgot password?</button>
              </div>

              <button className="auth-btn" onClick={submit} disabled={loading}>
                {loading ? "Signing in..." : "Sign in"}
                {!loading && <ArrowRight size={15} strokeWidth={2.5} />}
              </button>
            </div>

            <div style={{ textAlign: "center", marginTop: 28, fontSize: 14, color: "#262B37", fontWeight: 600 }}>
              No account yet?{" "}
              <button onClick={onGoSignup} className="auth-link">Create one</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ║  SIGNUP PAGE                                                               ║
// ═══════════════════════════════════════════════════════════════════════════════
function SignupPage({ onSignupSuccess, onGoLogin, onBack }: { onSignupSuccess: (token: string, user: any) => void; onGoLogin: () => void; onBack: () => void }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!firstName || !lastName) { setError("First and last name required"); return; }
    if (!email || !email.includes("@")) { setError("Enter a valid email"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    if (!agreed) { setError("You must agree to the Terms of Service"); return; }
    setError(""); setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/signup`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, firstName, lastName, company }),
      });
      const d = await r.json();
      if (!d.success) { setError(d.error || "Signup failed"); setLoading(false); return; }
      setToken(d.token);
      const stripeR = await fetch(`${API_URL}/api/stripe/create-subscription-checkout`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${d.token}` },
        body: JSON.stringify({ userId: d.user.id }),
      });
      const stripeD = await stripeR.json();
      if (stripeD.success && stripeD.url) {
        window.location.href = stripeD.url;
      } else {
        setError(stripeD.error || "Payment setup failed. Please try again.");
        setLoading(false);
      }
    } catch { setError("Connection error. Try again."); setLoading(false); }
  };

  return (
    <>
      <style>{SL_GLOBAL_STYLES}{AUTH_STYLES}</style>
      <div className="auth-split auth-body" style={{ minHeight: "100vh", display: "flex", background: "#FFFFFF" }}>

        <AuthBrandPanel />

        {/* ── RIGHT: Form - vertically centered, compact ── */}
        <div className="auth-form-panel" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 48px", background: "#FFFFFF", minHeight: "100vh" }}>
          <div style={{ width: "100%", maxWidth: 420 }}>
            <a href="https://solarlead.io" className="auth-back auth-fade" style={{ marginBottom: 0 }}>
              <ArrowLeft size={16} /> Back
            </a>

            <div className="auth-fade" style={{ marginTop: 16 }}>
              <h2 className="auth-display" style={{ fontSize: 32, color: "#0A0A0B", lineHeight: 1.04, marginBottom: 16 }}>Get started.</h2>

              {/* Plan summary - compact */}
              <div style={{ background: "#FFF4E8", border: "1.5px solid #FFD9B0", borderRadius: 11, padding: "10px 14px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div className="auth-display" style={{ fontSize: 14, color: "#0A0A0B", letterSpacing: "-0.01em" }}>SolarLead Pro</div>
                  <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 9, color: "#FF8800", marginTop: 2, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase" }}>
                    Everything included
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                  <span className="auth-display" style={{ fontSize: 26, color: "#FF8800", lineHeight: 1 }}>$599</span>
                  <span style={{ fontSize: 11, color: "#0A0A0B", fontWeight: 700 }}>/mo</span>
                </div>
              </div>

              {error && <div style={{ background: "#FEF3F2", border: "1px solid #FECDCA", borderRadius: 10, padding: "8px 11px", fontSize: 12, color: "#D92D20", fontWeight: 600, marginBottom: 12 }}>{error}</div>}

              {/* Name row */}
              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label className="auth-label" style={{ marginBottom: 4 }}>First name</label>
                  <input className="auth-input" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Jane" />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="auth-label" style={{ marginBottom: 4 }}>Last name</label>
                  <input className="auth-input" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Doe" />
                </div>
              </div>

              {/* Company + Email row */}
              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label className="auth-label" style={{ marginBottom: 4 }}>Company</label>
                  <input className="auth-input" value={company} onChange={e => setCompany(e.target.value)} placeholder="Solar Inc" />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="auth-label" style={{ marginBottom: 4 }}>Email</label>
                  <input className="auth-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" type="email" />
                </div>
              </div>

              {/* Password + Confirm row */}
              <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label className="auth-label" style={{ marginBottom: 4 }}>Password</label>
                  <div style={{ position: "relative" }}>
                    <input className="auth-input" value={password} onChange={e => setPassword(e.target.value)} placeholder="6+ characters" type={showPw ? "text" : "password"}
                      style={{ paddingRight: 36 }} />
                    <button onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#54546A", padding: 0 }}>
                      {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="auth-label" style={{ marginBottom: 4 }}>Confirm</label>
                  <input className="auth-input" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Re-enter" type="password" />
                </div>
              </div>

              {/* Terms */}
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14, cursor: "pointer" }}>
                <div onClick={() => setAgreed(!agreed)} className={"auth-checkbox" + (agreed ? " checked" : "")}>
                  {agreed && <Check size={12} color="#FFFFFF" strokeWidth={3} />}
                </div>
                <span style={{ fontSize: 12, color: "#262B37", lineHeight: 1.5, fontWeight: 600 }}>
                  I agree to the <a href="https://solarlead.io/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#FF8800", fontWeight: 700, textDecoration: "underline" }}>Terms of Service</a> and <a href="https://solarlead.io/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#FF8800", fontWeight: 700, textDecoration: "underline" }}>Privacy Policy</a>
                </span>
              </label>

              <button className="auth-btn" onClick={submit} disabled={loading}>
                {loading ? "Setting up..." : "Create account & subscribe"}
                {!loading && <ArrowRight size={15} strokeWidth={2.5} />}
              </button>

              <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10, color: "#FF8800", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", textAlign: "center", marginTop: 10 }}>
                $599/mo · cancel anytime · no setup fee
              </div>
            </div>

            <div style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: "#262B37", fontWeight: 600 }}>
              Already have an account?{" "}
              <button onClick={onGoLogin} className="auth-link">Sign in</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ║  TRIAL ONBOARDING  -  guided free-trial signup                              ║
// ║                                                                              ║
// ║  Reached at app.solarlead.io/?trial=1 (and from the marketing CTA). A short ║
// ║  guided flow: territory -> goal -> team -> account. On submit it calls       ║
// ║  POST /api/auth/start-trial (no Stripe), seeds a starter focus list via      ║
// ║  /api/focus/ai-build + /api/focus, then hands off to handleTrialComplete     ║
// ║  which drops the rep onto the dashboard scoped to their city.                ║
// ═══════════════════════════════════════════════════════════════════════════════
const TRIAL_GOALS = [
  { key: "installers",  label: "New installs",       sub: "Just-permitted homes, 0 to 30 days old",              word: "new solar installs from the last 30 days" },
  { key: "cleaning",    label: "Service & cleaning", sub: "1 to 2 years in, the first service window nobody works", word: "solar maintenance and cleaning for 1 to 2 year old systems" },
  { key: "battery",     label: "Battery & storage",  sub: "2 to 5 year systems, the peak window to add storage",  word: "battery storage upsell for 2 to 5 year old systems" },
  { key: "upgrade",     label: "System upgrade",     sub: "5 to 10 years in, warranties lapsing and hardware aging", word: "solar system upgrade for 5 to 10 year old systems" },
  { key: "replacement", label: "Replacement",        sub: "10+ years, loan paid off, your biggest-ticket deals",  word: "full system replacement for systems over 10 years old" },
];
const TRIAL_TEAMS = ["Just me", "2-5 reps", "6-20 reps", "20+ reps"];

function TrialOnboarding({ onComplete, onGoLogin }: { onComplete: (token: string, user: any, scope: { city: string; region: string }) => void; onGoLogin: () => void }) {
  const [step, setStep] = useState(0);
  const [regionName, setRegionName] = useState("");
  const [city, setCity] = useState("");
  const [goal, setGoal] = useState<string>("");
  const [teamSize, setTeamSize] = useState<string>("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");

  const regionCode = TERRITORY_NAME_TO_CODE[regionName] || "";
  const goalDef = TRIAL_GOALS.find((g) => g.key === goal) || null;
  const TOTAL = 4;

  const next = () => {
    setError("");
    if (step === 1 && !regionName) { setError("Pick the state or province you sell in."); return; }
    if (step === 2 && !goal) { setError("Pick what you want to chase first."); return; }
    setStep((s) => Math.min(s + 1, TOTAL));
  };
  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async () => {
    if (!firstName || !lastName) { setError("First and last name required"); return; }
    if (!email || !email.includes("@")) { setError("Enter a valid email"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setError(""); setLoading(true); setLoadingMsg("Creating your account...");
    try {
      const onboarding = { region: regionName, regionCode, city: city.trim(), goal, teamSize };
      const r = await fetch(`${API_URL}/api/auth/start-trial`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, firstName, lastName, company, onboarding }),
      });
      const d = await r.json();
      if (!d.success) { setError(d.error || "Could not start your trial."); setLoading(false); return; }
      setToken(d.token);

      // Seed a starter worklist. Best-effort: a hiccup here must never block
      // entry, so the rep always lands in the app even if seeding fails.
      try {
        setLoadingMsg(`Pulling live permits in ${city.trim() || regionName}...`);
        const word = goalDef ? goalDef.word : "new solar installs";
        const prompt = city.trim() ? `${word} in ${city.trim()}, ${regionCode}` : `${word} in ${regionName}`;
        const ab = await fetch(`${API_URL}/api/focus/ai-build`, {
          method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${d.token}` },
          body: JSON.stringify({ prompt }),
        });
        const abd = await ab.json();
        if (abd.success && !abd.empty && (abd.leadIds || []).length) {
          setLoadingMsg("Building your first worklist...");
          await fetch(`${API_URL}/api/focus`, {
            method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${d.token}` },
            body: JSON.stringify({
              title: (abd.parsed && abd.parsed.title) || `${city.trim() || regionName} starter`,
              directive: (abd.parsed && abd.parsed.directive) || undefined,
              scopeType: (abd.parsed && abd.parsed.scopeType) || undefined,
              scopeValue: (abd.parsed && abd.parsed.scopeValue) || undefined,
              leadIds: abd.leadIds || [],
            }),
          });
        }
      } catch (seedErr) { /* non-fatal: enter the app regardless */ }

      onComplete(d.token, d.user, { city: city.trim(), region: regionCode });
    } catch (e) { setError("Connection error. Try again."); setLoading(false); }
  };

  // Full-screen "magic moment" while the trial provisions + seeds.
  if (loading) {
    return (
      <>
        <style>{SL_GLOBAL_STYLES}{AUTH_STYLES}</style>
        <style>{`@keyframes sl-trial-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#FFFFFF", flexDirection: "column", gap: 22, padding: 24 }}>
          <div style={{ width: 46, height: 46, border: "3px solid #FFE3C2", borderTopColor: "#FF8800", borderRadius: "50%", animation: "sl-trial-spin 0.8s linear infinite" }} />
          <div className="auth-display" style={{ fontSize: 22, color: "#0A0A0B", textAlign: "center" }}>{loadingMsg || "Setting up..."}</div>
          <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, color: "#FF8800", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>Reading your market</div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{SL_GLOBAL_STYLES}{AUTH_STYLES}</style>
      <div className="auth-split auth-body" style={{ minHeight: "100vh", display: "flex", background: "#FFFFFF" }}>
        <AuthBrandPanel />
        <div className="auth-form-panel" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 48px", background: "#FFFFFF", minHeight: "100vh" }}>
          <div style={{ width: "100%", maxWidth: 440 }}>

            <div style={{ display: "flex", gap: 6, marginBottom: 22 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? "#FF8800" : "#EFE9E0" }} />
              ))}
            </div>

            {error && <div style={{ background: "#FEF3F2", border: "1px solid #FECDCA", borderRadius: 10, padding: "8px 11px", fontSize: 12, color: "#D92D20", fontWeight: 600, marginBottom: 14 }}>{error}</div>}

            {step === 0 && (
              <div className="auth-fade">
                <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, color: "#FF8800", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 12 }}>3 days free, no card</div>
                <h2 className="auth-display" style={{ fontSize: 34, color: "#0A0A0B", lineHeight: 1.05, marginBottom: 14 }}>See who is going solar in your city.</h2>
                <p style={{ fontSize: 15, color: "#262B37", fontWeight: 600, lineHeight: 1.55, marginBottom: 26 }}>
                  Pick your territory and what you chase. We will pull live permits and build your first worklist before you even sign in.
                </p>
                <button className="auth-btn" onClick={() => setStep(1)}>Start free <ArrowRight size={15} strokeWidth={2.5} /></button>
                <div style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: "#262B37", fontWeight: 600 }}>
                  Already have an account? <button onClick={onGoLogin} className="auth-link">Sign in</button>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="auth-fade">
                <h2 className="auth-display" style={{ fontSize: 30, color: "#0A0A0B", lineHeight: 1.05, marginBottom: 14 }}>Where do you sell?</h2>
                <label className="auth-label" style={{ marginBottom: 4 }}>State / province</label>
                <select className="auth-input" value={regionName} onChange={(e) => setRegionName(e.target.value)} style={{ marginBottom: 12 }}>
                  <option value="">Select...</option>
                  {FIRST_RUN_REGIONS.map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
                </select>
                <label className="auth-label" style={{ marginBottom: 4 }}>City (optional)</label>
                <input className="auth-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Austin" style={{ marginBottom: 18 }} />
                <div style={{ display: "flex", gap: 10 }}>
                  <button className="auth-btn" onClick={back} style={{ flex: "0 0 auto", width: 110, background: "#FFFFFF", color: "#0A0A0B", border: "1.5px solid rgba(10,10,10,0.18)" }}>Back</button>
                  <button className="auth-btn" onClick={next} style={{ flex: 1 }}>Continue <ArrowRight size={15} strokeWidth={2.5} /></button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="auth-fade">
                <h2 className="auth-display" style={{ fontSize: 30, color: "#0A0A0B", lineHeight: 1.05, marginBottom: 16 }}>What do you chase?</h2>
                <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
                  {TRIAL_GOALS.map((g) => (
                    <button key={g.key} onClick={() => setGoal(g.key)} style={{
                      textAlign: "left", padding: "12px 14px", borderRadius: 11, cursor: "pointer",
                      background: goal === g.key ? "#FFF4E8" : "#FFFFFF",
                      border: goal === g.key ? "1.5px solid #FF8800" : "1.5px solid rgba(10,10,10,0.14)",
                    }}>
                      <div className="auth-display" style={{ fontSize: 15, color: "#0A0A0B" }}>{g.label}</div>
                      <div style={{ fontSize: 12, color: "#262B37", fontWeight: 600, marginTop: 2 }}>{g.sub}</div>
                    </button>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button className="auth-btn" onClick={back} style={{ flex: "0 0 auto", width: 110, background: "#FFFFFF", color: "#0A0A0B", border: "1.5px solid rgba(10,10,10,0.18)" }}>Back</button>
                  <button className="auth-btn" onClick={next} style={{ flex: 1 }}>Continue <ArrowRight size={15} strokeWidth={2.5} /></button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="auth-fade">
                <h2 className="auth-display" style={{ fontSize: 30, color: "#0A0A0B", lineHeight: 1.05, marginBottom: 16 }}>How big is your team?</h2>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
                  {TRIAL_TEAMS.map((t) => (
                    <button key={t} onClick={() => setTeamSize(t)} style={{
                      padding: "14px 12px", borderRadius: 11, cursor: "pointer",
                      background: teamSize === t ? "#FFF4E8" : "#FFFFFF",
                      border: teamSize === t ? "1.5px solid #FF8800" : "1.5px solid rgba(10,10,10,0.14)",
                      fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 14, color: "#0A0A0B",
                    }}>{t}</button>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button className="auth-btn" onClick={back} style={{ flex: "0 0 auto", width: 110, background: "#FFFFFF", color: "#0A0A0B", border: "1.5px solid rgba(10,10,10,0.18)" }}>Back</button>
                  <button className="auth-btn" onClick={next} style={{ flex: 1 }}>Continue <ArrowRight size={15} strokeWidth={2.5} /></button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="auth-fade">
                <h2 className="auth-display" style={{ fontSize: 30, color: "#0A0A0B", lineHeight: 1.05, marginBottom: 6 }}>Create your login.</h2>
                <p style={{ fontSize: 13, color: "#262B37", fontWeight: 600, marginBottom: 16 }}>Your trial saves everything you do. Upgrade anytime, nothing is lost.</p>
                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label className="auth-label" style={{ marginBottom: 4 }}>First name</label>
                    <input className="auth-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jane" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="auth-label" style={{ marginBottom: 4 }}>Last name</label>
                    <input className="auth-input" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" />
                  </div>
                </div>
                <label className="auth-label" style={{ marginBottom: 4 }}>Company</label>
                <input className="auth-input" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Solar Inc" style={{ marginBottom: 10 }} />
                <label className="auth-label" style={{ marginBottom: 4 }}>Email</label>
                <input className="auth-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" type="email" style={{ marginBottom: 10 }} />
                <label className="auth-label" style={{ marginBottom: 4 }}>Password</label>
                <input className="auth-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6+ characters" type="password" style={{ marginBottom: 18 }} />
                <div style={{ display: "flex", gap: 10 }}>
                  <button className="auth-btn" onClick={back} style={{ flex: "0 0 auto", width: 110, background: "#FFFFFF", color: "#0A0A0B", border: "1.5px solid rgba(10,10,10,0.18)" }}>Back</button>
                  <button className="auth-btn" onClick={submit} style={{ flex: 1 }}>Start my free trial <ArrowRight size={15} strokeWidth={2.5} /></button>
                </div>
                <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10, color: "#FF8800", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", textAlign: "center", marginTop: 12 }}>
                  3 days free, no card required, cancel anytime
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}



// ═══════════════════════════════════════════════════════════════════════════════
// ║  FORGOT PASSWORD PAGE                                                       ║
// ║                                                                              ║
// ║  User enters email → POST /api/auth/request-password-reset → shows the      ║
// ║  "check your inbox" confirmation regardless of whether the email exists     ║
// ║  (the backend always returns success, anti-enumeration). User clicks the    ║
// ║  link in the email → lands back at app.solarlead.io/?reset=TOKEN → main     ║
// ║  App routes to ResetPasswordPage.                                            ║
// ═══════════════════════════════════════════════════════════════════════════════
function ForgotPasswordPage({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!email || !email.includes("@")) { setError("Enter a valid email"); return; }
    setError(""); setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/request-password-reset`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const d = await r.json();
      // Backend always returns success, so we treat any non-network failure the same.
      if (d.success !== false) setSent(true);
      else setError(d.error || "Something went wrong. Try again.");
    } catch {
      setError("Connection error. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{SL_GLOBAL_STYLES}{AUTH_STYLES}</style>
      <div className="auth-split auth-body" style={{ minHeight: "100vh", display: "flex", background: "#FFFFFF" }}>

        <AuthBrandPanel />

        {/* RIGHT form */}
        <div className="auth-form-panel" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 56px", background: "#FFFFFF" }}>
          <div style={{ width: "100%", maxWidth: 420 }}>
            <button onClick={onBack} className="auth-back auth-fade" style={{ cursor: "pointer" }}>
              <ArrowLeft size={16} /> Back to sign in
            </button>

            {!sent ? (
              <>
                <div className="auth-fade" style={{ marginTop: 40 }}>
                  <div className="auth-eyebrow" style={{ marginBottom: 14 }}>Password reset</div>
                  <h2 className="auth-display" style={{ fontSize: 40, color: "#0A0A0B", lineHeight: 1.04, marginBottom: 12 }}>Forgot password.</h2>
                  <p style={{ fontSize: 16, color: "#262B37", fontWeight: 600, marginBottom: 36 }}>
                    Enter your email and we&apos;ll send a reset link.
                  </p>
                </div>

                <div className="auth-fade-2">
                  {error && <div style={{ background: "#FEF3F2", border: "1px solid #FECDCA", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#D92D20", fontWeight: 600, marginBottom: 20 }}>{error}</div>}

                  <label className="auth-label">Email</label>
                  <input className="auth-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" type="email"
                    style={{ marginBottom: 32 }}
                    onKeyDown={e => e.key === "Enter" && submit()} />

                  <button className="auth-btn" onClick={submit} disabled={loading}>
                    {loading ? "Sending..." : "Send reset link"}
                    {!loading && <ArrowRight size={15} strokeWidth={2.5} />}
                  </button>
                </div>

                <div style={{ textAlign: "center", marginTop: 28, fontSize: 14, color: "#262B37", fontWeight: 600 }}>
                  Remembered it?{" "}
                  <button onClick={onBack} className="auth-link">Back to sign in</button>
                </div>
              </>
            ) : (
              <>
                <div className="auth-fade" style={{ marginTop: 40 }}>
                  <div className="auth-eyebrow" style={{ marginBottom: 14 }}>Check your inbox</div>
                  <h2 className="auth-display" style={{ fontSize: 40, color: "#0A0A0B", lineHeight: 1.04, marginBottom: 12 }}>Email sent.</h2>
                  <p style={{ fontSize: 16, color: "#262B37", fontWeight: 600, marginBottom: 16, lineHeight: 1.5 }}>
                    If an account exists for <span style={{ color: "#FF8800", fontWeight: 800 }}>{email}</span>, a reset link is on the way.
                  </p>
                  <p style={{ fontSize: 14, color: "#262B37", fontWeight: 500, marginBottom: 36, lineHeight: 1.5 }}>
                    The link expires in 1 hour. Check spam if you don&apos;t see it in a minute or two.
                  </p>
                </div>

                <div className="auth-fade-2">
                  <button className="auth-btn" onClick={onBack}>
                    Back to sign in
                    <ArrowRight size={15} strokeWidth={2.5} />
                  </button>
                </div>

                <div style={{ textAlign: "center", marginTop: 28, fontSize: 13, color: "#262B37", fontWeight: 500 }}>
                  Didn&apos;t get it?{" "}
                  <button onClick={() => { setSent(false); setError(""); }} className="auth-link" style={{ fontSize: 13 }}>Try again</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ║  RESET PASSWORD PAGE                                                        ║
// ║                                                                              ║
// ║  Reached via the link in the password-reset email: ?reset=TOKEN. Reads the  ║
// ║  token from the URL, asks for a new password (with confirmation), submits   ║
// ║  to POST /api/auth/reset-password. On success: clears any local JWT, strips ║
// ║  the reset param from the URL, and routes back to login.                    ║
// ║                                                                              ║
// ║  Token validation happens server-side, so we don't pre-check; we submit     ║
// ║  and let the backend tell us if the token is invalid/expired/used.          ║
// ═══════════════════════════════════════════════════════════════════════════════
function ResetPasswordPage({ onDone }: { onDone: () => void }) {
  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("reset") || "";
  }, []);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!token) { setError("This link is invalid. Request a new one."); return; }
    if (!password || password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setError(""); setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const d = await r.json();
      if (!d.success) {
        setError(d.error || "Could not reset password. Try again.");
        setLoading(false); return;
      }
      // Nuke any stale JWT. The old session is now in an inconsistent state with
      // the new password. User signs back in cleanly.
      clearToken();
      // Strip the ?reset= param so a refresh of "done" view doesn't loop.
      if (typeof window !== "undefined") {
        window.history.replaceState({}, "", window.location.pathname);
      }
      setDone(true);
      setLoading(false);
    } catch {
      setError("Connection error. Try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <style>{SL_GLOBAL_STYLES}{AUTH_STYLES}</style>
      <div className="auth-split auth-body" style={{ minHeight: "100vh", display: "flex", background: "#FFFFFF" }}>

        <AuthBrandPanel />

        {/* RIGHT form */}
        <div className="auth-form-panel" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 56px", background: "#FFFFFF" }}>
          <div style={{ width: "100%", maxWidth: 420 }}>

            {!done ? (
              <>
                <div className="auth-fade" style={{ marginTop: 40 }}>
                  <div className="auth-eyebrow" style={{ marginBottom: 14 }}>Password reset</div>
                  <h2 className="auth-display" style={{ fontSize: 40, color: "#0A0A0B", lineHeight: 1.04, marginBottom: 12 }}>New password.</h2>
                  <p style={{ fontSize: 16, color: "#262B37", fontWeight: 600, marginBottom: 36 }}>
                    Choose a new password for your account.
                  </p>
                </div>

                <div className="auth-fade-2">
                  {error && <div style={{ background: "#FEF3F2", border: "1px solid #FECDCA", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#D92D20", fontWeight: 600, marginBottom: 20 }}>{error}</div>}

                  <label className="auth-label">New password</label>
                  <div style={{ position: "relative", marginBottom: 22 }}>
                    <input className="auth-input" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" type={showPw ? "text" : "password"}
                      style={{ paddingRight: 44 }}
                      onKeyDown={e => e.key === "Enter" && submit()} />
                    <button onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#54546A" }}>
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>

                  <label className="auth-label">Confirm password</label>
                  <input className="auth-input" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Re-enter password" type={showPw ? "text" : "password"}
                    style={{ marginBottom: 32 }}
                    onKeyDown={e => e.key === "Enter" && submit()} />

                  <button className="auth-btn" onClick={submit} disabled={loading || !token}>
                    {loading ? "Saving..." : "Save new password"}
                    {!loading && <ArrowRight size={15} strokeWidth={2.5} />}
                  </button>
                </div>

                {!token && (
                  <div style={{ textAlign: "center", marginTop: 24, fontSize: 13, color: "#D92D20", fontWeight: 600 }}>
                    This link is missing its reset token. Request a fresh email.
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="auth-fade" style={{ marginTop: 40 }}>
                  <div className="auth-eyebrow" style={{ marginBottom: 14 }}>All set</div>
                  <h2 className="auth-display" style={{ fontSize: 40, color: "#0A0A0B", lineHeight: 1.04, marginBottom: 12 }}>Password updated.</h2>
                  <p style={{ fontSize: 16, color: "#262B37", fontWeight: 600, marginBottom: 36 }}>
                    Sign in with your new password to continue.
                  </p>
                </div>

                <div className="auth-fade-2">
                  <button className="auth-btn" onClick={onDone}>
                    Sign in
                    <ArrowRight size={15} strokeWidth={2.5} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}



// ═══════════════════════════════════════════════════════════════════════════════
// ║  PAYWALL PAGE - Gates non-admin, non-pro users from the entire app          ║
// ║                                                                              ║
// ║  Rendered when: authedUser exists, plan === 'free' (or missing), !isAdmin.   ║
// ║  Closes the abuse vector where an abandoned-Stripe-checkout signup would     ║
// ║  retain access to public permit/contractor data via the export CSV.          ║
// ║                                                                              ║
// ║  When user lands here with ?upgrade=success (post-Stripe redirect), polls    ║
// ║  /api/auth/me every 2s for up to 30s waiting for the webhook to flip them    ║
// ║  to plan='pro'. On detection, reloads cleanly into the app.                  ║
// ═══════════════════════════════════════════════════════════════════════════════
function PaywallPage({ user, onSignOut }: { user: any; onSignOut: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [activating, setActivating] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("upgrade") === "success";
  });

  // Poll for plan upgrade after Stripe redirect - webhook may not have fired
  // by the time the browser comes back from Stripe (usually 1-3s lag).
  useEffect(() => {
    if (!activating) return;
    const token = getToken();
    if (!token) { setActivating(false); return; }
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 15; // ~30 seconds at 2s intervals
    const poll = () => {
      if (cancelled) return;
      fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => {
          if (cancelled) return;
          if (d.success && d.user && d.user.plan === "pro") {
            // Plan upgraded - strip the query param and reload cleanly
            window.history.replaceState({}, "", window.location.pathname);
            window.location.reload();
          } else {
            attempts++;
            if (attempts < maxAttempts) setTimeout(poll, 2000);
            else setActivating(false); // Give up; show paywall with a retry option
          }
        })
        .catch(() => {
          attempts++;
          if (!cancelled && attempts < maxAttempts) setTimeout(poll, 2000);
          else if (!cancelled) setActivating(false);
        });
    };
    poll();
    return () => { cancelled = true; };
  }, [activating]);

  const subscribe = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/stripe/create-subscription-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else { alert(data.error || "Could not start checkout. Try again."); setSubmitting(false); }
    } catch (e: any) { alert("Checkout failed: " + e.message); setSubmitting(false); }
  };

  // ── ACTIVATING STATE - post-Stripe redirect, waiting on webhook ──────────
  if (activating) {
    return (
      <>
        <style>{`@keyframes paywall-bar { 0%, 100% { transform: scaleY(0.35); } 50% { transform: scaleY(1); } }`}</style>
        <div style={{ minHeight: "100vh", background: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div style={{ maxWidth: 560, width: "100%", textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, height: 32, marginBottom: 28 }}>
              {[0, 0.15, 0.3, 0.45].map(d => (
                <span key={d} style={{ display: "inline-block", width: 5, height: 32, background: "#FF8800", animation: `paywall-bar 0.9s ease-in-out ${d}s infinite` }} />
              ))}
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12, fontWeight: 700, color: "#FF8800", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 18 }}>
              Activating
            </div>
            <h1 style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 900, lineHeight: 1.04, letterSpacing: "-0.04em", color: "#0A0A0B", margin: 0, marginBottom: 16 }}>
              Welcome to SolarLead Pro.
            </h1>
            <div style={{ fontSize: 15, color: "#262B37", fontWeight: 500, lineHeight: 1.55 }}>
              Confirming your subscription with Stripe. Hang tight, this usually takes a few seconds.
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── PAYWALL STATE - default ─────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#F6F7F9", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 24px" }}>
      <div style={{ maxWidth: 620, width: "100%" }}>

        {/* Brand */}
        <div style={{ marginBottom: 28, display: "flex", justifyContent: "center" }}>
          <BrandLogo size="large" />
        </div>

        {/* Eyebrow */}
        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <span style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase",
            color: "#FF8800",
          }}>
            Almost in
          </span>
        </div>

        {/* Headline */}
        <h1 style={{
          fontFamily: "'Outfit', system-ui, sans-serif",
          fontSize: "clamp(40px, 5.5vw, 64px)", fontWeight: 900, lineHeight: 1.02,
          letterSpacing: "-0.045em", color: "#0A0A0B", margin: 0, marginBottom: 14, textAlign: "center",
        }}>
          One step left.
        </h1>
        <div style={{ fontSize: 15, color: "#262B37", fontWeight: 500, lineHeight: 1.55, maxWidth: 480, margin: "0 auto 32px", textAlign: "center" }}>
          SolarLead Pro is gated. Subscribe to unlock the full intelligence platform: 1,000,000+ permits, AI insights, and contact unlocks.
        </div>

        {/* Plan card */}
        <div style={{ border: "1px solid #E6E8EC", borderRadius: 16, padding: "28px 26px", background: "#FFFFFF", boxShadow: "0 18px 44px -20px rgba(10,10,11,0.18)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18, gap: 12 }}>
            <div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 900, color: "#0A0A0B", letterSpacing: "-0.02em" }}>SolarLead Pro</div>
              <div style={{ fontSize: 12, color: "#262B37", marginTop: 3, fontWeight: 500 }}>Full platform access</div>
            </div>
            <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
              <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 36, fontWeight: 900, color: "#FF8800", letterSpacing: "-1.5px" }}>$599</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#262B37" }}>/mo</span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 22 }}>
            {[
              "1,000,000+ solar permits across the US and Canada",
              "AI-powered market intelligence and daily briefs",
              "10 contact unlocks included per billing cycle",
              "Additional unlocks from $3 each in prepaid packs",
              "All territories, US and Canada",
            ].map(f => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Check size={15} color="#FF8800" strokeWidth={2.5} />
                <span style={{ fontSize: 13.5, color: "#262B37", fontWeight: 500 }}>{f}</span>
              </div>
            ))}
          </div>
          <button onClick={subscribe} disabled={submitting} style={{
            width: "100%", padding: "15px",
            background: submitting ? "#D97006" : "#FF8800",
            border: "none", borderRadius: 11,
            fontSize: 15, fontWeight: 800, color: "#FFFFFF",
            cursor: submitting ? "wait" : "pointer",
            fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.01em",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            transition: "background 120ms ease",
          }}>
            {submitting ? "Opening Stripe..." : "Subscribe · $599/mo"}
            {!submitting && <ArrowRight size={15} strokeWidth={2.5} />}
          </button>
        </div>

        {/* Sign out footer */}
        <div style={{ marginTop: 28, textAlign: "center", fontSize: 12, color: "#262B37", fontWeight: 500 }}>
          Signed in as <span style={{ color: "#0A0A0B", fontWeight: 700 }}>{user?.email || "your account"}</span>
          {" · "}
          <button onClick={onSignOut} style={{ background: "none", border: "none", color: "#FF8800", fontWeight: 700, cursor: "pointer", padding: 0, fontSize: 12, fontFamily: "inherit" }}>
            Sign out
          </button>
        </div>

      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ║  WHY SOLARLEAD PAGE - Full dark theme                                      ║
// ═══════════════════════════════════════════════════════════════════════════════
function WhySolarLeadPage({ onBack, onSignUp }: { onBack: () => void; onSignUp: () => void }) {
  return (
    <>
      <style>{SL_GLOBAL_STYLES}{`
        @keyframes whyUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .why-fade { animation: whyUp 0.6s ease both; }
        .why-card { transition: all 0.2s ease; cursor: default; }
        .why-card:hover { transform: translateY(-3px) !important; border-color: #FF8800 !important; background: rgba(255,255,255,0.12) !important; }
        .why-cta-btn { transition: all 0.15s ease; }
        .why-cta-btn:hover { background: #E07700 !important; transform: translateY(-1px); box-shadow: 0 8px 32px rgba(255,136,0,0.35); }
        .why-back-btn { transition: all 0.15s; }
        .why-back-btn:hover { border-color: rgba(255,255,255,0.3) !important; color: #fff !important; }
        @media (max-width: 768px) {
          .why-hero-wrap { padding: 40px 20px 40px !important; }
          .why-hero-text { font-size: 36px !important; letter-spacing: -1.5px !important; }
          .why-sub-text { font-size: 17px !important; }
          .why-big-num { font-size: 48px !important; }
          .why-section-title { font-size: 30px !important; }
          .why-grid-3 { grid-template-columns: 1fr !important; }
          .why-grid-2 { grid-template-columns: 1fr !important; gap: 16px !important; }
          .why-vs-col { display: none !important; }
          .why-stats-row { flex-direction: column !important; gap: 16px !important; }
          .why-nav-inner { padding: 0 20px !important; }
          .why-section { padding: 48px 20px !important; }
          .why-cta-section { padding: 48px 20px !important; }
        }
        @media (max-width: 390px) {
          .why-hero-text { font-size: 30px !important; }
          .why-hero-wrap { padding: 32px 16px !important; }
          .why-section { padding: 40px 16px !important; }
        }
      `}</style>
      <div style={{ width: "100%", background: "#09090F", color: "#fff", overflowX: "hidden" }}>

        {/* NAV */}
        <nav style={{ height: 64, borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", background: "#09090F", position: "sticky", top: 0, zIndex: 50 }}>
          <div className="why-nav-inner" style={{ width: "100%", maxWidth: "92%", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ cursor: "pointer" }} onClick={onBack}>
              <BrandLogo dark />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={onBack} className="why-back-btn" style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid rgba(255,255,255,0.15)", background: "transparent", fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.8)", cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>
                <ArrowLeft size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />Back
              </button>
              <button onClick={onSignUp} className="why-cta-btn" style={{ padding: "8px 22px", borderRadius: 9, border: "none", background: "#FF8800", fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>
                Get Started
              </button>
            </div>
          </div>
        </nav>

        {/* ═══ HERO ═══ */}
        <div className="why-hero-wrap" style={{ padding: "56px 0 48px", textAlign: "center", maxWidth: 900, margin: "0 auto" }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#FF8800", marginBottom: 20, fontFamily: "'Outfit',sans-serif" }}>The future of solar sales</div>
          <h1 className="why-hero-text" style={{ fontFamily: "'Outfit',sans-serif", fontSize: 54, fontWeight: 900, color: "#fff", letterSpacing: "-2.5px", lineHeight: 1.08, marginBottom: 24 }}>
            Lead lists are dead.<br/>
            <span style={{ color: "#FF8800" }}>Permit intelligence is the edge.</span>
          </h1>
          <p className="why-sub-text" style={{ fontSize: 18, color: "#fff", lineHeight: 1.7, maxWidth: 580, margin: "0 auto" }}>
            Every solar permit filed is a homeowner who already said yes to solar. Stop cold calling. Start closing.
          </p>
        </div>

        {/* ═══ THE NUMBER ═══ */}
        <div style={{ background: "#131320", padding: "48px 0", textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.12)", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <div className="why-big-num" style={{ fontFamily: "'Outfit',sans-serif", fontSize: 72, fontWeight: 900, color: "#fff", letterSpacing: "-4px", lineHeight: 0.9 }}>1,000,000+</div>
            <div style={{ fontSize: 18, color: "#fff", marginTop: 14, fontWeight: 500 }}>solar permits tracked across <span style={{ color: "#FF8800", fontWeight: 700 }}>3,690+ cities</span> in the US and Canada</div>
            <div className="why-stats-row" style={{ display: "flex", justifyContent: "center", gap: 48, marginTop: 36 }}>
              {[
                { num: "Updated daily", sub: "Fresh permits every morning" },
                { num: "US & Canada", sub: "Coast to coast coverage" },
                { num: "AI-powered", sub: "Smart segmentation & insights" },
              ].map(s => (
                <div key={s.num}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#FF8800" }}>{s.num}</div>
                  <div style={{ fontSize: 13, color: "#fff", marginTop: 4, fontWeight: 500 }}>{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ THE PROBLEM ═══ */}
        <div className="why-section" style={{ padding: "56px 0" }}>
          <div style={{ maxWidth: "92%", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <div style={{ display: "inline-block", fontSize: 11, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#FF8800", background: "rgba(255,136,0,0.1)", padding: "6px 16px", borderRadius: 6, marginBottom: 20 }}>The problem</div>
              <h2 className="why-section-title" style={{ fontFamily: "'Outfit',sans-serif", fontSize: 42, fontWeight: 900, color: "#fff", letterSpacing: "-2px", lineHeight: 1.1 }}>
                Your competitors buy lead lists.<br/>
                <span style={{ color: "#FF8800" }}>You see the permits first.</span>
              </h2>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 48px 1fr", gap: 0, alignItems: "stretch", maxWidth: 880, margin: "0 auto" }} className="why-grid-2">
              {/* OLD WAY */}
              <div style={{ padding: "36px 32px", borderRadius: 16, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.18)", textAlign: "center", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "#EF4444" }} />
                <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: 22, fontWeight: 900, color: "#EF4444", marginBottom: 14 }}>The old way is broken</div>
                <div style={{ fontSize: 15, color: "#fff", lineHeight: 1.7 }}>
                  Buy stale lists. Cold call strangers who don't want to hear from you. Compete on price. Watch your team burn out on dead leads.
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 18, justifyContent: "center" }}>
                  {["Stale data", "Cold calls", "Low close rate", "Wasted time"].map(t => (
                    <span key={t} style={{ fontSize: 11, fontWeight: 700, padding: "5px 11px", borderRadius: 6, background: "rgba(239,68,68,0.12)", color: "#f87171" }}>{t}</span>
                  ))}
                </div>
              </div>

              {/* VS */}
              <div className="why-vs-col" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: 14, fontWeight: 900, color: "rgba(255,255,255,0.2)", transform: "rotate(-90deg)", whiteSpace: "nowrap", letterSpacing: "2px" }}>VS</div>
              </div>

              {/* SOLARLEAD WAY */}
              <div style={{ padding: "36px 32px", borderRadius: 16, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.18)", textAlign: "center", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "#FF8800" }} />
                <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: 22, fontWeight: 900, color: "#FF8800", marginBottom: 14 }}>The SolarLead way</div>
                <div style={{ fontSize: 15, color: "#fff", lineHeight: 1.7 }}>
                  See every permit the day it's filed. Call homeowners who already said yes. Know exactly who your competitors are. Close faster.
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 18, justifyContent: "center" }}>
                  {["Real-time data", "Warm leads", "High close rate", "AI insights"].map(t => (
                    <span key={t} style={{ fontSize: 11, fontWeight: 700, padding: "5px 11px", borderRadius: 6, background: "rgba(255,136,0,0.12)", color: "#FF8800" }}>{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══ WHAT YOU GET ═══ */}
        <div className="why-section" style={{ background: "#131320", padding: "56px 0", borderTop: "1px solid rgba(255,255,255,0.12)", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
          <div style={{ maxWidth: "92%", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 36 }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#FF8800", marginBottom: 14 }}>What you get</div>
              <h2 className="why-section-title" style={{ fontFamily: "'Outfit',sans-serif", fontSize: 42, fontWeight: 900, color: "#fff", letterSpacing: "-2px", lineHeight: 1.1 }}>Every edge your team needs.<br/><span style={{ color: "#FF8800" }}>One platform.</span></h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }} className="why-grid-3">
              {[
                { icon: <Radio size={22} color="#FF8800" />, title: "Live permit tracking", desc: "Every solar permit filed across 3,690+ cities. Updated daily. See it before your competitors do." },
                { icon: <Target size={22} color="#FF8800" />, title: "6 opportunity segments", desc: "New installs, maintenance, battery storage, upgrades, loan payoffs, replacements. AI-categorized automatically." },
                { icon: <Search size={22} color="#FF8800" />, title: "Skip trace on demand", desc: "One click to get homeowner name, phone, email. Cached forever - you only pay once per address." },
                { icon: <BarChart2 size={22} color="#FF8800" />, title: "Market intelligence", desc: "Know which installers own your market. See storage adoption rates. Spot growth trends before they peak." },
                { icon: <Sparkles size={22} color="#FF8800" />, title: "AI daily brief", desc: "Every morning, AI analyzes the market and tells you exactly where to focus. No guessing." },
                { icon: <TrendingUp size={22} color="#FF8800" />, title: "Hot market radar", desc: "See which cities are surging in real time. Growth percentages, new permit velocity, competitive density." },
              ].map(f => (
                <div key={f.title} className="why-card" style={{ padding: "28px 24px", borderRadius: 14, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.1)" }}>
                  <div style={{ width: 44, height: 44, borderRadius: 11, background: "rgba(255,136,0,0.1)", border: "1px solid rgba(255,136,0,0.2)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                    {f.icon}
                  </div>
                  <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: 16, fontWeight: 800, color: "#fff", marginBottom: 8, letterSpacing: "-0.3px" }}>{f.title}</div>
                  <div style={{ fontSize: 14, color: "#fff", lineHeight: 1.65 }}>{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ LIVE PREVIEW ═══ */}
        <div className="why-section" style={{ padding: "56px 0", textAlign: "center" }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#FF8800", marginBottom: 16 }}>See it in action</div>
          <h2 className="why-section-title" style={{ fontFamily: "'Outfit',sans-serif", fontSize: 42, fontWeight: 900, color: "#fff", letterSpacing: "-2px", marginBottom: 40, lineHeight: 1.1 }}>Your morning brief. <span style={{ color: "#FF8800" }}>Every day.</span></h2>
          <div style={{ maxWidth: 800, margin: "0 auto", background: "rgba(255,255,255,0.1)", borderRadius: 16, padding: "28px 32px", textAlign: "left", border: "1px solid rgba(255,255,255,0.18)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <SolarMark size={14} color="brand" />
              <span style={{ fontSize: 12, fontWeight: 800, color: "#FF8800", fontFamily: "'Outfit',sans-serif", letterSpacing: "1px" }}>AI MARKET BRIEF</span>
              <span style={{ fontSize: 11, color: "#fff", fontWeight: 500 }}>Updated daily</span>
            </div>
            <div style={{ fontSize: 16, lineHeight: 1.7, color: "#fff", fontWeight: 500 }}>
              <span style={{ color: "#FF8800", fontWeight: 700 }}>78% of the market is independent.</span> Sunrun, Tesla, and Baker Electric Solar hold just 22% combined - the rest is yours for the taking. <span style={{ color: "#FFB347", fontWeight: 700 }}>Storage attach rate hit 52.4%</span> - if you're not quoting batteries, you're leaving money on the table.
            </div>
          </div>
        </div>

        {/* ═══ SEGMENTS ═══ */}
        <div className="why-section" style={{ background: "#131320", padding: "56px 0", borderTop: "1px solid rgba(255,255,255,0.12)", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
          <div style={{ maxWidth: "92%", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 36 }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#FF8800", marginBottom: 16 }}>Smart segmentation</div>
              <h2 className="why-section-title" style={{ fontFamily: "'Outfit',sans-serif", fontSize: 42, fontWeight: 900, color: "#fff", letterSpacing: "-2px", lineHeight: 1.1 }}>Not all permits are equal.<br/><span style={{ color: "#FF8800" }}>We sort them for you.</span></h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, maxWidth: 900, margin: "0 auto" }} className="why-grid-3">
              {[
                { icon: <Zap size={18} color="#FF8800" />, name: "New Installs", range: "0-30 days", hook: "First call wins. They just filed." },
                { icon: <Wrench size={18} color="#FF8800" />, name: "Maintenance", range: "1-2 years", hook: "Easy upsell. They trust solar." },
                { icon: <Battery size={18} color="#FF8800" />, name: "Battery Storage", range: "2-5 years", hook: "High margin. Growing fast." },
                { icon: <RefreshCw size={18} color="#FF8800" />, name: "System Upgrade", range: "5-10 years", hook: "Big ticket. Low competition." },
                { icon: <DollarSign size={18} color="#FF8800" />, name: "Loan Payoff", range: "10-15 years", hook: "Refinance-ready. Warm leads." },
                { icon: <Home size={18} color="#FF8800" />, name: "Replacement", range: "15-20 years", hook: "Full system. Premium deals." },
              ].map(s => (
                <div key={s.name} className="why-card" style={{ padding: "20px 22px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.1)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,136,0,0.1)", border: "1px solid rgba(255,136,0,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {s.icon}
                    </div>
                    <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: 15, fontWeight: 800, color: "#fff" }}>{s.name}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "#fff", marginBottom: 4, fontWeight: 500 }}>{s.range}</div>
                  <div style={{ fontSize: 14, color: "#FF8800", fontWeight: 700 }}>{s.hook}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ FINAL CTA ═══ */}
        <div className="why-cta-section" style={{ padding: "56px 0", textAlign: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,136,0,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />
          <div style={{ maxWidth: 600, margin: "0 auto", position: "relative", zIndex: 1 }}>
            <h2 style={{ fontFamily: "'Outfit',sans-serif", fontSize: 48, fontWeight: 900, color: "#fff", letterSpacing: "-2.5px", lineHeight: 1.1, marginBottom: 16 }}>
              Stop guessing.<br/>Start closing.
            </h2>
            <p style={{ fontSize: 17, color: "#fff", marginBottom: 32, fontWeight: 500 }}><span style={{ fontWeight: 800, color: "#FF8800" }}>1,000,000+</span> permits. <span style={{ fontWeight: 800, color: "#FF8800" }}>3,690+</span> cities. AI intelligence. One platform.</p>
            <button onClick={onSignUp} className="why-cta-btn" style={{ padding: "18px 52px", borderRadius: 12, background: "#FF8800", color: "#fff", border: "none", fontSize: 17, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.2px" }}>
              Get started
            </button>
            <div style={{ fontSize: 14, color: "#fff", marginTop: 14, fontWeight: 500 }}>Full access. All territories. Cancel anytime.</div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 0", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ maxWidth: "92%", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>© 2026 SolarLead Inc. All rights reserved.</div>
          <div style={{ display: "flex", gap: 16 }}>
            <a href="?page=terms" style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", textDecoration: "none", fontWeight: 500 }}>Terms</a>
            <a href="?page=privacy" style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", textDecoration: "none", fontWeight: 500 }}>Privacy</a>
          </div>
          </div>
        </div>
      </div>
    </>
  );
}



// ═══════════════════════════════════════════════════════════════════════════════
// ║  SPLASH PAGE - Premium dark theme                                         ║
// ═══════════════════════════════════════════════════════════════════════════════

// ─── SPLASH PAGE REDESIGN ─────────────────────────────────────────────────────
// Drop-in replacement for the SplashPage function in App.tsx
// Aesthetic: Linear meets Stripe - dark hero, refined typography, data-forward

const SPLASH_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { font-family: 'Outfit', sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
  ::selection { background: rgba(255,136,0,0.25); }

  @keyframes sp-fade-up {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes sp-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes sp-scale-in {
    from { opacity: 0; transform: scale(0.96); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes sp-ticker {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
  @keyframes sp-glow-pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.7; }
  }
  @keyframes sp-dot-pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.8); opacity: 0.4; }
  }

  .sp-stagger-1 { animation: sp-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) 0.1s both; }
  .sp-stagger-2 { animation: sp-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) 0.2s both; }
  .sp-stagger-3 { animation: sp-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) 0.3s both; }
  .sp-stagger-4 { animation: sp-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) 0.4s both; }
  .sp-stagger-5 { animation: sp-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) 0.55s both; }
  .sp-stagger-6 { animation: sp-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) 0.7s both; }
  .sp-fade-in-d { animation: sp-fade-in 1s 0.6s both; }

  .sp-cta-primary {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    height: 52px; padding: 0 32px;
    background: #FF8800; color: #fff; border: none; border-radius: 12px;
    font-family: 'Outfit', sans-serif; font-size: 15px; font-weight: 700;
    cursor: pointer; transition: all 0.2s ease; letter-spacing: -0.2px;
    position: relative; overflow: hidden;
  }
  .sp-cta-primary::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(255,255,255,0.12) 0%, transparent 50%);
    pointer-events: none;
  }
  .sp-cta-primary:hover {
    background: #E07700; transform: translateY(-1px);
    box-shadow: 0 8px 32px rgba(255,136,0,0.35);
  }

  .sp-cta-ghost {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    height: 52px; padding: 0 28px;
    background: transparent; color: #fff;
    border: 1px solid rgba(255,255,255,0.15); border-radius: 12px;
    font-family: 'Outfit', sans-serif; font-size: 15px; font-weight: 600;
    cursor: pointer; transition: all 0.2s ease;
  }
  .sp-cta-ghost:hover {
    border-color: rgba(255,255,255,0.3); color: #fff;
    background: rgba(255,255,255,0.1);
  }

  .sp-nav-link {
    font-size: 14px; font-weight: 500; color: #fff;
    text-decoration: none; transition: color 0.15s;
    cursor: pointer; background: none; border: none; font-family: inherit;
  }
  .sp-nav-link:hover { color: #FF8800; }

  .sp-feature-card {
    padding: 32px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 16px;
    transition: all 0.3s ease;
  }
  .sp-feature-card:hover {
    background: rgba(255,255,255,0.1);
    border-color: rgba(255,255,255,0.2);
    transform: translateY(-2px);
  }

  .sp-stat-card {
    text-align: center; padding: 24px;
  }

  .sp-ticker-track {
    display: flex; gap: 48px; animation: sp-ticker 40s linear infinite;
    white-space: nowrap;
  }

  .sp-section-dark { background: #09090F; }
  .sp-section-subtle { background: #0D0D14; }

  @media (max-width: 768px) {
    .sp-hero-h1 { font-size: 40px !important; letter-spacing: -1.5px !important; }
    .sp-hero-sub { font-size: 17px !important; max-width: 100% !important; line-height: 1.6 !important; }
    .sp-hero-ctas { flex-direction: column !important; }
    .sp-hero-ctas button { width: 100% !important; font-size: 17px !important; height: 56px !important; }
    .sp-feature-grid { grid-template-columns: 1fr !important; }
    .sp-stats-row { grid-template-columns: 1fr 1fr !important; }
    .sp-nav-links { display: none !important; }
    .sp-mobile-signin { display: flex !important; }
    .sp-hero-section { padding: 72px 20px 32px !important; min-height: auto !important; }
    .sp-section-pad { padding-left: 0 !important; padding-right: 0 !important; }
    .sp-footer-inner { flex-direction: column !important; gap: 12px !important; text-align: center !important; }
    .sp-preview-panel { display: none !important; }
    .sp-hero-checks { flex-direction: column !important; gap: 10px !important; }
    .sp-hero-checks span { font-size: 15px !important; }
    .sp-pricing-card { padding: 32px 24px !important; }
    .sp-pricing-amount { font-size: 44px !important; }
    .sp-section-features { padding-top: 40px !important; padding-bottom: 40px !important; }
    .sp-section-pricing { padding-top: 40px !important; padding-bottom: 40px !important; }
    .sp-section-cta { padding-top: 40px !important; padding-bottom: 40px !important; }
    .sp-cta-h2 { font-size: 32px !important; }
    .sp-feature-heading { font-size: 32px !important; }
    .sp-feature-card h3 { font-size: 20px !important; }
    .sp-feature-card p { font-size: 16px !important; }
    .sp-stat-card div:first-child { font-size: 28px !important; }
    .sp-stat-card div:last-child { font-size: 14px !important; }
    .sp-badge-pill { font-size: 13px !important; padding: 7px 16px 7px 12px !important; }
    .sp-ticker-track span { font-size: 16px !important; font-weight: 600 !important; }
  }
  @media (max-width: 390px) {
    .sp-hero-h1 { font-size: 34px !important; letter-spacing: -1px !important; }
    .sp-hero-sub { font-size: 16px !important; }
    .sp-hero-section { padding: 72px 16px 40px !important; }
    .sp-section-pad { padding-left: 0 !important; padding-right: 0 !important; }
    .sp-stats-row { gap: 0 !important; }
    .sp-pricing-amount { font-size: 38px !important; }
    .sp-cta-h2 { font-size: 28px !important; }
    .sp-feature-heading { font-size: 28px !important; }
  }
`;

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3.5 8.5l3 3 6-7" stroke="#FF8800" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function SplashPage({ onEnterApp, onSignIn, onSignUp, onWhy }: { onEnterApp: () => void; onSignIn?: () => void; onSignUp?: () => void; onWhy?: () => void }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const CITIES = [
    "San Diego, CA", "Los Angeles, CA", "Phoenix, AZ", "Austin, TX", "Sacramento, CA",
    "Miami, FL", "Denver, CO", "Nashville, TN", "Orlando, FL", "San Antonio, TX",
    "Honolulu, HI", "Boulder, CO", "Dallas, TX", "NYC, NY", "Portland, OR",
  ];

  const FEATURES = [
    {
      icon: "⚡",
      title: "Live Permit Feed",
      desc: "Every solar permit the moment it's filed. 1M+ permits across 3,690+ cities, updated daily by 340+ automated scrapers.",
    },
    {
      icon: "🎯",
      title: "Segment Intelligence",
      desc: "Leads scored and bucketed by lifecycle stage - new installs, maintenance windows, battery upsells, system upgrades.",
    },
    {
      icon: "📞",
      title: "One-Click Skip Trace",
      desc: "Homeowner phone, email, and mailing address in seconds. Cached results mean you never pay twice for the same contact.",
    },
    {
      icon: "🧠",
      title: "AI Market Analyst",
      desc: "Ask any question about your territory. Claude-powered insights grounded in real permit data, not generic advice.",
    },
    {
      icon: "📊",
      title: "Focus lists",
      desc: "Group leads into focus lists and drive each from to-do to won or lost, with notes and outcomes.",
    },
    {
      icon: "🗺️",
      title: "Route Optimization",
      desc: "AI plans your driving route based on lead density and score. Hit 15 doors in one neighborhood, not 15 zip codes.",
    },
  ];

  return (
    <>
      <style>{SPLASH_CSS}</style>

      <div style={{ width: "100%", minHeight: "100vh", background: "#09090F", color: "#fff", overflowX: "hidden" }}>

        {/* ─── NAV ─────────────────────────────────────────────── */}
        <nav style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          height: 64,
          background: scrolled ? "rgba(9,9,15,0.85)" : "transparent",
          backdropFilter: scrolled ? "blur(20px) saturate(180%)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(20px) saturate(180%)" : "none",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
          transition: "all 0.3s ease",
          display: "flex", alignItems: "center",
        }}>
          <div style={{ width: "100%", maxWidth: "92%", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            {/* Logo */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <SolarMark size={30} color="white" />
              <span style={{ fontSize: 19, fontWeight: 700, letterSpacing: "-0.4px" }}>
                Solar<span style={{ fontWeight: 900 }}>Lead</span>
              </span>
            </div>

            {/* Links */}
            <div className="sp-nav-links" style={{ display: "flex", alignItems: "center", gap: 32 }}>
              <button className="sp-nav-link" onClick={onWhy}>How it works</button>
              <button className="sp-nav-link" onClick={onSignIn || onEnterApp}>Sign in</button>
              <button className="sp-cta-primary" onClick={onSignUp || onEnterApp}
                style={{ height: 40, padding: "0 22px", fontSize: 13, borderRadius: 9 }}>
                Get started
              </button>
            </div>
            {/* Mobile nav */}
            <div className="sp-mobile-signin" style={{ display: "none", alignItems: "center", gap: 16 }}>
              <button className="sp-nav-link" onClick={onSignIn || onEnterApp} style={{ fontSize: 14, fontWeight: 600, color: "#fff", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>Sign in</button>
              <button className="sp-cta-primary" onClick={onSignUp || onEnterApp}
                style={{ height: 38, padding: "0 20px", fontSize: 13, borderRadius: 9 }}>
                Get started
              </button>
            </div>
          </div>
        </nav>

        {/* ─── HERO ────────────────────────────────────────────── */}
        <section className="sp-hero-section" style={{
          position: "relative", overflow: "hidden",
          padding: "88px 0 28px",
          display: "flex", alignItems: "center", justifyContent: "center",
          minHeight: "auto",
        }}>
          {/* Background gradient orbs */}
          <div style={{ position: "absolute", top: -200, right: -100, width: 600, height: 600, background: "radial-gradient(circle, rgba(255,136,0,0.08) 0%, transparent 65%)", pointerEvents: "none", animation: "sp-glow-pulse 6s ease-in-out infinite" }} />
          <div style={{ position: "absolute", bottom: -100, left: -200, width: 500, height: 500, background: "radial-gradient(circle, rgba(255,136,0,0.04) 0%, transparent 65%)", pointerEvents: "none" }} />
          {/* Subtle grid pattern */}
          <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)", backgroundSize: "64px 64px", pointerEvents: "none" }} />

          <div style={{ position: "relative", maxWidth: "92%", width: "100%", margin: "0 auto", display: "flex", alignItems: "center", gap: 48 }}>
            {/* Left - copy */}
            <div style={{ flex: "1 1 55%", minWidth: 0 }}>
              <div className="sp-stagger-1 sp-badge-pill" style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 20, padding: "6px 14px 6px 10px", background: "#FF8800", border: "none", borderRadius: 100 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", animation: "sp-dot-pulse 2s ease-in-out infinite" }} />
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "#fff" }}>1M+ permits tracked</span>
              </div>

              <h1 className="sp-stagger-2 sp-hero-h1" style={{
                fontSize: 64, fontWeight: 800, lineHeight: 1.05, letterSpacing: "-3px",
                color: "#fff", marginBottom: 24,
              }}>
                Every solar permit in your market.{" "}
                <span style={{ color: "#FF8800" }}>Before anyone else.</span>
              </h1>

              <p className="sp-stagger-3 sp-hero-sub" style={{
                fontSize: 18, lineHeight: 1.7, color: "#fff",
                maxWidth: 520, marginBottom: 28, fontWeight: 400,
              }}>
                SolarLead monitors every solar permit filed across 3,690+ cities so your team calls first, closes more, and never works a cold list again.
              </p>

              <div className="sp-stagger-4 sp-hero-ctas" style={{ display: "flex", gap: 12, marginBottom: 24 }}>
                <button className="sp-cta-primary" onClick={onSignUp || onEnterApp}>
                  Start closing more deals <ArrowIcon />
                </button>
                <button className="sp-cta-ghost" onClick={onWhy}>
                  See how it works
                </button>
              </div>

              <div className="sp-stagger-5 sp-hero-checks" style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                {["Full platform access", "All territories included", "Skip tracing built in"].map((t) => (
                  <div key={t} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <CheckIcon />
                    <span style={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - live data preview */}
            <div className="sp-preview-panel sp-stagger-6" style={{ flex: "1 1 40%", minWidth: 0 }}>
              <div style={{
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.18)",
                borderRadius: 20, padding: "32px 28px",
                backdropFilter: "blur(40px)",
                position: "relative", overflow: "hidden",
              }}>
                {/* Top glow */}
                <div style={{ position: "absolute", top: -60, right: -40, width: 200, height: 200, background: "radial-gradient(circle, rgba(255,136,0,0.12) 0%, transparent 70%)", pointerEvents: "none" }} />

                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "rgba(255,255,255,0.45)" }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#fff", letterSpacing: "1px", textTransform: "uppercase" }}>Product Preview</span>
                  </div>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>Sample data</span>
                </div>

                {/* Big stat */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#fff", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>Permits This Month</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                    <span style={{ fontSize: 56, fontWeight: 900, letterSpacing: "-2px", color: "#fff" }}>41,208</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#22C55E" }}>+8.3%</span>
                  </div>
                </div>

                {/* Mini cards row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
                  {[
                    { label: "Battery Upsell", value: "48,291", color: "#FF8800" },
                    { label: "Maintenance Due", value: "31,740", color: "#A78BFA" },
                  ].map((c) => (
                    <div key={c.label} style={{ background: "rgba(255,255,255,0.1)", borderRadius: 12, padding: "16px 18px", border: "1px solid rgba(255,255,255,0.18)" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#fff", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>{c.label}</div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: c.color, letterSpacing: "-0.5px" }}>{c.value}</div>
                    </div>
                  ))}
                </div>

                {/* Feed preview */}
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.12)", paddingTop: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#fff", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 12 }}>Recent Permits</div>
                  {[
                    { addr: "4821 Maple Creek Dr", city: "Miami-Dade, FL", score: 9.4, seg: "Battery" },
                    { addr: "1150 Sunset Blvd", city: "Kansas City, MO", score: 9.1, seg: "Upgrade" },
                    { addr: "782 Oakview Terrace", city: "Sacramento, CA", score: 8.8, seg: "Maintenance" },
                  ].map((l, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: i > 0 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{l.addr}</div>
                        <div style={{ fontSize: 12, color: "#fff", fontWeight: 500, marginTop: 2 }}>{l.city} · {l.seg}</div>
                      </div>
                      <div style={{
                        minWidth: 36, height: 28, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 13, fontWeight: 800,
                        background: l.score >= 9.2 ? "rgba(255,136,0,0.15)" : "rgba(255,255,255,0.06)",
                        color: l.score >= 9.2 ? "#FF8800" : "rgba(255,255,255,0.85)",
                      }}>
                        {l.score.toFixed(1)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── CITY TICKER ─────────────────────────────────────── */}
        <div className="sp-section-subtle" style={{ padding: "14px 0", borderTop: "1px solid rgba(255,255,255,0.12)", borderBottom: "1px solid rgba(255,255,255,0.12)", overflow: "hidden" }}>
          <div style={{ display: "flex", width: "max-content" }}>
            <div className="sp-ticker-track">
              {[...CITIES, ...CITIES].map((c, i) => (
                <span key={i} style={{ fontSize: 15, fontWeight: 600, color: "#fff", whiteSpace: "nowrap" }}>{c}</span>
              ))}
            </div>
          </div>
        </div>

        {/* ─── STATS BAR ──────────────────────────────────────── */}
        <section className="sp-section-dark section-pad" style={{ padding: "40px 0" }}>
          <div className="sp-stats-row" style={{ maxWidth: "92%", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 0 }}>
            {[
              { val: "1M+", label: "Solar permits tracked" },
              { val: "3,690+", label: "Cities monitored" },
              { val: "340+", label: "Live data scrapers" },
              { val: "Daily", label: "Automatic updates" },
            ].map((s, i) => (
              <div key={s.val} className="sp-stat-card" style={{ borderRight: i < 3 ? "1px solid rgba(255,255,255,0.18)" : "none" }}>
                <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: "-1.5px", color: "#fff", marginBottom: 6 }}>{s.val}</div>
                <div style={{ fontSize: 14, color: "#fff", fontWeight: 500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── FEATURES ────────────────────────────────────────── */}
        <section className="sp-section-subtle sp-section-pad sp-section-features" style={{ padding: "56px 0" }}>
          <div style={{ maxWidth: "92%", margin: "0 auto" }}>
            <div style={{ maxWidth: 520, marginBottom: 40 }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#FF8800", marginBottom: 16 }}>Platform</div>
              <h2 className="sp-feature-heading" style={{ fontSize: 40, fontWeight: 800, letterSpacing: "-1.5px", lineHeight: 1.15, color: "#fff", marginBottom: 16 }}>
                Data into insight.<br/>Insight into action.
              </h2>
              <p style={{ fontSize: 16, lineHeight: 1.7, color: "#fff", fontWeight: 400 }}>
                Every feature built for one purpose: helping solar sales teams find the right homeowner at the right time.
              </p>
            </div>

            <div className="sp-feature-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
              {FEATURES.map((f) => (
                <div key={f.title} className="sp-feature-card">
                  <div style={{ fontSize: 28, marginBottom: 16 }}>{f.icon}</div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 10, letterSpacing: "-0.3px" }}>{f.title}</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.65, color: "#fff", fontWeight: 400 }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FINAL CTA ──────────────────────────────────────── */}
        <section className="sp-section-subtle sp-section-pad sp-section-cta" style={{ padding: "56px 0", textAlign: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 600, height: 600, background: "radial-gradient(circle, rgba(255,136,0,0.06) 0%, transparent 65%)", pointerEvents: "none" }} />
          <div style={{ position: "relative", maxWidth: 600, margin: "0 auto" }}>
            <h2 className="sp-cta-h2" style={{ fontSize: 40, fontWeight: 800, letterSpacing: "-1.5px", color: "#fff", marginBottom: 16, lineHeight: 1.15 }}>
              Your competitors are already calling.
            </h2>
            <p style={{ fontSize: 16, color: "#fff", marginBottom: 40, lineHeight: 1.7 }}>
              Every day without SolarLead is another day someone else is reaching your best leads first.
            </p>
            <button className="sp-cta-primary" onClick={onSignUp || onEnterApp} style={{ height: 56, padding: "0 40px", fontSize: 16 }}>
              Start closing more deals <ArrowIcon />
            </button>
          </div>
        </section>

        {/* ─── FOOTER ──────────────────────────────────────────── */}
        <footer style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "24px 0" }}>
          <div className="sp-footer-inner" style={{ maxWidth: "92%", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <SolarMark size={22} color="white" />
              <span style={{ fontSize: 12, color: "#fff", fontWeight: 500 }}>© 2026 SolarLead Inc.</span>
            </div>
            <div style={{ display: "flex", gap: 24 }}>
              <a href="?page=terms" style={{ fontSize: 12, color: "#fff", textDecoration: "none", fontWeight: 500 }}>Terms</a>
              <a href="?page=privacy" style={{ fontSize: 12, color: "#fff", textDecoration: "none", fontWeight: 500 }}>Privacy</a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}


// ─── APP ───────────────────────────────────────────────────────────────────────
// ─── SupportBubble ───────────────────────────────────────────────────────────
// Floating bottom-right contact widget for logged-in users. Sends messages via
// POST /api/support/message → Resend → support@solarlead.io. User's email is
// set as reply-to so Mat can reply directly from his inbox.
//
// States:
//   closed   - small orange circle with chat icon
//   open     - 380px panel with subject + textarea + send
//   sending  - button shows spinner
//   sent     - form replaced by confirmation, auto-closes after 4s
//
// Mounted at App root, gated on authedUser being non-null.
function SupportBubble({ authedUser, onAskAI }: { authedUser: any; onAskAI: () => void }) {
  const [open, setOpen] = useState(false);
  // mode: "menu" shows the Ask-AI-forward launcher; "support" shows the contact form.
  const [mode, setMode] = useState<"menu" | "support">("menu");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  // ESC closes the panel
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  // Auto-close after sent confirmation
  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => {
      setOpen(false);
      // Reset state after the close animation
      setTimeout(() => {
        setSent(false);
        setSubject("");
        setMessage("");
        setMode("menu");
      }, 400);
    }, 4000);
    return () => clearTimeout(t);
  }, [sent]);

  if (!authedUser) return null;

  const submit = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    setError("");
    try {
      const token = localStorage.getItem("sl_token");
      const res = await fetch(`${API_URL}/api/support/message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ subject: subject.trim() || undefined, message: message.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setSent(true);
      } else {
        setError(data.error || "Could not send. Try emailing support@solarlead.io directly.");
      }
    } catch (e: any) {
      setError("Network error. Try emailing support@solarlead.io directly.");
    } finally {
      setSending(false);
    }
  };

  // ── CLOSED STATE - floating bubble ──
  if (!open) {
    return (
      <button
        onClick={() => { setMode("menu"); setOpen(true); }}
        aria-label="Ask AI or get help"
        style={{
          position: "fixed",
          bottom: "calc(env(safe-area-inset-bottom, 0) + 24px)",
          right: 24,
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "#FF8800",
          border: "none",
          color: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: "0 12px 32px -8px rgba(255,136,0,0.55), 0 4px 12px rgba(0,0,0,0.18)",
          zIndex: 9998,
          transition: "transform 180ms ease, box-shadow 180ms ease",
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1.06)"; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)"; }}
        className="sl-support-bubble"
      >
        <Sparkles size={24} strokeWidth={2.25} />
      </button>
    );
  }

  // ── OPEN STATE - panel ──
  return (
    <>
      {/* mobile-only scrim so the panel feels modal-ish on small screens */}
      <div
        className="sl-support-scrim"
        onClick={() => setOpen(false)}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(10,10,10,0.32)",
          backdropFilter: "blur(2px)",
          zIndex: 9997,
          display: "none",
        }}
      />
      <div
        role="dialog"
        aria-label="Contact support"
        style={{
          position: "fixed",
          bottom: "calc(env(safe-area-inset-bottom, 0) + 24px)",
          right: 24,
          width: "calc(100vw - 32px)",
          maxWidth: 400,
          background: "#FFFFFF",
          borderRadius: 16,
          border: "1px solid rgba(10,10,10,0.10)",
          boxShadow: "0 24px 80px -16px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.12)",
          zIndex: 9999,
          fontFamily: "'DM Sans', sans-serif",
          overflow: "hidden",
          animation: "slDrawerSlide 240ms ease",
        }}
      >
        {/* HEADER */}
        <div style={{
          padding: "18px 20px 14px",
          background: "#0E0E12",
          color: "#FFFFFF",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            {mode === "support" && (
              <button
                onClick={() => setMode("menu")}
                aria-label="Back"
                style={{ background: "transparent", border: "none", color: "#FFFFFF", cursor: "pointer", padding: 0, marginTop: 2, display: "flex" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#FFFFFF"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.6)"; }}
              >
                <ArrowLeft size={18} strokeWidth={2.25} />
              </button>
            )}
            <div>
              <div style={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "#FF8800",
                marginBottom: 6,
              }}>
                {mode === "menu" ? "SolarLead assist" : "Get in touch"}
              </div>
              <div style={{
                fontSize: 18,
                fontWeight: 800,
                letterSpacing: "-0.015em",
                fontFamily: "'Outfit', sans-serif",
                lineHeight: 1.2,
              }}>
                {mode === "menu" ? "What do you need?" : "Contact support"}
              </div>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              color: "#FFFFFF",
              cursor: "pointer",
              padding: 4,
              marginTop: -2,
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#FFFFFF"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.6)"; }}
          >
            <X size={18} strokeWidth={2.25} />
          </button>
        </div>

        {/* BODY */}
        {mode === "menu" ? (
          // ── LAUNCHER MENU - Ask AI hero + support secondary ──
          <div style={{ padding: "16px 18px 20px" }}>
            {/* Ask AI - hero action */}
            <button
              onClick={() => { onAskAI(); setOpen(false); }}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 14,
                padding: "16px 16px", marginBottom: 12,
                background: "#FFF7ED", border: "1.5px solid #FF8800", borderRadius: 12,
                cursor: "pointer", textAlign: "left", transition: "transform 140ms ease",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)"; }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "#FF8800", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Sparkles size={20} color="#fff" strokeWidth={2.25} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#0A0A0A", fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.01em" }}>
                  Ask AI
                </div>
                <div style={{ fontSize: 12.5, color: "#262B37", fontWeight: 500, marginTop: 1, lineHeight: 1.35 }}>
                  Markets, contractors, leads &mdash; just ask.
                </div>
              </div>
              <ArrowRight size={17} strokeWidth={2.5} color="#FF8800" />
            </button>

            {/* Contact support - secondary action */}
            <button
              onClick={() => setMode("support")}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 14,
                padding: "14px 16px",
                background: "#FAFAF6", border: "1.5px solid rgba(10,10,10,0.10)", borderRadius: 12,
                cursor: "pointer", textAlign: "left", transition: "border-color 140ms ease",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(10,10,10,0.22)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(10,10,10,0.10)"; }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "#0E0E12", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <MessageCircle size={19} color="#fff" strokeWidth={2.25} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#0A0A0A", fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.01em" }}>
                  Contact support
                </div>
                <div style={{ fontSize: 12.5, color: "#262B37", fontWeight: 500, marginTop: 1, lineHeight: 1.35 }}>
                  Bug, billing, or a question.
                </div>
              </div>
              <ArrowRight size={17} strokeWidth={2.5} color="#262B37" />
            </button>
          </div>
        ) : sent ? (
          // ── SUCCESS STATE ──
          <div style={{ padding: "32px 24px", textAlign: "center" }}>
            <div style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "#16A34A",
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}>
              <Check size={28} strokeWidth={3} />
            </div>
            <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: "#0A0A0A",
              fontFamily: "'Outfit', sans-serif",
              letterSpacing: "-0.015em",
              marginBottom: 8,
            }}>
              Message received.
            </div>
            <div style={{
              fontSize: 14,
              color: "#262B37",
              lineHeight: 1.5,
              fontWeight: 500,
            }}>
              A real human will reply within 24 hours - usually faster.
            </div>
          </div>
        ) : (
          // ── FORM ──
          <div style={{ padding: "18px 20px 20px" }}>
            <div style={{
              fontSize: 13,
              color: "#262B37",
              marginBottom: 14,
              lineHeight: 1.5,
              fontWeight: 500,
            }}>
              Bug, billing question, feature ask - we read everything. Replies come from Mat or the team within 24 hours.
            </div>

            <label style={{
              display: "block",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#262B37",
              marginBottom: 6,
            }}>
              Subject <span style={{ fontWeight: 600, color: "#262B37" }}>(optional)</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              placeholder="e.g. Question about Calgary coverage"
              disabled={sending}
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: 14,
                fontFamily: "'DM Sans', sans-serif",
                color: "#0A0A0A",
                background: "#FAFAF6",
                border: "1px solid rgba(10,10,10,0.18)",
                borderRadius: 8,
                outline: "none",
                marginBottom: 14,
                fontWeight: 500,
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = "#FF8800"; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(10,10,10,0.18)"; }}
            />

            <label style={{
              display: "block",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#262B37",
              marginBottom: 6,
            }}>
              Your message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={5000}
              placeholder="Tell us what's on your mind…"
              disabled={sending}
              rows={5}
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: 14,
                fontFamily: "'DM Sans', sans-serif",
                color: "#0A0A0A",
                background: "#FAFAF6",
                border: "1px solid rgba(10,10,10,0.18)",
                borderRadius: 8,
                outline: "none",
                resize: "vertical",
                minHeight: 110,
                fontWeight: 500,
                lineHeight: 1.5,
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = "#FF8800"; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(10,10,10,0.18)"; }}
            />

            {error && (
              <div style={{
                marginTop: 12,
                padding: "10px 12px",
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                borderRadius: 8,
                fontSize: 13,
                color: "#991B1B",
                fontWeight: 500,
                lineHeight: 1.4,
              }}>
                {error}
              </div>
            )}

            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 16,
              gap: 12,
            }}>
              <div style={{
                fontSize: 11,
                color: "#262B37",
                fontWeight: 600,
                letterSpacing: "0.04em",
              }}>
                or email support@solarlead.io
              </div>
              <button
                onClick={submit}
                disabled={!message.trim() || sending}
                style={{
                  background: (!message.trim() || sending) ? "rgba(10,10,10,0.18)" : "#FF8800",
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 16px",
                  fontSize: 14,
                  fontWeight: 800,
                  letterSpacing: "-0.005em",
                  cursor: (!message.trim() || sending) ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontFamily: "'Outfit', sans-serif",
                  transition: "background 150ms ease",
                }}
              >
                {sending ? (
                  <>
                    <span style={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      border: "2px solid rgba(255,255,255,0.4)",
                      borderTopColor: "#FFFFFF",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }} />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send size={14} strokeWidth={2.5} />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── OutOfCreditsModal ───────────────────────────────────────────────────────
// Shown when a user clicks Unlock with 0 credits. Presents the three credit
// packs with one-click Stripe checkout. Replaces the old window.confirm →
// Settings navigation. Keeps the user in their prospecting flow.
const SL_CREDIT_PACKS = [
  { credits: 10, price: 50,  per: "$5.00", label: null,        best: false },
  { credits: 25, price: 100, per: "$4.00", label: "Save 20%",  best: true  },
  { credits: 50, price: 175, per: "$3.50", label: "Save 30%",  best: false },
];

function OutOfCreditsModal({ open, onClose, onPurchase, purchasing }: {
  open: boolean;
  onClose: () => void;
  onPurchase: (credits: number) => void;
  purchasing: number | null;
}) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(10,10,12,0.62)", backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, animation: "slFade 180ms ease",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-label="Buy credits"
        style={{
          width: "100%", maxWidth: 460, background: "#FFFFFF",
          borderRadius: 18, overflow: "hidden",
          boxShadow: "0 32px 90px -20px rgba(0,0,0,0.45)",
          fontFamily: "'DM Sans', sans-serif",
          animation: "slModalPop 220ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Header */}
        <div style={{ background: "#0E0E12", padding: "26px 28px 22px", position: "relative" }}>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ position: "absolute", top: 18, right: 18, background: "transparent", border: "none", color: "#FFFFFF", cursor: "pointer", padding: 4 }}
          >
            <X size={18} strokeWidth={2.25} />
          </button>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.24em", textTransform: "uppercase", color: "#FF8800", marginBottom: 10 }}>
            Out of credits
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#fff", letterSpacing: "-0.025em", lineHeight: 1.1, fontFamily: "'Outfit', sans-serif" }}>
            Top up to keep unlocking.
          </div>
          <div style={{ fontSize: 14, color: "#FFFFFF", marginTop: 8, fontWeight: 500, lineHeight: 1.45 }}>
            You&apos;ve used all your skip-trace credits this cycle. Grab a pack - credits never expire.
          </div>
        </div>

        {/* Packs */}
        <div style={{ padding: "20px 20px 8px" }}>
          {SL_CREDIT_PACKS.map(p => {
            const isLoading = purchasing === p.credits;
            return (
              <button
                key={p.credits}
                onClick={() => onPurchase(p.credits)}
                disabled={purchasing !== null}
                style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "16px 18px", marginBottom: 12,
                  background: p.best ? "#FFF7ED" : "#FAFAF6",
                  border: p.best ? "1.5px solid #FF8800" : "1.5px solid rgba(10,10,10,0.10)",
                  borderRadius: 12, cursor: purchasing !== null ? "wait" : "pointer",
                  textAlign: "left", transition: "border-color 150ms, background 150ms",
                  opacity: purchasing !== null && !isLoading ? 0.5 : 1,
                }}
              >
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 19, fontWeight: 900, color: "#0A0A0A", fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.02em" }}>
                      {p.credits} credits
                    </span>
                    {p.label && (
                      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#FF8800", background: "rgba(255,136,0,0.12)", padding: "2px 7px", borderRadius: 5 }}>
                        {p.label}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: "#262B37", fontWeight: 600, marginTop: 3 }}>
                    {p.per} per unlock
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 22, fontWeight: 900, color: p.best ? "#FF8800" : "#0A0A0A", fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }}>
                    ${p.price}
                  </span>
                  {isLoading
                    ? <span style={{ display: "inline-block", width: 16, height: 16, border: "2px solid rgba(255,136,0,0.35)", borderTopColor: "#FF8800", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                    : <ArrowRight size={17} strokeWidth={2.5} color={p.best ? "#FF8800" : "#262B37"} />
                  }
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ padding: "4px 20px 22px", textAlign: "center", fontSize: 12, color: "#262B37", fontWeight: 600 }}>
          Secure checkout via Stripe · credits never expire
        </div>
      </div>
    </div>
  );
}

// ─── LowCreditBanner ─────────────────────────────────────────────────────────
// Persistent (dismissable) strip shown when credits run low (<= threshold).
// Plants top-up awareness before the user hits zero mid-prospecting.
function LowCreditBanner({ creditsRemaining, onTopUp, threshold = 2 }: {
  creditsRemaining: number;
  onTopUp: () => void;
  threshold?: number;
}) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  if (typeof creditsRemaining !== "number" || creditsRemaining > threshold) return null;

  const isZero = creditsRemaining <= 0;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      background: isZero ? "#FEF2F2" : "#FFF7ED",
      border: `1px solid ${isZero ? "#FECACA" : "#FED7AA"}`,
      borderRadius: 12, padding: "12px 14px", marginBottom: 16,
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
        background: isZero ? "#EF4444" : "#FF8800",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Zap size={15} color="#fff" strokeWidth={2.5} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#0A0A0A", fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.01em" }}>
          {isZero ? "You're out of credits" : `${creditsRemaining} credit${creditsRemaining === 1 ? "" : "s"} left this cycle`}
        </div>
        <div style={{ fontSize: 12.5, color: "#262B37", fontWeight: 500, marginTop: 1 }}>
          {isZero ? "Top up to keep unlocking contacts." : "Top up now so you don't stall mid-prospecting."}
        </div>
      </div>
      <button
        onClick={onTopUp}
        style={{
          flexShrink: 0, background: "#FF8800", color: "#fff", border: "none",
          borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 800,
          cursor: "pointer", fontFamily: "'Outfit', sans-serif", whiteSpace: "nowrap",
        }}
      >
        Top up
      </button>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        style={{ flexShrink: 0, background: "transparent", border: "none", color: "#262B37", cursor: "pointer", padding: 4 }}
      >
        <X size={16} strokeWidth={2.25} />
      </button>
    </div>
  );
}

// ─── ComplianceGateModal ─────────────────────────────────────────────────────
// Hard gate shown before a user's FIRST contact unlock. Requires an explicit,
// affirmative acknowledgment that THEY (not SolarLead) are responsible for
// complying with calling/texting/emailing laws. The acknowledgment is recorded
// server-side with a timestamp + version (see /api/user/acknowledge-skiptrace).
// Not dismissable by click-outside - the user must explicitly accept or cancel.
function ComplianceGateModal({ open, onAccept, onCancel, working }: {
  open: boolean;
  onAccept: () => void;
  onCancel: () => void;
  working: boolean;
}) {
  const [checked, setChecked] = useState(false);
  useEffect(() => { if (!open) setChecked(false); }, [open]);
  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 10001,
        background: "rgba(10,10,12,0.66)", backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, animation: "slFade 180ms ease",
      }}
    >
      <div
        role="dialog"
        aria-label="Compliance acknowledgment"
        style={{
          width: "100%", maxWidth: 480, background: "#FFFFFF",
          borderRadius: 18, overflow: "hidden",
          boxShadow: "0 32px 90px -20px rgba(0,0,0,0.5)",
          fontFamily: "'DM Sans', sans-serif",
          animation: "slModalPop 220ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Header */}
        <div style={{ background: "#0E0E12", padding: "26px 28px 22px" }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.24em", textTransform: "uppercase", color: "#FF8800", marginBottom: 10 }}>
            Before you unlock
          </div>
          <div style={{ fontSize: 23, fontWeight: 900, color: "#fff", letterSpacing: "-0.025em", lineHeight: 1.12, fontFamily: "'Outfit', sans-serif" }}>
            You&apos;re responsible for how you reach out.
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "22px 28px 12px" }}>
          <p style={{ fontSize: 14.5, color: "#2A2A3E", lineHeight: 1.6, fontWeight: 500, margin: "0 0 14px" }}>
            SolarLead gives you contact details sourced from public records and licensed data vendors, for <strong style={{ color: "#0A0A0A" }}>business-to-business outreach</strong>. How you use them is on you.
          </p>
          <p style={{ fontSize: 14.5, color: "#2A2A3E", lineHeight: 1.6, fontWeight: 500, margin: "0 0 14px" }}>
            Before you call, text, or email anyone, <strong style={{ color: "#0A0A0A" }}>you are solely responsible</strong> for complying with all applicable laws, including the <strong style={{ color: "#0A0A0A" }}>TCPA</strong> (US), <strong style={{ color: "#0A0A0A" }}>CASL</strong> (Canada), <strong style={{ color: "#0A0A0A" }}>CAN-SPAM</strong>, and all federal, state, provincial, and internal <strong style={{ color: "#0A0A0A" }}>Do-Not-Call</strong> rules.
          </p>
          <p style={{ fontSize: 13, color: "#6B6B7A", lineHeight: 1.55, fontWeight: 500, margin: "0 0 18px" }}>
            SolarLead is not a consumer reporting agency and this data may not be used for any FCRA-regulated purpose (credit, employment, insurance, housing). Full detail in our <a href="https://solarlead.io/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#FF8800", fontWeight: 700, textDecoration: "underline" }}>Terms of Service</a>.
          </p>

          {/* Acknowledge checkbox */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 11, cursor: "pointer", padding: "14px 16px", background: "#FAFAF6", border: `1.5px solid ${checked ? "#FF8800" : "rgba(10,10,10,0.12)"}`, borderRadius: 11, transition: "border-color 140ms ease" }}>
            <div
              onClick={() => setChecked(!checked)}
              style={{
                width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 0,
                background: checked ? "#FF8800" : "#fff",
                border: checked ? "none" : "1.5px solid rgba(10,10,10,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              {checked && <Check size={14} color="#fff" strokeWidth={3} />}
            </div>
            <span style={{ fontSize: 13.5, color: "#0A0A0A", fontWeight: 600, lineHeight: 1.45 }}>
              I understand and agree that I am solely responsible for complying with all applicable calling, texting, and emailing laws when I contact anyone using this data.
            </span>
          </label>
        </div>

        {/* Actions */}
        <div style={{ padding: "16px 28px 24px", display: "flex", gap: 12 }}>
          <button
            onClick={onCancel}
            disabled={working}
            style={{ flex: "0 0 auto", padding: "13px 20px", background: "transparent", border: "1.5px solid rgba(10,10,10,0.15)", borderRadius: 10, fontSize: 14, fontWeight: 700, color: "#6B6B7A", cursor: working ? "wait" : "pointer", fontFamily: "'Outfit', sans-serif" }}
          >
            Cancel
          </button>
          <button
            onClick={onAccept}
            disabled={!checked || working}
            style={{
              flex: 1, padding: "13px 20px", border: "none", borderRadius: 10,
              fontSize: 14, fontWeight: 800, fontFamily: "'Outfit', sans-serif",
              letterSpacing: "-0.01em",
              background: (!checked || working) ? "rgba(255,136,0,0.4)" : "#FF8800",
              color: "#fff", cursor: (!checked || working) ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            {working ? "Confirming\u2026" : "Agree & unlock contact"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [showSplash, setShowSplash] = useState(
    () => localStorage.getItem("siq_entered") !== "1"
  );
  // ── AUTH ──
  const [authView, setAuthView] = useState<"splash"|"login"|"signup"|"app"|"why"|"forgot-password"|"reset-password"|"onboarding">(() => {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset")) return "reset-password";
    if (params.get("signup") === "1") return "signup";
    if (params.get("trial") === "1") return "onboarding";
    if (params.get("login") === "1") return "login";
    if (params.get("upgrade") === "success") return "splash"; // post-Stripe redirect uses splash briefly
    // No explicit intent in URL. If user has no JWT, bounce to marketing site
    // (the in-app SplashPage is deprecated). Authenticated users with a token
    // continue past this - the auth-check useEffect will flip authView to "app".
    if (!localStorage.getItem("sl_token")) {
      window.location.replace("https://solarlead.io/");
    }
  }
  return "splash";
});
  const [authedUser, setAuthedUser] = useState<any>(null);
  const [authChecked, setAuthChecked] = useState(false);
  // True once a verified user exists (login or /me hydration). Boolean on
  // purpose: effects keyed on this fire once per signed-in session instead of
  // re-running whenever the authedUser object identity changes.
  const authReady = !!authedUser;

  // Load user's pipeline from backend
  const loadUserPipeline = (userEmail: string) => {
    fetch(`${API_URL}/api/user/pipeline?userId=${encodeURIComponent(userEmail)}`)
      .then(r => r.json())
      .then(d => {
        if (!d.success) { console.error('[loadUserPipeline] backend returned success=false:', d.error); return; }
        if (!d.pipeline) return;
        // Filter out archived leads - underlying permit was removed by cleanup.
        // Kept in DB (ON DELETE SET NULL) but nothing to render in UI.
        const live = d.pipeline.filter((p: any) => !p.archived);
        if (d.archived_count && d.archived_count > 0) {
          console.warn(`[loadUserPipeline] ${d.archived_count} pipeline lead(s) archived - underlying permits were cleaned up. Not displayed.`);
        }
        setPipeLeads(live);
      })
      .catch(err => console.error('[loadUserPipeline] network error:', err));
  };

  // Save pipeline entry to backend (upsert - used for add + stage changes)
  const savePipelineToBackend = (leadId: any, stage: string, focusListId: any = undefined) => {
    const email = authedUser?.email;
    if (!email) return;
    const body: any = { userId: email, leadId, stage };
    if (focusListId !== undefined) body.focusListId = focusListId; // number = that focus; null = Unfiled
    fetch(`${API_URL}/api/user/pipeline`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then(r => r.json())
      .then(d => { if (!d.success) console.error('[savePipelineToBackend] save failed:', d.error, 'leadId:', leadId); })
      .catch(err => console.error('[savePipelineToBackend] network error:', err, 'leadId:', leadId));
  };

  // Update pipeline stage in backend
  const updatePipelineStage = (leadId: any, stage: string) => {
    const email = authedUser?.email;
    if (!email) return;
    fetch(`${API_URL}/api/user/pipeline`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: email, leadId, stage }),
    })
      .then(r => r.json())
      .then(d => { if (!d.success) console.error('[updatePipelineStage] update failed:', d.error, 'leadId:', leadId); })
      .catch(err => console.error('[updatePipelineStage] network error:', err, 'leadId:', leadId));
  };

  // Remove lead from pipeline in backend. The UI calls this on the Remove
  // button in addition to updating local state - otherwise the row survives
  // server-side and reappears on next page load as a ghost lead.
  const removeFromPipelineBackend = (leadId: any) => {
    const email = authedUser?.email;
    if (!email || !leadId) return;
    fetch(`${API_URL}/api/user/pipeline/${leadId}?userId=${encodeURIComponent(email)}`, { method: "DELETE" })
      .then(r => r.json())
      .then(d => { if (!d.success) console.error('[removeFromPipelineBackend] delete failed:', d.error, 'leadId:', leadId); })
      .catch(err => console.error('[removeFromPipelineBackend] network error:', err, 'leadId:', leadId));
  };

  // Load user's previously unlocked contacts from backend
  const loadUserUnlocks = (userEmail: string) => {
    fetch(`${API_URL}/api/user/unlocks?userId=${encodeURIComponent(userEmail)}`)
      .then(r => r.json())
      .then(d => {
        if (!d.success) { console.error('[loadUserUnlocks] backend returned success=false:', d.error); return; }
        if (d.unlocks) setUnlocked((prev: any) => ({ ...prev, ...d.unlocks }));
      })
      .catch(err => console.error('[loadUserUnlocks] network error:', err));
  };

  useEffect(() => {
    // If the user is on a password-reset URL, skip auto-login. They need to
    // complete the reset flow first - their stale JWT (if any) will be cleared
    // when ResetPasswordPage succeeds.
    if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("reset")) {
      setAuthChecked(true);
      return;
    }
    const token = getToken();
    if (!token) { setAuthChecked(true); return; }
    fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        // Admin accounts are let through regardless of plan - they're internal
        // users whose access doesn't depend on a Stripe subscription being active.
        const hasAccess = d.success && d.user && (
          d.user.isAdmin || (d.user.plan && d.user.plan !== "free")
        );
        if (hasAccess) {
          setAuthedUser(d.user);
          // Seed the dashboard `user` state from the RELIABLE /me payload (verified by
          // authMiddleware) so isAdmin / credits / plan can't be clobbered by the
          // /api/user fallback below - that fallback was making admins show "out of credits".
          setUser((prev: any) => ({ ...prev, ...d.user, companyName: d.user.company || d.user.companyName || prev.companyName }));
          setAuthView("app");
          setShowSplash(false);
          setView("dashboard");
          localStorage.setItem("siq_entered", "1");
          // Load unlocked contacts and pipeline from database
          if (d.user.email) {
            loadUserUnlocks(d.user.email);
            loadUserPipeline(d.user.email);
          }
        } else { clearToken(); }
        setAuthChecked(true);
      })
      .catch(() => { clearToken(); setAuthChecked(true); });
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgrade") === "success") window.history.replaceState({}, "", window.location.pathname);
  }, []);

  useEffect(() => { document.title = "SolarLead - Solar Sales Intelligence"; }, []);

  const handleLogin = (token: string, user: any) => {
    setToken(token); setAuthedUser(user); setAuthView("app"); setShowSplash(false);
    // Seed dashboard `user` with authoritative login values (same fix as the /me path).
    setUser((prev: any) => ({ ...prev, ...user, companyName: user.company || user.companyName || prev.companyName }));
    setView("dashboard");
    localStorage.setItem("siq_entered", "1");
    // Load unlocked contacts and pipeline from database
    if (user.email) {
      loadUserUnlocks(user.email);
      loadUserPipeline(user.email);
    }
  };
  // Trial users arrive here from the guided onboarding. Same hydration as
  // handleLogin, plus committing their chosen territory (so the dashboard and
  // feed boot scoped to it) and marking first-run done so the picker never pops.
  const handleTrialComplete = (token: string, user: any, scope: { city: string; region: string }) => {
    setToken(token); setAuthedUser(user); setAuthView("app"); setShowSplash(false);
    setUser((prev: any) => ({ ...prev, ...user, companyName: user.company || user.companyName || prev.companyName }));
    const name = (scope.city && scope.region)
      ? `${scope.city}, ${scope.region}`
      : (scope.region ? (CODE_TO_TERRITORY_NAME[scope.region] || scope.region) : "All Markets");
    selectTerritory(name);
    finishFirstRun();
    setView("dashboard");
    try { localStorage.setItem("siq_entered", "1"); } catch {}
    if (user.email) {
      loadUserUnlocks(user.email);
      loadUserPipeline(user.email);
    }
  };
  const handleLogout = () => {
    clearToken(); setAuthedUser(null);
    // Clear local cache on logout
    setUnlocked({});
    setPipeLeads([]);
    setCallLog({});
    try { localStorage.removeItem('sl_unlocked'); localStorage.removeItem('sl_pipeline'); localStorage.removeItem('sl_calllog'); } catch {}
    // Redirect to marketing site - the in-app SplashPage is deprecated. Using
    // href (not replace) so the back button doesn't re-enter the signed-out app.
    if (typeof window !== "undefined") {
      window.location.href = "https://solarlead.io/";
    }
  };

  // ── ALL HOOKS MUST BE BEFORE ANY CONDITIONAL RETURNS ──
  const [view, setView] = useState("dashboard");
  // Contractor intelligence view - name being drilled into, null = leaderboard
  const [selectedContractor, setSelectedContractor] = useState<string | null>(null);
  // State for the Competitors view. Lifted here (not inside the Competitors
  // component) because Competitors is only rendered when view === "competitors",
  // so hooks declared inside it would violate rules-of-hooks (hook count changes
  // between renders → React refuses to render, page goes blank).
  const [compLeaderboard, setCompLeaderboard] = useState<any[]>([]);
  const [compLeaderLoading, setCompLeaderLoading] = useState(true);
  const [compContractor, setCompContractor] = useState<any>(null);
  const [compContractorLoading, setCompContractorLoading] = useState(false);
  const [compSearchQuery, setCompSearchQuery] = useState("");

  // ── Markets v2 state (city grid + drill-in detail) ─────────────────────────
  // selectedMarket null = landing grid; populated = detail page for that city.
  // Both lifted to App-level for the same rules-of-hooks reason as Competitors.
  const [selectedMarket, setSelectedMarket] = useState<{ city: string; province: string } | null>(null);
  const [marketDetail, setMarketDetail] = useState<any>(null);
  const [marketDetailLoading, setMarketDetailLoading] = useState(false);
  const [marketHotLeads, setMarketHotLeads] = useState<any[]>([]);
  const [marketHotLeadsLoading, setMarketHotLeadsLoading] = useState(false);
  const [marketsList, setMarketsList] = useState<any[]>([]);
  const [marketsPlatformTotals, setMarketsPlatformTotals] = useState<any>(null);
  const [marketsListLoading, setMarketsListLoading] = useState(false);
  const [marketsCountry, setMarketsCountry] = useState<"ALL" | "CA" | "US">("ALL");
  const [marketsSort, setMarketsSort] = useState<"month" | "growth" | "total">("total");
  const [marketsSearch, setMarketsSearch] = useState("");
  const [marketsShowAllMid, setMarketsShowAllMid] = useState(false);
  const [marketsShowAllQuiet, setMarketsShowAllQuiet] = useState(false);

  // ── Contractors v2 state (directory + filters) ─────────────────────────────
  const [contractorsResults, setContractorsResults] = useState<any>({ total: 0, rows: [] });
  const [contractorsLoading, setContractorsLoading] = useState(false);
  const [contractorsCountry, setContractorsCountry] = useState<"ALL" | "CA" | "US">("ALL");
  const [contractorsStatus, setContractorsStatus] = useState<string>(""); // "" = any
  const [contractorsSort, setContractorsSort] = useState<"permits" | "recent" | "growth" | "new">("permits");
  const [contractorsSearch, setContractorsSearch] = useState("");
  const [contractorsLimit, setContractorsLimit] = useState(50);
  const [contractorsMovement, setContractorsMovement] = useState<any[]>([]);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(USER_DEFAULTS);
  const [currentTerritory, setCurrentTerritory] = useState<string>(() => {
    try {
      const saved = localStorage.getItem("sl_territory");
      if (saved) return saved;
    } catch {}
    return "All Markets";
  });
  const [totalLeadCount, setTotalLeadCount] = useState(0);
  const [realTotalCount, setRealTotalCount] = useState(0);
  const [segmentCounts, setSegmentCounts] = useState<any>(null);
  // Live territory data from backend - falls back to hardcoded TERRITORY_GROUPS if fetch fails
  const [liveTerritoryGroups, setLiveTerritoryGroups] = useState<any[] | null>(null);
  // Competitor/contractor intel for the selected territory (top installers, growth %, avg value)
  const [cityIntel, setCityIntel] = useState<any>(null);
  const [territoryCount, setTerritoryCount] = useState(0);
  const [feedLeads, setFeedLeads] = useState<any[]>([]);
  const [feedTotal, setFeedTotal] = useState(0);
  const [feedPage, setFeedPage] = useState(0);
  const [feedLoading, setFeedLoading] = useState(false);
  const FEED_PAGE_SIZE = 50;
  const [briefing, setBriefing] = useState({
    newThisWeek: 0,
    serviceWindow: 0,
    goingCold: 0,
  });
  const [hotspots, setHotspots] = useState([]);
  const [hotspotDetail, setHotspotDetail] = useState(null);
  const [briefingLoaded, setBriefingLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [exTab, setExTab] = useState("browse");
  const [exCountry, setExCountry] = useState(null);
  const [exRegion, setExRegion] = useState(null);
  const [exCity, setExCity] = useState(null);
  const [exSegment, setExSegment] = useState(null);
  const [exSort, setExSort] = useState("smartScore");
  const [exPage, setExPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pendingSegFilter, setPendingSegFilter] = useState<string | null>(null);
  const [workspaceSegment, setWorkspaceSegment] = useState<string | null>(null);
  const PER_PAGE = 25;
  const [pipeLeads, setPipeLeads] = useState<any[]>([]);

  // ── PIPELINE STATE INVARIANT ────────────────────────────────────────────
  // pipeLeads is *mirrored* from user_pipeline table. Source of truth is the
  // database. Any client mutation that adds a lead, changes its stage, or
  // removes it MUST also call the matching backend helper:
  //   • Add or stage change → savePipelineToBackend(leadId, stage)
  //   • Remove              → removeFromPipelineBackend(leadId)
  // Mutating React state alone produces drift: the lead vanishes on next
  // page load because loadUserPipeline only knows about persisted rows.
  // This used to silently lose unlocked leads from pipeline.
  // ────────────────────────────────────────────────────────────────────────
  const [pipeStage, setPipeStage] = useState("all");
  const [pipeTag, setPipeTag] = useState("all");
  // ── FOCUS state (worklists) - lifted to App so the view persists across re-renders ──
  const [focusLists, setFocusLists] = useState<any[]>([]);
  const [focusUnfiled, setFocusUnfiled] = useState(0);
  const [focusSel, setFocusSel] = useState<any>(null);
  const [focusLeads, setFocusLeads] = useState<any[]>([]);
  const [focusLoading, setFocusLoading] = useState(false);
  const [focusBusy, setFocusBusy] = useState(false);
  const [focusShowNew, setFocusShowNew] = useState(false);
  const [focusNewTitle, setFocusNewTitle] = useState("");
  const [focusNewDirective, setFocusNewDirective] = useState("");
  const [focusNewDue, setFocusNewDue] = useState("");
  const [focusMoveFor, setFocusMoveFor] = useState<any>(null);
  const [focusCardMenu, setFocusCardMenu] = useState<any>(null);
  const [focusAiOpen, setFocusAiOpen] = useState(false);
  const [focusAiPrompt, setFocusAiPrompt] = useState("");
  const [focusAiTitle, setFocusAiTitle] = useState("");
  const [focusAiLoading, setFocusAiLoading] = useState(false);
  const [focusAiResult, setFocusAiResult] = useState<any>(null);
  const [focusAiError, setFocusAiError] = useState<any>(null);
  const [focusAiCreating, setFocusAiCreating] = useState(false);

  // ── FOCUS helpers ─────────────────────────────────────────────────────────
  // The fetch interceptor attaches the auth token to every API_URL call, and the
  // backend derives ownership from the JWT - so these need no userId in the body.
  const loadFocus = () => {
    setFocusLoading(true);
    fetch(`${API_URL}/api/focus`)
      .then(r => r.json())
      .then(d => {
        if (!d.success) { console.error('[loadFocus] failed:', d.error); return; }
        setFocusLists(d.lists || []);
        setFocusUnfiled(d.unfiled || 0);
      })
      .catch(err => console.error('[loadFocus] network error:', err))
      .finally(() => setFocusLoading(false));
  };

  const openFocus = (sel: any) => {
    setFocusSel(sel);
    setFocusLeads([]);
    setFocusMoveFor(null);
    const id = sel === "unfiled" ? "unfiled" : sel.focus_list_id;
    setFocusLoading(true);
    fetch(`${API_URL}/api/focus/${id}/leads`)
      .then(r => r.json())
      .then(d => { if (d.success) setFocusLeads(d.leads || []); else console.error('[openFocus] failed:', d.error); })
      .catch(err => console.error('[openFocus] network error:', err))
      .finally(() => setFocusLoading(false));
  };

  const createFocus = () => {
    const title = focusNewTitle.trim();
    if (!title) return;
    setFocusBusy(true);
    fetch(`${API_URL}/api/focus`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, directive: focusNewDirective.trim() || undefined, dueDate: focusNewDue || undefined }),
    })
      .then(r => r.json())
      .then(d => {
        if (!d.success) { console.error('[createFocus] failed:', d.error); return; }
        setFocusNewTitle(""); setFocusNewDirective(""); setFocusNewDue(""); setFocusShowNew(false);
        loadFocus();
      })
      .catch(err => console.error('[createFocus] network error:', err))
      .finally(() => setFocusBusy(false));
  };

  // ── AI focus builder ──────────────────────────────────────────────────────
  // Sends the prompt to /api/focus/ai-build (Claude parses it into safe filters
  // server-side), then previews the matches. Create reuses POST /api/focus with
  // the matched leadIds (one call creates the list + attaches the leads).
  const runAiBuild = () => {
    const p = focusAiPrompt.trim();
    if (!p || focusAiLoading) return;
    setFocusAiLoading(true); setFocusAiError(null); setFocusAiResult(null);
    fetch(`${API_URL}/api/focus/ai-build`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt: p }),
    })
      .then(r => r.json())
      .then(d => {
        if (!d.success) { setFocusAiError(d.error || "Something went wrong. Try again."); return; }
        if (d.empty) { setFocusAiError(`Couldn't tie that to a place or segment - try naming a city and a goal, e.g. "Edmonton battery upsell".`); return; }
        if (!(d.leadIds || []).length) { setFocusAiError("No leads matched that. Try a different city or segment."); return; }
        setFocusAiTitle(d.parsed?.title || "");
        setFocusAiResult(d);
      })
      .catch(() => setFocusAiError("Network error - try again."))
      .finally(() => setFocusAiLoading(false));
  };

  const createFocusFromAi = () => {
    const r = focusAiResult;
    if (!r || focusAiCreating) return;
    const title = (focusAiTitle.trim() || r.parsed?.title || "AI focus").slice(0, 80);
    setFocusAiCreating(true);
    fetch(`${API_URL}/api/focus`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title,
        directive: r.parsed?.directive || undefined,
        scopeType: r.parsed?.scopeType || undefined,
        scopeValue: r.parsed?.scopeValue || undefined,
        leadIds: r.leadIds || [],
      }),
    })
      .then(res => res.json())
      .then(d => {
        if (!d.success) { setFocusAiError(d.error || "Couldn't create the focus."); return; }
        setFocusAiOpen(false); setFocusAiPrompt(""); setFocusAiTitle(""); setFocusAiResult(null); setFocusAiError(null);
        loadFocus();
      })
      .catch(() => setFocusAiError("Network error creating the focus."))
      .finally(() => setFocusAiCreating(false));
  };

  // Stage/outcome pairs chosen so focus_rollup buckets stay mutually exclusive.
  const FOCUS_STATUS_WRITE: any = {
    todo: { stage: "todo", outcome: null },
    working: { stage: "working", outcome: null },
    won: { stage: "won", outcome: "won" },
    lost: { stage: "lost", outcome: "lost" },
  };
  const setItemStatus = (leadId: any, status: string) => {
    const w = FOCUS_STATUS_WRITE[status]; if (!w) return;
    setFocusLeads((prev: any[]) => prev.map((l) => l.id === leadId ? { ...l, stage: w.stage, outcome: w.outcome } : l));
    fetch(`${API_URL}/api/user/pipeline/${leadId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(w),
    })
      .then(r => r.json())
      .then(d => { if (!d.success) console.error('[setItemStatus] failed:', d.error); loadFocus(); })
      .catch(err => console.error('[setItemStatus] network error:', err));
  };

  const moveItemToFocus = (leadId: any, listId: any) => {
    setFocusMoveFor(null);
    fetch(`${API_URL}/api/user/pipeline/${leadId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ focusListId: listId, stage: "todo" }),
    })
      .then(r => r.json())
      .then(d => {
        if (!d.success) { console.error('[moveItemToFocus] failed:', d.error); return; }
        setFocusLeads((prev: any[]) => prev.filter((l) => l.id !== leadId));
        loadFocus();
      })
      .catch(err => console.error('[moveItemToFocus] network error:', err));
  };

  const archiveFocus = (listId: any, status: string) => {
    fetch(`${API_URL}/api/focus/${listId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    })
      .then(r => r.json())
      .then(d => { if (!d.success) console.error('[archiveFocus] failed:', d.error); setFocusSel(null); loadFocus(); })
      .catch(err => console.error('[archiveFocus] network error:', err));
  };

  const deleteFocus = (listId: any) => {
    fetch(`${API_URL}/api/focus/${listId}`, { method: "DELETE" })
      .then(r => r.json())
      .then(d => { if (!d.success) console.error('[deleteFocus] failed:', d.error); setFocusSel(null); loadFocus(); })
      .catch(err => console.error('[deleteFocus] network error:', err));
  };

  useEffect(() => {
    if (view === "pipeline") { setFocusSel(null); setFocusMoveFor(null); loadFocus(); }
    else if (view === "explore") { loadFocus(); } // feed focus-picker needs the list
  }, [view]);

  const [callLog, setCallLog] = useState(() => {
    try { const saved = localStorage.getItem('sl_calllog'); return saved ? JSON.parse(saved) : {}; } catch { return {}; }
  });
  // Sync pipeline changes to backend. Server is source of truth - we no longer
  // cache pipeLeads in localStorage. The useEffect fires on every pipeLeads
  // change and upserts each lead (no-op on server if already present, updates
  // stage if changed). Remove is handled explicitly via removeFromPipelineBackend.
  const pipelineSyncedRef = useRef(false);
  useEffect(() => {
    // Skip first run (the initial server-driven populate)
    if (!pipelineSyncedRef.current) { pipelineSyncedRef.current = true; return; }
    const email = authedUser?.email;
    if (!email || pipeLeads.length === 0) return;
    pipeLeads.forEach((lead: any) => {
      if (lead.id) savePipelineToBackend(lead.id, lead.stage || 'new');
    });
  }, [pipeLeads]);
  useEffect(() => {
    try { if (Object.keys(callLog).length > 0) localStorage.setItem('sl_calllog', JSON.stringify(callLog)); } catch {}
  }, [callLog]);
  const [loggingId, setLoggingId] = useState(null);
  const [logOutcome, setLogOutcome] = useState("");
  const [logNote, setLogNote] = useState("");
  const [unlocked, setUnlocked] = useState<any>({});
  // App-level credit purchase - lifted out of Settings so the out-of-credits
  // modal and low-credit banner can both trigger it. Redirects to Stripe
  // checkout; on return (?credits_purchased=N) credits are already applied.
  const purchaseCreditPack = async (credits: number) => {
    setCreditsPurchasing(credits);
    try {
      const res = await fetch(`${API_URL}/api/credits/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ pack: String(credits) }),
      });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; return; }
      alert(data.error || "Could not start purchase. Try again.");
    } catch (e: any) {
      alert("Purchase failed: " + e.message);
    } finally {
      setCreditsPurchasing(null);
    }
  };

  // Performs a contact unlock. Extracted to App root so it can be invoked both
  // from the lead-drawer button AND from the compliance gate after the user
  // acknowledges. Behaviour is identical to the original inline handler.
  const performUnlock = async (lead: any) => {
    setUnlocking(lead.id);
    try {
      const res = await fetch(`${API_URL}/api/leads/${lead.id}/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.email || "anonymous" }),
      });
      const data = await res.json();
      if (data.success && data.lead) {
        setUnlocked((u: any) => ({ ...u, [lead.id]: data.lead }));
        markFirstMove("unlock");
        if (!data.cached) {
          setUser((prev: any) => ({ ...prev, creditsRemaining: Math.max(0, (prev.creditsRemaining || 10) - 1) }));
        }
        const alreadyInPipe = pipeLeads.some((p: any) => p.id === lead.id);
        if (alreadyInPipe) {
          setPipeLeads((prev: any) => prev.map((p: any) => p.id === lead.id ? { ...p, stage: "unlocked" } : p));
          updatePipelineStage(lead.id, "unlocked");
        } else {
          setPipeLeads((prev: any) => [...prev, { ...lead, stage: "unlocked" }]);
          savePipelineToBackend(lead.id, "unlocked");
        }
      } else {
        const errMsg = (data.error || "").toLowerCase();
        const isOutOfCredits = errMsg.includes("no credits") || errMsg.includes("credits remaining");
        if (isOutOfCredits) {
          setShowCreditsModal(true);
        } else {
          alert(data.error || "Could not unlock contact. Try again.");
        }
      }
    } catch (err: any) {
      alert("Unlock failed: " + err.message);
    }
    setUnlocking(null);
  };

  // Records the one-time compliance acknowledgment server-side, then completes
  // the unlock the user was attempting. Admins never reach this (gated out).
  const acknowledgeAndUnlock = async () => {
    setAcknowledging(true);
    try {
      const res = await fetch(`${API_URL}/api/user/acknowledge-skiptrace`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
      });
      const data = await res.json();
      if (data.success) {
        setUser((prev: any) => ({ ...prev, skiptraceAckAt: data.skiptraceAckAt }));
        setShowComplianceGate(false);
        const lead = pendingUnlockLead;
        setPendingUnlockLead(null);
        if (lead) performUnlock(lead);
      } else {
        alert(data.error || "Could not record acknowledgment. Try again.");
      }
    } catch (e: any) {
      alert("Network error: " + e.message);
    } finally {
      setAcknowledging(false);
    }
  };
  // Unlocked contacts are server-owned (contact_unlocks table). No localStorage
  // cache - it previously caused stale unlock data to show when the server
  // disagreed. loadUserUnlocks hydrates state from the server on login.
  const [unlocking, setUnlocking] = useState(null);
  // Out-of-credits upsell: modal shown on unlock-with-0-credits, and the
  // low-credit banner. Both call purchaseCreditPack(). creditsPurchasing
  // holds the pack size mid-request so the button can show a spinner.
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditsPurchasing, setCreditsPurchasing] = useState<number | null>(null);
  // One-time skip-trace compliance gate: shown before a user's FIRST contact
  // unlock. pendingUnlockLead holds the lead they were trying to unlock so we
  // can complete it after they acknowledge.
  const [showComplianceGate, setShowComplianceGate] = useState(false);
  const [pendingUnlockLead, setPendingUnlockLead] = useState<any>(null);
  const [acknowledging, setAcknowledging] = useState(false);
  const [modalLead, setModalLead] = useState(null);
  const [modalTab, setModalTab] = useState("overview");
  const [modalMarketCtx, setModalMarketCtx] = useState<any>(null);
  const [modalMarketLoading, setModalMarketLoading] = useState(false);

  // Fetch market context when a lead is opened. Keyed on lead id so it runs
  // once per lead, not on every modal re-render (previous version fired in
  // render - caused ~1 fetch per React update while the drawer was open).
  useEffect(() => {
    if (!modalLead) { setModalMarketCtx(null); setModalMarketLoading(false); return; }
    const lead = modalLead;
    setModalMarketCtx(null);
    setModalMarketLoading(true);
    setModalTab("overview"); // reset to first tab on new lead
    const state = (lead.province || lead.state || "").trim();
    const qp = lead.zip_code
      ? `zip=${encodeURIComponent(lead.zip_code)}`
      : `city=${encodeURIComponent(lead.city || "")}${state ? `&state=${encodeURIComponent(state)}` : ""}`;
    let cancelled = false;
    fetch(`${API_URL}/api/market/context?${qp}`)
      .then((r: any) => r.json())
      .then((d: any) => {
        if (cancelled) return;
        if (d.success && d.context) setModalMarketCtx(d.context);
        setModalMarketLoading(false);
      })
      .catch(() => { if (!cancelled) setModalMarketLoading(false); });
    return () => { cancelled = true; };
  }, [modalLead?.id]);

  // Close drawer on Esc
  useEffect(() => {
    if (!modalLead) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setModalLead(null); setModalMarketCtx(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalLead]);

  // ── Lead-drawer helpers ────────────────────────────────────────────────────
  // mapServerLead mirrors the backend's mapLead in server.js. Keep them in sync
  // when either side adds a column. openLeadById is used when a row gives us a
  // permit id but not the full lead record (recent-permits rows in the
  // Competitors view come from the get_contractor_intel RPC payload, which is
  // snake_case and missing several columns the drawer renders). Stub-first for
  // instant feedback, then patches in the full lead once /api/leads/:id resolves.
  const mapServerLead = (l: any) => ({
    id: l.id, address: l.address, city: l.city,
    state: l.province || l.state, province: l.province, zip: l.zip,
    neighborhood: l.neighborhood, leadScore: l.lead_score,
    daysOld: l.days_old, permitType: l.permit_type,
    permitValue: l.permit_value, permitDate: l.permit_date,
    intentSignal: l.intent_signal, sqft: l.sqft,
    roofSqft: l.roof_sqft, estimatedBill: l.estimated_bill,
    ownerName: l.owner_name, ownerPhone: l.owner_phone,
    ownerEmail: l.owner_email, contactSource: l.contact_source,
    status: l.status, activities: l.activities,
    lastContactedAt: l.last_contacted_at, dealValue: l.deal_value,
    dealClosedAt: l.deal_closed_at, assignedTo: l.assigned_to,
    notes: l.notes, dataSource: l.data_source, country: l.country,
    contractorName: l.contractor_name,
    contractorPhone: l.contractor_phone,
    contractorEmail: l.contractor_email,
    description: l.description,
    neighborScore: l.neighbor_score,
    velocityScore: l.velocity_score,
    priorityScore: l.priority_score,
    signalScore: l.signal_score,      // 0-10 Signal Score (the score the app shows)
    signalFactors: l.signal_factors,  // per-pillar "why" jsonb
    property_data: l.property_data,  // Alberta enrichment (Calgary + Edmonton open data)
    intel: l.intel,                  // real per-lead intel from get_lead_intel RPC (entrenchment + recent-filing density)
  });

  const openLeadById = (id: any, stub: any = null) => {
    if (id == null) return;
    // Render stub immediately for instant UX. Don't stomp a same-id lead
    // already loaded with full data (handles double-click and re-open).
    if (stub) {
      setModalLead((prev: any) => (prev && String(prev.id) === String(id)) ? prev : stub);
    }
    setModalTab("overview");
    fetch(`${API_URL}/api/leads/${encodeURIComponent(id)}`)
      .then((r: any) => r.ok ? r.json() : null)
      .then((d: any) => {
        if (!d || !d.success || !d.lead) return;
        const full = mapServerLead(d.lead);
        // Guard against races: only swap in the full lead if the user is
        // still on this id (i.e. hasn't closed or clicked away).
        setModalLead((prev: any) => (prev && String(prev.id) === String(id)) ? full : prev);
      })
      .catch(() => { /* keep the stub on the screen - better than blanking */ });
  };

  // ── AI Ask state ───────────────────────────────────────────────────────────
  // Powers the /api/ask agentic endpoint. State is lifted to App root so it
  // survives navigation away from the Ask view (re-opening preserves the last
  // answer until the user asks something new).
  const [askInput, setAskInput] = useState("");
  const [askLoading, setAskLoading] = useState(false);
  const [askAnswer, setAskAnswer] = useState<any>(null);
  const [askError, setAskError] = useState<string | null>(null);
  const [askLastQuestion, setAskLastQuestion] = useState("");
  const [askStages, setAskStages] = useState<any[]>([]);
  const [askToolCalls, setAskToolCalls] = useState(0);
  const [askLoadingPhraseIdx, setAskLoadingPhraseIdx] = useState(0);
  const askAnswerRef = useRef<HTMLDivElement | null>(null);
  const searchDebounceRef = useRef<any>(null);
  const [askRecent, setAskRecent] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("sl_ask_recent") || "[]"); } catch { return []; }
  });
  const pushAskRecent = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setAskRecent(prev => {
      const next = [trimmed, ...prev.filter(p => p !== trimmed)].slice(0, 5);
      try { localStorage.setItem("sl_ask_recent", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Cycle loading phrases every 1.5s while a query is in flight.
  // Lives at App root because AskView is invoked as a function (not <AskView/>)
  // and can't safely host its own useEffect.
  useEffect(() => {
    if (!askLoading) { setAskLoadingPhraseIdx(0); return; }
    const id = setInterval(() => setAskLoadingPhraseIdx(i => i + 1), 1500);
    return () => clearInterval(id);
  }, [askLoading]);

  const submitAsk = async (question: string) => {
    const q = (question || "").trim();
    if (!q || askLoading) return;
    pushAskRecent(q);
    setAskInput(q);
    setAskLastQuestion(q);
    setAskLoading(true);
    setAskError(null);
    setAskAnswer(null);
    setAskStages([]);
    setAskToolCalls(0);
    try {
      const territoryCity = onbCity || (typeof window !== "undefined" ? (window as any).__siq_city : null);
      const territoryProvince = onbRegion || (typeof window !== "undefined" ? (window as any).__siq_region : null);
      const res = await fetch(`${API_URL}/api/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: q,
          context: territoryCity && territoryProvince ? { city: territoryCity, province: territoryProvince } : {},
        }),
      });
      const d = await res.json();
      if (d.success) {
        setAskAnswer(d.answer);
        setAskStages(d.stages || []);
        setAskToolCalls(d.tool_calls || 0);
        setTimeout(() => askAnswerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      } else {
        setAskError(d.error || "Something went wrong. Try rephrasing.");
      }
    } catch (e: any) {
      setAskError(e?.message || "Network error. Check your connection.");
    }
    setAskLoading(false);
  };

  const humanizeStage = (s: any) => {
    const args = s?.args || {};
    if (s.tool === "find_street_clusters")    return `Searching streets in ${args.city || "the market"}`;
    if (s.tool === "find_recent_activity")    return `Checking recent surges in ${args.city || "the market"}`;
    if (s.tool === "find_permits_by_segment") return `Pulling ${args.segment || "segment"} installs in ${args.city || "the market"}`;
    if (s.tool === "get_market_summary")      return `Summarising ${args.city || "market"}`;
    if (s.tool === "find_top_markets")        return `Ranking top markets by ${args.criterion || "activity"}`;
    if (s.tool === "get_contractor_intel")    return `Pulling intel on ${args.name || "contractor"}`;
    if (s.tool === "search_address")          return `Searching addresses for "${args.query || ""}"`;
    return s.tool || "Working";
  };


  const [onboarded, setOnboarded] = useState(true);
  const [onbStep, setOnbStep] = useState(0);
  const [onbCountry, setOnbCountry] = useState(null);
  const [onbRegion, setOnbRegion] = useState<any>(() => territoryToScope(currentTerritory).region || null);
  const [onbCity, setOnbCity] = useState<any>(() => territoryToScope(currentTerritory).city || null);

  // First-run setup overlay. Shown once for a brand-new user (no saved
  // territory yet). Skippable; never reappears once dismissed. See FirstRunSetup.tsx.
  const [showFirstRun, setShowFirstRun] = useState<boolean>(() => {
    try {
      if (localStorage.getItem("sl_firstrun_done")) return false;
      if (localStorage.getItem("sl_territory")) return false; // already chose a market
      return true;
    } catch { return false; }
  });
  // Single entry point for choosing a territory: keeps currentTerritory (drives
  // the briefing) and onbCity/onbRegion (drive the cards + feed) in lockstep.
  const selectTerritory = (name: string) => {
    setCurrentTerritory(name);
    try { localStorage.setItem("sl_territory", name); } catch {}
    markFirstMove("territory");
    const scope = territoryToScope(name);
    setOnbCity(scope.city);
    setOnbRegion(scope.region);
  };
  const finishFirstRun = () => {
    try { localStorage.setItem("sl_firstrun_done", "1"); } catch {}
    setShowFirstRun(false);
  };
  // First-3-moves strip (Today page). Walks a new rep to their first unlocked
  // homeowner in three taps. State is localStorage-only (sl_first_moves /
  // sl_first_moves_done); null = hidden (finished or dismissed). Step 3 is
  // marked done exclusively by a real unlock inside performUnlock.
  const [firstMoves, setFirstMoves] = useState<{ territory: boolean; segment: boolean; unlock: boolean } | null>(() => {
    try {
      if (localStorage.getItem("sl_first_moves_done")) return null;
      const raw = localStorage.getItem("sl_first_moves");
      const saved = raw ? JSON.parse(raw) : {};
      return {
        territory: !!localStorage.getItem("sl_territory") || !!saved.territory,
        segment: !!saved.segment,
        unlock: !!saved.unlock,
      };
    } catch { return null; }
  });
  const markFirstMove = (step: "territory" | "segment" | "unlock") => {
    setFirstMoves((prev) => {
      if (!prev || prev[step]) return prev;
      const next = { ...prev, [step]: true };
      try {
        localStorage.setItem("sl_first_moves", JSON.stringify(next));
        if (next.territory && next.segment && next.unlock) localStorage.setItem("sl_first_moves_done", "1");
      } catch {}
      return next;
    });
  };
  const dismissFirstMoves = () => {
    try { localStorage.setItem("sl_first_moves_done", "1"); } catch {}
    setFirstMoves(null);
  };
  const [onbSegments, setOnbSegments] = useState([]);
  const [calcRegion, setCalcRegion] = useState("TX");
  const [calcKw, setCalcKw] = useState(8);
  const [calcBattery, setCalcBattery] = useState(false);
  const [calcExpanded, setCalcExpanded] = useState(null);
  const [calcMode, setCalcMode] = useState("prep");
  const [calcMonthlyBill, setCalcMonthlyBill] = useState(150);
  const [calcSlide, setCalcSlide] = useState(0);
  const [showCredits, setShowCredits] = useState(false);
  const [showTerritoryPicker, setShowTerritoryPicker] = useState(false);

  // ── Competitors view data fetching (state declared near view state above) ──
  // Fetch leaderboard when on competitors view with no one drilled into
  useEffect(() => {
    if (view !== "competitors" || selectedContractor) return;
    setCompLeaderLoading(true);
    const stateParam = onbRegion ? `&state=${encodeURIComponent(onbRegion)}` : "";
    fetch(`${API_URL}/api/insights/competitor-moves?days=0&limit=20&samples=3${stateParam}`)
      .then((r: any) => r.json())
      .then((d: any) => {
        if (d.success) setCompLeaderboard(d.competitors || []);
        setCompLeaderLoading(false);
      })
      .catch((err: any) => {
        console.error("[competitors leaderboard] fetch failed:", err);
        setCompLeaderLoading(false);
      });
  }, [view, selectedContractor, onbRegion]);

  // Fetch full intel when a contractor is selected
  useEffect(() => {
    if (!selectedContractor) { setCompContractor(null); return; }
    setCompContractorLoading(true);
    fetch(`${API_URL}/api/insights/contractor-intel?name=${encodeURIComponent(selectedContractor)}`)
      .then((r: any) => r.json())
      .then((d: any) => {
        if (d.success) setCompContractor(d.contractor);
        setCompContractorLoading(false);
      })
      .catch((err: any) => {
        console.error("[contractor intel] fetch failed:", err);
        setCompContractorLoading(false);
      });
  }, [selectedContractor]);

  // Safety: the "competitors" view must never render without a contractor
  // drilled in (that path showed a stale legacy leaderboard). If we ever land
  // there with nothing selected, bounce to the Contractors directory.
  useEffect(() => {
    if (view === "competitors" && !selectedContractor) setView("roofers");
  }, [view, selectedContractor]);

  // ── Markets v2: fetch detail when a market is selected ──────────────────────
  useEffect(() => {
    if (!selectedMarket) { setMarketDetail(null); return; }
    setMarketDetailLoading(true);
    fetch(`${API_URL}/api/markets/${encodeURIComponent(selectedMarket.province)}/${encodeURIComponent(selectedMarket.city)}`)
      .then((r: any) => r.json())
      .then((d: any) => {
        if (d.success) setMarketDetail(d.market);
        setMarketDetailLoading(false);
      })
      .catch((err: any) => {
        console.error("[markets/detail] fetch failed:", err);
        setMarketDetailLoading(false);
      });
  }, [selectedMarket]);

  // ── Markets v2: fetch top hot leads for the selected market ─────────────────
  // Drives the "Top Opportunities" section on the market detail page.
  // Uses a SEPARATE state from the global feedLeads so navigating back to
  // Permits doesn't show the wrong territory's leads.
  useEffect(() => {
    if (!selectedMarket) { setMarketHotLeads([]); return; }
    setMarketHotLeadsLoading(true);
    const params = new URLSearchParams();
    const dbCity = CITY_ALIASES[selectedMarket.city] || selectedMarket.city;
    params.set("city", dbCity);
    params.set("state", selectedMarket.province);
    params.set("limit", "60");
    params.set("offset", "0");
    fetch(`${API_URL}/api/leads?${params}`)
      .then((r: any) => r.json())
      .then((d: any) => {
        if (d.success) {
          const leads = (d.leads || []) as any[];
          const top = leads
            .map((l: any) => ({ ...l, _smartScore: getSmartScore(l) }))
            .sort((a: any, b: any) => b._smartScore - a._smartScore)
            .slice(0, 6);
          setMarketHotLeads(top);
        }
        setMarketHotLeadsLoading(false);
      })
      .catch((err: any) => {
        console.error("[markets/hotLeads] fetch failed:", err);
        setMarketHotLeadsLoading(false);
      });
  }, [selectedMarket]);

  // ── Markets v2: load the city list once when entering Markets view ──────────
  useEffect(() => {
    if (view !== "zones") return;
    if (marketsList.length > 0) return;
    setMarketsListLoading(true);
    fetch(`${API_URL}/api/markets/list`)
      .then((r: any) => r.json())
      .then((d: any) => {
        if (d.success && Array.isArray(d.markets)) setMarketsList(d.markets);
        if (d.platformTotals) setMarketsPlatformTotals(d.platformTotals);
        setMarketsListLoading(false);
      })
      .catch((err: any) => {
        console.error("[markets/list] fetch failed:", err);
        setMarketsListLoading(false);
      });
  }, [view]);

  // ── Contractors v2: fetch directory whenever filters change ─────────────────
  // Debounced via setTimeout so typing in search doesn't fire on every keystroke.
  useEffect(() => {
    if (view !== "roofers" || selectedContractor) return;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      setContractorsLoading(true);
      const qs = new URLSearchParams({
        country: contractorsCountry === "ALL" ? "" : contractorsCountry,
        status:  contractorsStatus,
        sort:    contractorsSort,
        search:  contractorsSearch,
        limit:   String(contractorsLimit),
      });
      // Strip empty params
      Array.from(qs.entries()).forEach(([k, v]) => { if (!v) qs.delete(k); });
      fetch(`${API_URL}/api/contractors/directory?${qs}`, { signal: ctrl.signal })
        .then((r: any) => r.json())
        .then((d: any) => {
          if (d.success) setContractorsResults({ total: d.total, rows: d.rows });
          setContractorsLoading(false);
        })
        .catch((err: any) => {
          if (err.name !== "AbortError") {
            console.error("[contractors/directory] fetch failed:", err);
            setContractorsLoading(false);
          }
        });
    }, contractorsSearch ? 250 : 0); // debounce search typing
    return () => { ctrl.abort(); clearTimeout(t); };
  }, [view, selectedContractor, contractorsCountry, contractorsStatus, contractorsSort, contractorsSearch, contractorsLimit]);

  // ── Reset pagination when filters change (else load-more state leaks across queries)
  useEffect(() => {
    setContractorsLimit(50);
  }, [contractorsCountry, contractorsStatus, contractorsSort, contractorsSearch]);

  // ── Movement feed: load once when entering Contractors view ─────────────────
  useEffect(() => {
    if (view !== "roofers") return;
    if (contractorsMovement.length > 0) return; // already loaded
    fetch(`${API_URL}/api/contractors/movement?days=60&limit=20`)
      .then((r: any) => r.json())
      .then((d: any) => { if (d.success) setContractorsMovement(d.rows || []); })
      .catch(() => {});
  }, [view]);

  // Fetch live territories from backend on mount - replaces hardcoded 99-cities catalog.
  // HARDENED: strictly validates region codes against known US/CA allowlist and rejects
  // garbage city names (permit-type labels, data-source names, numbers-only strings, etc.)
  // that have leaked into the city column in the leads table.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/territories`);
        const data = await r.json();
        if (!active || !data?.success || !Array.isArray(data.territories) || data.territories.length === 0) return;

        // Canonical allowlists - any region code outside these sets is rejected entirely
        const US_STATES = new Set(["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"]);
        const CA_PROVINCES = new Set(["AB","BC","MB","NB","NL","NS","ON","PE","QC","SK","YT","NT","NU"]);
        // Combined allowlist - listed inline instead of spreading Sets (spread-iteration needs ES2015+ target)
        const VALID_REGIONS = new Set(["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","AB","BC","MB","NB","NL","NS","ON","PE","QC","SK","YT","NT","NU"]);

        const STATE_LABELS: Record<string, string> = {
          AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado",
          CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho",
          IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
          ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
          MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
          NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
          OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
          SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
          WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming", DC: "Washington DC",
          AB: "Alberta", BC: "British Columbia", MB: "Manitoba", NB: "New Brunswick",
          NL: "Newfoundland and Labrador", NS: "Nova Scotia", ON: "Ontario", PE: "Prince Edward Island",
          QC: "Quebec", SK: "Saskatchewan", YT: "Yukon", NT: "Northwest Territories", NU: "Nunavut",
        };

        // Known garbage tokens that have leaked into the city field from permit-type labels,
        // data-source names, company suffixes, or raw nulls. Matched case-insensitive as whole words.
        const GARBAGE_RE = /\b(etj|ltd|llc|inc|corp|corporation|online mapping|full purpose|dataset|api endpoint|unknown|none|null|n\/a|undefined|solar installation|photovoltaic|permits?|building|construction|dataset|data source|mapping|jurisdiction|test)\b/i;
        const isLikelyRealCity = (c: string) => {
          if (!c || c.length < 2 || c.length > 60) return false;
          if (!/[a-zA-Z]/.test(c)) return false;           // must contain at least one letter
          if (GARBAGE_RE.test(c)) return false;            // reject known garbage tokens
          if (/^[0-9\W]+$/.test(c)) return false;          // reject numbers-or-symbols only
          if (c.split(/\s+/).length > 5) return false;     // real city names rarely exceed 5 tokens
          return true;
        };

        const byCountry: Record<string, Record<string, { region: string; label: string; cities: string[] }>> = { US: {}, CA: {} };
        let rejected = 0;

        for (const t of data.territories) {
          const city = String(t.city || t.name || "").trim();
          const region = String(t.province || t.state || t.region || "").trim().toUpperCase();

          if (!VALID_REGIONS.has(region)) { rejected++; continue; }
          if (!isLikelyRealCity(city)) { rejected++; continue; }

          const country = CA_PROVINCES.has(region) ? "CA" : "US";
          if (!byCountry[country][region]) {
            byCountry[country][region] = { region, label: STATE_LABELS[region] || region, cities: [] };
          }
          // Case-insensitive dedupe
          const existingLower = new Set(byCountry[country][region].cities.map(c => c.toLowerCase()));
          if (!existingLower.has(city.toLowerCase())) {
            byCountry[country][region].cities.push(city);
          }
        }

        if (rejected > 0) {
          // Visible warning in console so we can trace bad records upstream
          console.warn(`[SolarLead] Territory fetch: rejected ${rejected} malformed records (unknown region code or garbage city name). Backend leads table needs cleanup.`);
        }

        const groups = [
          { country: "US", countryLabel: "United States", flag: "🇺🇸",
            regions: Object.values(byCountry.US).map(r => ({ ...r, cities: r.cities.slice().sort() })).sort((a, b) => a.label.localeCompare(b.label)) },
          { country: "CA", countryLabel: "Canada", flag: "🇨🇦",
            regions: Object.values(byCountry.CA).map(r => ({ ...r, cities: r.cities.slice().sort() })).sort((a, b) => a.label.localeCompare(b.label)) },
        ].filter(g => g.regions.length > 0);

        // Safety net: if validation killed everything, fall back to hardcoded
        const totalCities = groups.reduce((a, g) => a + g.regions.reduce((b, r) => b + r.cities.length, 0), 0);
        if (groups.length > 0 && totalCities >= 10) setLiveTerritoryGroups(groups);
      } catch (e) {
        // Silent fallback - hardcoded TERRITORY_GROUPS will be used
      }
    })();
    return () => { active = false; };
  }, []);

  // Fetch city intel (top contractors, growth, avg value) when territory changes
  useEffect(() => {
    if (!onbCity) { setCityIntel(null); return; }
    let active = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/market/city-intel?city=${encodeURIComponent(onbCity)}`);
        const data = await r.json();
        if (active && data?.success) setCityIntel(data);
      } catch {}
    })();
    return () => { active = false; };
  }, [onbCity]);
  const [showWelcomeTour, setShowWelcomeTour] = useState(() => localStorage.getItem("siq_tour_done") !== "1");
  const [tourStep, setTourStep] = useState(0);
  const [heroCount, setHeroCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [solarIntel, setSolarIntel] = useState<any>(null);
  const [marketData, setMarketData] = useState<any>(null);
  const [marketInstallers, setMarketInstallers] = useState<any>(null);
  const [marketPricing, setMarketPricing] = useState<any>(null);
  const [marketTech, setMarketTech] = useState<any>(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [radarMarkets, setRadarMarkets] = useState<any[]>([]);
  const [showDashOnboarding, setShowDashOnboarding] = useState(false);
  const [onbNameInput, setOnbNameInput] = useState("");
  const [onbTerritoryPick, setOnbTerritoryPick] = useState("");
  const [onbDashStep, setOnbDashStep] = useState(1);
  const [exLocalSearch, setExLocalSearch] = useState("");

  // ── ACTIVITY BELL ──────────────────────────────────────────────────────────
  // "What's new since you last opened the app." Reactive but feels proactive.
  // Persists last-seen timestamp in localStorage; on mount, fetches recent permits
  // in the user's territory created since that timestamp.
  const [notifItems, setNotifItems] = useState<any[]>([]);
  const [notifCount, setNotifCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const NOTIF_LAST_SEEN_KEY = "sl_last_seen_activity_v1";

  // ── ROOFERS PAGE ───────────────────────────────────────────────────────────
  // Full leaderboard view + drill-down drawer for one specific roofer.
  // List sorts by permit_count desc, supports search by name and CSV export.
  const [roofersList, setRoofersList] = useState<any[]>([]);
  const [roofersLoading, setRoofersLoading] = useState(false);
  const [roofersSearch, setRoofersSearch] = useState("");
  const [roofersSort, setRoofersSort] = useState<"permits" | "recent" | "name">("permits");
  const [openRoofer, setOpenRoofer] = useState<any>(null);
  const [openRooferPermits, setOpenRooferPermits] = useState<any[]>([]);
  const [openRooferLoading, setOpenRooferLoading] = useState(false);
  const [openRooferTotal, setOpenRooferTotal] = useState(0);
  const [copiedRooferId, setCopiedRooferId] = useState<string | null>(null);
  const [roofersTotalUnique, setRoofersTotalUnique] = useState(0);
  const [roofersSampleSize, setRoofersSampleSize] = useState(0);
  const [roofersTotalPermits, setRoofersTotalPermits] = useState(0);
  // State chip filter - Roofers-page-only override of the global territory.
  // Defaults to user's onbRegion if they have one, else null (nationwide view).
  const [roofersStateFilter, setRoofersStateFilter] = useState<string | null>(null);
  const [roofersAvailableStates, setRoofersAvailableStates] = useState<any[]>([]);
  const [exLocalSegment, setExLocalSegment] = useState<any>(null);
  const [exLoadError, setExLoadError] = useState("");

  const weather = useWeather(
    onbCity
      ? `${onbCity}, ${onbRegion || ""}`.trim().replace(/,\s*$/, "")
      : onbRegion,
    onbCountry
  );

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  // Fetch activity since last visit - powers the notification bell.
  // Uses /api/leads with sinceDate filter, scoped to user's territory.
  useEffect(() => {
    if (!authedUser) return;
    let active = true;
    // Local alias map - CITY_ALIASES is declared further down (TDZ), so we
    // mirror just the values we need here. Keep in sync with CITY_ALIASES below.
    const NOTIF_CITY_ALIAS: Record<string, string> = { "Washington DC": "Washington" };
    const lastSeenIso = localStorage.getItem(NOTIF_LAST_SEEN_KEY)
      || new Date(Date.now() - 7 * 86400000).toISOString(); // default: last 7 days
    const sinceDate = lastSeenIso.slice(0, 10);
    const params = new URLSearchParams({ sinceDate, limit: "50" });
    if (onbCity) params.set("city", NOTIF_CITY_ALIAS[onbCity] || onbCity);
    if (onbRegion) params.set("state", onbRegion);
    fetch(`${API_URL}/api/leads?${params}`)
      .then(r => r.json())
      .then(d => {
        if (!active) return;
        const leads = (d?.leads || []) as any[];
        // Cap at 50 for performance; real fresh lead density is the headline,
        // not a precise count past 50 (badge clamps to "50+" in the UI).
        setNotifItems(leads.slice(0, 50));
        setNotifCount(leads.length);
      })
      .catch(() => { if (active) { setNotifItems([]); setNotifCount(0); } });
    return () => { active = false; };
  }, [authedUser, onbCity, onbRegion]);

  // Mark all activity as read - updates timestamp, clears badge optimistically
  const markActivityRead = () => {
    try { localStorage.setItem(NOTIF_LAST_SEEN_KEY, new Date().toISOString()); } catch {}
    setNotifCount(0);
  };

  // When Roofers view first opens, default the state chip to the user's
  // global territory state (if set). Doesn't override if they've already
  // picked a chip during this session - only on initial entry.
  useEffect(() => {
    if (view !== "roofers") return;
    if (roofersStateFilter === null && onbRegion) {
      setRoofersStateFilter(onbRegion);
    }
  }, [view]);

  // Fetch the list of states with significant roofing permit data - once,
  // on first entry to the Roofers view. Powers the chip row.
  useEffect(() => {
    if (view !== "roofers" || roofersAvailableStates.length > 0) return;
    let active = true;
    fetch(`${API_URL}/api/roofing/states`)
      .then(r => r.json())
      .then(d => { if (active && d?.success) setRoofersAvailableStates(d.states || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [view]);

  // Fetch the full Roofers leaderboard ONCE when entering the Roofers view.
  // Pulls 1000 nationwide top contractors with state metadata. State chip
  // filtering, search, and sort all happen client-side from this dataset -
  // chip clicks are instant, no network round-trip.
  useEffect(() => {
    if (view !== "roofers") return;
    if (roofersList.length > 0) return; // already have the data
    let active = true;
    setRoofersLoading(true);
    fetch(`${API_URL}/api/roofing/leaderboard?days=180&limit=1000`)
      .then(r => r.json())
      .then(d => {
        if (!active) return;
        if (d?.success) {
          setRoofersList(d.leaderboard || []);
          setRoofersTotalUnique(d.unique_contractors || 0);
          setRoofersSampleSize(d.sample_size || 0);
          setRoofersTotalPermits(d.total_permits || 0);
        }
      })
      .catch(() => { if (active) { setRoofersList([]); setRoofersTotalUnique(0); setRoofersSampleSize(0); setRoofersTotalPermits(0); } })
      .finally(() => { if (active) setRoofersLoading(false); });
    return () => { active = false; };
  }, [view]);

  // Fetch permit history for the opened roofer drawer
  useEffect(() => {
    if (!openRoofer) return;
    let active = true;
    setOpenRooferLoading(true);
    setOpenRooferPermits([]);
    const params = new URLSearchParams({ name: openRoofer.name, days: "365", limit: "100" });
    if (onbCity) params.set("city", onbCity);
    if (onbRegion) params.set("state", onbRegion);
    fetch(`${API_URL}/api/roofing/contractor-permits?${params}`)
      .then(r => r.json())
      .then(d => {
        if (!active) return;
        if (d?.success) {
          setOpenRooferPermits(d.permits || []);
          setOpenRooferTotal(d.total || 0);
        }
      })
      .catch(() => { if (active) { setOpenRooferPermits([]); setOpenRooferTotal(0); } })
      .finally(() => { if (active) setOpenRooferLoading(false); });
    return () => { active = false; };
  }, [openRoofer, onbCity, onbRegion]);

  // Auth gate - uses conditional rendering (not early returns) to keep hooks stable
  const urlParams = new URLSearchParams(window.location.search);
  const legalPage = urlParams.get("page");
  const showAuthGate = !authChecked || authView === "login" || authView === "signup" || authView === "splash" || authView === "why" || authView === "forgot-password" || authView === "reset-password" || authView === "onboarding" || (showSplash && !authedUser);
  const showLegal = legalPage === "terms" || legalPage === "privacy";
  // Paywall - authenticated user without an active pro plan and not admin.
  // Catches abandoned-Stripe-checkout signups (plan='free') so they can't
  // browse public permit/contractor data via the export CSV without paying.
  const showPaywall = authChecked && !!authedUser && !authedUser.isAdmin && (!authedUser.plan || authedUser.plan === "free");

  // Animate hero count - runs on load with fallback, re-animates when real count arrives
  useEffect(() => {
    const target = realTotalCount > 0 ? realTotalCount : 921000;
    const duration = 1800;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    setHeroCount(0);
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setHeroCount(target); clearInterval(timer); }
      else setHeroCount(Math.floor(current));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [realTotalCount]);

  // ── FETCH REAL DATA FROM RAILWAY BACKEND ──────────────────────────────────
  // City aliases: maps picker display name → DB city name where they differ
  // ilike handles case (EDMONTON = Edmonton) automatically
  // Only need aliases where the name itself differs from what's in the DB
  const CITY_ALIASES: any = {
    // DC display name differs from DB
    "Washington DC": "Washington",
  };

  const fetchFeedPage = (city: any, region: any, page: number, segment: any = null, search: any = null) => {
    setFeedLoading(true);
    const params = new URLSearchParams();
    const dbCity = city ? (CITY_ALIASES[city] || city) : null;
    if (dbCity) params.set("city", dbCity);
    if (region) params.set("state", region);
    if (segment) params.set("segment", segment);
    if (search) params.set("search", search);
    params.set("limit", String(FEED_PAGE_SIZE));
    params.set("offset", String(page * FEED_PAGE_SIZE));
    fetch(`${API_URL}/api/leads?${params}`)
      .then((r: any) => { if (!r.ok) throw new Error(`Server error: ${r.status}`); return r.json(); })
      .then((d: any) => {
        if (d.success) {
          const newLeads = d.leads || [];
          if (page === 0) setFeedLeads(newLeads);
          else setFeedLeads((prev: any) => [...prev, ...newLeads]);
          setFeedTotal(d.total || 0);
          setFeedPage(page);
          // Auto-detect previously unlocked leads (contact info already in DB)
          const preUnlocked: any = {};
          newLeads.forEach((l: any) => {
            if (l.ownerPhone || l.ownerEmail || l.ownerName) {
              preUnlocked[l.id] = { owner_name: l.ownerName, owner_phone: l.ownerPhone, owner_email: l.ownerEmail };
            }
          });
          if (Object.keys(preUnlocked).length > 0) {
            setUnlocked((prev: any) => ({ ...prev, ...preUnlocked }));
          }
        } else {
          console.error("Feed error:", d.error);
        }
        setFeedLoading(false);
      })
      .catch((err: any) => { console.error("Feed fetch failed:", err); setFeedLoading(false); });
  };

  const fetchLeadsForTerritory = (city: any, region: any) => {
    const dbCity = city ? (CITY_ALIASES[city] || city) : null;
    const params = new URLSearchParams();
    if (dbCity) params.set("city", dbCity);
    if (region) params.set("state", region);
    params.set("limit", dbCity ? "200" : "50"); // Smaller limit when no city to avoid timeout

    // Fetch territory count
    if (dbCity || region) {
      const countParams = new URLSearchParams();
      if (dbCity) countParams.set("city", dbCity);
      if (region) countParams.set("state", region);
      fetch(`${API_URL}/api/leads/count?${countParams}`)
        .then((r: any) => r.json())
        .then((d: any) => { if (d.success && d.count > 0) setTerritoryCount(d.count); })
        .catch((_: any) => {});
    } else {
      // Fetch global count for "All territories"
      fetch(`${API_URL}/api/leads/count`)
        .then((r: any) => r.json())
        .then((d: any) => { if (d.success && d.count > 0) setTerritoryCount(d.count); })
        .catch((_: any) => setTerritoryCount(0));
    }

    // Only fetch leads if city is set - state-only is too slow
    if (dbCity) {
      fetch(`${API_URL}/api/leads?${params}`)
        .then((r: any) => r.json())
        .then((d: any) => {
          if (d.success && d.leads?.length > 0) setLeads(d.leads);
          else setLeads([]);
        })
        .catch((_: any) => setLeads([]));
    } else {
      setLeads([]);
    }
  };

  const fetchBriefingAndHotspots = (city: any, region: any) => {
    const dbCity = city ? (CITY_ALIASES[city] || city) : null;
    const params = new URLSearchParams();
    if (region) params.set("state", region);
    if (dbCity) params.set("city", dbCity);
    const q = params.toString() ? `?${params}` : "";
    fetch(`${API_URL}/api/dashboard/briefing${q}`)
      .then((r: any) => r.json())
      .then((d: any) => { if (d.success) { setBriefing(d.briefing); setBriefingLoaded(true); } })
      .catch((_: any) => {});
    fetch(`${API_URL}/api/dashboard/hotspots${q}`)
      .then((r: any) => r.json())
      .then((d: any) => { if (d.success) setHotspots(d.hotspots); })
      .catch((_: any) => {});
    // Fetch real segment counts - now paginated on server, no 5000 cap
    fetch(`${API_URL}/api/leads/segments${q}`)
      .then((r: any) => r.json())
      .then((d: any) => {
        if (d.success && d.segments) {
          const segTotal = Object.values(d.segments).reduce((a: any, b: any) => a + b, 0) as number;
          if (segTotal > 0 || d.total > 0) {
            setSegmentCounts(d.segments);
            const total = d.total || segTotal;
            setRealTotalCount(total);
            setTotalLeadCount(total);
            if (d.new_this_week !== undefined || d.service_window !== undefined) {
              setBriefing(prev => ({
                ...prev,
                newThisWeek: d.new_this_week ?? prev.newThisWeek,
                serviceWindow: d.service_window ?? prev.serviceWindow,
              }));
              setBriefingLoaded(true);
            }
          }
        }
      })
      .catch((_: any) => {});
    // Fetch market intelligence from solar_installations table
    const stateParam = region ? `?state=${region}` : "";
    const installerParam = region ? `?state=${region}&limit=10` : "?limit=10";
    setMarketLoading(true);
    // Also fetch radar for real hot markets
    fetch(`${API_URL}/api/market/radar`)
      .then((r: any) => r.json())
      .then((d: any) => { if (d.success && d.markets) setRadarMarkets(d.markets); })
      .catch(() => {});
    // Fetch AI solar intelligence - personalized to territory, cached 24h per territory
    const intelParams = new URLSearchParams();
    if (dbCity) intelParams.set("city", dbCity);
    if (region) intelParams.set("state", region);
    const intelQ = intelParams.toString() ? `?${intelParams}` : "";
    fetch(`${API_URL}/api/insights/solar-intel${intelQ}`)
      .then((r: any) => r.json())
      .then((d: any) => { if (d.success && d.data) setSolarIntel(d.data); })
      .catch(() => {});
    Promise.all([
      fetch(`${API_URL}/api/market/overview${stateParam}`).then((r: any) => r.json()).catch(() => null),
      fetch(`${API_URL}/api/market/installers${installerParam}`).then((r: any) => r.json()).catch(() => null),
      fetch(`${API_URL}/api/market/pricing${stateParam}`).then((r: any) => r.json()).catch(() => null),
      fetch(`${API_URL}/api/market/technology${stateParam}`).then((r: any) => r.json()).catch(() => null),
    ]).then(([overview, installers, pricing, tech]) => {
      if (overview?.success) setMarketData(overview.data);
      if (installers?.success) setMarketInstallers(installers.installers);
      if (pricing?.success) setMarketPricing(pricing.trends);
      if (tech?.success) setMarketTech(tech);
      setMarketLoading(false);
    }).catch(() => setMarketLoading(false));
  };

  useEffect(() => {
    // AUTH GATE - this is the root App component, and React runs a component's
    // effects no matter which branch its render returned. So on a fresh login
    // this effect used to fire while the screen still showed LoginPage: every
    // fetch below went out with no token, got a silent 401 (the catch/d.success
    // guards swallow failures by design), and never re-ran because the deps
    // never changed. Result: blank dashboard counts until a manual reload (by
    // which time the token existed at mount). Gating on authReady makes this
    // run exactly once per signed-in session - at handleLogin, or after the
    // boot /api/auth/me hydration on a reload.
    if (!authReady) return;
    // Total count comes from stats table via segments endpoint - stable, no live race
    // setRealTotalCount is updated when segments fetch returns below

    // Fetch user (with auth)
    fetch(`${API_URL}/api/user`, { headers: authHeaders() })
      .then((r: any) => r.json())
      .then((ud: any) => {
        // Ignore the unauthenticated fallback (it has no `id` and would clobber
        // isAdmin/credits/plan with free-tier zeros). Only trust a real, authed payload.
        if (ud.success && ud.user && ud.user.id) {
          const fn = ud.user.firstName || ud.user.first_name || "";
          setUser((prev: any) => ({
            ...prev, ...ud.user,
            firstName: fn || prev.firstName,
            lastName: ud.user.lastName || ud.user.last_name || prev.lastName,
            companyName: ud.user.companyName || ud.user.company_name || prev.companyName,
          }));
          if (fn) {
            localStorage.setItem("siq_onb_done", "1");
            setShowDashOnboarding(false);
          } else if (!localStorage.getItem("siq_onb_done")) {
            setShowDashOnboarding(true);
          }
        }
      })
      .catch((_: any) => {});

    // Fetch leads for current territory (or all if none set)
    fetchLeadsForTerritory(onbCity, onbRegion);
    fetchBriefingAndHotspots(onbCity, onbRegion);

    // Trigger stats refresh if stale (keeps segment counts fresh)
    fetch(`${API_URL}/api/stats/refresh`).catch(() => {});

    setLoading(false);

    // Pipeline starts empty - user adds leads from Permit Feed
    setPipeLeads([]);
  }, [authReady]);

  useEffect(() => {
    if (loading || !authReady) return;
    // Clear stale data immediately so dashboard shows loading state
    setSegmentCounts(null);
    setBriefingLoaded(false);
    // Refetch everything when territory changes
    fetchLeadsForTerritory(onbCity, onbRegion);
    fetchBriefingAndHotspots(onbCity, onbRegion);
  }, [onbRegion, onbCity]);

  const firstName =
    user.firstName || user.accountOwner?.split(" ")[0] || "there";
  const initials = getInitials(user);
  const territoryLabel = onbCity
    ? `${onbCity}, ${onbRegion}`
    : onbRegion
    ? onbRegion
    : "All territories";

  const topLeads = useMemo(() => {
    const pool = onbCity
      ? leads.filter(
          (l) => (l.city || "").toLowerCase() === onbCity.toLowerCase()
        )
      : onbRegion
      ? leads.filter((l) => getState(l) === onbRegion)
      : leads;
    const sorted = [...pool].sort(
      (a, b) => getSmartScore(b) - getSmartScore(a)
    );
    return sorted.length >= 3
      ? sorted.slice(0, 12)
      : [...leads]
          .sort((a, b) => getSmartScore(b) - getSmartScore(a))
          .slice(0, 12);
  }, [leads, onbRegion, onbCity]);

  const exLeads = useMemo(() => {
    let r = [...leads];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      r = r.filter(
        (l) =>
          (l.address || "").toLowerCase().includes(q) ||
          (l.city || "").toLowerCase().includes(q)
      );
    }
    if (exCountry) r = r.filter((l) => getCountry(l) === exCountry);
    if (exCity)
      r = r.filter(
        (l) => (l.city || "").toLowerCase() === exCity.toLowerCase()
      );
    else if (exRegion) r = r.filter((l) => getState(l) === exRegion);
    if (exSegment) r = r.filter((l) => getSegment(l) === exSegment);
    r.sort((a, b) =>
      exSort === "smartScore"
        ? getSmartScore(b) - getSmartScore(a)
        : exSort === "newest"
        ? getDays(a) - getDays(b)
        : getDays(b) - getDays(a)
    );
    return r;
  }, [leads, searchTerm, exCountry, exRegion, exCity, exSegment, exSort]);

  const exPageLeads = exLeads.slice((exPage - 1) * PER_PAGE, exPage * PER_PAGE);
  const totalPages = Math.max(1, Math.ceil(exLeads.length / PER_PAGE));

  const filteredPipe = useMemo(() => {
    let r = pipeLeads.map((l) => {
      const fresh = leads.find((x) => x.id === l.id);
      return fresh ? { ...fresh, stage: l.stage, source: l.source, tag: l.tag } : l;
    });
    if (pipeStage === "ai") r = r.filter((l) => l.source === "ai-insights" || l.source === "ai-route");
    else if (pipeStage !== "all") r = r.filter((l) => l.stage === pipeStage);
    if (pipeTag !== "all") r = r.filter((l) => l.tag === pipeTag);
    return r;
  }, [pipeLeads, leads, pipeStage, pipeTag]);

  const calcResults = useMemo(() => {
    const region = CALC_REGIONS[calcRegion];
    if (!region) return null;
    const country = region.country,
      systemKw = calcKw;
    const grossCost = systemKw * 1000 * region.avgWatt;
    const batteryCost = calcBattery ? 11500 : 0;
    const totalGross = grossCost + batteryCost;
    let fedSavings =
      country === "US"
        ? totalGross * FEDERAL.US.rate
        : Math.min(5000, totalGross * 0.15);
    let stateSavings = 0;
    const s = region.state;
    if (s.amount > 0) {
      if (s.type === "flat") stateSavings = s.amount;
      else if (s.type === "per_watt") stateSavings = s.amount * systemKw * 1000;
      else if (s.type === "per_kw") stateSavings = s.amount * systemKw;
    }
    const netCost = Math.max(0, totalGross - fedSavings - stateSavings);
    const annualKwh = systemKw * region.sun * 365 * 0.8;
    const annualSavings = annualKwh * region.utilityRate;
    const payback = annualSavings > 0 ? netCost / annualSavings : 0;
    return {
      grossCost,
      batteryCost,
      totalGross,
      fedSavings,
      stateSavings,
      netCost,
      annualSavings,
      payback,
      monthlySavings: annualSavings / 12,
      roi25yr: annualSavings * 25 - netCost,
      country,
      region,
    };
  }, [calcRegion, calcKw, calcBattery]);

  // ── SIDEBAR ────────────────────────────────────────────────────────────────────
  // 5-item IA. Each nav item maps to one of the product's core objects:
  //   Today      - curated AI surface (what to act on right now)
  //   Permits    - the lead feed
  //   Markets    - territories with stats + AI-clustered hotspot neighborhoods
  //   Contractors- searchable directory (formerly "Roofers"). Click a contractor
  //                anywhere → opens the contractor-intel detail page (view="competitors").
  //   Pipeline   - the rep's working set
  //
  // Routes that still work but are not in main nav (reached via in-app links):
  //   - insights   - opened from dashboard "Ask follow-up" CTA
  //   - competitors- opened by clicking any contractor name (lead card or Contractors row)
  //   - settings   - opened from the user-menu dropdown (avatar)
  //   - hotspot    - opened by drilling into a Market
  //   - terms/privacy - legal pages, reached from settings
  const NAV = [
    { key: "dashboard", label: "Today",       icon: Sunrise },
    { key: "ask",       label: "Ask",         icon: Sparkles },
    { key: "explore",   label: "Permits",     icon: MapPin },
    { key: "zones",     label: "Markets",     icon: BarChart2 },
    { key: "roofers",   label: "Contractors", icon: Wrench },
    { key: "pipeline",  label: "Focus",    icon: Target },
  ];

  // User menu dropdown state (used by sidebar profile button)
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // ─── SIDEBAR - desktop (wide with labels) ─────────────────────────────────
  const sidebarJSX = (
    <div
      style={{
        width: 220,
        height: "100vh",
        background: T.sidebar,
        display: "flex",
        flexDirection: "column",
        padding: "22px 0 22px",
        flexShrink: 0,
        position: "relative",
      }}
    >
      {/* Logo + SolarLead wordmark + activity bell */}
      <div style={{
        marginBottom: 26,
        padding: "0 14px 0 20px",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}>
        <div
          onClick={() => { setView("dashboard"); setMobileOpen(false); }}
          style={{
            flex: 1,
            minWidth: 0,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 11,
          }}
          title="SolarLead"
        >
          <span
            onMouseEnter={(e: any) => { e.currentTarget.style.transform = "rotate(15deg)"; }}
            onMouseLeave={(e: any) => { e.currentTarget.style.transform = "none"; }}
            style={{ display: "inline-flex", color: "#fff", transition: "transform 0.25s ease", flexShrink: 0 }}
          >
            <SolarMark size={28} color="white" />
          </span>
          <span style={{
            fontFamily: "'Outfit',sans-serif",
            fontSize: 19,
            fontWeight: 700,
            color: "#fff",
            letterSpacing: "-0.02em",
          }}>
            Solar<span style={{ fontWeight: 900 }}>Lead</span>
          </span>
        </div>

        {/* Activity bell */}
        <button
          onClick={() => setShowNotifPanel(s => !s)}
          aria-label="Recent activity"
          style={{
            position: "relative",
            background: showNotifPanel ? "rgba(255,136,0,0.16)" : "transparent",
            border: `1px solid ${showNotifPanel ? "rgba(255,136,0,0.45)" : "rgba(255,255,255,0.12)"}`,
            width: 30,
            height: 30,
            borderRadius: 7,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "all 0.15s",
          }}
          onMouseEnter={(e: any) => { if (!showNotifPanel) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
          onMouseLeave={(e: any) => { if (!showNotifPanel) e.currentTarget.style.background = "transparent"; }}
        >
          <Bell size={13} color={notifCount > 0 ? "#FF8800" : "#FFFFFF"} strokeWidth={notifCount > 0 ? 2.5 : 1.8} />
          {notifCount > 0 && (
            <span style={{
              position: "absolute",
              top: -3,
              right: -3,
              minWidth: 14,
              height: 14,
              padding: "0 3px",
              borderRadius: 7,
              background: "#FF8800",
              border: "2px solid #0E0E12",
              color: "#fff",
              fontSize: 8,
              fontWeight: 900,
              fontFamily: "'Outfit',sans-serif",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "content-box",
            }}>
              {notifCount > 50 ? "50+" : notifCount}
            </span>
          )}
        </button>
      </div>

      {/* Activity panel - opens to the right of the sidebar */}
      {showNotifPanel && (
        <>
          <div onClick={() => setShowNotifPanel(false)} style={{ position: "fixed", inset: 0, zIndex: 198, background: "transparent" }} />
          <div
            style={{
              position: "absolute",
              left: 228,
              top: 18,
              width: 380,
              maxHeight: "calc(100vh - 48px)",
              background: T.bg,
              borderRadius: 12,
              border: `1px solid ${T.border}`,
              boxShadow: "0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.06)",
              zIndex: 199,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: T.primary, marginBottom: 3, fontFamily: "'JetBrains Mono', monospace" }}>
                  Since last visit
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.01em" }}>
                  {notifCount === 0 ? "All caught up" : notifCount === 1 ? "1 new permit" : `${notifCount > 50 ? "50+" : notifCount} new permits`}
                  {(onbCity || onbRegion) && (
                    <span style={{ fontSize: 12, fontWeight: 500, color: T.textSub, marginLeft: 6 }}>
                      in {onbCity || onbRegion}
                    </span>
                  )}
                </div>
              </div>
              {notifCount > 0 && (
                <button
                  onClick={markActivityRead}
                  style={{ background: "none", border: "none", color: T.textSub, fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "4px 6px", letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: "'Outfit',sans-serif" }}
                >
                  Mark read
                </button>
              )}
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {notifItems.length === 0 ? (
                <div style={{ padding: "40px 24px", textAlign: "center" }}>
                  <Bell size={22} color={T.textSub} style={{ opacity: 0.4, marginBottom: 10 }} />
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>No new activity</div>
                  <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.5 }}>
                    {(onbCity || onbRegion) ? "We'll surface new permits here as they file." : "Pick a territory to see activity in your market."}
                  </div>
                </div>
              ) : (
                notifItems.slice(0, 8).map((lead: any) => {
                  const days = getDays(lead);
                  const score = getSmartScore(lead);
                  const isHot = scoreTier(score) === "hot";
                  return (
                    <div
                      key={lead.id}
                      onClick={() => { setShowNotifPanel(false); setModalLead(lead); setModalTab("overview"); }}
                      style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, cursor: "pointer", display: "flex", gap: 12, alignItems: "flex-start", transition: "background 0.1s" }}
                      onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgOff}
                      onMouseLeave={(e: any) => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: isHot ? T.dangerLight : T.bgSub, border: `1px solid ${isHot ? "#FECACA" : T.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 900, color: isHot ? T.danger : T.textSub, fontFamily: "'Outfit',sans-serif" }}>{fmtScore(score)}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'JetBrains Mono', monospace" }}>
                          {cleanAddress(lead.address)}
                        </div>
                        <div style={{ fontSize: 11, color: T.textSub, marginTop: 3 }}>
                          {lead.city}{getState(lead) ? `, ${getState(lead)}` : ""} · {fmtAge(days)}
                        </div>
                        {lead.contractorName && (
                          <div style={{ fontSize: 10, color: "#7C2D12", marginTop: 4, fontWeight: 700 }}>
                            vs {lead.contractorName}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {notifItems.length > 0 && (
              <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.border}`, background: T.bgOff }}>
                <button
                  onClick={() => { setShowNotifPanel(false); markActivityRead(); setView("explore"); }}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: 9, border: "none", background: T.text, color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "'Outfit',sans-serif", letterSpacing: "0.04em" }}
                >
                  Open permit feed →
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Nav items */}
      {NAV.map((n) => {
        const active =
          view === n.key
          || (view === "hotspot" && n.key === "dashboard")
          || (view === "competitors" && n.key === "roofers");
        return (
          <button
            key={n.key}
            onClick={() => { setView(n.key); setMobileOpen(false); }}
            aria-label={n.label}
            style={{
              width: "calc(100% - 16px)",
              marginLeft: 8,
              marginRight: 8,
              height: 42,
              marginBottom: 2,
              border: "none",
              background: "transparent",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 12,
              paddingLeft: 14,
              paddingRight: 14,
              cursor: "pointer",
              color: active ? "#FFFFFF" : T.sidebarText,
              position: "relative",
              transition: "color 0.12s, background 0.12s",
            }}
            onMouseEnter={(e: any) => { if (!active) { e.currentTarget.style.color = "#FFFFFF"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; } }}
            onMouseLeave={(e: any) => { if (!active) { e.currentTarget.style.color = T.sidebarText; e.currentTarget.style.background = "transparent"; } }}
          >
            <n.icon size={20} strokeWidth={active ? 2.25 : 1.75} />
            <span style={{
              fontFamily: "'Outfit',sans-serif",
              fontSize: 15,
              fontWeight: active ? 800 : 600,
              letterSpacing: "-0.01em",
            }}>
              {n.label}
            </span>
            {active && (
              <span
                style={{
                  position: "absolute",
                  left: -8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 3,
                  height: 22,
                  borderRadius: "0 2px 2px 0",
                  background: T.primary,
                }}
              />
            )}
          </button>
        );
      })}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Divider */}
      <div style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "0 16px 12px" }} />

      {/* Avatar + name + plan (user menu trigger) */}
      <div style={{ position: "relative", padding: "0 12px" }}>
        <button
          onClick={() => setShowUserMenu(s => !s)}
          aria-label="Account menu"
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: showUserMenu ? "rgba(255,255,255,0.06)" : "transparent",
            border: "none",
            padding: "8px 10px",
            borderRadius: 8,
            cursor: "pointer",
            textAlign: "left",
            transition: "background 0.12s",
          }}
          onMouseEnter={(e: any) => { if (!showUserMenu) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
          onMouseLeave={(e: any) => { if (!showUserMenu) e.currentTarget.style.background = "transparent"; }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #FF8800 0%, #FF5500 100%)",
              color: "#0A0A0A",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 800,
              fontFamily: "'Outfit',sans-serif",
              letterSpacing: "-0.02em",
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: "'Outfit',sans-serif",
              fontSize: 13,
              fontWeight: 800,
              color: "#fff",
              letterSpacing: "-0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              {firstName === "there" ? (authedUser?.email?.split("@")[0] || "Account") : firstName}
            </div>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9,
              color: "#FFFFFF",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontWeight: 700,
              marginTop: 3,
            }}>
              {user.isAdmin ? "Admin" : "Pro"}
            </div>
          </div>
          <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth={2.5} style={{ flexShrink: 0, transform: showUserMenu ? "rotate(180deg)" : "none", transition: "transform .15s" }}>
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>

        {/* User menu popover */}
        {showUserMenu && (
          <>
            <div onClick={() => setShowUserMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 198 }} />
            <div
              style={{
                position: "absolute",
                bottom: "calc(100% + 8px)",
                left: 12,
                right: 12,
                background: T.bg,
                borderRadius: 12,
                border: `1px solid ${T.border}`,
                boxShadow: "0 16px 48px rgba(0,0,0,0.16)",
                zIndex: 199,
                overflow: "hidden",
              }}
            >
              <div style={{ padding: "14px 14px 12px", borderBottom: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.01em" }}>
                  {user.firstName} {user.lastName}
                </div>
                <div style={{ fontSize: 11.5, color: T.textSub, marginTop: 3, wordBreak: "break-all", fontFamily: "'JetBrains Mono', monospace" }}>
                  {user.email}
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, padding: "3px 7px", borderRadius: 4, background: T.primaryLight, color: T.primary, fontFamily: "'Outfit',sans-serif", fontWeight: 800, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  {user.isAdmin ? "Admin" : "Pro"}
                </div>
              </div>
              {[
                { label: "Account & settings", icon: "⚙️", action: () => { setView("settings"); setShowUserMenu(false); } },
                { label: "Change territory", icon: "📍", action: () => { setShowTerritoryPicker(true); setShowUserMenu(false); } },
                { label: user.isAdmin ? "Credits: Unlimited" : `Credits: ${user.creditsRemaining || 0}`, icon: "🎯", action: () => { setView("settings"); setShowUserMenu(false); } },
                { label: "Help & support", icon: "💬", action: () => { window.location.href = "mailto:support@solarlead.io?subject=" + encodeURIComponent("SolarLead support"); setShowUserMenu(false); } },
              ].map((item: any) => (
                <button
                  key={item.label}
                  onClick={item.action}
                  style={{ width: "100%", padding: "11px 14px", display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", borderBottom: `1px solid ${T.border}`, cursor: "pointer", textAlign: "left", transition: "background 0.1s" }}
                  onMouseEnter={(e: any) => { e.currentTarget.style.background = T.bgOff; }}
                  onMouseLeave={(e: any) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <span style={{ fontSize: 13 }}>{item.icon}</span>
                  <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{item.label}</span>
                </button>
              ))}
              <button
                onClick={handleLogout}
                style={{ width: "100%", padding: "11px 14px", display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", textAlign: "left", transition: "background 0.1s" }}
                onMouseEnter={(e: any) => { e.currentTarget.style.background = T.bgOff; }}
                onMouseLeave={(e: any) => { e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ fontSize: 13 }}>🚪</span>
                <span style={{ fontSize: 13, color: T.danger, fontWeight: 600 }}>Sign out</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // ─── SIDEBAR - mobile drawer (compact 68px icon-only) ─────────────────────
  const sidebarMobileJSX = (
    <div
      style={{
        width: 68,
        height: "100vh",
        background: T.sidebar,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px 0 22px",
        flexShrink: 0,
      }}
    >
      <div
        onClick={() => { setView("dashboard"); setMobileOpen(false); }}
        style={{ marginBottom: 14, cursor: "pointer", color: "#fff" }}
        title="SolarLead"
      >
        <SolarMark size={36} color="white" />
      </div>
      <button
        onClick={() => setShowNotifPanel(s => !s)}
        aria-label="Recent activity"
        style={{
          position: "relative",
          background: "transparent",
          border: `1px solid rgba(255,255,255,0.12)`,
          width: 36,
          height: 36,
          borderRadius: 8,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 14,
        }}
      >
        <Bell size={14} color={notifCount > 0 ? "#FF8800" : "#FFFFFF"} strokeWidth={notifCount > 0 ? 2.5 : 1.8} />
        {notifCount > 0 && (
          <span style={{
            position: "absolute", top: -3, right: -3, minWidth: 14, height: 14, padding: "0 3px",
            borderRadius: 7, background: "#FF8800", border: "2px solid #0E0E12",
            color: "#fff", fontSize: 8, fontWeight: 900, fontFamily: "'Outfit',sans-serif",
            display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "content-box",
          }}>
            {notifCount > 50 ? "50+" : notifCount}
          </span>
        )}
      </button>
      {NAV.map((n) => {
        const active =
          view === n.key
          || (view === "hotspot" && n.key === "dashboard")
          || (view === "competitors" && n.key === "roofers");
        return (
          <button
            key={n.key}
            onClick={() => { setView(n.key); setMobileOpen(false); }}
            aria-label={n.label}
            style={{
              width: 44,
              height: 44,
              marginBottom: 4,
              border: "none",
              background: "transparent",
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: active ? "#FFFFFF" : T.sidebarText,
              position: "relative",
            }}
          >
            <n.icon size={22} strokeWidth={active ? 2.25 : 1.75} />
            {active && (
              <span style={{ position: "absolute", left: -12, top: "50%", transform: "translateY(-50%)", width: 3, height: 22, borderRadius: "0 2px 2px 0", background: T.primary }} />
            )}
          </button>
        );
      })}
      <div style={{ flex: 1 }} />
      <button
        onClick={() => { setView("settings"); setMobileOpen(false); }}
        aria-label="Settings"
        style={{
          width: 44, height: 44, marginBottom: 4, border: "none", background: "transparent",
          borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", color: view === "settings" ? "#FFFFFF" : T.sidebarText, position: "relative",
        }}
      >
        <Settings size={22} strokeWidth={view === "settings" ? 2.25 : 1.75} />
        {view === "settings" && (
          <span style={{ position: "absolute", left: -12, top: "50%", transform: "translateY(-50%)", width: 3, height: 22, borderRadius: "0 2px 2px 0", background: T.primary }} />
        )}
      </button>
      <div style={{ width: 28, height: 1, background: "rgba(255,255,255,0.08)", margin: "6px 0 10px" }} />
      <button
        onClick={() => setShowUserMenu(s => !s)}
        aria-label="Account menu"
        style={{
          width: 36, height: 36, borderRadius: "50%",
          background: "linear-gradient(135deg, #FF8800 0%, #FF5500 100%)",
          color: "#0A0A0A", display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, fontWeight: 800, fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.02em",
          cursor: "pointer", border: "none",
          boxShadow: showUserMenu ? "0 0 0 3px rgba(255,136,0,0.32)" : "none",
        }}
      >
        {initials}
      </button>
    </div>
  );

  // ── Topbar - removed. Bell is back in the sidebar.
  const topbarJSX = (
    <div className="show-mobile" style={{ alignItems: "center", justifyContent: "space-between", height: 52, flexShrink: 0, padding: "0 16px", background: "#FFFFFF", borderBottom: `1px solid ${T.border}` }}>
      <div onClick={() => setView("dashboard")} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
        <SolarMark size={24} color="brand" />
        <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: 18, fontWeight: 800, color: T.text, letterSpacing: "-0.02em" }}>Solar<span style={{ fontWeight: 900 }}>Lead</span></span>
      </div>
      <button onClick={() => setShowMobileMenu(s => !s)} aria-label="Account menu" style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg, #FF8800 0%, #FF5500 100%)", color: "#0A0A0A", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 800, fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.02em", cursor: "pointer", border: "none", boxShadow: showMobileMenu ? "0 0 0 3px rgba(255,136,0,0.32)" : "none" }}>
        {initials}
      </button>
    </div>
  );

  // ── WELCOME TOUR ───────────────────────────────────────────────────────────────
  const TOUR_STEPS = [
    {
      title: `Know every solar job
before anyone else.`,
      subtitle: "You now have access to the most complete solar permit database in North America.",
      stat: { val: ((realTotalCount || 921000) + 3664197).toLocaleString(), label: "solar records tracked across US & Canada" },
      accent: T.primary,
      cta: "Let's go →",
    },
    {
      title: `Your territory.
Your market.`,
      subtitle: "Filter down to any city or region. Every number on this dashboard updates instantly to your market.",
      stat: { val: "80+", label: "cities with live permit data" },
      accent: "#3B82F6",
      cta: "Got it →",
    },
    {
      title: `Battery storage
is the hottest play right now.`,
      subtitle: "Systems aged 2-5 years. No original installer. Homeowners warm to the idea. This is your highest close-rate segment.",
      stat: { val: "2-5yr", label: "the sweet spot for upsells" },
      accent: "#F59E0B",
      cta: "Show me →",
    },
    {
      title: `Unlock the
homeowner's contact.`,
      subtitle: "Every lead has a SmartScore. Click any lead, unlock their phone and email with one credit. Call them today.",
      stat: { val: "50", label: "free unlocks included with your plan" },
      accent: T.primary,
      cta: "Start exploring →",
    },
  ];

  const WelcomeTour = () => {
    if (!showWelcomeTour) return null;
    const step = TOUR_STEPS[tourStep];
    const isLast = tourStep === TOUR_STEPS.length - 1;
    const dismiss = () => { setShowWelcomeTour(false); localStorage.setItem("siq_tour_done", "1"); };

    // Background colors per step
    const bgColors = ["#0F1610", "#0C1A2E", "#1A1200", "#0F1610"];
    const bg = bgColors[tourStep] || "#0F1610";

    return (
      <div onClick={dismiss} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div onClick={(e: any) => e.stopPropagation()}
          style={{ background: bg, borderRadius: 24, width: 520, maxWidth: "100%", boxShadow: "0 60px 120px rgba(0,0,0,0.5)", overflow: "hidden", border: "1px solid rgba(255,255,255,0.18)" }}>

          {/* Top bar */}
          <div style={{ padding: "20px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: 6 }}>
              {TOUR_STEPS.map((_: any, i: any) => (
                <button key={i} onClick={() => setTourStep(i)} style={{ width: i === tourStep ? 28 : 8, height: 8, borderRadius: 4, background: i === tourStep ? step.accent : "rgba(255,255,255,0.2)", border: "none", cursor: "pointer", padding: 0, transition: "all 0.25s" }} />
              ))}
            </div>
            <button onClick={dismiss} style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFFFFF", fontSize: 16 }}>
              ✕
            </button>
          </div>

          {/* Main content */}
          <div style={{ padding: "32px 32px 28px" }}>

            {/* Step number */}
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#FFFFFF", marginBottom: 20 }}>
              Step {tourStep + 1} of {TOUR_STEPS.length}
            </div>

            {/* Big headline with green highlight on key word */}
            <div style={{ fontSize: 32, fontWeight: 900, color: "#fff", fontFamily: "'Outfit',sans-serif", letterSpacing: "-1px", lineHeight: 1.2, marginBottom: 16, whiteSpace: "pre-line" }}>
              {step.title.split('\n').map((line: any, i: any) => (
                <div key={i}>
                  {i === 0 ? (
                    <span style={{ position: "relative", display: "inline-block" }}>
                      {line}
                      <span style={{ position: "absolute", bottom: 2, left: -2, right: -2, height: 12, background: step.accent, opacity: 0.25, borderRadius: 3, zIndex: -1 }} />
                    </span>
                  ) : (
                    <span style={{ color: step.accent }}>{line}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Subtitle */}
            <div style={{ fontSize: 15, color: "#ffffff", lineHeight: 1.7, marginBottom: 28, fontWeight: 500 }}>
              {step.subtitle}
            </div>

            {/* Stat callout card */}
            <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: 14, border: `1px solid rgba(255,255,255,0.1)`, padding: "16px 20px", marginBottom: 28, display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 4, height: 48, borderRadius: 2, background: step.accent, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 36, fontWeight: 900, color: step.accent, fontFamily: "'Outfit',sans-serif", letterSpacing: "-1.5px", lineHeight: 1 }}>{step.stat.val}</div>
                <div style={{ fontSize: 13, color: "#FFFFFF", marginTop: 4 }}>{step.stat.label}</div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <button onClick={dismiss} style={{ fontSize: 13, color: "#FFFFFF", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                Skip tour
              </button>
              <button
                onClick={() => { if (isLast) dismiss(); else setTourStep(tourStep + 1); }}
                style={{ background: step.accent, border: "none", borderRadius: 12, padding: "13px 28px", fontSize: 15, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.2px", boxShadow: `0 4px 20px ${step.accent}40` }}>
                {isLast ? "Start exploring →" : step.cta}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  
  
  // ── DASHBOARD ──────────────────────────────────────────────────────────────────
  // Dashboard Hotspots preview
  const [dashZones, setDashZones] = useState<any[]>([]);
  useEffect(() => {
    if (view !== "dashboard" || !onbCity) return;
    fetch(`${API_URL}/api/strike-zones?city=${encodeURIComponent(onbCity)}${onbRegion ? `&state=${encodeURIComponent(onbRegion)}` : ""}&limit=3`)
      .then(r => r.json()).then(d => setDashZones(d.zones || [])).catch(() => {});
  }, [view, onbCity, onbRegion]);

  // First 3 moves strip - renders above the briefing for new accounts only.
  const FirstMovesStrip = () => {
    if (!firstMoves) return null;
    if (firstMoves.territory && firstMoves.segment && firstMoves.unlock) return null;
    const steps = [
      { key: "territory", n: 1, title: "Set your territory", sub: "Lock the map to your market", done: firstMoves.territory, go: () => setShowTerritoryPicker(true) },
      { key: "segment", n: 2, title: "Open today's new permits", sub: "The freshest homeowners in your area", done: firstMoves.segment, go: () => { markFirstMove("segment"); setWorkspaceSegment("installers"); setView("workspace"); } },
      { key: "unlock", n: 3, title: "Unlock your first homeowner", sub: "Name, phone, email - 1 credit", done: firstMoves.unlock, go: () => { setWorkspaceSegment("installers"); setView("workspace"); } },
    ];
    const doneCount = steps.filter((st) => st.done).length;
    return (
      <div className="fm-wrap">
        <style>{`
          .fm-wrap{padding:24px 40px 0;}
          .fm-card{background:#fff;border:1.5px solid ${T.border};border-radius:14px;padding:18px 20px 20px;}
          .fm-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:14px;}
          @media (max-width:768px){ .fm-wrap{padding:20px 18px 0;} .fm-grid{grid-template-columns:1fr;} }
        `}</style>
        <div className="fm-card">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: "#FF8800" }}>Get set up</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#262B37" }}>{doneCount} of 3</span>
              <button onClick={dismissFirstMoves} aria-label="Dismiss setup" style={{ background: "transparent", border: "none", cursor: "pointer", color: "#262B37", fontSize: 16, fontWeight: 700, lineHeight: 1, padding: 2 }}>{"\u2715"}</button>
            </span>
          </div>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 19, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.025em", marginTop: 6 }}>Your first 3 moves</div>
          <div className="fm-grid">
            {steps.map((st) => (
              <button key={st.key} onClick={st.go} disabled={st.done}
                style={{ textAlign: "left", fontFamily: "'Outfit', sans-serif", background: st.done ? "#ECFDF3" : "#fff", border: `1.5px solid ${st.done ? "#BBF7D0" : T.borderMid}`, borderRadius: 11, padding: "13px 14px", cursor: st.done ? "default" : "pointer", display: "flex", gap: 11, alignItems: "flex-start", transition: "border-color 120ms, box-shadow 120ms" }}
                onMouseEnter={(e: any) => { if (!st.done) { e.currentTarget.style.borderColor = "#0A0A0A"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.07)"; } }}
                onMouseLeave={(e: any) => { if (!st.done) { e.currentTarget.style.borderColor = T.borderMid; e.currentTarget.style.boxShadow = "none"; } }}>
                <span style={{ width: 24, height: 24, borderRadius: 8, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: st.done ? "#16A34A" : "#FF8800", color: "#fff", fontSize: 12.5, fontWeight: 900 }}>
                  {st.done ? "\u2713" : st.n}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 14.5, fontWeight: 800, color: "#0A0A0A", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{st.title}</span>
                  <span style={{ display: "block", fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: "#262B37", marginTop: 3, lineHeight: 1.35 }}>{st.sub}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const Dashboard = () => (
    <>
      {FirstMovesStrip()}
      <IntelligenceBriefing
      lifecycleSlot={<LifecycleBoard counts={segmentCounts} onOpenSegment={(seg) => { setWorkspaceSegment(seg); setView("workspace"); }} />}
      userFirstName={user.firstName || user.accountOwner?.split(" ")[0] || ""}
      territory={currentTerritory}
      onChangeTerritory={selectTerritory}
      onViewPermits={(city: string, province: string) => {
        setOnbCity(city);
        setOnbRegion(province);
        setView("explore");
      }}
      onViewPermitsByMotion={(motion: string, territory?: string) => {
        // Card CTAs land here. Sync the explore view's city/region filter to
        // the DASHBOARD's territory so play card numbers match destination counts.
        // "All Markets" → clear filter (platform-wide).
        // "City, ST" → set both. Else → region-only.
        setPendingSegFilter(motion);
        if (!territory || territory === "All Markets") {
          setOnbCity("");
          setOnbRegion("");
        } else {
          const m = territory.match(/^(.+),\s*([A-Z]{2})$/);
          if (m) {
            setOnbCity(m[1]);
            setOnbRegion(m[2]);
          } else {
            setOnbCity("");
            setOnbRegion(territory);
          }
        }
        setView("explore");
      }}
      onViewPipeline={() => setView("pipeline")}
      onViewContractor={(contractor: string) => {
        setSelectedContractor(contractor);
        setView("competitors");
      }}
      onOpenContractors={() => setView("roofers")}
      onAskFollowUp={() => setView("insights")}
      onOpenMap={() => setView("zones")}
      onOpenLead={(address: string, city: string) => {
        // Live ticker → open the lead drawer. Look up the matching lead in
        // our loaded leads array. If found, pop the drawer. Else fall back
        // to routing to the permits feed for that city.
        const cityName = (city || "").split(",")[0].trim();
        const found = leads.find((l: any) =>
          (l.address || "") === address &&
          ((l.city || "").toLowerCase() === cityName.toLowerCase())
        );
        if (found) {
          setModalLead(found);
          setModalTab("overview");
          return;
        }
        const parts = (city || "").split(",").map((s: string) => s.trim());
        setOnbCity(parts[0] || "");
        setOnbRegion(parts[1] || "");
        setView("explore");
      }}
      />
    </>
  );

  const HotspotDetail = () => {
    if (!hotspotDetail) return null;
    const hs = hotspotDetail as any;
    const streetLeads = leads.filter(
      (l) =>
        (l.address || "")
          .toLowerCase()
          .includes((hs.street || "").toLowerCase()) &&
        (l.city || "").toLowerCase() === (hs.city || "").toLowerCase()
    );
    return (
      <div style={{ flex: 1, overflowY: "auto", background: T.bgOff }}>
        <div
          style={{
            padding: "16px 20px",
            background: T.bg,
            borderBottom: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <button
            onClick={() => setView("dashboard")}
            style={{
              background: T.bgSub,
              border: "none",
              borderRadius: 8,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 700,
              color: T.textMid,
              cursor: "pointer",
            }}
          >
            ← Back
          </button>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
              {hs.street}
            </div>
            <div style={{ fontSize: 12, color: T.textSub }}>
              {hs.city}, {hs.state} · {hs.count} leads in this block
            </div>
          </div>
        </div>
        {streetLeads.length > 0
          ? streetLeads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                callCount={(callLog[lead.id] || []).length}
                onClick={() => {
                  setModalLead(lead);
                  setModalTab("overview");
                }}
              />
            ))
          : topLeads.slice(0, 5).map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                callCount={(callLog[lead.id] || []).length}
                onClick={() => {
                  setModalLead(lead);
                  setModalTab("overview");
                }}
              />
            ))}
      </div>
    );
  };

  // ── PERMIT FEED ────────────────────────────────────────────────────────────────
  // Effects live at parent level to avoid hooks-in-function bug
  const feedFetchRef = { current: 0 };

  useEffect(() => {
    if (view === "explore") {
      const fetchId = ++feedFetchRef.current;
      setFeedLeads([]);
      setFeedLoading(true);
      setExLoadError("");
      // Real headline count for the current scope. The /api/leads `total` is only
      // a pagination sentinel (e.g. 501 = 500 loaded + "more"), so we fetch the
      // true count separately. Reset first so a prior territory's count can't linger.
      setTerritoryCount(0);
      {
        const cp = new URLSearchParams();
        if (onbCity) cp.set("city", CITY_ALIASES[onbCity] || onbCity);
        if (onbRegion) cp.set("state", onbRegion);
        const cParam = cp.toString();
        fetch(`${API_URL}/api/leads/count${cParam ? `?${cParam}` : ""}`)
          .then((r: any) => r.json())
          .then((d: any) => { if (fetchId === feedFetchRef.current && d?.success && d.count > 0) setTerritoryCount(d.count); })
          .catch(() => {});
      }
      const seg = pendingSegFilter || exLocalSegment;
      if (pendingSegFilter) { setExLocalSegment(pendingSegFilter); setPendingSegFilter(null); }
      const params = new URLSearchParams();
      if (onbCity) {
        const dbCity = CITY_ALIASES[onbCity] || onbCity;
        params.set("city", dbCity);
      }
      if (onbRegion) params.set("state", onbRegion);
      if (seg) params.set("segment", seg);
      params.set("limit", onbCity ? String(FEED_PAGE_SIZE) : "500");
      params.set("offset", "0");
      fetch(`${API_URL}/api/leads?${params}`)
        .then((r: any) => { if (!r.ok) throw new Error(`Server error: ${r.status}`); return r.json(); })
        .then((d: any) => {
          if (fetchId !== feedFetchRef.current) return;
          if (d.success) {
            setFeedLeads(d.leads || []);
            setFeedTotal(d.total || 0);
            setFeedPage(0);
            const preUnlocked: any = {};
            (d.leads || []).forEach((l: any) => {
              if (l.ownerPhone || l.ownerEmail || l.ownerName) {
                preUnlocked[l.id] = { owner_name: l.ownerName, owner_phone: l.ownerPhone, owner_email: l.ownerEmail };
              }
            });
            if (Object.keys(preUnlocked).length > 0) setUnlocked((prev: any) => ({ ...prev, ...preUnlocked }));
          }
          setFeedLoading(false);
        })
        .catch((err: any) => {
          if (fetchId !== feedFetchRef.current) return;
          console.error("Feed fetch failed:", err);
          setFeedLoading(false);
        });
    }
  }, [view, onbCity, onbRegion]);

  const Explore = () => {
    const handleSearch = (val: string) => {
      setExLocalSearch(val);
      // Search hits the server (city/address/contractor are trigram-indexed),
      // so a city like "Edmonton" resolves across all 990K rows - not just the
      // ~500 currently loaded. Debounced to avoid a request per keystroke.
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        fetchFeedPage(onbCity, onbRegion, 0, exLocalSegment, val.trim() || null);
      }, 280);
    };

    const handleSegment = (seg: any) => {
      const next = exLocalSegment === seg ? null : seg;
      setExLocalSegment(next);
      fetchFeedPage(onbCity, onbRegion, 0, next, exLocalSearch || null);
    };

    const loadMore = () => {
      if (!feedLoading) fetchFeedPage(onbCity, onbRegion, feedPage + 1, exLocalSegment, exLocalSearch || null);
    };

    // Flip to the map. The active segment chip drives it; with no chip selected we
    // open the busiest segment in this territory (falling back to maintenance).
    const goToMap = () => {
      // Permits MAP toggle opens the full map. No chip selected -> "all" (every
      // permit on the map); an active chip narrows to that segment. The in-map
      // selector lets the user switch segments or back to All.
      setWorkspaceSegment(exLocalSegment || "all");
      setView("workspace");
    };

    // displayLeads draws from segmentedFeed (defined below) so search + segment compose
    const _searchLeads = (arr: any[]) => {
      const s = exLocalSearch.trim().toLowerCase();
      return s
        ? arr.filter((l: any) => (l.address || "").toLowerCase().includes(s) || (l.city || "").toLowerCase().includes(s) || (l.contractorName || "").toLowerCase().includes(s))
        : arr;
    };

    const territory = onbCity
      ? (onbRegion ? `${onbCity}, ${onbRegion}` : onbCity)
      : (onbRegion || "All territories");
    // Headline shows the real count from /api/leads/count. feedTotal is only a
    // pagination sentinel (e.g. 501) so it must NOT drive the headline - it stays
    // reserved for the load-more check below.
    const shownTotal = territoryCount;

    // Defensive client-side segment filter - the backend isn't reliably
    // honoring the segment query param. Without this, clicking "Replacement"
    // can return 7-day-old "New Install" permits because the backend ignores
    // the filter. getSegment() maps deterministically from permit age so the
    // frontend filter is authoritative.
    const segmentedFeed = exLocalSegment
      ? feedLeads.filter((l: any) => getSegment(l) === exLocalSegment)
      : feedLeads;

    // Stats from the filtered set (keeps strip consistent with what's shown)
    const hotInFeed = segmentedFeed.filter((l: any) => (getSmartScore(l) ?? 0) >= SCORE_HOT).length;
    const uniqueContractors = new Set(
      segmentedFeed.filter((l: any) => l.contractorName).map((l: any) => l.contractorName)
    ).size;
    const pipelineCount = pipeLeads.length;
    const isLive = !feedLoading && feedLeads.length > 0;

    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff" }}>
        {/* ─── HEADER ─── */}
        <div style={{ padding: "22px 40px 0", background: "#fff" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, flexWrap: "wrap", marginBottom: 14 }}>
            <div style={{ minWidth: 0, flex: "1 1 auto" }}>
              <h1 style={{
                fontFamily: "'Outfit', system-ui, sans-serif",
                fontSize: "clamp(34px, 6vw, 68px)", fontWeight: 900, lineHeight: 0.96,
                letterSpacing: "-0.045em", color: T.text, margin: 0, marginBottom: 10,
              }}>
                Every permit. <span style={{ color: T.primary }}>Every address.</span>
              </h1>
              <div style={{ fontSize: 15.5, color: T.textSub, fontWeight: 500, maxWidth: 680, lineHeight: 1.5 }}>
                {feedLoading && feedLeads.length === 0
                  ? "Loading leads…"
                  : <><strong style={{ color: T.text, fontWeight: 800 }}>{shownTotal > 0 ? shownTotal.toLocaleString() : "-"}</strong> actionable leads tracked across <strong style={{ color: T.text, fontWeight: 700 }}>{territory}</strong>.</>}
              </div>
            </div>
            <button onClick={() => setShowTerritoryPicker(true)}
              style={{
                background: T.text, border: "none",
                padding: "14px 22px", fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                fontSize: 12, fontWeight: 800, letterSpacing: "0.14em",
                color: T.bg, cursor: "pointer",
                transition: "background 120ms ease",
                flexShrink: 0, textTransform: "uppercase",
                alignSelf: "flex-start",
              }}
              onMouseEnter={(e: any) => (e.currentTarget.style.background = T.primary)}
              onMouseLeave={(e: any) => (e.currentTarget.style.background = T.text)}
            >
              {onbCity || onbRegion ? territory : "Select territory"}
            </button>
          </div>

          {/* List / Map view toggle */}
          <div style={{ display: "inline-flex", background: "#ECEAE1", border: `1px solid ${T.border}`, borderRadius: 12, padding: 4, gap: 4, marginTop: 6, marginBottom: 2 }}>
            <button style={{ display: "inline-flex", alignItems: "center", gap: 7, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontWeight: 800, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", border: "none", borderRadius: 9, padding: "9px 18px", cursor: "default", background: T.text, color: "#fff" }}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
              List
            </button>
            <button onClick={goToMap} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontWeight: 800, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", border: "none", borderRadius: 9, padding: "9px 18px", cursor: "pointer", background: "transparent", color: T.text }}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" /><line x1="9" y1="3" x2="9" y2="18" /><line x1="15" y1="6" x2="15" y2="21" /></svg>
              Map
            </button>
          </div>

          {/* Low-credit nudge - only renders when credits <= 2 */}
          <LowCreditBanner
            creditsRemaining={user?.isAdmin ? 9999 : (user?.creditsRemaining ?? 10)}
            onTopUp={() => setShowCreditsModal(true)}
          />

        </div>

        {/* ─── FILTER BAR ─── */}
        <div className="feed-filter-bar" style={{ padding: "0 40px 12px", display: "flex", alignItems: "center", gap: 8, overflowX: "auto", WebkitOverflowScrolling: "touch", borderBottom: "1px solid #EAEAE2" }}>
          {/* Search */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#FAFAF6", border: "1.5px solid #EAEAE2", borderRadius: 8, padding: "0 14px", height: 38, minWidth: 220, flex: "0 1 280px", transition: "border-color 120ms" }}>
            <Search size={14} color="#262B37" />
            <input value={exLocalSearch} onChange={(e: any) => handleSearch(e.target.value)}
              placeholder="Search city or contractor…"
              style={{ flex: 1, border: "none", background: "transparent", fontSize: 13, color: T.text, fontFamily: "'Inter', sans-serif", outline: "none", fontWeight: 500 }} />
            {exLocalSearch && <button onClick={() => handleSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}><X size={13} color="#262B37" /></button>}
          </div>
          {/* Divider */}
          <div style={{ width: 1, height: 24, background: "#EAEAE2", flexShrink: 0 }} />
          {/* Pills */}
          {Object.keys(SEGMENTS).map((k) => {
            const isActive = exLocalSegment === k;
            return (
              <button key={k} onClick={() => handleSegment(k)}
                style={{
                  padding: "8px 14px", borderRadius: 999,
                  fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: isActive ? 800 : 700,
                  border: `1.5px solid ${isActive ? T.text : "#EAEAE2"}`,
                  background: isActive ? T.text : "#fff",
                  color: isActive ? "#fff" : T.text,
                  cursor: "pointer", transition: "all 120ms",
                  whiteSpace: "nowrap", letterSpacing: "-0.005em",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
                onMouseEnter={(e: any) => { if (!isActive) { e.currentTarget.style.background = "#FAFAF6"; e.currentTarget.style.borderColor = T.text; } }}
                onMouseLeave={(e: any) => { if (!isActive) { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#EAEAE2"; } }}
              >
                <span>{SEGMENTS[k].emoji}</span>
                <span>{SEGMENTS[k].label}</span>
              </button>
            );
          })}
        </div>

        {/* Push to Pipeline bar - preserved unchanged */}
        {onbCity && exLocalSegment && feedLeads.length > 0 && (
          <div style={{ padding: "10px 40px", borderBottom: `1.5px solid ${T.primary}30`, background: "#FFF7ED", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Target size={14} color={T.primary} />
              <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                {shownTotal > 0 ? shownTotal.toLocaleString() : feedLeads.length} {SEGMENTS[exLocalSegment]?.label || exLocalSegment} leads in {onbCity}
              </span>
            </div>
            <button onClick={() => {
              const segLabel = SEGMENTS[exLocalSegment]?.label || exLocalSegment;
              const tag = `${onbCity} · ${segLabel}`;
              const existingIds = new Set(pipeLeads.map((p: any) => p.id));
              const newEntries = feedLeads
                .filter((l: any) => !existingIds.has(l.id))
                .map((l: any) => ({ ...l, stage: "new", source: "permit-feed", tag }));
              if (newEntries.length === 0) { alert("All visible leads are already in your focus."); return; }
              setPipeLeads((prev: any) => [...prev, ...newEntries]);
              newEntries.forEach((l: any) => savePipelineToBackend(l.id, l.stage));
              setView("pipeline");
              setPipeTag(tag);
            }}
              style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: T.primary, color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "'Outfit'", display: "flex", alignItems: "center", gap: 6 }}>
              <GitMerge size={12} /> Add to focus
            </button>
          </div>
        )}

        {/* ─── LEAD LIST ─── */}
        <div className="permits-feed-scroll" style={{ flex: 1, overflowY: "auto", scrollbarColor: "#A0A0AE #FAFAF6", scrollbarWidth: "thin" }}>
          <style>{`
            .permits-feed-scroll::-webkit-scrollbar { width: 12px; }
            .permits-feed-scroll::-webkit-scrollbar-track { background: #FAFAF6; border-left: 1px solid #EAEAE2; }
            .permits-feed-scroll::-webkit-scrollbar-thumb { background: #C5C5D0; border-radius: 6px; border: 2px solid #FAFAF6; }
            .permits-feed-scroll::-webkit-scrollbar-thumb:hover { background: #0A0A0A; }
          `}</style>
          {feedLoading && feedLeads.length === 0 ? (
            <div>
              <style>{`@keyframes slShimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }`}</style>
              {[0,1,2,3,4,5].map(i => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 40px", borderBottom: `1px solid ${T.border}`, borderLeft: "3px solid transparent" }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer 1.4s ease-in-out infinite", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ height: 14, width: `${55 + (i * 7) % 35}%`, borderRadius: 4, background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer 1.4s ease-in-out infinite", marginBottom: 8 }} />
                    <div style={{ height: 11, width: `${30 + (i * 9) % 25}%`, borderRadius: 4, background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer 1.4s ease-in-out infinite", marginBottom: 8 }} />
                    <div style={{ display: "flex", gap: 6 }}>
                      <div style={{ height: 16, width: 70, borderRadius: 6, background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer 1.4s ease-in-out infinite" }} />
                      <div style={{ height: 16, width: 50, borderRadius: 6, background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer 1.4s ease-in-out infinite" }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : _searchLeads(segmentedFeed).length === 0 ? (
            <div style={{ padding: "80px 40px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#FAFAF6", border: "1.5px solid #EAEAE2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Search size={22} color="#262B37" />
              </div>
              <div>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 6, letterSpacing: "-0.015em" }}>No leads match this filter</div>
                <div style={{ fontSize: 13.5, color: T.textSub, marginBottom: 18, lineHeight: 1.5 }}>Try a different segment or change your territory to see more.</div>
              </div>
              <button onClick={() => setShowTerritoryPicker(true)}
                style={{ padding: "12px 24px", borderRadius: 8, background: T.text, border: "none", color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.005em", transition: "background 120ms" }}
                onMouseEnter={(e: any) => (e.currentTarget.style.background = T.primary)}
                onMouseLeave={(e: any) => (e.currentTarget.style.background = T.text)}
              >
                Change territory →
              </button>
            </div>
          ) : (
            <>
              {_searchLeads(segmentedFeed).map((lead: any) => (
                <LeadCard key={lead.id} lead={lead} callCount={(callLog[lead.id] || []).length}
                  inPipeline={pipeLeads.some((p: any) => p.id === lead.id)}
                  focusLists={focusLists}
                  onAddToFocus={(focusListId: any) => {
                    if (pipeLeads.some((p: any) => p.id === lead.id)) return;
                    setPipeLeads((prev: any) => [...prev, { ...lead, stage: "new", source: "permit-feed", focus_list_id: focusListId ?? null }]);
                    savePipelineToBackend(lead.id, "new", focusListId);
                  }}
                  onAddToPipeline={() => {
                    if (pipeLeads.some((p: any) => p.id === lead.id)) return;
                    setPipeLeads((prev: any) => [...prev, { ...lead, stage: "new", source: "permit-feed" }]);
                    savePipelineToBackend(lead.id, "new");
                  }}
                  onClick={() => { setModalLead(lead); setModalTab("overview"); }} />
              ))}
              {feedLeads.length < feedTotal && !exLocalSearch && (
                <div style={{ padding: "24px 40px", textAlign: "center", borderTop: "1px solid #EAEAE2" }}>
                  <button onClick={loadMore} disabled={feedLoading}
                    style={{ padding: "12px 28px", borderRadius: 8, background: "#fff", border: `1.5px solid ${T.text}`, fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 800, color: T.text, cursor: feedLoading ? "wait" : "pointer", letterSpacing: "-0.005em", transition: "all 120ms" }}
                    onMouseEnter={(e: any) => { if (!feedLoading) { e.currentTarget.style.background = T.text; e.currentTarget.style.color = "#fff"; } }}
                    onMouseLeave={(e: any) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = T.text; }}
                  >
                    {feedLoading ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // ─── STRIKE ZONES ──────────────────────────────────────────────────────────
  const [szZones, setSzZones] = useState<any[]>([]);
  const [szLoading, setSzLoading] = useState(false);
  const [szExpanded, setSzExpanded] = useState<string|null>(null);
  const [szSelected, setSzSelected] = useState<Set<number>>(new Set());
  const [szUnlocking, setSzUnlocking] = useState(false);
  const [szStats, setSzStats] = useState<any>(null);
  const [szFilter, setSzFilter] = useState<string>("all");

  useEffect(() => {
    if (view !== "zones" || !onbCity) return;
    setSzLoading(true);
    setSzExpanded(null);
    setSzSelected(new Set());
    setSzFilter("all");
    fetch(`${API_URL}/api/strike-zones?city=${encodeURIComponent(onbCity)}${onbRegion ? `&state=${encodeURIComponent(onbRegion)}` : ""}`)
      .then(r => r.json())
      .then(d => { setSzZones(d.zones || []); setSzStats({ total_leads: d.total_leads, total_zones: d.total_zones }); })
      .catch(() => {})
      .finally(() => setSzLoading(false));
  }, [view, onbCity, onbRegion]);

  const szToggleSelect = (id: number) => {
    setSzSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const szSelectAll = (zone: any) => {
    const ids = zone.leads.filter((l: any) => !l.hasContact).map((l: any) => l.id);
    setSzSelected(prev => { const n = new Set(prev); ids.forEach((id: number) => n.has(id) ? n.delete(id) : n.add(id)); return n; });
  };
  const szBulkUnlock = async () => {
    if (!szSelected.size) return;
    setSzUnlocking(true);
    try {
      const res = await fetch(`${API_URL}/api/leads/bulk-unlock`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leadIds: Array.from(szSelected), userId: user.email }),
      });
      const data = await res.json();
      if (data.success) {
        setUser((prev: any) => ({ ...prev, creditsRemaining: data.credits_remaining }));
        setSzSelected(new Set());
        fetch(`${API_URL}/api/strike-zones?city=${encodeURIComponent(onbCity)}${onbRegion ? `&state=${encodeURIComponent(onbRegion)}` : ""}`)
          .then(r => r.json()).then(d => { setSzZones(d.zones || []); });
      } else {
        const errMsg = (data.error || "").toLowerCase();
        const isOutOfCredits = errMsg.includes("no credits") || errMsg.includes("credits remaining") || errMsg.includes("insufficient");
        if (isOutOfCredits) {
          if (window.confirm("You don't have enough credits for that batch. Open Settings to buy a credit pack?")) {
            setView("settings");
          }
        } else {
          alert(data.error || "Bulk unlock failed");
        }
      }
    } catch (e: any) { alert("Error: " + e.message); }
    setSzUnlocking(false);
  };

  const StrikeZones = () => {
    const segColor: any = { "New Install": "#059669", "Maintenance": "#D97706", "Battery Upsell": "#DC2626", "System Upgrade": "#7C3AED", "Full Replacement": "#2563EB" };
    const segEmoji: any = { "New Install": "🔥", "Maintenance": "🧹", "Battery Upsell": "🔋", "System Upgrade": "⚡", "Full Replacement": "🔄" };
    const segFilters = ["all", "New Install", "Maintenance", "Battery Upsell", "System Upgrade", "Full Replacement"];

    // Filter zones by selected segment
    const filteredZones = szFilter === "all" ? szZones : szZones.filter((z: any) => z.dominant?.segment === szFilter || (z.segments && z.segments[szFilter] > 0));

    return (
      <div style={{ flex: 1, overflowY: "auto", background: T.bgOff }}>
        {/* Header - matches Permit Feed style */}
        <div style={{ background: T.bg, padding: "32px 32px 0", borderBottom: `1.5px solid ${T.border}` }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "2.5px", textTransform: "uppercase", color: T.primary, marginBottom: 10 }}>● HOTSPOTS · {onbCity ? "LIVE" : "SELECT TERRITORY"}</div>
                <h1 style={{ fontFamily: "'Outfit',sans-serif", fontSize: 38, fontWeight: 900, color: T.text, letterSpacing: "-1.5px", lineHeight: 1.1, margin: 0 }}>
                  Where the market{" "}<span style={{ color: T.primary }}>is moving.</span>
                </h1>
              </div>
              <button onClick={() => setShowTerritoryPicker(true)}
                style={{ padding: "10px 20px", borderRadius: 10, background: T.text, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit'", whiteSpace: "nowrap" }}>
                {onbCity ? `${onbCity}, ${onbRegion || ""}`.trim().replace(/,\s*$/, "") : "Select territory"}
              </button>
            </div>
            {onbCity && (
              <div style={{ fontSize: 14, color: T.textSub, marginTop: 4, marginBottom: 16 }}>
                {szStats && szZones.length > 0
                  ? `${szStats.total_zones} neighborhood clusters with 3+ solar permits in ${onbCity}. Pick a hotspot, unlock contacts, hand your reps the call list.`
                  : `Scanning ${onbCity} for neighborhood clusters...`}
              </div>
            )}

            {/* Stats bar */}
            {szStats && szZones.length > 0 && (
              <div style={{ display: "flex", gap: 32, marginBottom: 16 }}>
                {[
                  { val: szStats.total_zones, label: "zones found" },
                  { val: szZones.reduce((a: number, z: any) => a + z.count, 0).toLocaleString(), label: "permits in zones" },
                  { val: szZones.filter((z: any) => z.batteryCount > 0).length, label: "battery upsell" },
                  { val: szZones.filter((z: any) => z.newCount > 0).length, label: "new installs" },
                ].map((s: any) => (
                  <div key={s.label}>
                    <div style={{ fontFamily: "'Outfit'", fontSize: 24, fontWeight: 900, color: T.text, letterSpacing: "-1px" }}>{s.val}</div>
                    <div style={{ fontSize: 11, color: T.textSub, fontWeight: 600 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Segment filter pills */}
            {szZones.length > 0 && (
              <div style={{ display: "flex", gap: 8, paddingBottom: 16, overflowX: "auto" }}>
                {segFilters.map((seg: string) => {
                  const active = szFilter === seg;
                  const count = seg === "all" ? szZones.length : szZones.filter((z: any) => z.dominant?.segment === seg || (z.segments && z.segments[seg] > 0)).length;
                  return (
                    <button key={seg} onClick={() => setSzFilter(seg)}
                      style={{ padding: "7px 14px", borderRadius: 99, border: `1.5px solid ${active ? T.primary : T.border}`, background: active ? "#FFF7ED" : T.bg, color: active ? T.primary : T.textMid, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 5 }}>
                      {seg !== "all" && <span>{segEmoji[seg]}</span>}
                      {seg === "all" ? "All zones" : seg}
                      <span style={{ fontSize: 10, color: active ? T.primary : T.textSub, fontWeight: 600 }}>({count})</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Bulk unlock bar */}
        {szSelected.size > 0 && (
          <div style={{ position: "sticky", top: 0, zIndex: 20, background: T.text, borderBottom: `2px solid ${T.primary}`, padding: "12px 28px", display: "flex", alignItems: "center", justifyContent: "center", gap: 16 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{szSelected.size} leads selected</span>
            <button onClick={szBulkUnlock} disabled={szUnlocking}
              style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: szUnlocking ? "#D97006" : T.primary, color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "'Outfit'", display: "flex", alignItems: "center", gap: 8 }}>
              {szUnlocking ? <RefreshCw size={14} style={{ animation: "spin 0.8s linear infinite" }} /> : <Unlock size={14} />}
              Unlock {szSelected.size} contacts ({szSelected.size} credits)
            </button>
            <button onClick={() => setSzSelected(new Set())}
              style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.2)", background: "transparent", color: "#FFFFFF", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Clear</button>
          </div>
        )}

        {/* Zone cards */}
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "20px 32px" }}>
          {szLoading ? (
            <div style={{ textAlign: "center", padding: 60, color: T.textSub }}>
              <RefreshCw size={20} style={{ animation: "spin 0.8s linear infinite", marginBottom: 12 }} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>Scanning streets for clusters...</div>
            </div>
          ) : !onbCity ? (
            <div style={{ textAlign: "center", padding: "60px 24px" }}>
              <Target size={32} color={T.textSub} style={{ marginBottom: 14 }} />
              <div style={{ fontFamily: "'Outfit'", fontSize: 22, fontWeight: 900, color: T.text, marginBottom: 10, letterSpacing: "-0.5px" }}>Pick a territory to see hotspots</div>
              <div style={{ fontSize: 14, color: T.textSub, marginBottom: 4, maxWidth: 440, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 }}>Hotspots cluster permits by neighborhood so your reps concentrate their dials where the market is most active.</div>
              <div style={{ fontSize: 13, color: T.textSub, marginTop: 18 }}>Use the <span style={{ fontWeight: 700, color: T.text }}>Select territory</span> button in the top-right to begin.</div>
            </div>
          ) : filteredZones.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: T.textSub }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>{szFilter !== "all" ? `No ${szFilter} zones in ${onbCity}` : `No Hotspots found in ${onbCity}`}</div>
              <div style={{ fontSize: 13 }}>{szFilter !== "all" ? "Try a different segment filter or select All zones." : "Zones require 3+ permits on the same street. Try a larger market."}</div>
              {szFilter !== "all" && <button onClick={() => setSzFilter("all")} style={{ marginTop: 12, padding: "8px 18px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Show all zones</button>}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {filteredZones.map((zone: any, i: number) => {
                const isExpanded = szExpanded === `${zone.street}|||${zone.city}`;
                const zoneKey = `${zone.street}|||${zone.city}`;
                return (
                  <div key={zoneKey} style={{ background: T.bg, borderRadius: 14, border: `1.5px solid ${isExpanded ? T.primary + "40" : T.border}`, overflow: "hidden", transition: "border-color 0.15s" }}>
                    {/* Zone header */}
                    <div onClick={() => setSzExpanded(isExpanded ? null : zoneKey)}
                      style={{ padding: "16px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 16 }}
                      onMouseEnter={(e: any) => { if (!isExpanded) e.currentTarget.style.background = T.bgOff; }}
                      onMouseLeave={(e: any) => { e.currentTarget.style.background = "transparent"; }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: i < 3 ? T.primary : T.bgSub, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 900, color: i < 3 ? "#fff" : T.textMid, fontFamily: "'Outfit'" }}>#{i + 1}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: "'Outfit'", letterSpacing: "-0.3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{zone.street}</div>
                        <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>{zone.city}, {zone.state} · {zone.count} permits · avg {zone.avgYears}yr</div>
                      </div>
                      {zone.dominant && (
                        <div style={{ padding: "4px 10px", borderRadius: 8, background: (segColor[zone.dominant.segment] || T.primary) + "12", border: `1px solid ${(segColor[zone.dominant.segment] || T.primary)}25`, flexShrink: 0 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: segColor[zone.dominant.segment] || T.primary }}>
                            {segEmoji[zone.dominant.segment]} {zone.dominant.segment} × {zone.dominant.count}
                          </span>
                        </div>
                      )}
                      <div style={{ width: 44, height: 44, borderRadius: 10, background: zone.score >= 60 ? "#FFF7ED" : T.bgOff, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${zone.score >= 60 ? "#FDBA74" : T.border}` }}>
                        <div style={{ fontSize: 16, fontWeight: 900, color: zone.score >= 60 ? T.primary : T.text, fontFamily: "'Outfit'", lineHeight: 1 }}>{zone.score}</div>
                      </div>
                      <ChevronRight size={16} color={T.textSub} style={{ transform: isExpanded ? "rotate(90deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }} />
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div style={{ borderTop: `1px solid ${T.border}`, background: T.bgOff }}>
                        <div style={{ padding: "12px 20px", display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {Object.entries(zone.segments).map(([seg, count]: any) => (
                            <span key={seg} style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, background: (segColor[seg] || "#666") + "10", color: segColor[seg] || "#666", border: `1px solid ${(segColor[seg] || "#666")}20` }}>
                              {segEmoji[seg]} {seg}: {count}
                            </span>
                          ))}
                          {zone.unlockedCount > 0 && (
                            <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, background: "#FFF7ED", color: T.primary }}>✓ {zone.unlockedCount} already unlocked</span>
                          )}
                        </div>
                        <div style={{ padding: "8px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, background: T.bg }}>
                          <button onClick={() => szSelectAll(zone)} style={{ fontSize: 12, fontWeight: 700, color: T.primary, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                            Select all unlockable ({zone.leads.filter((l: any) => !l.hasContact).length})
                          </button>
                          <span style={{ fontSize: 11, color: T.textSub }}>{szSelected.size} selected across all zones</span>
                        </div>
                        {zone.leads.map((lead: any) => {
                          const isSelected = szSelected.has(lead.id);
                          return (
                            <div key={lead.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", borderBottom: `1px solid ${T.border}`, background: isSelected ? T.primary + "08" : "transparent" }}>
                              <div onClick={() => !lead.hasContact && szToggleSelect(lead.id)}
                                style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${lead.hasContact ? T.primary : isSelected ? T.primary : T.borderMid}`, background: lead.hasContact ? T.primary + "15" : isSelected ? T.primary : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: lead.hasContact ? "default" : "pointer", flexShrink: 0 }}>
                                {(isSelected || lead.hasContact) && <Check size={12} color={lead.hasContact ? T.primary : "#fff"} />}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lead.address}</div>
                                <div style={{ fontSize: 11, color: T.textSub }}>{lead.segment} · {lead.ageYears}yr{getSmartScore(lead) == null ? " · New" : ` · Score ${fmtScore(getSmartScore(lead))}`}</div>
                              </div>
                              {lead.contractorName && (
                                <span style={{ fontSize: 10, fontWeight: 700, color: "#D97706", background: "#FFFBEB", padding: "2px 7px", borderRadius: 5, maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", border: "1px solid #F59E0B25" }}>{lead.contractorName}</span>
                              )}
                              {lead.hasContact ? (
                                <span style={{ fontSize: 10, fontWeight: 700, color: T.primary, background: "#FFF7ED", padding: "3px 8px", borderRadius: 6 }}>📞 Ready</span>
                              ) : (
                                <Unlock size={13} color={T.textSub} />
                              )}
                            </div>
                          );
                        })}
                        <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", background: T.bg }}>
                          <button onClick={() => { setOnbCity(zone.city); setOnbRegion(zone.state); setView("explore"); }}
                            style={{ fontSize: 12, fontWeight: 700, color: T.primary, background: "none", border: "none", cursor: "pointer" }}>View in Permit Feed →</button>
                          <button onClick={() => szSelectAll(zone)}
                            style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: T.primary, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit'", display: "flex", alignItems: "center", gap: 6 }}>
                            <Unlock size={12} /> Unlock zone ({zone.leads.filter((l: any) => !l.hasContact).length} credits)
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

    // ── FOCUS ────────────────────────────────────────────────────────────────────
  const Focus = () => {
    const fmtDue = (d: any) => {
      if (!d) return null;
      const due = new Date(d + "T00:00:00");
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const days = Math.round((due.getTime() - today.getTime()) / 86400000);
      if (days < 0) return { label: `${Math.abs(days)}d overdue`, overdue: true, soon: false };
      if (days === 0) return { label: "Due today", overdue: false, soon: true };
      if (days === 1) return { label: "Due tomorrow", overdue: false, soon: true };
      if (days <= 7) return { label: `Due in ${days}d`, overdue: false, soon: true };
      return { label: due.toLocaleDateString(undefined, { month: "short", day: "numeric" }), overdue: false, soon: false };
    };

    const Stat = (label: string, value: any, color: string, mono?: boolean) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ fontFamily: mono ? "ui-monospace, 'JetBrains Mono', monospace" : "'Outfit', sans-serif", fontSize: 22, fontWeight: 900, color, lineHeight: 1, letterSpacing: "-0.02em" }}>{value}</div>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textSub }}>{label}</div>
      </div>
    );

    const ProgressBar = (todo: number, working: number, won: number, lost: number) => {
      const total = todo + working + won + lost;
      const seg = [
        { v: todo, c: "#C4C4BC" },
        { v: working, c: "#FF8800" },
        { v: won, c: "#16A34A" },
        { v: lost, c: "#E2E2DC" },
      ];
      return (
        <div style={{ height: 10, borderRadius: 3, overflow: "hidden", display: "flex", background: "#EEEEE8", width: "100%" }}>
          {total > 0 && seg.map((s, i) => (s.v > 0 ? <div key={i} style={{ width: `${(s.v / total) * 100}%`, background: s.c }} /> : null))}
        </div>
      );
    };

    const FocusCard = (list: any, archived?: boolean) => {
      const total = Number(list.total) || 0;
      const todo = Number(list.todo) || 0, working = Number(list.working) || 0, won = Number(list.won) || 0, lost = Number(list.lost) || 0;
      const conv = list.conversion != null ? Number(list.conversion) : null;
      const due = fmtDue(list.due_date);
      const segByLabel: any = { "New Install": "installers", "Maintenance": "cleaning", "Battery Storage": "battery", "System Upgrade": "takeover", "Loan Payoff": "loanpayoff", "Replacement": "longterm" };
      const segKey = list.scope_type === "segment" ? segByLabel[list.scope_value] : null;
      const segObj: any = segKey ? (SEGMENTS as any)[segKey] : null;
      // Segment color is the ink: eyebrows and accents carry the segment hue straight.
      const accentFill = segObj ? segObj.color : (list.scope_type === "location" ? "#FF8800" : "#0A0A0A");
      const accentInk = accentFill;
      const scopeLabel = (list.scope_type && list.scope_type !== "leads" && list.scope_value) ? String(list.scope_value) : "Custom list";
      const legend: any[] = [["To-do", todo, "#B8B8B0"], ["Working", working, "#FF8800"], ["Won", won, "#16A34A"], ["Lost", lost, "#DADAD4"]];
      let nudge = "", nudgeColor = "#0A0A0A";
      if (total === 0) { nudge = "No leads yet \u00b7 save from the feed"; nudgeColor = T.textMid; }
      else if (todo > 0) { nudge = `${todo} to work`; nudgeColor = "#FF8800"; }
      else if (working > 0) { nudge = `${working} in progress`; nudgeColor = "#0A0A0A"; }
      else { nudge = "All worked"; nudgeColor = "#16A34A"; }
      return (
        <div key={list.focus_list_id} onClick={() => openFocus(list)}
          style={{ background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 14, padding: "16px 16px 14px", cursor: "pointer", transition: "all 150ms", opacity: archived ? 0.6 : 1, display: "flex", flexDirection: "column", gap: 14 }}
          onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.text; e.currentTarget.style.boxShadow = "0 6px 22px rgba(0,0,0,0.07)"; }}
          onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 7 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: accentFill, flexShrink: 0 }} />
                <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: accentInk, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{scopeLabel}</span>
                {archived && <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textMid, background: T.bgSub, padding: "2px 6px", borderRadius: 4, flexShrink: 0 }}>Archived</span>}
              </div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 900, color: T.text, letterSpacing: "-0.02em", lineHeight: 1.15 }}>{list.title}</div>
              {list.directive && <div style={{ fontSize: 13, color: T.textMid, fontWeight: 500, marginTop: 5, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{list.directive}</div>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              {due && (
                <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: due.overdue ? "#DC2626" : due.soon ? "#FF8800" : T.text, background: due.overdue ? "#FEF2F2" : due.soon ? "#FFF3E5" : T.bgSub, padding: "3px 9px", borderRadius: 6, whiteSpace: "nowrap" }}>
                  {due.label}
                </span>
              )}
              <div style={{ position: "relative" }}>
                <button onClick={(e: any) => { e.stopPropagation(); setFocusCardMenu(focusCardMenu === list.focus_list_id ? null : list.focus_list_id); }}
                  title="Options"
                  style={{ background: "transparent", border: "none", color: "#262B37", cursor: "pointer", fontSize: 18, fontWeight: 700, padding: "0 4px", lineHeight: 1 }}>&#8943;</button>
                {focusCardMenu === list.focus_list_id && (
                  <div style={{ position: "absolute", right: 0, top: "100%", marginTop: 4, background: "#fff", border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.12)", zIndex: 50, minWidth: 160, overflow: "hidden" }}>
                    <button onClick={(e: any) => { e.stopPropagation(); setFocusCardMenu(null); archiveFocus(list.focus_list_id, archived ? "active" : "archived"); }}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", background: "#fff", border: "none", fontSize: 13, fontWeight: 700, color: T.text, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                      {archived ? "Reactivate" : "Archive"}
                    </button>
                    <button onClick={(e: any) => { e.stopPropagation(); setFocusCardMenu(null); if (window.confirm("Delete this focus? Its leads move to Unfiled.")) deleteFocus(list.focus_list_id); }}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", background: "#fff", border: "none", borderTop: `1px solid ${T.bgSub}`, fontSize: 13, fontWeight: 700, color: "#DC2626", cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {ProgressBar(todo, working, won, lost)}
            {total > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "5px 14px" }}>
                {legend.map((it: any, i: number) => (
                  <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: it[2], flexShrink: 0 }} />
                    <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 12, fontWeight: 800, color: T.text }}>{it[1]}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{it[0]}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 11, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 12.5, fontWeight: 800, color: nudgeColor }}>{nudge}</span>
            <span style={{ display: "flex", alignItems: "baseline", gap: 8, flexShrink: 0 }}>
              <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: T.text }}>{total} {total === 1 ? "lead" : "leads"}</span>
              {conv != null && <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 13, fontWeight: 800, color: "#FF8800", background: "#FFF3E5", padding: "2px 7px", borderRadius: 5 }}>{conv}%</span>}
            </span>
          </div>
        </div>
      );
    };

    const UnfiledCard = (count: number) => (
      <div key="__unfiled__" onClick={() => openFocus("unfiled")}
        style={{ background: T.bgOff, border: `1.5px dashed ${T.border}`, borderRadius: 14, padding: "16px 16px 14px", cursor: "pointer", transition: "all 150ms", display: "flex", flexDirection: "column", gap: 14, justifyContent: "space-between" }}
        onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = "#FF8800"; }}
        onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 7 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: "#FF8800", flexShrink: 0 }} />
            <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#FF8800" }}>Unfiled</span>
          </div>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 900, color: T.text, letterSpacing: "-0.02em", lineHeight: 1.15 }}>Saved, not sorted</div>
          <div style={{ fontSize: 13, color: T.textMid, fontWeight: 500, marginTop: 5, lineHeight: 1.4 }}>Pulled from the feed, waiting to be filed into a focus.</div>
        </div>
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 11, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: "#FF8800" }}>Sort into a focus</span>
          <span style={{ display: "flex", alignItems: "baseline", gap: 5, flexShrink: 0 }}>
            <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 20, fontWeight: 900, color: T.text, letterSpacing: "-0.02em" }}>{count}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.textMid }}>waiting</span>
          </span>
        </div>
      </div>
    );

    const STAT_BTN = [
      { key: "todo", label: "To do", on: { bg: "#F4F4EE", fg: "rgba(10,10,10,0.72)", bd: T.text } },
      { key: "working", label: "Working", on: { bg: "#EFF6FF", fg: "#1D4ED8", bd: "#1D4ED8" } },
      { key: "won", label: "Won", on: { bg: "#F0FDF4", fg: "#16A34A", bd: "#16A34A" } },
      { key: "lost", label: "Lost", on: { bg: "#FAFAF6", fg: "#262B37", bd: "rgba(10,10,10,0.3)" } },
    ];

    const LeadRow = (lead: any, isUnfiledRow: boolean) => {
      const score = getSmartScore(lead);
      const tier = scoreTier(score);
      const seg = SEGMENTS[getSegment(lead)];
      const cur = lead.outcome === "won" ? "won" : lead.outcome === "lost" ? "lost" : lead.stage === "working" ? "working" : "todo";
      const moveTargets = focusLists.filter((l: any) => l.status !== "archived" && !(focusSel && focusSel !== "unfiled" && l.focus_list_id === focusSel.focus_list_id));
      return (
        <div key={lead.id} style={{ background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 12 }}>
          <div style={{ padding: "13px 16px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 999, background: tier === "hot" ? "#FFF3E5" : tier === "warm" ? "#FEF3C7" : "#F4F4EE", flexShrink: 0 }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: tier === "hot" ? "#FF8800" : tier === "warm" ? "#FACC15" : "#94A3B8" }} />
              <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 12.5, fontWeight: 800, color: tier === "hot" ? "#FF5500" : tier === "warm" ? "#FF8800" : "#262B37" }}>{fmtScore(score)}</span>
            </div>
            <div onClick={() => { setModalLead(lead); setModalTab("overview"); }} style={{ minWidth: 0, flex: "1 1 220px", cursor: "pointer" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 800, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cleanAddress(lead.address)}</div>
              <div style={{ fontSize: 12.5, color: T.textSub, fontWeight: 500, marginTop: 2 }}>
                {[lead.city, getState(lead)].filter(Boolean).join(", ")}{(lead.city || getState(lead)) ? " \u00B7 " : ""}{seg?.label || "Solar"} &middot; {fmtAge(getDays(lead))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
              {STAT_BTN.map((b) => {
                const active = cur === b.key;
                return (
                  <button key={b.key} onClick={(e: any) => { e.stopPropagation(); setItemStatus(lead.id, b.key); }}
                    style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, fontWeight: active ? 800 : 600, padding: "6px 12px", borderRadius: 8, cursor: "pointer", border: `1.5px solid ${active ? b.on.bd : T.border}`, background: active ? b.on.bg : "#fff", color: active ? b.on.fg : "rgba(10,10,10,0.45)", transition: "all 100ms" }}>
                    {b.label}
                  </button>
                );
              })}
            </div>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <button onClick={(e: any) => { e.stopPropagation(); setFocusMoveFor(focusMoveFor === lead.id ? null : lead.id); }}
                title="Move to another focus"
                style={{ background: "transparent", border: "none", color: "#262B37", cursor: "pointer", fontSize: 18, fontWeight: 700, padding: "2px 8px", lineHeight: 1 }}>&#8943;</button>
              {focusMoveFor === lead.id && (
                <div style={{ position: "absolute", right: 0, top: "100%", marginTop: 4, background: "#fff", border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.12)", zIndex: 50, minWidth: 210, overflow: "hidden" }}>
                  <div style={{ padding: "8px 12px", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: T.textSub, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", borderBottom: `1px solid ${T.border}` }}>Move to focus</div>
                  {moveTargets.map((l: any) => (
                    <button key={l.focus_list_id} onClick={(e: any) => { e.stopPropagation(); moveItemToFocus(lead.id, l.focus_list_id); }}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", background: "#fff", border: "none", borderBottom: `1px solid ${T.bgSub}`, fontSize: 13, fontWeight: 700, color: T.text, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                      {l.title}
                    </button>
                  ))}
                  {moveTargets.length === 0 && <div style={{ padding: "10px 12px", fontSize: 12.5, color: T.textSub }}>No other focuses yet.</div>}
                  {!isUnfiledRow && (
                    <button onClick={(e: any) => { e.stopPropagation(); moveItemToFocus(lead.id, null); }}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", background: "#fff", border: "none", fontSize: 13, fontWeight: 700, color: "#262B37", cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                      Remove from focus
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    if (focusSel !== null) {
      const isUnfiled = focusSel === "unfiled";
      const list: any = isUnfiled ? null : focusSel;
      const title = isUnfiled ? "Unfiled" : list.title;
      const directive = isUnfiled ? "Leads you've saved but not yet assigned to a focus. Move them into one to start tracking." : list.directive;
      const cnt = { todo: 0, working: 0, won: 0, lost: 0 };
      focusLeads.forEach((l: any) => {
        if (l.outcome === "won") cnt.won++;
        else if (l.outcome === "lost") cnt.lost++;
        else if (l.stage === "working") cnt.working++;
        else cnt.todo++;
      });
      const total = focusLeads.length;
      const closed = cnt.won + cnt.lost;
      const conv = closed > 0 ? Math.round((cnt.won / closed) * 100) : null;
      const due = !isUnfiled ? fmtDue(list.due_date) : null;

      return (
        <div style={{ background: "#fff", minHeight: "100%" }}>
          <div style={{ padding: "20px 32px", borderBottom: "1px solid #EAEAE2" }}>
            <button onClick={() => { setFocusSel(null); setFocusLeads([]); setFocusMoveFor(null); }}
              style={{ background: "none", border: "none", color: T.textSub, fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 14, fontFamily: "'Outfit', sans-serif", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <ChevronRight size={14} style={{ transform: "rotate(180deg)" }} /> All focuses
            </button>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 900, lineHeight: 1, letterSpacing: "-0.04em", color: isUnfiled ? T.textSub : T.text, margin: 0 }}>{title}</h1>
                  {due && <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: due.overdue ? "#DC2626" : due.soon ? T.primary : T.textSub, background: due.overdue ? "#FEF2F2" : due.soon ? "#FFF3E5" : T.bgSub, padding: "4px 10px", borderRadius: 7 }}>{due.label}</span>}
                </div>
                {directive && <div style={{ fontSize: 15, color: T.textSub, fontWeight: 500, maxWidth: 680, lineHeight: 1.45, marginTop: 8 }}>{directive}</div>}
              </div>
              {!isUnfiled && (
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button onClick={() => archiveFocus(list.focus_list_id, list.status === "archived" ? "active" : "archived")}
                    style={{ background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, color: T.textMid, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                    {list.status === "archived" ? "Reactivate" : "Archive"}
                  </button>
                  <button onClick={() => { if (window.confirm("Delete this focus? Its leads move to Unfiled.")) deleteFocus(list.focus_list_id); }}
                    style={{ background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, color: "#262B37", cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                    Delete
                  </button>
                </div>
              )}
            </div>
            {total > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", marginTop: 18 }}>
                {ProgressBar(cnt.todo, cnt.working, cnt.won, cnt.lost)}
                <div style={{ display: "flex", gap: 18 }}>
                  {Stat("To do", cnt.todo, T.textMid)}
                  {Stat("Working", cnt.working, "#1D4ED8")}
                  {Stat("Won", cnt.won, "#16A34A")}
                  {Stat("Lost", cnt.lost, "#262B37")}
                  {conv != null && Stat("Win rate", `${conv}%`, T.primary, true)}
                </div>
              </div>
            )}
          </div>

          <div style={{ padding: "18px 28px 32px", display: "flex", flexDirection: "column", gap: 10 }}>
            {focusLoading ? (
              <div style={{ padding: 40, textAlign: "center", color: T.textSub, fontWeight: 600 }}>Loading&hellip;</div>
            ) : total === 0 ? (
              <div style={{ padding: "40px 24px", textAlign: "center" }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 900, color: T.text, marginBottom: 6 }}>Nothing here yet</div>
                <div style={{ fontSize: 14, color: T.textSub, marginBottom: 18 }}>Add leads from the feed, then assign them to this focus.</div>
                <button onClick={() => setView("explore")} style={{ padding: "10px 22px", borderRadius: 8, background: T.primary, border: "none", color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>Browse the feed &rarr;</button>
              </div>
            ) : (
              focusLeads.map((lead: any) => LeadRow(lead, isUnfiled))
            )}
          </div>
        </div>
      );
    }

    const activeLists = focusLists.filter((l: any) => l.status !== "archived");
    const archivedLists = focusLists.filter((l: any) => l.status === "archived");
    const hasAnything = focusLists.length > 0 || focusUnfiled > 0;

    return (
      <div style={{ background: "#fff", minHeight: "100%" }}>
        <div style={{ padding: "22px 32px 22px", borderBottom: "1px solid #EAEAE2" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, flexWrap: "wrap", marginBottom: (focusShowNew || focusAiOpen) ? 18 : 0 }}>
            <div style={{ minWidth: 0, flex: "1 1 auto" }}>
              <h1 style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: "clamp(34px, 6vw, 64px)", fontWeight: 900, lineHeight: 0.96, letterSpacing: "-0.045em", color: T.text, margin: 0, marginBottom: 12 }}>
                Your worklist. <span style={{ color: T.primary }}>What gets done.</span>
              </h1>
              <div style={{ fontSize: 15.5, color: T.textSub, fontWeight: 500, maxWidth: 700, lineHeight: 1.5 }}>
                {hasAnything
                  ? <>Direct your week &mdash; a ZIP, a segment, a target. Each focus tracks its leads from <strong style={{ color: T.text, fontWeight: 800 }}>to do &rarr; won</strong>, so you always know what's moving.</>
                  : "A focus is a directed worklist \u2014 \u201CThis week: Halifax battery retrofits.\u201D Create one, fill it from the feed, and drive every lead to won or lost."}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
              <button onClick={() => { setFocusAiOpen((s: any) => !s); setFocusShowNew(false); setFocusAiError(null); }}
                style={{ background: focusAiOpen ? "#fff" : "#FFF7ED", border: `1.5px solid ${focusAiOpen ? T.text : "#FDBA74"}`, borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 800, color: focusAiOpen ? T.text : T.primary, cursor: "pointer", fontFamily: "'Outfit', sans-serif", transition: "all 120ms", display: "inline-flex", alignItems: "center", gap: 6 }}>
                {focusAiOpen ? "Cancel" : "✨ Build with AI"}
              </button>
              <button onClick={() => { setFocusShowNew((s: any) => !s); setFocusAiOpen(false); }}
                style={{ background: focusShowNew ? "#fff" : T.primary, border: focusShowNew ? `1.5px solid ${T.text}` : "none", borderRadius: 8, padding: "10px 22px", fontSize: 13, fontWeight: 800, color: focusShowNew ? T.text : "#fff", cursor: "pointer", fontFamily: "'Outfit', sans-serif", transition: "all 120ms" }}>
                {focusShowNew ? "Cancel" : "+ New focus"}
              </button>
            </div>
          </div>

          {focusShowNew && (
            <div style={{ background: T.bgOff, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18, marginTop: 4 }}>
              <input autoFocus value={focusNewTitle} onChange={(e: any) => setFocusNewTitle(e.target.value)} placeholder="Name this focus - e.g. This week: Dartmouth new installs"
                onKeyDown={(e: any) => { if (e.key === "Enter" && focusNewTitle.trim()) createFocus(); }}
                style={{ width: "100%", boxSizing: "border-box", fontFamily: "'Outfit', sans-serif", fontSize: 17, fontWeight: 800, color: T.text, border: "none", borderBottom: `2px solid ${T.border}`, padding: "6px 0", marginBottom: 14, outline: "none", background: "transparent" }} />
              <input value={focusNewDirective} onChange={(e: any) => setFocusNewDirective(e.target.value)} placeholder="Directive (optional) - what's the goal? who's it for?"
                style={{ width: "100%", boxSizing: "border-box", fontFamily: "'Outfit', sans-serif", fontSize: 14, color: T.textMid, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 14, outline: "none", background: "#fff" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <label style={{ fontSize: 12.5, fontWeight: 700, color: T.textSub, display: "flex", alignItems: "center", gap: 8 }}>
                  Due
                  <input type="date" value={focusNewDue} onChange={(e: any) => setFocusNewDue(e.target.value)}
                    style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 12.5, color: T.text, border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 10px", outline: "none" }} />
                </label>
                <button disabled={!focusNewTitle.trim() || focusBusy} onClick={createFocus}
                  style={{ background: focusNewTitle.trim() ? T.text : T.border, border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 13, fontWeight: 800, color: "#fff", cursor: focusNewTitle.trim() ? "pointer" : "default", fontFamily: "'Outfit', sans-serif", marginLeft: "auto" }}>
                  {focusBusy ? "Creating\u2026" : "Create focus"}
                </button>
              </div>
            </div>
          )}

          {focusAiOpen && (
            <div style={{ background: "#FFFBF5", border: "1px solid #FDBA74", borderRadius: 12, padding: 18, marginTop: 4 }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 900, color: T.text, marginBottom: 4, display: "flex", alignItems: "center", gap: 7 }}>
                <span>✨</span> Build a focus with AI
              </div>
              <div style={{ fontSize: 13, color: T.textSub, fontWeight: 500, marginBottom: 12 }}>Describe the leads you want in plain English - Claude turns it into a ready-to-work focus.</div>
              <textarea value={focusAiPrompt} onChange={(e: any) => setFocusAiPrompt(e.target.value)}
                onKeyDown={(e: any) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) runAiBuild(); }}
                placeholder="e.g. Edmonton homes likely needing a battery upgrade or upsell"
                rows={2}
                style={{ width: "100%", boxSizing: "border-box", fontFamily: "'Inter', sans-serif", fontSize: 14, color: T.text, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px", outline: "none", background: "#fff", resize: "vertical", lineHeight: 1.45 }} />
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 10 }}>
                {["Edmonton battery upsell", "New installs in Calgary", "Old Halifax systems due for replacement"].map((ex) => (
                  <button key={ex} onClick={() => setFocusAiPrompt(ex)}
                    style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 999, padding: "5px 12px", fontSize: 12, fontWeight: 600, color: T.textMid, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                    {ex}
                  </button>
                ))}
              </div>
              {focusAiError && <div style={{ fontSize: 13, color: "#DC2626", fontWeight: 600, marginTop: 12 }}>{focusAiError}</div>}
              {!focusAiResult && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                  <button disabled={!focusAiPrompt.trim() || focusAiLoading} onClick={runAiBuild}
                    style={{ background: focusAiPrompt.trim() ? T.primary : T.border, border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 13, fontWeight: 800, color: "#fff", cursor: focusAiPrompt.trim() && !focusAiLoading ? "pointer" : "default", fontFamily: "'Outfit', sans-serif" }}>
                    {focusAiLoading ? "Thinking…" : "Build →"}
                  </button>
                </div>
              )}
              {focusAiResult && (
                <div style={{ marginTop: 16, borderTop: "1px solid #FDBA74", paddingTop: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                    <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 24, fontWeight: 800, color: T.text }}>{focusAiResult.total}{focusAiResult.capped ? "+" : ""}</span>
                    <span style={{ fontSize: 14, color: T.textMid, fontWeight: 600 }}>matching {focusAiResult.total === 1 ? "lead" : "leads"}</span>
                    {focusAiResult.parsed?.city && <span style={{ fontSize: 12, fontWeight: 700, color: T.textMid, background: "#fff", border: `1px solid ${T.border}`, padding: "3px 9px", borderRadius: 6 }}>📍 {focusAiResult.parsed.city}</span>}
                    {focusAiResult.parsed?.segmentLabel && <span style={{ fontSize: 12, fontWeight: 700, color: T.primary, background: "#FFF7ED", border: "1px solid #FDBA74", padding: "3px 9px", borderRadius: 6 }}>◆ {focusAiResult.parsed.segmentLabel}</span>}
                    {(focusAiResult.parsed?.keywords || []).map((k: any) => <span key={k} style={{ fontSize: 12, fontWeight: 700, color: T.textMid, background: "#fff", border: `1px solid ${T.border}`, padding: "3px 9px", borderRadius: 6 }}>“{k}”</span>)}
                  </div>
                  <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textSub, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", display: "block", marginBottom: 6 }}>Focus name</label>
                  <input value={focusAiTitle} onChange={(e: any) => setFocusAiTitle(e.target.value)}
                    style={{ width: "100%", boxSizing: "border-box", fontFamily: "'Outfit', sans-serif", fontSize: 16, fontWeight: 800, color: T.text, border: `1px solid ${T.border}`, borderRadius: 8, padding: "9px 12px", marginBottom: 14, outline: "none", background: "#fff" }} />
                  {(focusAiResult.sample || []).length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textSub, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", marginBottom: 7 }}>Sample of what's inside</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        {focusAiResult.sample.map((s: any) => (
                          <div key={s.id} style={{ fontSize: 13, color: T.textMid, fontWeight: 500 }}>
                            <span style={{ fontWeight: 700, color: T.text }}>{s.address || "-"}</span>{s.city ? <span style={{ color: T.textSub }}> · {s.city}{s.province ? `, ${s.province}` : ""}</span> : null}{s.contractor ? <span style={{ color: T.textSub }}> · {s.contractor}</span> : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "flex-end" }}>
                    <button onClick={() => { setFocusAiResult(null); setFocusAiError(null); }}
                      style={{ background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 700, color: T.textMid, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                      Start over
                    </button>
                    <button disabled={focusAiCreating} onClick={createFocusFromAi}
                      style={{ background: T.primary, border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 13, fontWeight: 800, color: "#fff", cursor: focusAiCreating ? "default" : "pointer", fontFamily: "'Outfit', sans-serif" }}>
                      {focusAiCreating ? "Creating…" : `Create focus · ${focusAiResult.total}${focusAiResult.capped ? "+" : ""} leads`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: "22px 28px 32px" }}>
          {focusLoading && focusLists.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: T.textSub, fontWeight: 600 }}>Loading&hellip;</div>
          ) : !hasAnything ? (
            <div style={{ padding: "48px 24px", textAlign: "center", maxWidth: 460, margin: "0 auto" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 900, color: T.text, marginBottom: 8, letterSpacing: "-0.02em" }}>No focuses yet</div>
              <div style={{ fontSize: 14.5, color: T.textSub, marginBottom: 22, lineHeight: 1.5 }}>Create your first focus to turn the feed into a plan you can actually work.</div>
              <button onClick={() => setFocusShowNew(true)} style={{ padding: "11px 26px", borderRadius: 8, background: T.primary, border: "none", color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>+ New focus</button>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
              {activeLists.map((list: any) => FocusCard(list, false))}
              {focusUnfiled > 0 && UnfiledCard(focusUnfiled)}
              {archivedLists.map((list: any) => FocusCard(list, true))}
            </div>
          )}
        </div>
      </div>
    );
  };

    // ── INSTALLER ROI CALCULATOR ────────────────────────────────────────────────
  const InstallerROI = () => {
    const [closeRate, setCloseRate] = useState(15);
    const [dealSize, setDealSize] = useState(25000);
    const [currentLeads, setCurrentLeads] = useState(20);
    const [extraLeads] = useState(40);

    const monthlyRevNow = Math.round((currentLeads * closeRate / 100) * dealSize);
    const monthlyRevWith = Math.round(((currentLeads + extraLeads) * closeRate / 100) * dealSize);
    const monthlyLift = monthlyRevWith - monthlyRevNow;
    const annualLift = monthlyLift * 12;
    const roi = Math.round(monthlyLift / 599);
    const paybackDays = monthlyLift > 0 ? Math.round((599 / monthlyLift) * 30) : null;

    const fmt = (n: any) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : n >= 1000 ? `$${(n/1000).toFixed(0)}K` : `$${n}`;

    return (
      <div style={{ flex: 1, overflowY: "auto", background: T.bgOff, padding: "24px" }}>
        <div style={{ maxWidth: 600, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Header */}
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.text, fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.5px", marginBottom: 4 }}>SolarLead ROI Calculator</div>
            <div style={{ fontSize: 13, color: T.textSub }}>See exactly how much additional revenue SolarLead generates for your business at $599/month.</div>
          </div>

          {/* Inputs */}
          <div style={{ background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, padding: "20px 22px", display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: T.textSub, marginBottom: -8 }}>Your business today</div>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Leads per month</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: T.primary }}>{currentLeads}</span>
              </div>
              <input type="range" min={5} max={100} value={currentLeads} onChange={(e: any) => setCurrentLeads(+e.target.value)} style={{ width: "100%", accentColor: T.primary }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.textSub, marginTop: 2 }}>
                <span>5</span><span>100</span>
              </div>
            </div>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Close rate</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: T.primary }}>{closeRate}%</span>
              </div>
              <input type="range" min={5} max={40} value={closeRate} onChange={(e: any) => setCloseRate(+e.target.value)} style={{ width: "100%", accentColor: T.primary }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.textSub, marginTop: 2 }}>
                <span>5%</span><span>40%</span>
              </div>
            </div>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Average deal size</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: T.primary }}>{fmt(dealSize)}</span>
              </div>
              <input type="range" min={10000} max={80000} step={1000} value={dealSize} onChange={(e: any) => setDealSize(+e.target.value)} style={{ width: "100%", accentColor: T.primary }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.textSub, marginTop: 2 }}>
                <span>$10K</span><span>$80K</span>
              </div>
            </div>
          </div>

          {/* SolarLead adds row */}
          <div style={{ background: T.primaryLight, borderRadius: 12, border: `1px solid #FDBA74`, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.primaryDark, marginBottom: 2 }}>SolarLead adds to your focus</div>
              <div style={{ fontSize: 12, color: T.textMid }}>Qualified, scored solar permit leads in your territory</div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: T.primary, fontFamily: "'Outfit',sans-serif" }}>+{extraLeads} leads/mo</div>
          </div>

          {/* Results */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              { label: "Revenue without SolarLead", val: fmt(monthlyRevNow) + "/mo", sub: fmt(monthlyRevNow * 12) + "/yr", color: T.textMid, bg: T.bg },
              { label: "Revenue with SolarLead", val: fmt(monthlyRevWith) + "/mo", sub: fmt(monthlyRevWith * 12) + "/yr", color: T.primary, bg: T.primaryLight },
              { label: "Monthly revenue lift", val: "+" + fmt(monthlyLift), sub: "from SolarLead", color: T.primary, bg: T.bg },
              { label: "Annual revenue lift", val: "+" + fmt(annualLift), sub: "per year", color: T.primary, bg: T.bg },
            ].map((r) => (
              <div key={r.label} style={{ background: r.bg, borderRadius: 12, border: `1px solid ${T.border}`, padding: "16px 18px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: T.textSub, marginBottom: 8 }}>{r.label}</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: r.color, fontFamily: "'Outfit',sans-serif", letterSpacing: "-1px" }}>{r.val}</div>
                <div style={{ fontSize: 12, color: T.textSub, marginTop: 4 }}>{r.sub}</div>
              </div>
            ))}
          </div>

          {/* ROI verdict */}
          <div style={{ background: T.text, borderRadius: 14, padding: "20px 22px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: "#FFFFFF", marginBottom: 10 }}>Your SolarLead ROI</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 48, fontWeight: 900, color: "#FFBE4F", fontFamily: "'Outfit',sans-serif", letterSpacing: "-2px" }}>{roi}x</div>
              <div style={{ fontSize: 14, color: "#FFFFFF" }}>return on your $599/month</div>
            </div>
            {paybackDays && paybackDays <= 30 && (
              <div style={{ fontSize: 13, color: "#FFFFFF" }}>
                SolarLead pays for itself in <span style={{ color: "#FFBE4F", fontWeight: 700 }}>{paybackDays} days</span> - one closed deal covers months of subscription.
              </div>
            )}
            {paybackDays && paybackDays > 30 && (
              <div style={{ fontSize: 13, color: "#FFFFFF" }}>
                Break-even in <span style={{ color: "#FFBE4F", fontWeight: 700 }}>{paybackDays} days</span>. Adjust your close rate or deal size to see the impact.
              </div>
            )}
          </div>

        </div>
      </div>
    );
  };


  // ── ROUTE PLANNER ────────────────────────────────────────────────────────────
  const RoutePlanner = () => {
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [plan, setPlan] = useState<any>(null);
    const [error, setError] = useState("");

    // Build dense lead clusters from loaded leads
    const buildClusters = () => {
      const streetMap: any = {};
      leads.forEach((l: any) => {
        const raw = (l.address || "");
        // Strip house number to get street name
        const street = raw.replace(/^\d+[\w-]*\s+/, "").split(/,/)[0].trim();
        if (!street || street.length < 4) return;
        const key = `${street.toLowerCase()}|||${(l.city || "").toLowerCase()}`;
        if (!streetMap[key]) streetMap[key] = { street, city: l.city || "", state: getState(l), leads: [] };
        streetMap[key].leads.push(l);
      });
      return Object.values(streetMap)
        .filter((g: any) => g.leads.length >= 2)
        .map((g: any) => ({
          street: g.street,
          city: g.city,
          state: g.state,
          count: g.leads.length,
          avgScore: Math.round((g.leads.reduce((s: any, l: any) => s + (getSmartScore(l) ?? 0), 0) / g.leads.length) * 10) / 10,
          topScore: Math.max(...g.leads.map((l: any) => getSmartScore(l))),
          segment: SEGMENTS[getSegment(g.leads[0])]?.label || "",
          avgAge: Math.round(g.leads.reduce((s: any, l: any) => s + getDays(l), 0) / g.leads.length / 365 * 10) / 10,
          addresses: g.leads.slice(0, 5).map((l: any) => l.address),
        }))
        .sort((a: any, b: any) => (b.count * b.avgScore) - (a.count * a.avgScore));
    };

    const handlePlan = async () => {
      if (!query.trim() || loading) return;
      setLoading(true);
      setError("");
      setPlan(null);

      const clusters = buildClusters();
      const territory = onbCity ? `${onbCity}, ${onbRegion || ""}` : onbRegion || "All North American markets";
      const topClusters = clusters.slice(0, 40);

      const systemPrompt = `You are SolarLead's AI Prospecting Intelligence engine. You analyse solar permit clusters and build hyper-efficient outreach plans for sales teams - whether they work by phone, field, or both.

You have access to real permit data clustered by street density. Your job is to identify the highest-value prospecting opportunities and group nearby streets into logical zones - so a sales rep can work an entire area in one outreach session (one dialing block, or one field trip).

CURRENT TERRITORY: ${territory}
TOTAL LEADS IN DATABASE: ${((realTotalCount > 0 ? realTotalCount : 1000000)).toLocaleString()}

TOP STREET CLUSTERS (streets with 2+ solar permits, ranked by density × score):
${topClusters.map((g: any, i: number) => `${i+1}. ${g.street}, ${g.city} - ${g.count} homes | Avg score: ${g.avgScore} | Segment: ${g.segment} | Avg age: ${g.avgAge}yr`).join('\n')}

USER REQUEST: "${query}"

Analyse the clusters and build a smart prospecting plan. Group geographically close streets into ZONES (2-5 streets per zone). Each zone should be worked together as a single outreach block - either dialed consecutively or walked in one trip.

Respond ONLY with valid JSON, no markdown, no explanation:
{
  "headline": "Sharp 5-8 word plan title",
  "insight": "2 sentences of market intelligence about why these streets are hot right now",
  "zones": [
    {
      "name": "Zone name (e.g. North Austin Battery Belt)",
      "priority": "HIGH" | "MEDIUM",
      "streets": [
        {
          "street": "Street name",
          "city": "City",
          "homes": 4,
          "avgScore": 8.8,
          "segment": "Battery Storage",
          "avgAge": "3.2yr",
          "hook": "Specific 1-line opener for this street based on permit age and segment"
        }
      ],
      "zoneNote": "Why these streets cluster well together"
    }
  ],
  "script": "Universal opener for this outreach - works for phone, text, or in-person (2-3 sentences)",
  "timing": "Best day/time recommendation with reasoning",
  "avoidNote": "One thing to avoid or watch out for in this area"
}`;

      try {
        const res = await fetch(`${API_URL}/api/ai/route-plan`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt: systemPrompt }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          console.error("Route plan API error:", res.status, errData);
          throw new Error(errData.error || `API ${res.status}`);
        }
        const data = await res.json();
        if (data.success && data.plan) {
          setPlan(data.plan);
        } else {
          throw new Error(data.error || "Invalid response");
        }
      } catch (e: any) {
        setError(e.message?.includes("API key") 
          ? "AI Route Planner requires an Anthropic API key. Add ANTHROPIC_API_KEY to your Railway environment variables."
          : "Couldn't generate plan - try rephrasing or check your connection.");
      }
      setLoading(false);
    };

    const examplePrompts = [
      "Best battery storage streets in Austin this week",
      "High density replacement leads near Scottsdale",
      "Plan a full day of outreach for Chicago solar upgrades",
    ];

    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: T.bgOff }}>

        {/* Hero header - light, bold, splash-page inspired */}
        <div style={{ flexShrink: 0, borderBottom: `1px solid ${T.border}`, background: T.bg, padding: "36px 32px 28px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#1A1A2E", boxShadow: `0 0 0 3px ${T.primaryLight}, 0 0 12px rgba(255,136,0,0.3)` }} />
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "2.5px", textTransform: "uppercase", color: T.primary }}>AI-Powered · Industry First</span>
          </div>

          {/* Bold headline - "Know every solar roof in your city with AI" */}
          <div style={{ fontSize: 44, fontWeight: 900, color: T.text, fontFamily: "'Outfit',sans-serif", letterSpacing: "-2px", lineHeight: 1.1, marginBottom: 6 }}>
            Know every{" "}
            <span style={{ color: "#FF8800" }}>solar roof</span>
            {" "}in your city
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div style={{ fontSize: 44, fontWeight: 900, color: T.text, fontFamily: "'Outfit',sans-serif", letterSpacing: "-2px", lineHeight: 1.1 }}>
              ✦
            </div>
            <div style={{ padding: "6px 16px", borderRadius: 20, background: "linear-gradient(135deg, #D97006, #FF8800)", color: "#fff", fontSize: 16, fontWeight: 800, fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.5px" }}>
              with AI
            </div>
          </div>

          <div style={{ fontSize: 15, color: T.textMid, marginBottom: 24, maxWidth: 520, lineHeight: 1.65 }}>
            Describe your ideal prospecting run. Our AI reads {(realTotalCount > 0 ? realTotalCount : 1000000).toLocaleString()} real permits, finds the densest neighborhood clusters, and builds outreach zones with opening scripts for your reps.
          </div>

          {/* Search input - clean, light */}
          <div style={{ position: "relative", maxWidth: 680 }}>
            <div style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)" }}>
              <Navigation size={16} color={loading ? T.primary : T.textSub} />
            </div>
            <input
              value={query}
              onChange={(e: any) => setQuery(e.target.value)}
              onKeyDown={(e: any) => e.key === "Enter" && handlePlan()}
              placeholder="e.g. Best battery storage streets near Austin TX..."
              style={{ width: "100%", padding: "16px 120px 16px 46px", borderRadius: 14, border: `1.5px solid ${loading ? T.primary : T.border}`, background: T.bgOff, color: T.text, fontSize: 15, outline: "none", boxSizing: "border-box", fontFamily: "'DM Sans',sans-serif", transition: "border-color 0.2s" }}
            />
            <button onClick={handlePlan} disabled={loading || !query.trim()}
              style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", padding: "11px 20px", borderRadius: 10, background: query.trim() ? T.primary : T.bgSub, border: "none", cursor: query.trim() ? "pointer" : "default", color: query.trim() ? "#fff" : T.textSub, fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 7, transition: "all 0.15s", fontFamily: "'Outfit',sans-serif" }}>
              {loading ? <RefreshCw size={14} style={{ animation: "spin 0.8s linear infinite" }} /> : <Navigation size={14} />}
              {loading ? "Planning..." : "Build route"}
            </button>
          </div>

          {/* Example chips */}
          {!plan && !loading && (
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              {examplePrompts.map((ex) => (
                <button key={ex} onClick={() => { setQuery(ex); }} style={{ padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, border: `1px solid ${T.border}`, background: T.bg, color: T.textMid, cursor: "pointer", transition: "all 0.15s" }}
                  onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.color = T.primaryDark; e.currentTarget.style.background = T.primaryLight; }}
                  onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMid; e.currentTarget.style.background = T.bg; }}>
                  {ex}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Loading */}
          {loading && (
            <div style={{ padding: "48px 24px", textAlign: "center" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", border: `2px solid ${T.primary}`, borderTopColor: "transparent", margin: "0 auto 16px", animation: "spin 0.8s linear infinite" }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>Analysing {((realTotalCount > 0 ? realTotalCount : 1000000)).toLocaleString()} permits...</div>
              <div style={{ fontSize: 13, color: T.textSub }}>Clustering by street, scoring by opportunity</div>
            </div>
          )}

          {/* Error */}
          {error && <div style={{ padding: "14px 16px", background: "rgba(239,68,68,0.15)", borderRadius: 12, border: "1px solid rgba(239,68,68,0.3)", fontSize: 13, color: "#FCA5A5" }}>{error}</div>}

          {/* Plan output */}
          {plan && !loading && (
            <>
              {/* Headline + insight */}
              <div style={{ background: T.text, borderRadius: 16, border: `1px solid ${T.primary}30`, padding: "20px 22px" }}>
                <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "2px", textTransform: "uppercase", color: T.primary, marginBottom: 8 }}>✦ AI PROSPECTING PLAN</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.5px", marginBottom: 10 }}>{plan.headline}</div>
                <div style={{ fontSize: 14, color: "#FFFFFF", lineHeight: 1.65 }}>{plan.insight}</div>
              </div>

              {/* Timing + avoid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {plan.timing && (
                  <div style={{ background: T.bg, borderRadius: 12, border: `1px solid ${T.border}`, padding: "14px 16px" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: T.primary, marginBottom: 6 }}>⏰ Best time</div>
                    <div style={{ fontSize: 13, color: T.text, lineHeight: 1.55 }}>{plan.timing}</div>
                  </div>
                )}
                {plan.avoidNote && (
                  <div style={{ background: "rgba(239,68,68,0.07)", borderRadius: 12, border: "1px solid rgba(239,68,68,0.2)", padding: "14px 16px" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "#FCA5A5", marginBottom: 6 }}>⚠️ Watch out</div>
                    <div style={{ fontSize: 13, color: "#FFFFFF", lineHeight: 1.55 }}>{plan.avoidNote}</div>
                  </div>
                )}
              </div>

              {/* Opening script */}
              {plan.script && (
                <div style={{ background: "rgba(255,136,0,0.12)", borderRadius: 12, border: `1px solid ${T.primary}40`, padding: "16px 18px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: T.primary, marginBottom: 8 }}>🗣 Opening script</div>
                  <div style={{ fontSize: 14, color: T.text, lineHeight: 1.7, fontStyle: "italic" }}>"{plan.script}"</div>
                </div>
              )}

              {/* Zones */}
              {(plan.zones || []).map((zone: any, zi: number) => (
                <div key={zi} style={{ background: T.bg, borderRadius: 16, border: `1px solid ${zone.priority === "HIGH" ? T.primary + "60" : "rgba(255,255,255,0.1)"}`, overflow: "hidden" }}>
                  {/* Zone header */}
                  <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${T.border}`, background: zone.priority === "HIGH" ? T.primaryLight : T.bgOff }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        {zone.priority === "HIGH" && <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "1px", textTransform: "uppercase", padding: "2px 8px", borderRadius: 6, background: "#1A1A2E", color: "#fff" }}>PRIORITY</span>}
                        <div style={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: "'Outfit',sans-serif" }}>{zone.name}</div>
                      </div>
                      <div style={{ fontSize: 12, color: "#FFFFFF" }}>{zone.zoneNote}</div>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: T.primary, fontFamily: "'Outfit',sans-serif", flexShrink: 0 }}>
                      {(zone.streets || []).reduce((s: any, st: any) => s + (st.homes || 0), 0)}
                      <div style={{ fontSize: 10, color: "#FFFFFF", fontWeight: 500, textAlign: "right" }}>doors</div>
                    </div>
                  </div>

                  {/* Streets in zone */}
                  {(zone.streets || []).map((st: any, si: number) => (
                    <div key={si} style={{ padding: "13px 18px", borderBottom: si < zone.streets.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none", display: "flex", gap: 14, alignItems: "flex-start" }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: st.avgScore >= SCORE_HOT ? "rgba(239,68,68,0.2)" : "rgba(245,158,11,0.15)", border: `1px solid ${st.avgScore >= SCORE_HOT ? "rgba(239,68,68,0.4)" : "rgba(245,158,11,0.3)"}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 900, color: st.avgScore >= SCORE_HOT ? "#FCA5A5" : "#FCD34D", lineHeight: 1 }}>{st.avgScore}</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{st.street}</div>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(255,255,255,0.1)", color: "#FFFFFF" }}>{st.homes} homes</span>
                        </div>
                        <div style={{ fontSize: 12, color: T.textSub, marginBottom: st.hook ? 6 : 0 }}>
                          {st.city} · {st.segment} · {st.avgAge} old
                        </div>
                        {st.hook && (
                          <div style={{ fontSize: 12, color: T.textMid, padding: "7px 10px", background: T.primaryLight, borderRadius: 8, border: `1px solid #FDBA74`, lineHeight: 1.55 }}>
                            💬 {st.hook}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {/* New plan button */}
              <button onClick={() => { setPlan(null); setQuery(""); }} style={{ padding: "13px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, fontSize: 13, fontWeight: 700, color: T.textMid, cursor: "pointer" }}>
                ↺ Plan a different run
              </button>
            </>
          )}

          {/* Empty state */}
          {!plan && !loading && !error && (
            <div style={{ padding: "40px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📍</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8 }}>Where do you want to work?</div>
              <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.65, maxWidth: 340, margin: "0 auto" }}>
                Type a city, area, or describe what you're looking for. AI will find the highest-density permit clusters and build your outreach zones.
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

    // ── SETTINGS ─────────────────────────────────────────────────────────────────
  const SettingsPage = () => {
    // ── Tier resolution - drives what UI is shown ──────────────────────────
    const tier: "admin" | "pro" | "free" = user.isAdmin
      ? "admin"
      : (user.plan && user.plan !== "free" ? "pro" : "free");

    // Credit pack pricing - MUST match server.js /api/credits/purchase PACKS
    // exactly, otherwise UI promises one price and Stripe charges another.
    const CREDIT_PACKS = [
      { credits: 10,  price: 50,  perCredit: "$5.00", label: "Top-up" },
      { credits: 25,  price: 100, perCredit: "$4.00", label: "Save 20%", best: true },
      { credits: 50,  price: 175, perCredit: "$3.50", label: "Save 30%" },
    ];

    // ── Wired button handlers ──────────────────────────────────────────────
    const startSubscriptionCheckout = async () => {
      try {
        const res = await fetch(`${API_URL}/api/stripe/create-subscription-checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (data.url) window.location.href = data.url;
        else alert(data.error || "Could not start checkout. Try again.");
      } catch (e: any) { alert("Checkout failed: " + e.message); }
    };

    const openBillingPortal = async () => {
      try {
        const res = await fetch(`${API_URL}/api/stripe/create-portal`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
        });
        const data = await res.json();
        if (data.url) window.location.href = data.url;
        else alert(data.error || "Could not open billing portal.");
      } catch (e: any) { alert("Portal error: " + e.message); }
    };

    // Deep-links straight into Stripe's cancellation flow. FTC click-to-cancel:
    // one click from Settings → Stripe cancel screen (which itself has its own
    // confirm step, so we don't add a second confirm here - that'd be friction
    // beyond what the rule permits).
    const cancelSubscription = async () => {
      try {
        const res = await fetch(`${API_URL}/api/stripe/create-portal`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ mode: "cancel" }),
        });
        const data = await res.json();
        if (data.url) window.location.href = data.url;
        else alert(data.error || "Could not open cancellation flow.");
      } catch (e: any) { alert("Cancellation error: " + e.message); }
    };

    const purchasePack = async (credits: number) => {
      try {
        const res = await fetch(`${API_URL}/api/credits/purchase`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ pack: String(credits) }),
        });
        const data = await res.json();
        if (data.url) window.location.href = data.url;
        else alert(data.error || "Could not start purchase.");
      } catch (e: any) { alert("Purchase failed: " + e.message); }
    };

    // Password reset - uses the existing anti-enumeration endpoint, which always
    // returns 200, so we show the same confirmation regardless.
    const requestPasswordReset = async () => {
      try {
        await fetch(`${API_URL}/api/auth/request-password-reset`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: user.email }),
        }).then(r => r.json()).catch(() => ({}));
        alert(`If an account exists for ${user.email || "this address"}, a password-reset link is on its way. Check your email.`);
      } catch (e: any) { alert("Could not start password reset: " + e.message); }
    };

    // Section header - consistent editorial eyebrow style
    const SectionHeader = ({ children }: { children: any }) => (
      <div style={{ padding: "14px 22px", borderBottom: `1px solid ${T.border}`, background: T.bg }}>
        <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", color: T.textSub }}>
          {children}
        </div>
      </div>
    );

    return (
    <div style={{ flex: 1, overflowY: "auto", background: T.bgOff }}>
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "40px 24px 60px", display: "flex", flexDirection: "column", gap: 16 }}>

        {/* Editorial header */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase", color: T.primary, marginBottom: 10 }}>
            Settings
          </div>
          <h1 style={{
            fontFamily: "'Outfit', system-ui, sans-serif",
            fontSize: "clamp(34px, 4.5vw, 52px)", fontWeight: 900, lineHeight: 1,
            letterSpacing: "-0.04em", color: T.text, margin: 0,
          }}>
            Your account.
          </h1>
        </div>

        {/* ── Account ──────────────────────────────────────────────────── */}
        <div style={{ background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden" }}>
          <SectionHeader>Profile</SectionHeader>
          <div style={{ padding: "18px 22px", display: "flex", alignItems: "center", gap: 14, borderBottom: `1px solid ${T.border}` }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg, #FF8800 0%, #FF5500 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 900, fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.02em", flexShrink: 0 }}>
              {initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 900, color: T.text, letterSpacing: "-0.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user.firstName ? `${user.firstName}${user.lastName ? " " + user.lastName : ""}` : (user.email ? user.email.split("@")[0] : "Your account")}
              </div>
              <div style={{ fontSize: 12.5, color: T.textSub, marginTop: 2, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user.email || "-"}
              </div>
            </div>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 800, color: tier === "admin" ? "#15803D" : T.primary, background: tier === "admin" ? "#DCFCE7" : T.primaryLight, padding: "4px 9px", borderRadius: 6, textTransform: "uppercase", letterSpacing: "0.08em", flexShrink: 0, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", whiteSpace: "nowrap" }}>
              {tier === "admin" ? "Owner · Admin" : "Pro"}
            </span>
          </div>
          <div style={{ padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 14, color: T.textMid }}>Company</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{user.companyName || "-"}</span>
          </div>
        </div>

        {/* ── Subscription ─────────────────────────────────────────────── */}
        <div style={{ background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden" }}>
          <SectionHeader>Subscription</SectionHeader>

          {/* PRO - compact management card, NO feature re-sell */}
          {tier === "pro" && (
            <div style={{ padding: "20px 22px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, gap: 12 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 19, fontWeight: 900, color: T.text, letterSpacing: "-0.02em" }}>SolarLead Pro</span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 800, color: "#15803D", background: "#DCFCE7", padding: "3px 9px", borderRadius: 6, letterSpacing: "0.02em" }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16A34A" }} /> Active
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: T.textSub, marginTop: 4, fontWeight: 500 }}>$599/mo · renews monthly</div>
                </div>
              </div>
              <button onClick={openBillingPortal} style={{ width: "100%", padding: "13px", background: T.text, border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800, color: "#fff", cursor: "pointer", fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.01em" }}>
                Manage billing in Stripe →
              </button>
              {/* FTC click-to-cancel: explicitly labeled, deep-links to Stripe cancel flow. */}
              <button onClick={cancelSubscription} style={{ width: "100%", padding: "10px", marginTop: 8, background: "transparent", border: "none", fontSize: 13, fontWeight: 600, color: T.textSub, cursor: "pointer", fontFamily: "'Inter', sans-serif", textDecoration: "underline", textUnderlineOffset: 3 }}>
                Cancel subscription
              </button>
            </div>
          )}

          {/* ADMIN - internal, no billing */}
          {tier === "admin" && (
            <div style={{ padding: "20px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 900, color: T.text, letterSpacing: "-0.02em" }}>Admin access</div>
                <div style={{ fontSize: 13, color: T.textSub, marginTop: 3, fontWeight: 500 }}>Unlimited platform access · no billing applies</div>
              </div>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 800, color: "#15803D", background: "#DCFCE7", padding: "4px 10px", borderRadius: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16A34A" }} /> Active
              </span>
            </div>
          )}

          {/* FREE - the only place we re-sell. Full feature card + subscribe. */}
          {tier === "free" && (
            <div style={{ padding: "22px" }}>
              <div style={{ border: `1.5px solid ${T.primary}`, borderRadius: 14, padding: "24px 22px", background: T.primaryLight }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14, gap: 12 }}>
                  <div>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 20, fontWeight: 900, color: T.text, letterSpacing: "-0.02em" }}>SolarLead Pro</div>
                    <div style={{ fontSize: 12, color: "#2A2A3E", marginTop: 2 }}>Full platform access</div>
                  </div>
                  <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 32, fontWeight: 900, color: T.primary, letterSpacing: "-1px" }}>$599</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#2A2A3E" }}>/mo</span>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
                  {[
                    "1,000,000+ solar permits across the US and Canada",
                    "AI-powered market intelligence & daily briefs",
                    "10 skip traces included per billing cycle",
                    "$5 per additional skip trace",
                    "All territories - US & Canada",
                  ].map(f => (
                    <div key={f} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Check size={14} color={T.primary} strokeWidth={2.5} />
                      <span style={{ fontSize: 13, color: "#0F0F1A", fontWeight: 500 }}>{f}</span>
                    </div>
                  ))}
                </div>
                <button onClick={startSubscriptionCheckout} style={{ width: "100%", padding: "13px", background: T.primary, border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800, color: "#fff", cursor: "pointer", fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.01em" }}>
                  Subscribe - $599/mo →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Skip trace credits ───────────────────────────────────────── */}
        {tier === "admin" && (
          <div style={{ background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden" }}>
            <SectionHeader>Skip trace credits</SectionHeader>
            <div style={{ padding: "20px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 14, color: T.textMid }}>Credits remaining</div>
                <div style={{ fontSize: 11, color: T.textSub, marginTop: 3 }}>Internal admin - no usage caps apply.</div>
              </div>
              <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 20, fontWeight: 900, color: T.text, letterSpacing: "-0.02em" }}>
                Unlimited
              </span>
            </div>
          </div>
        )}

        {tier === "pro" && (
          <div style={{ background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden" }}>
            <SectionHeader>Skip trace credits</SectionHeader>
            <div style={{ padding: "16px 22px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 14, color: T.textMid }}>Remaining this cycle</span>
              <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 900, color: (user.creditsRemaining ?? 0) <= 3 ? T.danger : T.text, letterSpacing: "-0.02em" }}>
                {(user.creditsRemaining ?? 0)} / 10
              </span>
            </div>
            <div style={{ padding: "18px 22px" }}>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 14 }}>10 included each cycle. Need more? Grab a pack - credits never expire.</div>
              <div style={{ fontSize: 11.5, color: T.textSub, marginBottom: 14, padding: "10px 12px", background: T.bgOff, borderRadius: 8, border: `1px solid ${T.border}`, lineHeight: 1.45 }}>
                <strong style={{ color: T.textMid }}>Coverage note:</strong> Contact enrichment currently returns results for <strong style={{ color: T.textMid }}>US addresses</strong>. Canadian contact lookup is in progress - permit data and intelligence cover both countries.
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                {CREDIT_PACKS.map((bundle) => (
                  <button key={bundle.credits} onClick={() => purchasePack(bundle.credits)} style={{
                    flex: 1, padding: "14px 8px", borderRadius: 10,
                    border: `${bundle.best ? "1.5px" : "1px"} solid ${bundle.best ? T.primary : T.border}`,
                    background: bundle.best ? T.primaryLight : T.bg,
                    cursor: "pointer", textAlign: "center",
                    transition: "all 120ms ease", position: "relative",
                  }}
                  onMouseEnter={e => { if (!bundle.best) (e.currentTarget.style.borderColor = T.text); }}
                  onMouseLeave={e => { if (!bundle.best) (e.currentTarget.style.borderColor = T.border); }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: bundle.best ? T.primary : T.textSub, marginBottom: 4, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>{bundle.credits} credits</div>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 900, color: bundle.best ? T.primary : T.text, letterSpacing: "-0.025em", lineHeight: 1 }}>${bundle.price}</div>
                    <div style={{ fontSize: 10.5, color: T.textSub, marginTop: 4, fontWeight: 600 }}>{bundle.perCredit} each</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Territory ────────────────────────────────────────────────── */}
        <div style={{ background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden" }}>
          <SectionHeader>Territory</SectionHeader>
          <div style={{ padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <span style={{ fontSize: 14, color: T.textMid }}>Current territory</span>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{onbCity ? `${onbCity}, ${onbRegion}` : onbRegion || "All territories"}</span>
              <button onClick={() => setShowTerritoryPicker(true)} style={{ fontSize: 13, fontWeight: 700, color: T.primary, background: "none", border: "none", cursor: "pointer", padding: 0 }}>Change</button>
            </div>
          </div>
        </div>

        {/* ── Security ─────────────────────────────────────────────────── */}
        <div style={{ background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden" }}>
          <SectionHeader>Security</SectionHeader>
          <div style={{ padding: "16px 22px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>Password</div>
              <div style={{ fontSize: 12, color: T.textSub, marginTop: 1 }}>We'll email you a secure reset link.</div>
            </div>
            <button onClick={requestPasswordReset} style={{ fontSize: 13, fontWeight: 700, color: T.primary, background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontFamily: "'Outfit', sans-serif", whiteSpace: "nowrap" }}>
              Change password
            </button>
          </div>
          <button onClick={handleLogout} style={{ width: "100%", padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", cursor: "pointer", textAlign: "left", transition: "background 0.1s" }}
            onMouseEnter={(e: any) => { e.currentTarget.style.background = T.bgOff; }}
            onMouseLeave={(e: any) => { e.currentTarget.style.background = "transparent"; }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <LogOut size={16} color={T.danger} />
              <span style={{ fontSize: 14, color: T.danger, fontWeight: 700 }}>Sign out</span>
            </div>
            <ChevronRight size={14} color={T.textSub} />
          </button>
        </div>

        {/* ── Support ──────────────────────────────────────────────────── */}
        <div style={{ background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden" }}>
          <SectionHeader>Support</SectionHeader>
          <a href="mailto:support@solarlead.io" style={{ textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 22px", borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Mail size={16} color={T.textSub} />
              <div>
                <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>Email support</div>
                <div style={{ fontSize: 12, color: T.textSub, marginTop: 1 }}>support@solarlead.io - a real human replies</div>
              </div>
            </div>
            <ChevronRight size={14} color={T.textSub} />
          </a>
          <div style={{ padding: "14px 22px", fontSize: 12.5, color: T.textSub, lineHeight: 1.5 }}>
            Or tap the orange chat bubble in the bottom-right corner from anywhere in the app.
          </div>
        </div>

        {/* ── Legal ───────────────────────────────────────────────────── */}
        <div style={{ background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden" }}>
          <SectionHeader>Legal</SectionHeader>
          <div onClick={() => setView("terms")} style={{ padding: "16px 22px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
            <span style={{ fontSize: 14, color: T.textMid }}>Terms of Service</span>
            <ChevronRight size={14} color={T.textSub} />
          </div>
          <div onClick={() => setView("privacy")} style={{ padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
            <span style={{ fontSize: 14, color: T.textMid }}>Privacy Policy</span>
            <ChevronRight size={14} color={T.textSub} />
          </div>
        </div>

        {/* ── Tour ────────────────────────────────────────────────────── */}
        <button onClick={() => { localStorage.removeItem("siq_tour_done"); setShowWelcomeTour(true); setTourStep(0); setView("dashboard"); }} style={{ padding: "14px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, fontSize: 13, fontWeight: 700, color: T.textMid, cursor: "pointer" }}>
          Restart welcome tour
        </button>

      </div>
    </div>
    );
  };



  // ═════════════════════════════════════════════════════════════════════════
  // ║  MARKETS v2 - city grid landing + market detail drill-in              ║
  // ║                                                                       ║
  // ═════════════════════════════════════════════════════════════════════════
  // ║  ASK - AI-powered cross-database intelligence                          ║
  // ║                                                                       ║
  // ║  view === "ask" renders this. Backed by POST /api/ask, which runs a   ║
  // ║  tool-use loop on Claude Sonnet 4 against 7 typed SQL tools. The      ║
  // ║  rep asks any question ("where should I knock in Toronto", "who's     ║
  // ║  losing share in Calgary", "find 5-year-old installs in Edmonton")    ║
  // ║  and gets bullet-pointed answers grounded in real permit data.        ║
  // ║                                                                       ║
  // ║  Citations are clickable: clicking one re-asks a drill-down query.    ║
  // ║  Follow-ups at the bottom let the rep go deeper without typing.       ║
  // ║                                                                       ║
  // ║  This is the centerpiece feature that makes permit farming credible   ║
  // ║  vs. cold lead lists.                                                 ║
  // ═════════════════════════════════════════════════════════════════════════
  const AskView = () => {
    const tCity = onbCity;
    const tRegion = onbRegion;
    // Each suggestion has a category (icon + color hint) for visual grouping.
    // CRITICAL: every question MUST include a city scope. Generic questions like
    // "Find streets with takeover-ready installs" cause the AI to ask back
    // "which city?" - terrible first-click UX. Fallbacks below use Toronto +
    // Edmonton + Calgary which all have strong data coverage.
    type Suggestion = { q: string; cat: "action" | "intel" | "market" | "opportunity" };
    const cityA = tCity || "Toronto";
    const cityB = tCity || "Edmonton";
    const cityC = tCity || "Calgary";
    const suggested: Suggestion[] = [
      { q: `Where should I knock in ${cityA} right now?`,             cat: "action"      as const },
      { q: `Show me 5-year-old installs in ${cityB}`,                 cat: "action"      as const },
      { q: `Who's the dominant installer in ${cityA}?`,               cat: "intel"       as const },
      { q: `What are the top markets in Canada right now?`,           cat: "market"      as const },
      { q: `Find streets with takeover-ready installs in ${cityC}`,   cat: "opportunity" as const },
      { q: `Which contractors are growing fastest in ${cityB}?`,      cat: "intel"       as const },
    ];

    // Icon + color per category
    const CAT_META: Record<string, { color: string; bg: string }> = {
      action:       { color: "#FF8800", bg: "#FFF3E5" },
      intel:        { color: "#0B8A87", bg: "#DFF3F2" },
      market:       { color: "#15803D", bg: "#DCFCE7" },
      opportunity:  { color: "#FF8800", bg: "#FFF3E5" },
    };
    const CatIcon = ({ cat, size = 14 }: { cat: string; size?: number }) => {
      const c = CAT_META[cat]?.color || "#0A0A0A";
      if (cat === "action")      return <Zap         size={size} color={c} strokeWidth={2.5} />;
      if (cat === "intel")       return <Building2   size={size} color={c} strokeWidth={2.5} />;
      if (cat === "market")      return <TrendingUp  size={size} color={c} strokeWidth={2.5} />;
      if (cat === "opportunity") return <Target      size={size} color={c} strokeWidth={2.5} />;
      return null;
    };

    const handleCitationClick = (cite: string) => {
      if (!cite) return;
      const trimmed = cite.trim();
      const looksLikeContractor = /^[A-Z0-9 ,.&/-]+$/.test(trimmed) && !trimmed.includes(",");

      // Contractor citation → open the contractor drawer on Contractors view.
      // {name} is enough; the existing useEffect fetches permit history.
      if (looksLikeContractor) {
        setOpenRoofer({ name: trimmed });
        setView("roofers");
        return;
      }

      // Street citation ("131 Avenue NW, Edmonton") → Permits feed
      // pre-filtered by city + a lenient street search.
      //
      // The street name from find_street_clusters is canonical ("131 Avenue NW")
      // but raw addresses in the leads table store variants ("1234 131 AVE NW",
      // "131 av nw"). To improve substring-match hit rate, we strip the trailing
      // directional ("NW", "SW", "N", etc.) from the search term. The city
      // filter prevents the broader term from matching wrong neighborhoods.
      // If the search still returns 0, rep can clear it and see all city leads.
      if (trimmed.includes(",")) {
        const parts = trimmed.split(",").map((s: string) => s.trim());
        const street = parts[0];
        const city = parts[1];
        const DIR = /\s+(NW|NE|SW|SE|N|S|E|W|NORTH|SOUTH|EAST|WEST)$/i;
        const searchTerm = street.replace(DIR, "").trim() || street;
        if (city) setOnbCity(city);
        setExLocalSearch(searchTerm);
        fetchFeedPage(city || onbCity, onbRegion, 0, null, searchTerm);
        setView("explore");
        return;
      }

      // Fallback: anything else → ask the model for more detail
      submitAsk(`Tell me more about ${trimmed}`);
    };

    // Render finding text with numeric figures in a mono accent (data-product feel)
    const renderFindingText = (txt: string) => {
      const parts = String(txt || "").split(/(\d[\d,]*(?:\.\d+)?(?:\s?-\s?\d[\d,]*)?)/g);
      return parts.map((part: string, i: number) =>
        /^\d/.test(part)
          ? <span key={i} style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontWeight: 700, fontSize: 13.5, color: T.text }}>{part}</span>
          : part
      );
    };

    return (
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", background: "#fff" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "22px 32px 60px", minWidth: 0, width: "100%" }}>

          {/* HERO - full size in empty state, collapsed to eyebrow once loading or answer present */}
          {(!askLoading && !askAnswer && !askError) ? (
            <div style={{ marginBottom: 28 }}>
              <div style={{ marginBottom: 18 }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "9px 14px",
                  background: T.text,
                  color: "#fff",
                  fontFamily: "'Outfit', system-ui, sans-serif",
                  fontSize: 12, fontWeight: 900,
                  letterSpacing: "0.14em", textTransform: "uppercase",
                }}>
                  <Sparkles size={14} color="#FF8800" strokeWidth={2.5} />
                  AI · Ask the data
                </span>
              </div>
              <h1 style={{
                fontFamily: "'Outfit', system-ui, sans-serif",
                fontSize: "clamp(32px, 5.5vw, 68px)", fontWeight: 900, lineHeight: 0.95,
                letterSpacing: "-0.045em", color: T.text, margin: 0, marginBottom: 14,
              }}>
                Find the next door.<br/>
                <span style={{ color: T.primary }}>Every permit. Every angle.</span>
              </h1>
              <div style={{ fontSize: 15, color: "#0A0A0A", fontWeight: 500, maxWidth: 640, lineHeight: 1.55 }}>
                SolarLead AI reads 1,000,000+ permits across 3,690+ cities and answers any angle: clusters, segments, competitors, openings. Built for reps who farm permits instead of buying lead lists.
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 18 }}>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "9px 14px",
                background: T.text,
                color: "#fff",
                fontFamily: "'Outfit', system-ui, sans-serif",
                fontSize: 12, fontWeight: 900,
                letterSpacing: "0.14em", textTransform: "uppercase",
              }}>
                <Sparkles size={14} color="#FF8800" strokeWidth={2.5} />
                AI · Ask the data
              </span>
            </div>
          )}

          {/* INPUT */}
          <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
            <input
              value={askInput}
              onChange={(e: any) => setAskInput(e.target.value)}
              onKeyDown={(e: any) => { if (e.key === "Enter" && askInput.trim()) submitAsk(askInput); }}
              placeholder={tCity ? `Where should I knock in ${tCity}?` : "Where should I knock in Toronto right now?"}
              disabled={askLoading}
              style={{
                flex: 1, minWidth: 0,
                padding: "16px 20px",
                fontSize: 16, fontFamily: "'Outfit', sans-serif", fontWeight: 500,
                color: T.text, background: "#fff",
                border: `2px solid ${askLoading ? T.border : T.text}`,
                outline: "none",
              }}
            />
            <button
              onClick={() => askInput.trim() && submitAsk(askInput)}
              disabled={askLoading || !askInput.trim()}
              style={{
                padding: "0 30px",
                background: askLoading ? T.bgSub : T.primary,
                color: "#0A0A0A",
                opacity: (!askLoading && !askInput.trim()) ? 0.7 : 1,
                border: "none",
                fontSize: 15, fontWeight: 800, fontFamily: "'Outfit', sans-serif",
                letterSpacing: "-0.01em",
                cursor: askLoading ? "wait" : (!askInput.trim() ? "not-allowed" : "pointer"),
                whiteSpace: "nowrap",
                transition: "background 120ms ease, opacity 120ms ease",
              }}
            >
              {askLoading ? "Thinking…" : "Ask →"}
            </button>
          </div>

          {/* SUGGESTED + RECENT - empty state only */}
          {!askLoading && !askAnswer && !askError && (
            <div style={{ marginBottom: 36 }}>
              {askRecent.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{
                    display: "flex", alignItems: "baseline", justifyContent: "space-between",
                    marginBottom: 12,
                  }}>
                    <div style={{
                      fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                      fontSize: 10, color: "#0A0A0A", fontWeight: 800,
                      letterSpacing: "0.16em", textTransform: "uppercase",
                      display: "inline-flex", alignItems: "center", gap: 8,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: 2, background: "#FF8800" }} />
                      Recent
                    </div>
                    <button
                      onClick={() => {
                        setAskRecent([]);
                        try { localStorage.removeItem("sl_ask_recent"); } catch {}
                      }}
                      style={{
                        background: "none", border: "none",
                        fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 700,
                        color: "#0A0A0A", cursor: "pointer", letterSpacing: "-0.005em",
                        textDecoration: "underline", textUnderlineOffset: 3,
                      }}
                    >
                      Clear
                    </button>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {askRecent.map((q, i) => (
                      <button
                        key={`recent-${i}-${q.slice(0,20)}`}
                        onClick={() => submitAsk(q)}
                        style={{
                          padding: "10px 16px",
                          background: "#FFF7ED",
                          border: `1.5px solid #FDBA74`,
                          borderRadius: 10,
                          fontSize: 13, fontWeight: 600, fontFamily: "'Outfit', sans-serif",
                          color: "#0A0A0A", cursor: "pointer", textAlign: "left",
                          transition: "all 120ms ease",
                          display: "inline-flex", alignItems: "center", gap: 7,
                          maxWidth: 460,
                        }}
                        onMouseEnter={(e: any) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#FF8800"; }}
                        onMouseLeave={(e: any) => { e.currentTarget.style.background = "#FFF7ED"; e.currentTarget.style.borderColor = "#FDBA74"; }}
                      >
                        <Sparkles size={12} color="#FF8800" strokeWidth={2.5} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Try one */}
              <div>
                <div style={{
                  fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                  fontSize: 10, color: "#0A0A0A", fontWeight: 800,
                  letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 12,
                  display: "inline-flex", alignItems: "center", gap: 8,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: 2, background: T.primary }} />
                  Try one
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {suggested.map((s) => {
                    const meta = CAT_META[s.cat];
                    return (
                      <button
                        key={s.q}
                        onClick={() => submitAsk(s.q)}
                        style={{
                          padding: "11px 16px",
                          background: "#fff",
                          border: `1.5px solid ${T.border}`,
                          borderRadius: 10,
                          fontSize: 13.5, fontWeight: 600, fontFamily: "'Outfit', sans-serif",
                          color: "#0A0A0A", cursor: "pointer", textAlign: "left",
                          transition: "all 120ms ease",
                          display: "inline-flex", alignItems: "center", gap: 8,
                        }}
                        onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = "#0A0A0A"; e.currentTarget.style.background = meta.bg; }}
                        onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = "#fff"; }}
                      >
                        <CatIcon cat={s.cat} size={13} />
                        {s.q}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* LOADING */}
          {askLoading && (() => {
            const ASK_LOADING_PHRASES = [
              "Reading 1M+ permits across 3,690+ cities",
              "Checking contractor activity this week",
              "Looking at street-level clusters",
              "Cross-referencing competitor movement",
              "Finding the highest-signal patterns",
              "Pulling install-age and battery candidates",
            ];
            const currentPhrase = ASK_LOADING_PHRASES[askLoadingPhraseIdx % ASK_LOADING_PHRASES.length];
            const Bar = ({ delay }: { delay: number }) => (
              <span style={{
                display: "inline-block", width: 4, height: 22,
                background: T.primary,
                transformOrigin: "center",
                animation: `ask-bar 0.9s ease-in-out ${delay}s infinite`,
              }} />
            );
            return (
              <div style={{
                padding: "32px 32px 28px",
                background: T.text,
                color: "#fff",
                marginBottom: 24,
                position: "relative",
                overflow: "hidden",
              }}>
                {/* Header row: bars + AI WORKING */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 3, height: 22 }}>
                    <Bar delay={0} />
                    <Bar delay={0.15} />
                    <Bar delay={0.3} />
                    <Bar delay={0.45} />
                  </div>
                  <div style={{
                    fontFamily: "'Outfit', system-ui, sans-serif",
                    fontSize: 12, fontWeight: 900, color: T.primary,
                    letterSpacing: "0.18em", textTransform: "uppercase",
                  }}>
                    AI · Working
                  </div>
                </div>

                {/* The question being analysed */}
                <div style={{
                  fontFamily: "'Outfit', system-ui, sans-serif",
                  fontSize: 22, fontWeight: 800, color: "#fff",
                  lineHeight: 1.25, letterSpacing: "-0.02em",
                  marginBottom: 18,
                }}>
                  {(askLastQuestion || askInput).slice(0, 120)}{(askLastQuestion || askInput).length > 120 ? "…" : ""}
                </div>

                {/* Rotating status line */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{
                    display: "inline-block", width: 6, height: 6, borderRadius: "50%",
                    background: T.primary,
                    animation: "ask-pulse 1.2s ease-in-out infinite",
                  }} />
                  <div style={{
                    fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                    fontSize: 12, color: "#FFFFFF",
                    letterSpacing: "0.04em",
                  }}>
                    {currentPhrase}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ERROR */}
          {askError && (
            <div style={{
              padding: "20px 24px",
              background: "#FEF2F2",
              border: "1.5px solid #FCA5A5",
              marginBottom: 24,
            }}>
              <div style={{
                fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                fontSize: 10, color: "#B91C1C", fontWeight: 700,
                letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 8,
              }}>
                Couldn't answer that
              </div>
              <div style={{ fontSize: 14, color: "#7F1D1D", lineHeight: 1.55 }}>{askError}</div>
            </div>
          )}

          {/* ANSWER */}
          {askAnswer && (
            <div ref={askAnswerRef}>
              <div style={{ marginBottom: 22 }}>
                <div style={{
                  fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                  fontSize: 10, color: T.textSub, fontWeight: 700,
                  letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 8,
                }}>
                  You asked
                </div>
                <div style={{
                  fontFamily: "'Outfit', system-ui, sans-serif",
                  fontSize: 22, fontWeight: 800, color: T.text,
                  letterSpacing: "-0.02em", lineHeight: 1.3,
                }}>
                  {askLastQuestion}
                </div>
              </div>

              {askAnswer.summary && (
                <div style={{
                  background: T.sidebar,
                  borderLeft: `4px solid ${T.primary}`,
                  padding: "22px 26px 24px",
                  marginBottom: 26,
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 9, marginBottom: 12,
                    fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                    fontSize: 10, fontWeight: 700, color: "#FFFFFF",
                    letterSpacing: "0.2em", textTransform: "uppercase",
                  }}>
                    <span style={{ width: 8, height: 8, background: T.primary }} />
                    The verdict
                  </div>
                  <div style={{
                    color: "#FFFFFF", fontSize: 23, fontWeight: 800,
                    lineHeight: 1.32, letterSpacing: "-0.02em",
                    fontFamily: "'Outfit', system-ui, sans-serif",
                  }}>
                    {askAnswer.summary}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 9,
                  fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                  fontSize: 11, fontWeight: 700, color: T.text,
                  letterSpacing: "0.2em", textTransform: "uppercase",
                }}>
                  <span style={{ width: 7, height: 7, background: T.primary }} />
                  Findings
                </div>
                {(askAnswer.bullets || []).length > 0 && (
                  <div style={{
                    fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                    fontSize: 10, fontWeight: 700, color: T.text,
                    letterSpacing: "0.12em", textTransform: "uppercase",
                    border: `1px solid ${T.borderMid}`, padding: "3px 8px",
                  }}>
                    {(askAnswer.bullets || []).length} {(askAnswer.bullets || []).length === 1 ? "signal" : "signals"}
                  </div>
                )}
              </div>

              <div style={{ background: "#fff", border: `1px solid ${T.border}`, marginBottom: 22 }}>
                {(askAnswer.bullets || []).map((b: any, i: number) => (
                  <div key={i} style={{
                    padding: "18px 22px",
                    borderBottom: i < (askAnswer.bullets?.length || 1) - 1 ? `1px solid ${T.border}` : "none",
                    display: "flex", gap: 16, alignItems: "flex-start",
                  }}>
                    <div style={{
                      flexShrink: 0, width: 24, height: 24, marginTop: 1,
                      background: T.text, color: "#fff",
                      fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                      fontSize: 11, fontWeight: 700,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 15, color: T.text, lineHeight: 1.6,
                        fontFamily: "'Outfit', sans-serif", fontWeight: 500,
                      }}>
                        {renderFindingText(b.text)}
                      </div>
                      {b.citations && b.citations.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 11 }}>
                          {b.citations.map((c: string, ci: number) => (
                            <button
                              key={ci}
                              onClick={() => handleCitationClick(c)}
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 7,
                                padding: "5px 11px",
                                background: "#fff",
                                border: `1.25px solid ${T.text}`,
                                fontSize: 11, fontWeight: 700,
                                color: T.text,
                                fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                                letterSpacing: "0.02em",
                                cursor: "pointer",
                                transition: "background 120ms ease, color 120ms ease",
                              }}
                              onMouseEnter={(e: any) => { e.currentTarget.style.background = T.text; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e: any) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = T.text; }}
                            >
                              {c}
                              <span style={{ color: T.primary, fontWeight: 700 }}>↗</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {askAnswer.follow_ups && askAnswer.follow_ups.length > 0 && (
                <div style={{ marginBottom: 22 }}>
                  <div style={{
                    fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                    fontSize: 10, color: T.textSub, fontWeight: 700,
                    letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 12,
                  }}>
                    Ask next
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {askAnswer.follow_ups.map((q: string, i: number) => (
                      <button
                        key={i}
                        onClick={() => submitAsk(q)}
                        style={{
                          padding: "14px 18px",
                          background: "#fff",
                          border: `1.5px solid ${T.border}`,
                          fontSize: 14, fontWeight: 600, fontFamily: "'Outfit', sans-serif",
                          color: T.text, cursor: "pointer", textAlign: "left",
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          transition: "all 120ms ease",
                        }}
                        onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; }}
                        onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; }}
                      >
                        <span>{q}</span>
                        <span style={{ color: T.primary, fontSize: 16 }}>→</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={{
                paddingTop: 16,
                borderTop: `1px solid ${T.border}`,
                display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
                fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                fontSize: 10.5, color: T.text, fontWeight: 600,
                letterSpacing: "0.06em",
              }}>
                <span>{askToolCalls} {askToolCalls === 1 ? "query" : "queries"} run</span>
                <span>·</span>
                <span>{askStages.map((s: any) => s.tool).join(" + ") || "no tools"}</span>
                <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 9, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.primary }} />
                    Live data
                  </span>
                  <span>·</span>
                  <span>1,000,000+ permits</span>
                  <span>·</span>
                  <span style={{ fontWeight: 700 }}>Claude Sonnet 4</span>
                </span>
              </div>
            </div>
          )}

        </div>
      </div>
    );
  };

  // ║  view === "zones" renders this. Two modes:                            ║
  // ║    • selectedMarket === null → grid of cities (landing)               ║
  // ║    • selectedMarket populated → detail page for that city             ║
  // ║                                                                       ║
  // ║  Click a market card → setSelectedMarket → fetch /api/markets/...     ║
  // ║  Click "← All markets" → setSelectedMarket(null) → back to grid       ║
  // ═════════════════════════════════════════════════════════════════════════
  const MarketsView = () => {
    // Status badge colors - single source of truth, used by both Markets+Contractors
    const STATUS_COLORS: any = {
      growing:   { bg: "#DCFCE7", fg: "#15803D", dot: "#16A34A" },
      stable:    { bg: "#DBEAFE", fg: "#1D4ED8", dot: "#3B82F6" },
      shrinking: { bg: "#FEF2F2", fg: "#DC2626", dot: "#DC2626" },
      dormant:   { bg: "#F1F5F9", fg: "#64748B", dot: "#94A3B8" },
      new:       { bg: "#FFEDD5", fg: "#C2410C", dot: "#F97316" },
    };
    const StatusBadge = ({ status }: { status: string }) => {
      const c = STATUS_COLORS[status] || STATUS_COLORS.dormant;
      return (
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: c.bg, color: c.fg, padding: "4px 10px",
          borderRadius: 999, fontSize: 10.5, fontWeight: 800,
          letterSpacing: "0.08em", textTransform: "uppercase",
          fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
        }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: c.dot }} />
          {status}
        </span>
      );
    };

    // ─── DETAIL MODE ──────────────────────────────────────────────────────
    if (selectedMarket) {
      const m = marketDetail;
      const rawTrend = (m?.trend || []) as any[];
      const contractors = (m?.contractors || []) as any[];
      const H = m?.header || {};
      const nf = (n: any) => Number(n || 0).toLocaleString();
      const dShort = (d: any) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });

      // The RPC only emits weeks that actually have permits, so a quiet market
      // would render one or two giant blocks instead of a readable sparkline.
      // Pad to a contiguous run (at least 12 weeks) ending at the latest week we
      // have, filling empty weeks with zero so the x-axis is honest.
      const buildSeries = () => {
        if (rawTrend.length === 0) return [] as any[];
        const byKey: any = {};
        let latest = 0, earliest = Infinity;
        rawTrend.forEach((w: any) => {
          const t = new Date(w.week_start).getTime();
          byKey[new Date(t).toISOString().slice(0, 10)] = w;
          if (t > latest) latest = t;
          if (t < earliest) earliest = t;
        });
        const wkMs = 7 * 86400000;
        const span = Math.round((latest - earliest) / wkMs) + 1;
        const count = Math.max(12, span);
        const out: any[] = [];
        for (let i = count - 1; i >= 0; i--) {
          const d = new Date(latest - i * wkMs);
          const hit = byKey[d.toISOString().slice(0, 10)];
          out.push({ week_start: d.toISOString(), permit_count: hit ? (hit.permit_count || 0) : 0, distinct_contractors: hit ? (hit.distinct_contractors || 0) : 0 });
        }
        return out;
      };
      const trend = buildSeries();

      // Derived intelligence (only from fields the RPC already returns)
      const totalWindow = trend.reduce((s: number, w: any) => s + (w.permit_count || 0), 0);
      const weeklyAvg = trend.length ? Math.round(totalWindow / trend.length) : 0;
      const recent4 = trend.slice(-4).reduce((s: number, w: any) => s + (w.permit_count || 0), 0);
      const prior4 = trend.slice(-8, -4).reduce((s: number, w: any) => s + (w.permit_count || 0), 0);
      const delta = prior4 > 0 ? Math.round(((recent4 - prior4) / prior4) * 100) : null;
      const nzCounts = trend.map((w: any) => w.permit_count || 0).filter((n: number) => n > 0).sort((a: number, b: number) => a - b);
      const wkMedian = nzCounts.length ? nzCounts[Math.floor(nzCounts.length / 2)] : 0;
      const peakWeek = trend.reduce((mx: any, w: any) => ((w.permit_count || 0) > (mx ? mx.permit_count : -1) ? w : mx), null);
      const spike = peakWeek && wkMedian > 0 && (peakWeek.permit_count || 0) >= wkMedian * 3 ? peakWeek : null;
      const newEntrants = contractors.filter((c: any) => c.is_new_in_city);
      const growthPct = Number(H.growth_pct ?? 0);
      const topC = contractors[0] || null;
      const status = m?.status || "dormant";
      const statusStyle: any = ({
        growing:   { bg: "#F0FDF4", text: "#16A34A", border: "#86EFAC", label: "Growing" },
        steady:    { bg: "#EFF6FF", text: "#1D4ED8", border: "#93C5FD", label: "Steady" },
        shrinking: { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA", label: "Slipping" },
        dormant:   { bg: "#F4F4EE", text: "#0A0A0A", border: T.border, label: "Dormant" },
      } as any)[status] || { bg: "#F4F4EE", text: "#0A0A0A", border: T.border, label: "Dormant" };

      const CITY = H.city || selectedMarket.city;
      let insight = "";
      if (status === "dormant") {
        insight = `${CITY} has gone quiet. ${nf(H.total_leads)} permits on record, but nothing new this month` + (topC ? `. ${topC.name} has installed the most here.` : ".");
      } else if (growthPct >= 15) {
        insight = `${CITY} is heating up: ${nf(H.new_this_month)} permits added this month, up ${growthPct}% on the prior window` + (topC ? `, led by ${topC.name}.` : ".");
      } else if (growthPct <= -15) {
        insight = `${CITY} is cooling: ${nf(H.new_this_month)} permits this month, down ${Math.abs(growthPct)}% on the prior window` + (topC ? `. ${topC.name} is still the most active installer.` : ".");
      } else {
        insight = `${CITY} is steady: ${nf(H.new_this_month)} permits this month across the market` + (topC ? `, with ${topC.name} installing the most.` : ".") + (newEntrants.length ? ` ${newEntrants.length} new ${newEntrants.length === 1 ? "installer" : "installers"} just entered.` : "");
      }

      const flags: any[] = [];
      if (spike) flags.push({ dot: "#FF8800", title: "Volume spike", body: `Week of ${dShort(spike.week_start)} saw ${nf(spike.permit_count)} permits, well above the local norm of ${wkMedian} per week.` });
      if (status === "dormant") flags.push({ dot: "#DC2626", title: "Market dormant", body: `No new permits this month. The opportunity here is an installed base of ${nf(H.total_leads)} systems, not fresh filings.` });
      if (newEntrants.length > 0) flags.push({ dot: "#16A34A", title: newEntrants.length === 1 ? "New entrant" : "New entrants", body: `${newEntrants.slice(0, 3).map((c: any) => c.name).join(", ")}${newEntrants.length > 3 ? ` and ${newEntrants.length - 3} more` : ""} just filed a first permit here.` });
      if (delta != null && delta >= 25) flags.push({ dot: "#16A34A", title: "Accelerating", body: `Up ${delta}% over the last 4 weeks versus the prior 4.` });
      else if (delta != null && delta <= -25) flags.push({ dot: "#DC2626", title: "Slowing", body: `Down ${Math.abs(delta)}% over the last 4 weeks versus the prior 4.` });

      return (
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", background: T.bgOff }}>
          <div style={{ maxWidth: 1400, margin: "0 auto", padding: "32px 28px 48px" }}>
            {/* Back link */}
            <button
              onClick={() => { setSelectedMarket(null); setMarketDetail(null); }}
              style={{
                background: "transparent", border: "none", padding: 0, cursor: "pointer",
                color: "#0A0A0A", fontSize: 13, fontWeight: 700, marginBottom: 18,
                display: "inline-flex", alignItems: "center", gap: 6,
                fontFamily: "ui-monospace, 'JetBrains Mono', monospace", letterSpacing: "0.05em",
              }}
            >
              ← ALL MARKETS
            </button>

            {marketDetailLoading && !m && (
              <div style={{ color: "#0A0A0A", fontSize: 14, padding: "60px 0" }}>
                Loading {selectedMarket.city}…
              </div>
            )}

            {m && (
              <>
                {/* HERO */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 32, marginBottom: 8, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, color: "#FF8800", fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 6 }}>
                      Market Intelligence
                    </div>
                    <h1 style={{
                      fontFamily: "'Outfit', system-ui, sans-serif",
                      fontSize: "clamp(48px, 7vw, 88px)",
                      fontWeight: 900, lineHeight: 0.92, letterSpacing: "-0.045em",
                      color: "#0A0A0A", margin: 0,
                    }}>
                      {CITY}
                    </h1>
                    <div style={{ fontSize: 16, color: "#0A0A0A", marginTop: 8, fontWeight: 600 }}>
                      {H.province} · {H.country === "CA" ? "Canada" : H.country === "US" ? "United States" : H.country}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: statusStyle.bg, color: statusStyle.text, border: `1px solid ${statusStyle.border}`, padding: "5px 12px", borderRadius: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "ui-monospace, 'JetBrains Mono', monospace" }}>
                      {statusStyle.label}
                    </span>
                  </div>
                </div>

                {/* HEADLINE INSIGHT */}
                <div style={{ marginTop: 14, marginBottom: 8, padding: "16px 18px", background: "#FFF7ED", border: "1px solid #FDBA74", borderRadius: 12, borderLeft: "3px solid #FF8800" }}>
                  <div style={{ fontSize: 15.5, fontWeight: 600, color: "#0A0A0A", lineHeight: 1.5 }}>{insight}</div>
                </div>

                {/* HEADLINE STATS */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 1, background: T.border,
                  border: `1px solid ${T.border}`,
                  marginTop: 24, marginBottom: 40,
                }}>
                  {[
                    { label: "Total permits",   value: nf(H.total_leads), color: "#0A0A0A" },
                    { label: "New this month",  value: nf(H.new_this_month), color: "#0A0A0A" },
                    { label: "This week",       value: nf(H.new_this_week), color: "#0A0A0A" },
                    (() => {
                      const gp = growthPct;
                      const color = gp > 0 ? "#16A34A" : gp < 0 ? "#DC2626" : "#0A0A0A";
                      const value = gp > 0 ? `↑ ${gp}%` : gp < 0 ? `↓ ${Math.abs(gp)}%` : "→ flat";
                      return { label: "Growth (30d)", value, color };
                    })(),
                  ].map((s: any, i: number) => (
                    <div key={i} style={{ background: T.bg, padding: "22px 22px 20px" }}>
                      <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#0A0A0A", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 10 }}>
                        {s.label}
                      </div>
                      <div style={{
                        fontFamily: "'Outfit', system-ui, sans-serif",
                        fontSize: 36, fontWeight: 900, lineHeight: 1, letterSpacing: "-0.03em",
                        color: s.color,
                      }}>{s.value}</div>
                    </div>
                  ))}
                </div>

                {/* 12-WEEK TREND */}
                <div style={{ background: T.bg, border: `1px solid ${T.border}`, padding: "28px 28px 24px", marginBottom: 40 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 28, gap: 16, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10.5, color: "#FF8800", fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 4 }}>
                        {trend.length}-Week Trend
                      </div>
                      <div style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 22, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.02em" }}>
                        Permit volume
                      </div>
                    </div>
                    {trend.length > 0 && (
                      <div style={{ display: "flex", gap: 22, alignItems: "baseline" }}>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9, color: "#0A0A0A", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>Total</div>
                          <div style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 22, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.025em", lineHeight: 1 }}>
                            {nf(totalWindow)}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9, color: "#0A0A0A", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>Weekly Avg</div>
                          <div style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 22, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.025em", lineHeight: 1 }}>
                            {weeklyAvg}
                          </div>
                        </div>
                        {delta != null && (
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9, color: "#0A0A0A", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>vs Prior 4w</div>
                            <div style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 22, fontWeight: 900, color: delta >= 0 ? "#16A34A" : "#DC2626", letterSpacing: "-0.025em", lineHeight: 1 }}>
                              {delta >= 0 ? "+" : ""}{delta}%
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {trend.length === 0 ? (
                    <div style={{ padding: "40px 0", color: "#0A0A0A", fontSize: 14 }}>No recent activity in this market.</div>
                  ) : (() => {
                    const maxVal = Math.max(1, ...trend.map((w: any) => w.permit_count || 0));
                    const avgVal = totalWindow / trend.length;
                    const avgPct = (avgVal / maxVal) * 100;
                    const BAR_AREA_HEIGHT = 280;
                    const labelEvery = isMobile ? Math.max(2, Math.ceil(trend.length / 4)) : (trend.length > 16 ? 2 : 1);
                    return (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {/* Numbers above the bars (non-zero only) */}
                        <div style={{ display: "flex", gap: 6, height: 24, marginBottom: 10 }}>
                          {trend.map((w: any, i: number) => {
                            const isLast = i === trend.length - 1;
                            const isPeak = !!(spike && peakWeek && w.week_start === peakWeek.week_start);
                            return (
                              <div key={`n-${w.week_start}`} style={{
                                flex: 1, textAlign: "center",
                                fontFamily: "'Outfit', system-ui, sans-serif",
                                fontSize: 13, fontWeight: 900,
                                color: isPeak ? "#FF8800" : isLast ? "#FF8800" : "#0A0A0A",
                                letterSpacing: "-0.02em",
                              }}>
                                {(w.permit_count || 0) > 0 ? nf(w.permit_count) : ""}
                              </div>
                            );
                          })}
                        </div>
                        {/* Bar area */}
                        <div style={{
                          height: BAR_AREA_HEIGHT, position: "relative",
                          display: "flex", gap: 6, alignItems: "flex-end",
                          borderBottom: `2px solid #0A0A0A`,
                          padding: "8px 0 0",
                        }}>
                          {[0.25, 0.5, 0.75].map(p => (
                            <div key={`grid-${p}`} style={{
                              position: "absolute", left: 0, right: 0,
                              bottom: `${p * 100}%`, height: 1,
                              background: T.border, opacity: 0.6, pointerEvents: "none",
                            }} />
                          ))}
                          {avgVal > 0 && (
                            <div style={{
                              position: "absolute", left: 0, right: 0,
                              bottom: `${avgPct}%`, height: 0,
                              borderTop: `1.5px dashed #FF8800`,
                              pointerEvents: "none", zIndex: 2,
                            }}>
                              <div style={{
                                position: "absolute", right: 0, top: -10,
                                background: T.bg, padding: "0 6px",
                                fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                                fontSize: 9, fontWeight: 800, color: "#FF8800",
                                letterSpacing: "0.12em",
                              }}>
                                AVG {Math.round(avgVal)}
                              </div>
                            </div>
                          )}
                          {trend.map((w: any, i: number) => {
                            const pct = ((w.permit_count || 0) / maxVal) * 100;
                            const isLast = i === trend.length - 1;
                            const isPeak = !!(spike && peakWeek && w.week_start === peakWeek.week_start);
                            const hasVal = (w.permit_count || 0) > 0;
                            return (
                              <div
                                key={`bar-${w.week_start}`}
                                title={`${new Date(w.week_start).toLocaleDateString("en-US", { month: "long", day: "numeric" })} · ${nf(w.permit_count)} permits · ${w.distinct_contractors || 0} contractors`}
                                style={{
                                  flex: 1,
                                  height: hasVal ? `${Math.max(pct, 2)}%` : 3,
                                  minHeight: hasVal ? 6 : 3,
                                  background: !hasVal ? T.bgSub : (isPeak || isLast) ? "#FF8800" : "#0A0A0A",
                                  position: "relative",
                                  transition: "transform 150ms ease, filter 150ms ease",
                                  cursor: "default",
                                }}
                                onMouseEnter={e => { if (hasVal) { e.currentTarget.style.filter = "brightness(1.15)"; e.currentTarget.style.transform = "scaleY(1.02)"; e.currentTarget.style.transformOrigin = "bottom"; } }}
                                onMouseLeave={e => { e.currentTarget.style.filter = ""; e.currentTarget.style.transform = ""; }}
                              />
                            );
                          })}
                        </div>
                        {/* Dates below the bars */}
                        <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                          {trend.map((w: any, i: number) => {
                            const isLast = i === trend.length - 1;
                            const show = isLast || i % labelEvery === 0;
                            return (
                              <div key={`d-${w.week_start}`} style={{
                                flex: 1, minWidth: 0, textAlign: "center",
                                fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                                fontSize: 9.5, fontWeight: 700,
                                color: isLast ? "#FF8800" : "#0A0A0A",
                                letterSpacing: "0.04em",
                                whiteSpace: "nowrap",
                              }}>
                                {show ? new Date(w.week_start).toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase() : ""}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* UNUSUAL ACTIVITY */}
                {flags.length > 0 && (
                  <div style={{ background: T.bg, border: `1px solid ${T.border}`, marginBottom: 40, padding: "22px 28px 24px" }}>
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10.5, color: "#FF8800", fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 4 }}>
                      Signals
                    </div>
                    <div style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 22, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.02em", marginBottom: 16 }}>
                      Unusual activity
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {flags.map((f: any, i: number) => (
                        <div key={i} style={{ display: "flex", gap: 12, padding: "12px 14px", border: `1px solid ${T.border}`, borderRadius: 10, borderLeft: `3px solid ${f.dot}`, background: T.bg }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10.5, fontWeight: 800, color: f.dot, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{f.title}</div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: "#0A0A0A", lineHeight: 1.45 }}>{f.body}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* TOP OPPORTUNITIES */}
                <div style={{ background: T.bg, border: `1px solid ${T.border}`, marginBottom: 40 }}>
                  <div style={{ padding: "22px 28px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10.5, color: "#FF8800", fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 4 }}>
                        Top Opportunities
                      </div>
                      <div style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 22, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.02em" }}>
                        Hot permits in {CITY}
                      </div>
                    </div>
                    {marketHotLeads.length > 0 && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10.5, color: "#0A0A0A", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>
                        <span style={{ width: 6, height: 6, borderRadius: 999, background: "#FF8800" }} />
                        {marketHotLeads.filter((l: any) => (l._smartScore ?? 0) >= SCORE_HOT).length} scored 8+
                      </div>
                    )}
                  </div>
                  {marketHotLeadsLoading ? (
                    <div style={{ padding: "32px 28px", color: "#0A0A0A", fontSize: 14 }}>Loading top opportunities…</div>
                  ) : marketHotLeads.length === 0 ? (
                    <div style={{ padding: "32px 28px", color: "#0A0A0A", fontSize: 14, lineHeight: 1.55 }}>
                      No hot opportunities loaded yet for this market. Use "View all permits" below to browse the full feed.
                    </div>
                  ) : (
                    <div>
                      {marketHotLeads.map((lead: any) => (
                        <LeadCard
                          key={lead.id}
                          lead={lead}
                          callCount={(callLog[lead.id] || []).length}
                          onClick={() => { setModalLead(lead); setModalTab("overview"); }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* TOP CONTRACTORS */}
                <div style={{ background: T.bg, border: `1px solid ${T.border}` }}>
                  <div style={{ padding: "22px 28px 18px", borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10.5, color: "#FF8800", fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 4 }}>
                      Top Contractors Here
                    </div>
                    <div style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 22, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.02em" }}>
                      Who is installing in {CITY}
                    </div>
                  </div>
                  {contractors.length === 0 ? (
                    <div style={{ padding: "32px 28px", color: "#0A0A0A", fontSize: 14, lineHeight: 1.6 }}>
                      Permit records in this market do not include installer names, common for jurisdictions that pull data from incentive programs (NYSERDA, CSI) rather than building-department permit logs. Contractor intelligence here is currently limited.
                    </div>
                  ) : (
                    <div>
                      {contractors.map((c: any, i: number) => (
                        <button
                          key={c.name}
                          onClick={() => { setSelectedContractor(c.name); setView("competitors"); }}
                          style={{
                            display: isMobile ? "flex" : "grid",
                            flexDirection: "column",
                            gridTemplateColumns: "32px 1fr auto auto auto auto",
                            alignItems: isMobile ? "stretch" : "center",
                            gap: isMobile ? 10 : 16,
                            width: "100%", textAlign: "left",
                            background: T.bg, border: "none",
                            padding: isMobile ? "14px 18px" : "16px 28px",
                            borderTop: i > 0 ? `1px solid ${T.border}` : "none",
                            cursor: "pointer",
                            transition: "background 120ms ease",
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = T.bgOff)}
                          onMouseLeave={e => (e.currentTarget.style.background = T.bg)}
                        >
                          {/* Rank + name */}
                          <div style={{ display: isMobile ? "flex" : "contents", flexDirection: "row", alignItems: "center", gap: 10, minWidth: 0 }}>
                            <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 12, color: "#0A0A0A", fontWeight: 800, flexShrink: 0 }}>
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <span style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 16, fontWeight: 800, color: "#0A0A0A", letterSpacing: "-0.015em", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {c.name}
                              {c.is_new_in_city && (
                                <span style={{ marginLeft: 10, fontSize: 9, fontWeight: 800, letterSpacing: "0.12em", color: "#FF8800", background: T.primaryLight, padding: "2px 7px", borderRadius: 3 }}>
                                  NEW
                                </span>
                              )}
                            </span>
                          </div>
                          {/* Stats */}
                          <div style={{ display: isMobile ? "flex" : "contents", flexDirection: "row", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                            <span style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 16, fontWeight: 800, color: "#0A0A0A", textAlign: isMobile ? "left" : "right", minWidth: isMobile ? "auto" : 80 }}>
                              {nf(c.permits)}
                            </span>
                            {(() => {
                              const cur = c.last_30d || 0;
                              const prior = c.prior_30d || 0;
                              const isGrowing = cur > prior;
                              const isSteady = cur === prior;
                              const color = isGrowing ? "#16A34A" : isSteady ? "#0A0A0A" : "#DC2626";
                              const label = isGrowing ? `↑ ${cur} filed` : isSteady ? `→ ${cur} filed` : `↓ ${cur} filed`;
                              return (
                                <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12.5, color, fontWeight: 700, minWidth: isMobile ? "auto" : 100, textAlign: isMobile ? "left" : "right", letterSpacing: "-0.005em" }}>
                                  {label}
                                  <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#0A0A0A", fontWeight: 700, marginLeft: 6, letterSpacing: "0.08em" }}>30D</span>
                                </span>
                              );
                            })()}
                            <span style={{ minWidth: isMobile ? "auto" : 100, textAlign: "right" }}>
                              <StatusBadge status={c.global_status || "dormant"} />
                            </span>
                            <span style={{ color: "#0A0A0A", fontSize: 18, display: isMobile ? "none" : "inline" }}>→</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* VIEW ALL PERMITS CTA */}
                <div style={{ marginTop: 32, paddingTop: 24, borderTop: `1.5px solid ${T.border}`, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                  <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10.5, color: "#0A0A0A", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase" }}>
                    Ready to work this market?
                  </div>
                  <button
                    onClick={() => {
                      setOnbCity(CITY);
                      setOnbRegion(H.province);
                      setSelectedMarket(null);
                      setMarketDetail(null);
                      setView("explore");
                    }}
                    style={{
                      padding: "16px 32px",
                      background: "#0A0A0A", color: "#fff", border: "none",
                      fontFamily: "'Outfit', system-ui, sans-serif",
                      fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em",
                      cursor: "pointer", transition: "background 120ms ease",
                      display: "inline-flex", alignItems: "center", gap: 10,
                    }}
                    onMouseEnter={(e: any) => (e.currentTarget.style.background = "#FF8800")}
                    onMouseLeave={(e: any) => (e.currentTarget.style.background = "#0A0A0A")}
                  >
                    View all {nf(H.total_leads)} permits in {CITY}
                    <span style={{ fontSize: 17 }}>→</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }

    // ─── LANDING MODE - grid of all markets ─────────────────────────────
    // Source: marketsList from /api/markets/list, which pulls from city_stats
    // (the nightly-refreshed source of truth). Each row already has total_leads,
    // new_this_month, growth_pct, country, province, city - no extra joins needed.
    //
    // Display-time market identity rollup. The underlying city_stats data is
    // jurisdictionally faithful - Queens, Brooklyn, Manhattan etc. are separate
    // rows because they're separate permit-issuing authorities at the source. A
    // rep doesn't think that way ("I want to farm Queens"); they think "I want
    // to farm NYC." Same for "Los Angeles County" vs "Los Angeles." So we fold
    // sub-jurisdictions into their parent market for display.
    //
    // TODO post-Stripe: server-side rollup on the detail endpoint
    // (/api/markets/:province/:city) so clicking "New York" surfaces all 5
    // boroughs combined. Today, detail click returns only the primary city's
    // data - totals will look smaller on the detail page than on the grid card.
    const MARKET_ALIASES: Record<string, string> = {
      "Queens": "New York",
      "Brooklyn": "New York",
      "Bronx": "New York",
      "Staten Island": "New York",
      "Manhattan": "New York",
      "Los Angeles County": "Los Angeles",
    };

    const rollupMarkets = (rows: any[]) => {
      const groups = new Map<string, any>();
      for (const r of rows) {
        const displayCity = MARKET_ALIASES[r.city] || r.city;
        const key = `${displayCity}|${r.province}`;
        const existing = groups.get(key);
        if (!existing) {
          groups.set(key, { ...r, city: displayCity, _sourceCount: 1, _sourceCities: [r.city] });
        } else {
          // Weighted-average growth_pct BEFORE mutating new_this_month
          const wExisting = existing._weightSum || existing.new_this_month || 1;
          const wNew = r.new_this_month || 1;
          const totalW = wExisting + wNew;
          if (totalW > 0) {
            existing.growth_pct = ((Number(existing.growth_pct) || 0) * wExisting + (Number(r.growth_pct) || 0) * wNew) / totalW;
            existing._weightSum = totalW;
          }
          existing.total_leads     = (existing.total_leads || 0)     + (r.total_leads || 0);
          existing.new_this_month  = (existing.new_this_month || 0)  + (r.new_this_month || 0);
          existing.new_this_week   = (existing.new_this_week || 0)   + (r.new_this_week || 0);
          existing._sourceCount   += 1;
          existing._sourceCities.push(r.city);
        }
      }
      return Array.from(groups.values()).map(m => ({
        ...m,
        growth_pct: typeof m.growth_pct === "number" ? Math.round(m.growth_pct * 10) / 10 : m.growth_pct,
      }));
    };

    const searchLower = marketsSearch.trim().toLowerCase();
    const filtered = rollupMarkets(
      (marketsList || []).filter(m => marketsCountry === "ALL" || m.country === marketsCountry)
    )
      .filter(m => !searchLower ||
        (m.city || "").toLowerCase().includes(searchLower) ||
        (m.province || "").toLowerCase().includes(searchLower))
      .sort((a, b) => {
        if (marketsSort === "month")  return (b.new_this_month || 0) - (a.new_this_month || 0);
        if (marketsSort === "growth") return (Number(b.growth_pct) || 0) - (Number(a.growth_pct) || 0);
        return (b.total_leads || 0) - (a.total_leads || 0);
      })
      .slice(0, 120);

    // Active/quiet split. Stale markets (low recent activity) generate noisy
    // growth_pct that erodes trust - "-95%" on a market with 1 fresh permit
    // looks like a crash but is statistical noise on a thin sample. Relegate
    // them to a compact pill row at the bottom so they're findable but don't
    // compete with action-worthy markets for screen real estate.
    //
    // Threshold of 10 new_this_month = at least 1 fresh permit every 3 days,
    // enough signal for a rep to plan door-knocking. Below that = noise.
    const QUIET_THRESHOLD = 10;
    const LARGE_FLOOR = 10000; // mega + large markets (10K+ permits) are never "quiet" regardless of recent activity
    const hasActiveSearch = !!searchLower;
    const activeMarkets = hasActiveSearch
      ? filtered
      : filtered.filter(m => (m.new_this_month || 0) >= QUIET_THRESHOLD || (m.total_leads || 0) >= LARGE_FLOOR);
    const quietMarkets = hasActiveSearch
      ? []
      : filtered.filter(m => (m.new_this_month || 0) < QUIET_THRESHOLD && (m.total_leads || 0) < LARGE_FLOOR);

    // ─── PHASE 5C: Tiered rendering with stat strip + sparklines ─────────
    // Stats computed from country-filtered + rolled-up list (pre-search/sort)
    // so the headline numbers reflect the region toggle but not in-progress search.
    const allRolledUp = rollupMarkets(
      (marketsList || []).filter(m => marketsCountry === "ALL" || m.country === marketsCountry)
    );
    const totalMarketsCount = allRolledUp.length;
    const totalPermitsCount = allRolledUp.reduce((s: number, m: any) => s + (m.total_leads || 0), 0);
    const totalAddedThisMonth = allRolledUp.reduce((s: number, m: any) => s + (m.new_this_month || 0), 0);
    const megaMarketsCount = allRolledUp.filter((m: any) => (m.total_leads || 0) >= 50000).length;
    const displayCitiesCount  = marketsPlatformTotals?.citiesCovered  || totalMarketsCount;
    const displayPermitsCount = marketsPlatformTotals?.totalPermits   || totalPermitsCount;
    const displayAddedMonth   = marketsPlatformTotals?.addedThisMonth || totalAddedThisMonth;

    // Tier the active markets by volume
    const megaMarkets   = activeMarkets.filter((m: any) => (m.total_leads || 0) >= 50000);
    const largeMarkets  = activeMarkets.filter((m: any) => (m.total_leads || 0) >= 10000 && (m.total_leads || 0) < 50000);
    const midMarkets    = activeMarkets.filter((m: any) => (m.total_leads || 0) < 10000);

    const MID_CAP = 16;
    const visibleMidMarkets = marketsShowAllMid ? midMarkets : midMarkets.slice(0, MID_CAP);

    // Slope-derived sparkline endpoint y-coordinate (lower = higher visual peak).
    // Growth >= 30%: steep climb. Growth 0-5%: gentle. <= 0: flat (per no-decrease rule).
    const sparkEndY = (gp: any, h: number) => {
      const g = Number(gp) || 0;
      if (g >= 30) return h * 0.07;
      if (g >= 15) return h * 0.18;
      if (g >= 5)  return h * 0.34;
      if (g >  0)  return h * 0.55;
      return h * 0.80; // flat - no downward slopes on data
    };

    // Build an SVG path through 6 intermediate points (smooth curve illusion)
    const sparkPath = (gp: any, w: number, h: number) => {
      const startY = h * 0.85;
      const endY = sparkEndY(gp, h);
      const dy = startY - endY;
      const pts = [
        [0,           startY],
        [w * 0.20,    startY - dy * 0.12],
        [w * 0.40,    startY - dy * 0.32],
        [w * 0.60,    startY - dy * 0.55],
        [w * 0.80,    startY - dy * 0.78],
        [w,           endY],
      ];
      return pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
    };

    const fmtGrowthChip = (newThisMonth: number) => {
      if (newThisMonth > 30) return { txt: `↑ ${newThisMonth.toLocaleString()} added this month`, color: "#15803D" };
      if (newThisMonth > 0)  return { txt: `↑ ${newThisMonth.toLocaleString()} this month`,       color: "#15803D" };
      return { txt: `→ steady this month`, color: "#262B37" };
    };
    const fmtGrowthMid = (newThisMonth: number) => {
      if (newThisMonth > 30) return { txt: `↑ ${newThisMonth.toLocaleString()}`, color: "#15803D" };
      if (newThisMonth > 0)  return { txt: `↑ ${newThisMonth.toLocaleString()}`, color: "#15803D" };
      return { txt: `→ ${newThisMonth.toLocaleString()}`, color: "#262B37" };
    };

    const isLive = !marketsListLoading && (marketsList || []).length > 0;

    return (
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", background: "#fff" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "22px 32px 60px" }}>
          {/* ── HEADER ────────────────────────────────────────────────── */}
          <h1 style={{
            fontFamily: "'Outfit', system-ui, sans-serif",
            fontSize: "clamp(34px, 6vw, 68px)", fontWeight: 900, lineHeight: 0.96,
            letterSpacing: "-0.045em", color: T.text, margin: 0, marginBottom: 12,
          }}>
            Where the market is <span style={{ color: T.primary }}>moving.</span>
          </h1>
          <div style={{ fontSize: 15.5, color: T.textSub, fontWeight: 500, maxWidth: 720, lineHeight: 1.5, marginBottom: 22 }}>
            <strong style={{ color: T.text, fontWeight: 800 }}>{displayCitiesCount.toLocaleString()} cities tracked</strong> - ranked by what's happening now. Click any market to see its trend, who's installing there, and what's next.
          </div>

          {/* ── STAT STRIP ───────────────────────────────────────────── */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 1, background: "#EAEAE2",
            border: "1px solid #EAEAE2",
            marginBottom: 22,
          }}>
            <div style={{ background: "#fff", padding: "13px 18px", display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase" }}>Markets tracked</div>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 26, fontWeight: 900, color: T.text, letterSpacing: "-0.03em", lineHeight: 1 }}>
                  {displayCitiesCount.toLocaleString()}
                </div>
                <svg width="60" height="22" viewBox="0 0 60 22"><path d="M 2 18 L 12 16 L 22 14 L 32 11 L 42 8 L 58 4" fill="none" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </div>
              <div style={{ fontSize: 11.5, color: "#262B37", fontWeight: 500 }}>{marketsCountry === "ALL" ? "USA + Canada" : marketsCountry === "US" ? "United States" : "Canada"}</div>
            </div>

            <div style={{ background: "#fff", padding: "13px 18px", display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase" }}>Total permits</div>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 26, fontWeight: 900, color: T.text, letterSpacing: "-0.03em", lineHeight: 1 }}>
                  {displayPermitsCount.toLocaleString()}
                </div>
                <svg width="60" height="22" viewBox="0 0 60 22"><path d="M 2 18 L 12 15 L 22 12 L 32 9 L 42 5 L 58 2" fill="none" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </div>
              <div style={{ fontSize: 11.5, color: "#262B37", fontWeight: 500 }}>Across every market</div>
            </div>

            <div style={{ background: "#fff", padding: "13px 18px", display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase" }}>Added this month</div>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 26, fontWeight: 900, color: "#FF8800", letterSpacing: "-0.03em", lineHeight: 1 }}>
                  {displayAddedMonth.toLocaleString()}
                </div>
                <svg width="60" height="22" viewBox="0 0 60 22"><path d="M 2 18 L 12 17 L 22 14 L 32 11 L 42 7 L 58 3" fill="none" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </div>
              <div style={{ fontSize: 11.5, color: "#262B37", fontWeight: 500 }}>Permits added across all cities</div>
            </div>

            <div style={{ background: "#fff", padding: "13px 18px", display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase" }}>Mega markets</div>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 26, fontWeight: 900, color: T.text, letterSpacing: "-0.03em", lineHeight: 1 }}>
                  {megaMarketsCount.toLocaleString()}
                </div>
                <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#262B37", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>50K+ permits</span>
              </div>
              <div style={{ fontSize: 11.5, color: "#262B37", fontWeight: 500 }}>Top-tier cities</div>
            </div>
          </div>

          {/* ── FILTER BAR ───────────────────────────────────────────── */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 14, borderBottom: `1px solid ${T.border}`, marginBottom: 22, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase", marginRight: 4 }}>Region</span>
              {(["ALL", "US", "CA"] as const).map(c => {
                const isActive = marketsCountry === c;
                return (
                  <button key={c} onClick={() => setMarketsCountry(c)}
                    style={{
                      padding: "7px 14px", borderRadius: 999,
                      fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: isActive ? 800 : 700,
                      border: `1.5px solid ${isActive ? T.text : T.border}`,
                      background: isActive ? T.text : "#fff",
                      color: isActive ? "#fff" : T.text,
                      cursor: "pointer", transition: "all 120ms", letterSpacing: "-0.005em",
                    }}
                    onMouseEnter={(e: any) => { if (!isActive) { e.currentTarget.style.background = "#FAFAF6"; e.currentTarget.style.borderColor = T.text; } }}
                    onMouseLeave={(e: any) => { if (!isActive) { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = T.border; } }}
                  >
                    {c === "ALL" ? "All" : c === "US" ? "USA" : "Canada"}
                  </button>
                );
              })}
            </div>
            <div style={{ width: 1, height: 22, background: T.border, flexShrink: 0 }} />
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase", marginRight: 4 }}>Sort</span>
              {(["total", "month", "growth"] as const).map(s => {
                const isActive = marketsSort === s;
                const label = s === "total" ? "Total" : s === "month" ? "Most active" : "Fastest growing";
                return (
                  <button key={s} onClick={() => setMarketsSort(s)}
                    style={{
                      padding: "7px 14px", borderRadius: 999,
                      fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: isActive ? 800 : 700,
                      border: `1.5px solid ${isActive ? T.text : T.border}`,
                      background: isActive ? T.text : "#fff",
                      color: isActive ? "#fff" : T.text,
                      cursor: "pointer", transition: "all 120ms", letterSpacing: "-0.005em",
                    }}
                    onMouseEnter={(e: any) => { if (!isActive) { e.currentTarget.style.background = "#FAFAF6"; e.currentTarget.style.borderColor = T.text; } }}
                    onMouseLeave={(e: any) => { if (!isActive) { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = T.border; } }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, background: "#FAFAF6", border: `1.5px solid ${T.border}`, borderRadius: 8, padding: "0 14px", height: 38, minWidth: 220, flex: "0 1 280px" }}>
              <Search size={14} color="#262B37" />
              <input value={marketsSearch} onChange={(e: any) => setMarketsSearch(e.target.value)}
                placeholder="Search city or state…"
                style={{ flex: 1, border: "none", background: "transparent", fontSize: 13, color: T.text, outline: "none", fontWeight: 500 }} />
              {marketsSearch && <button onClick={() => setMarketsSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}><X size={13} color="#262B37" /></button>}
            </div>
          </div>

          {/* ── LOADING / EMPTY STATES ───────────────────────────────── */}
          {marketsListLoading && (marketsList || []).length === 0 ? (
            <div style={{ padding: "60px 0", color: T.textSub, fontSize: 14, textAlign: "center" }}>
              Loading markets…
            </div>
          ) : activeMarkets.length === 0 && quietMarkets.length === 0 ? (
            <div style={{ padding: "80px 0", textAlign: "center" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 6, letterSpacing: "-0.015em" }}>No markets match those filters</div>
              <div style={{ fontSize: 13.5, color: T.textSub }}>Try a broader search or change the region.</div>
            </div>
          ) : (
            <>
              {/* ── MEGA TIER ───────────────────────────────────────── */}
              {megaMarkets.length > 0 && (
                <>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "8px 0 12px", paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "#262B37", fontWeight: 800, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#FF8800" }} />
                      Mega markets · 50K+ permits
                    </div>
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#262B37", fontWeight: 600, letterSpacing: "0.1em" }}>
                      {megaMarkets.length} market{megaMarkets.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 8 }}>
                    {megaMarkets.map(m => {
                      const chip = fmtGrowthChip(m.new_this_month || 0);
                      return (
                        <button key={`mega-${m.province}|${m.city}`}
                          onClick={() => setSelectedMarket({ city: m.city, province: m.province })}
                          style={{
                            background: "#fff", border: `1.5px solid ${T.border}`, borderLeft: "3px solid #FF8800",
                            padding: "26px 30px 22px", cursor: "pointer", textAlign: "left",
                            transition: "all 150ms", display: "flex", flexDirection: "column", minHeight: 200,
                          }}
                          onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.text; e.currentTarget.style.borderLeftColor = "#FF8800"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.06)"; }}
                          onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.borderLeftColor = "#FF8800"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                          <div>
                            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 42, fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.95, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.city}</div>
                            <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, letterSpacing: "0.18em", color: "#262B37", fontWeight: 700, textTransform: "uppercase", marginTop: 6 }}>
                              {m.province} · {m.country === "CA" ? "Canada" : "USA"}{(m._sourceCount && m._sourceCount > 1) ? ` · incl. ${m._sourceCount} jurisdictions` : ""}
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 20, marginTop: 22, flex: 1 }}>
                            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 52, fontWeight: 900, color: T.text, letterSpacing: "-0.045em", lineHeight: 0.92 }}>
                              {(m.total_leads || 0).toLocaleString()}
                            </div>
                            <svg width="200" height="52" viewBox="0 0 200 52" style={{ flexShrink: 0 }}>
                              <defs>
                                <linearGradient id={`mega-g-${m.city.replace(/\s+/g,'')}-${m.province}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#16A34A" stopOpacity="0.18" />
                                  <stop offset="100%" stopColor="#16A34A" stopOpacity="0" />
                                </linearGradient>
                              </defs>
                              <path d={`${sparkPath(m.growth_pct, 200, 52)} L 200 52 L 0 52 Z`} fill={`url(#mega-g-${m.city.replace(/\s+/g,'')}-${m.province})`} />
                              <path d={sparkPath(m.growth_pct, 200, 52)} fill="none" stroke="#16A34A" strokeWidth="1.75" strokeLinecap="round" />
                            </svg>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 16, marginTop: 16, borderTop: `1px solid ${T.border}` }}>
                            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13.5, fontWeight: 800, color: chip.color, letterSpacing: "-0.005em" }}>{chip.txt}</span>
                            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, fontWeight: 800, color: T.text, letterSpacing: "0.04em", textTransform: "uppercase", opacity: 0.7 }}>View market →</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* ── LARGE TIER ──────────────────────────────────────── */}
              {largeMarkets.length > 0 && (
                <>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "24px 0 12px", paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "#262B37", fontWeight: 800, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#F59E0B" }} />
                      Large markets · 10K-50K permits
                    </div>
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#262B37", fontWeight: 600, letterSpacing: "0.1em" }}>
                      {largeMarkets.length} markets
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12, marginBottom: 8 }}>
                    {largeMarkets.map(m => {
                      const chip = fmtGrowthChip(m.new_this_month || 0);
                      return (
                        <button key={`large-${m.province}|${m.city}`}
                          onClick={() => setSelectedMarket({ city: m.city, province: m.province })}
                          style={{
                            background: "#fff", border: `1.5px solid ${T.border}`, borderLeft: "2px solid #F59E0B",
                            padding: "18px 20px 16px", cursor: "pointer", textAlign: "left",
                            transition: "all 150ms", display: "flex", flexDirection: "column", minHeight: 165,
                          }}
                          onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.text; e.currentTarget.style.borderLeftColor = "#F59E0B"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.05)"; }}
                          onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.borderLeftColor = "#F59E0B"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                          <div>
                            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 23, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.city}</div>
                            <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase", marginTop: 5 }}>
                              {m.province} · {m.country === "CA" ? "Canada" : "USA"}{(m._sourceCount && m._sourceCount > 1) ? ` · ${m._sourceCount} jur.` : ""}
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 8, marginTop: 14, flex: 1 }}>
                            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 30, fontWeight: 900, color: T.text, letterSpacing: "-0.035em", lineHeight: 0.95 }}>
                              {(m.total_leads || 0).toLocaleString()}
                            </div>
                            <svg width="80" height="30" viewBox="0 0 80 30" style={{ flexShrink: 0 }}>
                              <path d={sparkPath(m.growth_pct, 80, 30)} fill="none" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 10, marginTop: 10, borderTop: `1px solid ${T.border}` }}>
                            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11.5, fontWeight: 800, color: chip.color, letterSpacing: "-0.005em" }}>{chip.txt}</span>
                            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: T.textSub }}>→</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* ── MID TIER ────────────────────────────────────────── */}
              {midMarkets.length > 0 && (
                <>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "24px 0 12px", paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "#262B37", fontWeight: 800, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#94A3B8" }} />
                      Mid markets · &lt;10K permits
                    </div>
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#262B37", fontWeight: 600, letterSpacing: "0.1em" }}>
                      {midMarkets.length} markets{midMarkets.length > MID_CAP && !marketsShowAllMid ? ` · showing ${MID_CAP}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8, marginBottom: 8 }}>
                    {visibleMidMarkets.map(m => {
                      const chip = fmtGrowthMid(m.new_this_month || 0);
                      return (
                        <button key={`mid-${m.province}|${m.city}`}
                          onClick={() => setSelectedMarket({ city: m.city, province: m.province })}
                          style={{
                            background: "#fff", border: `1px solid ${T.border}`,
                            padding: "13px 16px 11px", cursor: "pointer", textAlign: "left",
                            transition: "border-color 120ms",
                          }}
                          onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.text; }}
                          onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; }}>
                          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 17, fontWeight: 800, letterSpacing: "-0.02em", color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{m.city}</div>
                            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 19, fontWeight: 900, color: T.text, letterSpacing: "-0.025em", flexShrink: 0 }}>{(m.total_leads || 0).toLocaleString()}</div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 3 }}>
                            <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.14em", color: "#262B37", fontWeight: 700, textTransform: "uppercase" }}>
                              {m.province} · {m.country === "CA" ? "Canada" : "USA"}
                            </div>
                            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 700, color: chip.color }}>{chip.txt}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {midMarkets.length > MID_CAP && (
                    <div style={{ textAlign: "center", marginTop: 16, marginBottom: 8 }}>
                      <button onClick={() => setMarketsShowAllMid(!marketsShowAllMid)}
                        style={{
                          padding: "10px 22px", borderRadius: 8,
                          background: "#fff", border: `1.5px solid ${T.text}`,
                          fontFamily: "'Outfit', sans-serif", fontSize: 12.5, fontWeight: 800,
                          color: T.text, cursor: "pointer", letterSpacing: "-0.005em", transition: "all 120ms",
                        }}
                        onMouseEnter={(e: any) => { e.currentTarget.style.background = T.text; e.currentTarget.style.color = "#fff"; }}
                        onMouseLeave={(e: any) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = T.text; }}
                      >
                        {marketsShowAllMid ? `Show first ${MID_CAP} only` : `Show all ${midMarkets.length} mid markets →`}
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* ── QUIET MARKETS - preserved pill row ──────────────── */}
              {quietMarkets.length > 0 && (
                <div style={{ marginTop: 32 }}>
                  {/* Tier label - matches mega/large/mid header style */}
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "0 0 12px", paddingTop: 22, paddingBottom: 8, borderTop: `1.5px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "#262B37", fontWeight: 800, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#64748B" }} />
                      Quieter markets · &lt;10 new this month
                    </div>
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#262B37", fontWeight: 600, letterSpacing: "0.1em" }}>{quietMarkets.length} markets</div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(marketsShowAllQuiet ? quietMarkets : quietMarkets.slice(0, 60)).map(m => (
                      <button key={`quiet-${m.province}|${m.city}`}
                        onClick={() => setSelectedMarket({ city: m.city, province: m.province })}
                        style={{
                          padding: "6px 12px", background: "#FAFAF6", border: `1px solid ${T.border}`,
                          borderRadius: 999, fontFamily: "'Outfit', sans-serif", fontSize: 12, fontWeight: 600,
                          color: "#262B37", cursor: "pointer", transition: "all 120ms",
                          display: "inline-flex", alignItems: "center", gap: 6,
                        }}
                        onMouseEnter={(e: any) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = T.text; e.currentTarget.style.borderColor = T.text; }}
                        onMouseLeave={(e: any) => { e.currentTarget.style.background = "#FAFAF6"; e.currentTarget.style.color = "rgba(10,10,10,0.6)"; e.currentTarget.style.borderColor = T.border; }}
                      >
                        <span>{m.city}</span>
                        <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#262B37", fontWeight: 700, letterSpacing: "0.05em" }}>{m.province}</span>
                      </button>
                    ))}
                    {quietMarkets.length > 60 && (
                      <button onClick={() => setMarketsShowAllQuiet(!marketsShowAllQuiet)}
                        style={{
                          padding: "6px 14px", background: "#fff", border: `1.5px solid ${T.text}`,
                          borderRadius: 999, fontFamily: "'Outfit', sans-serif", fontSize: 12, fontWeight: 800,
                          color: T.text, cursor: "pointer", transition: "all 120ms",
                          letterSpacing: "-0.005em",
                        }}
                        onMouseEnter={(e: any) => { e.currentTarget.style.background = T.text; e.currentTarget.style.color = "#fff"; }}
                        onMouseLeave={(e: any) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = T.text; }}
                      >
                        {marketsShowAllQuiet ? "Show less" : `+${(quietMarkets.length - 60).toLocaleString()} more →`}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ║  CONTRACTORS v2 - searchable directory + movement feed                ║
  // ║                                                                       ║
  // ║  view === "roofers" renders this. Search-first UX. Country toggle,    ║
  // ║  status pills, sort dropdown. Movement feed pinned at top.            ║
  // ║                                                                       ║
  // ║  Click a row → setSelectedContractor + setView("competitors") for     ║
  // ║  the existing detail page (which was wired up tonight).               ║
  // ═════════════════════════════════════════════════════════════════════════
  const ContractorsView = () => {
    // ─── Status color/label config (Phase 5a colors + Phase 5e labels) ──
    const STATUS_COLORS: any = {
      growing:   { bg: "#DCFCE7", fg: "#15803D", dot: "#16A34A", label: "Growing"     },
      stable:    { bg: "#DBEAFE", fg: "#1D4ED8", dot: "#3B82F6", label: "Stable"      },
      shrinking: { bg: "#FEF2F2", fg: "#DC2626", dot: "#DC2626", label: "Slipping"    },
      dormant:   { bg: "#F1F5F9", fg: "#64748B", dot: "#94A3B8", label: "Dormant"     },
      new:       { bg: "#FFEDD5", fg: "#C2410C", dot: "#F97316", label: "New entrant" },
    };

    const StatusBadge = ({ status }: { status: string }) => {
      const c = STATUS_COLORS[status] || STATUS_COLORS.dormant;
      return (
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: c.bg, color: c.fg, padding: "4px 10px",
          borderRadius: 6, fontSize: 10.5, fontWeight: 800,
          letterSpacing: "0.06em", textTransform: "uppercase",
          fontFamily: "'Outfit', sans-serif", whiteSpace: "nowrap",
        }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: c.dot }} />
          {c.label}
        </span>
      );
    };

    const rows = contractorsResults?.rows || [];
    const total = contractorsResults?.total || 0;
    const movement = (contractorsMovement || []).filter(
      (m: any) => (m.permits_in_city || 0) >= 2 || (m.contractor_total_permits || 0) >= 5,
    );

    // Stats - total is global, growing/slipping counts are computed from loaded rows
    const growingInView = rows.filter((r: any) => r.status === "growing").length;
    const slippingInView = rows.filter((r: any) => r.status === "shrinking").length;
    const newEntrantsCount = movement.length;
    const isLive = !contractorsLoading && rows.length > 0;

    return (
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", background: "#fff" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "22px 32px 60px" }}>
          {/* ── HEADER ──────────────────────────────────────────────────── */}
          <h1 style={{
            fontFamily: "'Outfit', system-ui, sans-serif",
            fontSize: "clamp(34px, 6vw, 68px)", fontWeight: 900, lineHeight: 0.96,
            letterSpacing: "-0.045em", color: T.text, margin: 0, marginBottom: 12,
          }}>
            Every installer. <span style={{ color: T.primary }}>Every market.</span>
          </h1>
          <div style={{ fontSize: 15.5, color: T.textSub, fontWeight: 500, maxWidth: 720, lineHeight: 1.5, marginBottom: 22 }}>
            <strong style={{ color: T.text, fontWeight: 800 }}>{total > 0 ? total.toLocaleString() : "-"} contractors tracked</strong> across Canada &amp; USA. See who's growing, who's leaving territory open, and who just entered your market.
          </div>

          {/* ── STAT STRIP ──────────────────────────────────────────────── */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 1, background: "#EAEAE2",
            border: "1px solid #EAEAE2",
            marginBottom: 22,
          }}>
            <div style={{ background: "#fff", padding: "13px 18px", display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase" }}>Total tracked</div>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 26, fontWeight: 900, color: T.text, letterSpacing: "-0.03em", lineHeight: 1 }}>
                  {total > 0 ? total.toLocaleString() : "-"}
                </div>
                <svg width="60" height="22" viewBox="0 0 60 22"><path d="M 2 18 L 12 16 L 22 14 L 32 11 L 42 8 L 58 4" fill="none" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </div>
              <div style={{ fontSize: 11.5, color: "#262B37", fontWeight: 500 }}>
                {contractorsCountry === "ALL" ? "USA + Canada" : contractorsCountry === "US" ? "United States" : "Canada"}
              </div>
            </div>

            <div style={{ background: "#fff", padding: "13px 18px", display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase" }}>New entrants 60d</div>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 26, fontWeight: 900, color: "#FF8800", letterSpacing: "-0.03em", lineHeight: 1 }}>
                  {newEntrantsCount}
                </div>
                <svg width="60" height="22" viewBox="0 0 60 22"><path d="M 2 18 L 18 18 L 30 14 L 42 8 L 58 4" fill="none" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </div>
              <div style={{ fontSize: 11.5, color: "#262B37", fontWeight: 500 }}>Just arrived in new cities</div>
            </div>

            <div style={{ background: "#fff", padding: "13px 18px", display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase" }}>Growing</div>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 26, fontWeight: 900, color: "#15803D", letterSpacing: "-0.03em", lineHeight: 1 }}>
                  {growingInView}
                </div>
                <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#262B37", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>in view</span>
              </div>
              <div style={{ fontSize: 11.5, color: "#262B37", fontWeight: 500 }}>Adding permits 30d</div>
            </div>

            <div style={{ background: "#fff", padding: "13px 18px", display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase" }}>Opportunities</div>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 26, fontWeight: 900, color: "#FF8800", letterSpacing: "-0.03em", lineHeight: 1 }}>
                  {slippingInView}
                </div>
                <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#262B37", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>in view</span>
              </div>
              <div style={{ fontSize: 11.5, color: "#262B37", fontWeight: 500 }}>Slipping · market share open</div>
            </div>
          </div>

          {/* ── FILTER BAR ──────────────────────────────────────────────── */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 14, borderBottom: `1px solid ${T.border}`, marginBottom: 24, flexWrap: "wrap" }}>
            {/* Search */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#FAFAF6", border: `1.5px solid ${T.border}`, borderRadius: 8, padding: "0 14px", height: 38, minWidth: 240, flex: "0 1 320px" }}>
              <Search size={14} color="#262B37" />
              <input
                value={contractorsSearch}
                onChange={(e: any) => setContractorsSearch(e.target.value)}
                placeholder='Search "SKYFIRE", "Tesla", "Northern PWR"…'
                style={{ flex: 1, border: "none", background: "transparent", fontSize: 13, color: T.text, outline: "none", fontWeight: 500 }}
              />
              {contractorsSearch && <button onClick={() => setContractorsSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}><X size={13} color="#262B37" /></button>}
            </div>

            <div style={{ width: 1, height: 22, background: T.border, flexShrink: 0 }} />

            {/* Region */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase", marginRight: 4 }}>Region</span>
              {(["ALL", "US", "CA"] as const).map(c => {
                const isActive = contractorsCountry === c;
                return (
                  <button key={c} onClick={() => setContractorsCountry(c)}
                    style={{
                      padding: "7px 14px", borderRadius: 999,
                      fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: isActive ? 800 : 700,
                      border: `1.5px solid ${isActive ? T.text : T.border}`,
                      background: isActive ? T.text : "#fff",
                      color: isActive ? "#fff" : T.text,
                      cursor: "pointer", transition: "all 120ms", letterSpacing: "-0.005em",
                    }}
                    onMouseEnter={(e: any) => { if (!isActive) { e.currentTarget.style.background = "#FAFAF6"; e.currentTarget.style.borderColor = T.text; } }}
                    onMouseLeave={(e: any) => { if (!isActive) { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = T.border; } }}
                  >
                    {c === "ALL" ? "All" : c === "US" ? "USA" : "Canada"}
                  </button>
                );
              })}
            </div>

            <div style={{ width: 1, height: 22, background: T.border, flexShrink: 0 }} />

            {/* Status */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase", marginRight: 4 }}>Status</span>
              {[
                { v: "",          label: "Any",        dot: T.textSub                  },
                { v: "growing",   label: "Growing",    dot: STATUS_COLORS.growing.dot  },
                { v: "shrinking", label: "Slipping",   dot: STATUS_COLORS.shrinking.dot},
                { v: "stable",    label: "Stable",     dot: STATUS_COLORS.stable.dot   },
                { v: "new",       label: "New (90d)",  dot: STATUS_COLORS.new.dot      },
                { v: "dormant",   label: "Dormant",    dot: STATUS_COLORS.dormant.dot  },
              ].map(s => {
                const isActive = contractorsStatus === s.v;
                return (
                  <button key={s.v || "any"} onClick={() => setContractorsStatus(s.v)}
                    style={{
                      padding: "7px 12px", borderRadius: 999,
                      fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: isActive ? 800 : 700,
                      border: `1.5px solid ${isActive ? T.text : T.border}`,
                      background: isActive ? T.text : "#fff",
                      color: isActive ? "#fff" : T.text,
                      cursor: "pointer", transition: "all 120ms", letterSpacing: "-0.005em",
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                    onMouseEnter={(e: any) => { if (!isActive) { e.currentTarget.style.background = "#FAFAF6"; e.currentTarget.style.borderColor = T.text; } }}
                    onMouseLeave={(e: any) => { if (!isActive) { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = T.border; } }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: isActive ? "#fff" : s.dot }} />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Second row: Sort pills */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 22, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", color: "#262B37", fontWeight: 700, textTransform: "uppercase", marginRight: 4 }}>Sort</span>
            {[
              { v: "permits", label: "Most permits"     },
              { v: "recent",  label: "Most recent"      },
              { v: "growth",  label: "Fastest growing"  },
              { v: "new",     label: "Newest entrants"  },
            ].map(s => {
              const isActive = contractorsSort === s.v;
              return (
                <button key={s.v} onClick={() => setContractorsSort(s.v as any)}
                  style={{
                    padding: "7px 14px", borderRadius: 999,
                    fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: isActive ? 800 : 700,
                    border: `1.5px solid ${isActive ? T.text : T.border}`,
                    background: isActive ? T.text : "#fff",
                    color: isActive ? "#fff" : T.text,
                    cursor: "pointer", transition: "all 120ms", letterSpacing: "-0.005em",
                  }}
                  onMouseEnter={(e: any) => { if (!isActive) { e.currentTarget.style.background = "#FAFAF6"; e.currentTarget.style.borderColor = T.text; } }}
                  onMouseLeave={(e: any) => { if (!isActive) { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = T.border; } }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* ── NEW MOVEMENT ────────────────────────────────────────────── */}
          {movement.length > 0 && !contractorsSearch && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "0 0 12px", paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>
                <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "#262B37", fontWeight: 800, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#F97316" }} />
                  New movement · last 60 days
                </div>
                <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#262B37", fontWeight: 600, letterSpacing: "0.1em" }}>
                  Showing 4 of {movement.length} · sort by "Newest entrants" to see all
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
                {movement.slice(0, 4).map((mv: any, i: number) => (
                  <button
                    key={i}
                    onClick={() => { setSelectedContractor(mv.contractor); setView("competitors"); }}
                    style={{
                      background: "#fff", border: `1px solid ${T.border}`, borderLeft: `2px solid #F97316`,
                      padding: "14px 16px 12px", textAlign: "left", cursor: "pointer", transition: "all 150ms",
                    }}
                    onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.text; e.currentTarget.style.borderLeftColor = "#F97316"; }}
                    onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.borderLeftColor = "#F97316"; }}
                  >
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9.5, color: T.primary, fontWeight: 800, letterSpacing: "0.18em", marginBottom: 6, textTransform: "uppercase" }}>
                      New in {mv.new_in_city}, {mv.new_in_province}
                    </div>
                    <div style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 15, fontWeight: 800, color: T.text, letterSpacing: "-0.015em", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {mv.contractor}
                    </div>
                    <div style={{ fontSize: 11.5, color: T.textSub, fontWeight: 500 }}>
                      First permit {mv.days_active_in_city}d ago · {mv.permits_in_city} so far
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── RESULTS LIST ────────────────────────────────────────────── */}
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "0 0 12px", paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>
            <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "#262B37", fontWeight: 800, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#0A0A0A" }} />
              {contractorsLoading ? "Loading…" : `${total.toLocaleString()} contractors`}
              {contractorsSearch && <span style={{ color: "#262B37", marginLeft: 8 }}>· "{contractorsSearch}"</span>}
            </div>
            <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#262B37", fontWeight: 600, letterSpacing: "0.1em" }}>
              Showing {rows.length}
            </div>
          </div>

          {contractorsLoading && rows.length === 0 ? (
            <div style={{ padding: "60px 0", color: T.textSub, fontSize: 14, textAlign: "center" }}>
              Loading contractors…
            </div>
          ) : rows.length === 0 ? (
            <div style={{ padding: "80px 0", textAlign: "center" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 6, letterSpacing: "-0.015em" }}>No contractors match those filters</div>
              <div style={{ fontSize: 13.5, color: T.textSub }}>Try a broader search or change the status filter.</div>
            </div>
          ) : (
            <div style={{ background: "#fff", border: `1px solid ${T.border}` }}>
              {rows.map((c: any, i: number) => {
                const cur = c.last_30d || 0;
                const prior = c.prior_30d || 0;
                const delta = cur - prior;
                const isGrowing = delta > 0;
                const isSteady = delta === 0;
                const deltaColor = isGrowing ? "#16A34A" : isSteady ? "#262B37" : "#DC2626";
                const deltaLabel = isGrowing ? `↑ ${cur} filed` : isSteady ? `→ ${cur} filed` : `${cur} filed`;
                return (
                  <button
                    key={c.name}
                    onClick={() => { setSelectedContractor(c.name); setView("competitors"); }}
                    style={{
                      display: isMobile ? "flex" : "grid",
                      flexDirection: "column",
                      gridTemplateColumns: "1fr auto auto auto auto",
                      alignItems: isMobile ? "stretch" : "center",
                      gap: isMobile ? 12 : 18,
                      width: "100%", textAlign: "left",
                      background: "#fff", border: "none",
                      padding: isMobile ? "16px 16px" : "18px 22px",
                      borderTop: i > 0 ? `1px solid ${T.border}` : "none",
                      cursor: "pointer", transition: "background 120ms",
                    }}
                    onMouseEnter={(e: any) => (e.currentTarget.style.background = "#FAFAF6")}
                    onMouseLeave={(e: any) => (e.currentTarget.style.background = "#fff")}
                  >
                    {/* Name + location */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 18, fontWeight: 800, color: T.text, letterSpacing: "-0.015em", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.name}
                        {c.is_new_entrant && (
                          <span style={{ marginLeft: 10, fontSize: 9, fontWeight: 800, letterSpacing: "0.12em", color: "#C2410C", background: "#FFEDD5", padding: "2px 7px", borderRadius: 3, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase" }}>
                            New
                          </span>
                        )}
                      </div>
                      <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, color: T.textSub, fontWeight: 600, letterSpacing: "0.05em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.primary_city}, {c.primary_province} · {c.cities_count} {c.cities_count === 1 ? "city" : "cities"} · {c.country_scope === "BOTH" ? "CA + US" : c.country_scope}
                      </div>
                    </div>
                    {/* Stats: own row on mobile, grid cells on desktop */}
                    <div style={{ display: isMobile ? "flex" : "contents", flexDirection: "row", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
                      <div style={{ textAlign: isMobile ? "left" : "right", minWidth: isMobile ? "auto" : 90 }}>
                        <div style={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: 22, fontWeight: 900, color: T.text, letterSpacing: "-0.025em", lineHeight: 1 }}>
                          {(c.total_permits || 0).toLocaleString()}
                        </div>
                        <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9, color: T.textSub, fontWeight: 700, letterSpacing: "0.14em", marginTop: 3, textTransform: "uppercase" }}>
                          Permits
                        </div>
                      </div>
                      <div style={{ textAlign: isMobile ? "left" : "right", minWidth: isMobile ? "auto" : 120 }}>
                        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 800, color: deltaColor, letterSpacing: "-0.005em", lineHeight: 1 }}>
                          {deltaLabel}
                        </div>
                        <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9, color: "#262B37", fontWeight: 700, letterSpacing: "0.14em", marginTop: 3, textTransform: "uppercase" }}>
                          30 days
                        </div>
                      </div>
                      <span style={{ minWidth: isMobile ? "auto" : 110, textAlign: "right" }}>
                        <StatusBadge status={c.is_new_entrant ? "new" : (c.status || "dormant")} />
                      </span>
                      <span style={{ color: "#262B37", fontSize: 18, display: isMobile ? "none" : "inline" }}>→</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* ── LOAD MORE - visible whenever there are more contractors to fetch */}
          {!contractorsLoading && rows.length > 0 && rows.length < total && (
            <div style={{ padding: "24px 0 0", textAlign: "center" }}>
              <button onClick={() => setContractorsLimit(contractorsLimit + 50)}
                style={{
                  padding: "12px 28px", borderRadius: 8,
                  background: "#fff", border: `1.5px solid ${T.text}`,
                  fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 800,
                  color: T.text, cursor: "pointer", letterSpacing: "-0.005em", transition: "all 120ms",
                }}
                onMouseEnter={(e: any) => { e.currentTarget.style.background = T.text; e.currentTarget.style.color = "#fff"; }}
                onMouseLeave={(e: any) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = T.text; }}
              >
                Load more · {(total - rows.length).toLocaleString()} remaining
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };



  // ═════════════════════════════════════════════════════════════════════════
  // ║  ROOFERS VIEW - full leaderboard + roofer detail drawer               ║
  // ═════════════════════════════════════════════════════════════════════════
  const Roofers = () => {
    // Filter + sort the leaderboard client-side. Plain computation (not useMemo)
    // because Roofers is conditionally invoked from the parent - using a hook
    // inside would change the parent's hook count when entering/leaving this
    // view, which violates rules-of-hooks. Up to 1000 items × 3 filter steps
    // runs in <5ms, so chip clicks feel instant.
    const filtered = (() => {
      let list = roofersList;
      // State chip filter - only show contractors who have ≥1 permit in the
      // selected state. When filtered, remap permit_count to in-state count
      // so the badge accurately reflects "permits in this state."
      if (roofersStateFilter) {
        list = list
          .filter((r: any) => r.states?.includes(roofersStateFilter))
          .map((r: any) => ({
            ...r,
            permit_count: r.state_counts?.[roofersStateFilter] ?? r.permit_count,
            _state_filtered: true,
          }));
      } else {
        list = [...list];
      }
      // Search filter - name, phone, email
      if (roofersSearch) {
        const q = roofersSearch.toLowerCase();
        list = list.filter((r: any) =>
          (r.name || "").toLowerCase().includes(q)
          || (r.phone || "").toLowerCase().includes(q)
          || (r.email || "").toLowerCase().includes(q)
        );
      }
      // Sort
      if (roofersSort === "permits") list.sort((a: any, b: any) => b.permit_count - a.permit_count);
      else if (roofersSort === "recent") list.sort((a: any, b: any) => (b.most_recent || "").localeCompare(a.most_recent || ""));
      else list.sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""));
      return list;
    })();

    // Recompute headline counts based on the active filter so they match
    // what the user actually sees in the list below.
    //
    // When a state chip is active, we use the chip's OWN permit_count and
    // contractor_count - those numbers come from the /api/roofing/states
    // endpoint which scans the full table, not just our top-1000 sample.
    // The list below shows top contractors active in the state, but the
    // headline number reflects the true total volume for that state.
    const activeStateMeta = roofersStateFilter
      ? roofersAvailableStates.find((s: any) => s.state === roofersStateFilter)
      : null;
    const displayRooferCount = activeStateMeta
      ? activeStateMeta.contractor_count
      : roofersTotalUnique;
    const displayPermitTotal = activeStateMeta
      ? activeStateMeta.permit_count
      : roofersTotalPermits;
    // Visible (in-list) numbers, used for the "showing N of M" hint
    const visibleRooferCount = filtered.length;

    // Territory label respects the active chip first, then falls back to the
    // global onbCity/onbRegion territory. When the chip narrows to a state
    // that matches onbRegion, we surface the city for context (e.g.
    // "Cleveland, OH"); otherwise we show the state alone (e.g. "Ohio").
    const territory = roofersStateFilter
      ? (onbCity && onbRegion === roofersStateFilter ? `${onbCity}, ${roofersStateFilter}` : roofersStateFilter)
      : "Nationwide";

    const downloadCsv = () => {
      const header = ["Name", "Phone", "Email", "Website", "Permits (180d)", "Median Roof Squares", "Most Recent"];
      const rows = filtered.map((r: any) => [
        `"${(r.name || "").replace(/"/g, '""')}"`,
        `"${r.phone || ""}"`,
        `"${r.email || ""}"`,
        `"${r.website || ""}"`,
        r.permit_count || 0,
        r.median_squares != null ? r.median_squares : "",
        r.most_recent || "",
      ].join(","));
      const csv = [header.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `roofers-${(onbCity || onbRegion || "all").toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    };

    const onCopyContact = (e: any, r: any) => {
      e.stopPropagation();
      const text = `${r.name}${r.phone ? ` · ${r.phone}` : ""}${r.email ? ` · ${r.email}` : ""}`;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          setCopiedRooferId(r.name);
          setTimeout(() => setCopiedRooferId(prev => prev === r.name ? null : prev), 1500);
        }).catch(() => {});
      }
    };

    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: T.bgOff }}>
        {/* Header */}
        <div style={{ padding: "32px 36px 20px", borderBottom: `1.5px solid ${T.border}`, background: T.bg }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Home size={14} color={T.primary} />
            <span style={{ fontFamily: "'Outfit'", fontSize: 11, fontWeight: 800, color: T.primary, textTransform: "uppercase", letterSpacing: "2.5px" }}>Active roofers</span>
          </div>
          <h1 style={{ fontFamily: "'Outfit'", fontSize: 40, fontWeight: 900, color: T.text, margin: "0 0 8px 0", letterSpacing: "-1.5px", lineHeight: 1.1 }}>
            Who's roofing in your markets. <span style={{ color: T.primary }}>Call them today.</span>
          </h1>
          <p style={{ fontSize: 14, color: T.textSub, marginTop: 0, marginBottom: 0, lineHeight: 1.55, maxWidth: 720 }}>
            Every re-roof is a solar lead 6 months out. {roofersStateFilter ? `These are the contractors filing in ${territory}.` : "These are the contractors filing across North America."} Click any name to see what they've shipped.
          </p>
          {(roofersTotalPermits > 0 || roofersTotalUnique > 0) && (
            <div style={{ marginTop: 10, fontSize: 11, color: T.textSub, fontWeight: 600 }}>
              {displayRooferCount.toLocaleString()} unique roofers · {displayPermitTotal.toLocaleString()} permits · last 180 days
              {visibleRooferCount < displayRooferCount && (
                <span style={{ marginLeft: 6, color: T.textSub, fontWeight: 500 }}>
                  · showing top {visibleRooferCount.toLocaleString()} most active
                </span>
              )}
            </div>
          )}
        </div>

        {/* State chip row - quick territory slice without leaving Roofers */}
        {roofersAvailableStates.length > 0 && (
          <div style={{ padding: "12px 36px", background: T.bg, borderBottom: `1px solid ${T.border}`, overflowX: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: "fit-content" }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "1.2px", textTransform: "uppercase", color: T.textSub, paddingRight: 6, flexShrink: 0 }}>State</span>
              {/* Nationwide chip - clears state filter */}
              <button onClick={() => setRoofersStateFilter(null)}
                style={{
                  padding: "6px 11px",
                  borderRadius: 7,
                  border: `1px solid ${roofersStateFilter === null ? T.text : T.border}`,
                  background: roofersStateFilter === null ? T.text : T.bg,
                  color: roofersStateFilter === null ? "#fff" : T.text,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "'Outfit'",
                  flexShrink: 0,
                  transition: "all 0.1s",
                }}>
                All US
              </button>
              {roofersAvailableStates.map((s: any) => {
                const active = roofersStateFilter === s.state;
                return (
                  <button key={s.state} onClick={() => setRoofersStateFilter(active ? null : s.state)}
                    style={{
                      padding: "6px 11px",
                      borderRadius: 7,
                      border: `1px solid ${active ? T.primary : T.border}`,
                      background: active ? T.primaryLight : T.bg,
                      color: active ? T.primaryDark : T.text,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "'Outfit'",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      transition: "all 0.1s",
                    }}>
                    {s.state}
                    <span style={{ fontSize: 10, color: active ? T.primaryDark : T.textSub, fontWeight: 600, opacity: 0.85 }}>
                      {s.permit_count >= 1000 ? `${Math.round(s.permit_count / 100) / 10}k` : s.permit_count.toLocaleString()}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div style={{ padding: "14px 36px", display: "flex", alignItems: "center", gap: 12, background: T.bg, borderBottom: `1px solid ${T.border}` }}>
          {/* Search */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: T.bgSub, borderRadius: 9, padding: "9px 13px", flex: 1, maxWidth: 360, border: `1px solid ${T.border}` }}>
            <Search size={13} color={T.textSub} />
            <input value={roofersSearch} onChange={(e: any) => setRoofersSearch(e.target.value)} placeholder="Search name, phone, email..." style={{ flex: 1, border: "none", background: "transparent", fontSize: 13, color: T.text, outline: "none", fontFamily: "'Outfit'" }} />
            {roofersSearch && <button onClick={() => setRoofersSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}><X size={12} color={T.textSub} /></button>}
          </div>
          {/* Sort */}
          <div style={{ display: "flex", gap: 4, background: T.bgSub, padding: 3, borderRadius: 8, border: `1px solid ${T.border}` }}>
            {[
              { key: "permits", label: "Most active" },
              { key: "recent", label: "Most recent" },
              { key: "name", label: "A-Z" },
            ].map(s => (
              <button key={s.key} onClick={() => setRoofersSort(s.key as any)}
                style={{ padding: "6px 11px", borderRadius: 6, border: "none", background: roofersSort === s.key ? T.bg : "transparent", color: roofersSort === s.key ? T.text : T.textSub, fontSize: 12, fontWeight: roofersSort === s.key ? 700 : 600, cursor: "pointer", fontFamily: "'Outfit'", boxShadow: roofersSort === s.key ? "0 1px 2px rgba(0,0,0,0.05)" : "none", transition: "all 0.1s" }}>
                {s.label}
              </button>
            ))}
          </div>
          {/* Export */}
          <button onClick={downloadCsv} disabled={filtered.length === 0}
            style={{ padding: "9px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 12, fontWeight: 700, cursor: filtered.length === 0 ? "not-allowed" : "pointer", fontFamily: "'Outfit'", display: "flex", alignItems: "center", gap: 6, opacity: filtered.length === 0 ? 0.4 : 1 }}>
            <ArrowUpRight size={13} /> Export CSV
          </button>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 36px 40px" }}>
          {roofersLoading && roofersList.length === 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <style>{`@keyframes slShimmer2 { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }`}</style>
              {[0,1,2,3,4,5,6,7,8,9].map(i => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderRadius: 12, background: T.bg, border: `1px solid ${T.border}` }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer2 1.4s ease-in-out infinite", flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 14, width: `${40 + (i * 9) % 35}%`, borderRadius: 4, background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer2 1.4s ease-in-out infinite", marginBottom: 7 }} />
                    <div style={{ height: 11, width: `${30 + (i * 7) % 25}%`, borderRadius: 4, background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer2 1.4s ease-in-out infinite" }} />
                  </div>
                  <div style={{ height: 22, width: 60, borderRadius: 6, background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer2 1.4s ease-in-out infinite", flexShrink: 0 }} />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: "60px 24px", textAlign: "center", background: T.bg, borderRadius: 12, border: `1px solid ${T.border}` }}>
              <Home size={26} color={T.textSub} style={{ opacity: 0.4, marginBottom: 12 }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>
                {roofersSearch ? `No roofers match "${roofersSearch}"` : "No roofer data in this market"}
              </div>
              <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.5, maxWidth: 480, margin: "0 auto" }}>
                {roofersSearch
                  ? "Try clearing the search or switching to a different territory."
                  : "Local jurisdictions in this market don't publish contractor info on permits. Try Fort Wayne IN, Reno NV, or any Florida metro for the richest data."}
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filtered.map((r: any, i: number) => {
                const contactPieces = [r.phone, r.email].filter(Boolean);
                const hasContact = contactPieces.length > 0;
                const recentDate = r.most_recent ? new Date(r.most_recent).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "-";
                return (
                  <div key={`${r.name}-${i}`}
                    onClick={() => setOpenRoofer(r)}
                    style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderRadius: 12, background: T.bg, border: `1px solid ${T.border}`, cursor: "pointer", transition: "all 0.1s" }}
                    onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.04)"; }}
                    onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                    {/* Rank */}
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: i < 3 && roofersSort === "permits" && !roofersSearch ? "#FEF3C7" : T.bgSub, border: `1px solid ${i < 3 && roofersSort === "permits" && !roofersSearch ? "#FCD34D" : T.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontSize: 12, fontWeight: 900, color: i < 3 && roofersSort === "permits" && !roofersSearch ? "#92400E" : T.textSub, fontFamily: "'Outfit'" }}>{i + 1}</span>
                    </div>
                    {/* Name + contact */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.name}
                      </div>
                      <div style={{ fontSize: 11, color: T.textSub, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.phone || ""}{r.phone && r.email ? " · " : ""}{r.email || ""}
                        {!hasContact && <span style={{ color: T.textSub, fontStyle: "italic" }}>contact info unavailable</span>}
                      </div>
                    </div>
                    {/* Stats */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0, marginRight: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: T.text, fontFamily: "'Outfit'" }}>
                        {r.permit_count} {r.permit_count === 1 ? "permit" : "permits"}
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: T.textSub, display: "flex", alignItems: "center", gap: 6 }}>
                        latest {recentDate}
                        {r.median_squares != null && (
                          <span style={{ padding: "1px 6px", borderRadius: 4, background: "#FEF3C7", color: "#92400E", fontWeight: 800, letterSpacing: "0.2px" }}>
                            ~{r.median_squares} sqs
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Actions */}
                    {hasContact && (
                      <button onClick={(e: any) => onCopyContact(e, r)}
                        style={{ fontSize: 10, fontWeight: 700, padding: "5px 9px", borderRadius: 6, background: copiedRooferId === r.name ? T.successLight : T.bgSub, color: copiedRooferId === r.name ? T.success : T.textSub, border: `1px solid ${copiedRooferId === r.name ? "#BBF7D0" : T.border}`, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.5px", flexShrink: 0, transition: "all 0.15s" }}>
                        {copiedRooferId === r.name ? "Copied" : "Copy"}
                      </button>
                    )}
                    <ChevronRight size={16} color={T.textSub} style={{ flexShrink: 0 }} />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Roofer detail drawer */}
        {openRoofer && (
          <>
            <div onClick={() => setOpenRoofer(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 250, backdropFilter: "blur(2px)" }} />
            <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 540, maxWidth: "100%", background: T.bg, zIndex: 251, display: "flex", flexDirection: "column", boxShadow: "-12px 0 40px rgba(0,0,0,0.18)" }}>
              {/* Drawer header */}
              <div style={{ padding: "22px 26px 18px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", color: T.primary, marginBottom: 6 }}>Roofer</div>
                    <h2 style={{ fontFamily: "'Outfit'", fontSize: 22, fontWeight: 900, color: T.text, margin: 0, lineHeight: 1.2, letterSpacing: "-0.4px" }}>
                      {openRoofer.name}
                    </h2>
                    {openRoofer.website && (
                      <a href={openRoofer.website.startsWith("http") ? openRoofer.website : `https://${openRoofer.website}`} target="_blank" rel="noopener noreferrer"
                        style={{ display: "inline-block", fontSize: 12, color: T.primary, fontWeight: 600, marginTop: 6, textDecoration: "none" }}>
                        {openRoofer.website} ↗
                      </a>
                    )}
                  </div>
                  <button onClick={() => setOpenRoofer(null)} style={{ background: T.bgSub, border: "none", borderRadius: 8, width: 30, height: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <X size={14} color={T.text} />
                  </button>
                </div>
                {/* Contact actions */}
                <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                  {openRoofer.phone && (
                    <a href={`tel:${openRoofer.phone}`} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 9, background: T.text, color: "#fff", fontSize: 12, fontWeight: 700, textDecoration: "none", fontFamily: "'Outfit'" }}>
                      <Phone size={13} /> {openRoofer.phone}
                    </a>
                  )}
                  {openRoofer.email && (
                    <a href={`mailto:${openRoofer.email}`} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 9, background: T.bgSub, color: T.text, fontSize: 12, fontWeight: 700, textDecoration: "none", border: `1px solid ${T.border}`, fontFamily: "'Outfit'" }}>
                      <Mail size={13} /> Email
                    </a>
                  )}
                </div>
                {/* Stats strip */}
                <div style={{ display: "flex", gap: 18, marginTop: 16, padding: "12px 14px", background: T.bgSub, borderRadius: 9, border: `1px solid ${T.border}` }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: T.text, fontFamily: "'Outfit'" }}>{openRooferTotal || openRoofer.permit_count}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textSub, textTransform: "uppercase", letterSpacing: "0.7px", marginTop: 1 }}>permits</div>
                  </div>
                  <div style={{ width: 1, background: T.border }} />
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: T.text, fontFamily: "'Outfit'" }}>
                      {openRoofer.most_recent ? new Date(openRoofer.most_recent).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "-"}
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textSub, textTransform: "uppercase", letterSpacing: "0.7px", marginTop: 1 }}>most recent</div>
                  </div>
                </div>
              </div>

              {/* Drawer body - permit list */}
              <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px 24px" }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", color: T.textSub, marginBottom: 10 }}>
                  Recent permits
                </div>
                {openRooferLoading ? (
                  <div style={{ padding: "36px 12px", textAlign: "center", color: T.textSub, fontSize: 13 }}>
                    Loading permit history...
                  </div>
                ) : openRooferPermits.length === 0 ? (
                  <div style={{ padding: "36px 12px", textAlign: "center", color: T.textSub, fontSize: 13 }}>
                    No permits found in the last year.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {openRooferPermits.map((p: any) => {
                      const dateStr = p.permit_date ? new Date(p.permit_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";
                      const ageStr = p.days_old != null ? (p.days_old < 7 ? `${p.days_old}d ago` : p.days_old < 30 ? `${Math.floor(p.days_old / 7)}w ago` : `${Math.floor(p.days_old / 30)}mo ago`) : dateStr;
                      const onCopyAddr = () => {
                        const text = `${p.address}, ${p.city}${p.state ? ", " + p.state : ""}${p.zip ? " " + p.zip : ""}`;
                        if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
                      };
                      return (
                        <div key={p.id} onClick={onCopyAddr}
                          style={{ padding: "10px 12px", background: T.bgSub, borderRadius: 8, cursor: "pointer", border: `1px solid transparent`, transition: "border 0.1s", display: "flex", alignItems: "flex-start", gap: 10 }}
                          onMouseEnter={(e: any) => e.currentTarget.style.borderColor = T.border}
                          onMouseLeave={(e: any) => e.currentTarget.style.borderColor = "transparent"}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{p.address}</div>
                            <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>
                              {p.city}{p.state ? `, ${p.state}` : ""}{p.zip ? ` ${p.zip}` : ""} · {ageStr}
                            </div>
                          </div>
                          {p.squares != null && (
                            <div style={{ padding: "4px 9px", borderRadius: 6, background: "#FEF3C7", border: "1px solid #FCD34D", fontSize: 11, fontWeight: 800, color: "#92400E", flexShrink: 0, fontFamily: "'Outfit'", whiteSpace: "nowrap" }}>
                              {p.squares} sqs
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {openRooferTotal > openRooferPermits.length && (
                  <div style={{ marginTop: 12, padding: "10px 14px", textAlign: "center", fontSize: 11, fontWeight: 600, color: T.textSub, background: T.bgOff, borderRadius: 8 }}>
                    Showing {openRooferPermits.length} of {openRooferTotal} permits in the last year.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };


  // ═════════════════════════════════════════════════════════════════════════
  // ║  COMPETITORS VIEW - contractor leaderboard + drill-down intelligence  ║
  // ═════════════════════════════════════════════════════════════════════════
  const Competitors = () => {
    // State lifted to App level (see useState/useEffect block after selectedContractor
    // declaration) to avoid rules-of-hooks violation: this function is only
    // called when view === "competitors", so defining hooks inside it would
    // change the hook count between renders and React would throw.

    // ── LEADERBOARD MODE ──
    if (!selectedContractor) {
      const filtered = compSearchQuery.trim()
        ? compLeaderboard.filter((c: any) =>
            (c.name || "").toLowerCase().includes(compSearchQuery.toLowerCase()))
        : compLeaderboard;

      return (
        <div style={{ flex: 1, overflow: "auto", background: "#fff" }}>
          {/* Header */}
          <div style={{ padding: "32px 28px 20px", borderBottom: `1.5px solid ${T.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#FF8800" }} />
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "2.5px", textTransform: "uppercase", color: "#FF8800" }}>Market intelligence · Last 90 days</span>
            </div>
            <div style={{ fontFamily: "'Outfit'", fontSize: 40, fontWeight: 900, color: T.text, letterSpacing: "-1.5px", lineHeight: 1.1, marginBottom: 8 }}>
              Who&apos;s moving. <span style={{ color: "#FF8800" }}>Where.</span>
            </div>
            <div style={{ fontSize: 14, color: T.textSub, maxWidth: 620, lineHeight: 1.55 }}>
              Top solar contractors by recent permit volume. Click any name for full market intelligence - cities, trends, new territory moves, and weekly activity.
            </div>

            {/* Search bar */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 20, maxWidth: 420, background: T.bgOff, padding: "10px 14px", borderRadius: 10, border: `1px solid ${T.border}` }}>
              <Search size={14} color={T.textSub} />
              <input
                value={compSearchQuery}
                onChange={(e: any) => setCompSearchQuery(e.target.value)}
                placeholder="Filter contractors..."
                style={{ flex: 1, border: "none", background: "transparent", fontSize: 14, color: T.text, outline: "none" }}
              />
            </div>
          </div>

          {/* Leaderboard list */}
          <div style={{ padding: "20px 28px 40px" }}>
            {compLeaderLoading && compLeaderboard.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[0,1,2,3,4,5,6,7].map(i => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 12, background: T.bg, border: `1px solid ${T.border}` }}>
                    {/* Rank skeleton */}
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer 1.4s ease-in-out infinite", flexShrink: 0 }} />
                    {/* Name + subtitle skeleton */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ height: 14, width: `${40 + (i * 11) % 40}%`, borderRadius: 4, background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer 1.4s ease-in-out infinite", marginBottom: 6 }} />
                      <div style={{ height: 11, width: `${25 + (i * 7) % 20}%`, borderRadius: 4, background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer 1.4s ease-in-out infinite" }} />
                    </div>
                    {/* Permit count skeleton */}
                    <div style={{ height: 22, width: 50, borderRadius: 6, background: "linear-gradient(90deg, #F0F0F4 0%, #F8F8FA 50%, #F0F0F4 100%)", backgroundSize: "800px 100%", animation: "slShimmer 1.4s ease-in-out infinite", flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: "60px 20px", textAlign: "center" }}>
                <Wrench size={28} color={T.textSub} style={{ opacity: 0.4, marginBottom: 12 }} />
                <div style={{ fontSize: 14, color: T.textSub }}>
                  {compSearchQuery ? `No contractors match "${compSearchQuery}"` : "No contractor data yet."}
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {filtered.map((c: any, i: number) => {
                  const isTop3 = i < 3 && !compSearchQuery;
                  return (
                    <div
                      key={c.name + i}
                      onClick={() => setSelectedContractor(c.name)}
                      style={{
                        display: "flex", alignItems: "center", gap: 16, padding: "16px 18px",
                        borderRadius: 12, border: `1px solid ${T.border}`,
                        background: isTop3 ? "#FFFBEB" : "#fff", cursor: "pointer",
                        transition: "all 0.1s",
                      }}
                      onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.06)"; }}
                      onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                      {/* Rank pill */}
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: isTop3 ? "#FEF3C7" : T.bgOff, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: isTop3 ? "1px solid #F59E0B40" : "none" }}>
                        <span style={{ fontSize: 16, fontWeight: 900, color: isTop3 ? "#D97706" : T.textSub, fontFamily: "'Outfit'" }}>#{i + 1}</span>
                      </div>
                      {/* Name + tagline */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: "'Outfit'", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {c.name}
                        </div>
                        <div style={{ fontSize: 12, color: T.textSub, marginTop: 3 }}>
                          {c.permitCount} permits · {c.uniqueCities} {c.uniqueCities === 1 ? "city" : "cities"}
                          {c.topCities && c.topCities.length > 0 && ` · Top: ${c.topCities[0].city}`}
                        </div>
                      </div>
                      {/* Freshness */}
                      {c.freshestDays !== null && c.freshestDays <= 7 && (
                        <span style={{ fontSize: 10, fontWeight: 800, padding: "4px 8px", borderRadius: 5, background: "#DCFCE7", color: "#15803D", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                          Active
                        </span>
                      )}
                      <ChevronRight size={16} color={T.textSub} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      );
    }

    // ── CONTRACTOR DETAIL MODE ──
    if (compContractorLoading && !compContractor) {
      return (
        <div style={{ flex: 1, background: "#fff", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "20px 28px", borderBottom: `1px solid ${T.border}` }}>
            <button onClick={() => { setSelectedContractor(null); setView("roofers"); }} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "#0A0A0A", fontSize: 13, fontWeight: 700 }}>
              <ArrowLeft size={14} /> Back to contractors
            </button>
          </div>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ textAlign: "center" }}>
              <RefreshCw size={24} color={T.textSub} style={{ animation: "spin 0.8s linear infinite", marginBottom: 12 }} />
              <div style={{ fontSize: 13, color: T.textSub }}>Loading {selectedContractor}…</div>
            </div>
          </div>
        </div>
      );
    }

    if (!compContractor) {
      return (
        <div style={{ flex: 1, background: "#fff", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "20px 28px", borderBottom: `1px solid ${T.border}` }}>
            <button onClick={() => { setSelectedContractor(null); setView("roofers"); }} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "#0A0A0A", fontSize: 13, fontWeight: 700 }}>
              <ArrowLeft size={14} /> Back to contractors
            </button>
          </div>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
            <div style={{ textAlign: "center", maxWidth: 360 }}>
              <Wrench size={28} color={T.textSub} style={{ opacity: 0.4, marginBottom: 12 }} />
              <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.55 }}>
                No intelligence available for <strong>{selectedContractor}</strong>.
                They may have too few permits to aggregate.
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Layout the compContractor detail
    const C = compContractor;
    const nm = (C.new_markets || []) as any[];
    const tc = (C.top_cities || []) as any[];
    const wk = (C.weekly_activity || []) as any[];
    const top = tc[0] || null;
    const statusColors: any = {
      growing:   { bg: "#F0FDF4", text: "#16A34A", border: "#86EFAC", label: "Growing" },
      steady:    { bg: "#EFF6FF", text: "#1D4ED8", border: "#93C5FD", label: "Steady" },
      shrinking: { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA", label: "Slipping" },
      dormant:   { bg: "#F4F4EE", text: "#0A0A0A", border: T.border,  label: "Dormant" },
    };
    const statusStyle = statusColors[C.status] || statusColors.dormant;
    const sparkMax = Math.max(1, ...wk.map((w: any) => w.permit_count || 0));
    const peakWeek = wk.reduce((m: any, w: any) => ((w.permit_count || 0) > (m ? m.permit_count : -1) ? w : m), null);
    const nzCounts = wk.map((w: any) => w.permit_count || 0).filter((n: number) => n > 0).sort((a: number, b: number) => a - b);
    const wkMedian = nzCounts.length ? nzCounts[Math.floor(nzCounts.length / 2)] : 0;
    const recent4 = wk.slice(Math.max(0, wk.length - 4)).reduce((s: number, w: any) => s + (w.permit_count || 0), 0);
    const prior4 = wk.slice(Math.max(0, wk.length - 8), Math.max(0, wk.length - 4)).reduce((s: number, w: any) => s + (w.permit_count || 0), 0);
    let momentumPct: number | null = null;
    let momentumLabel = "Quiet", momentumColor = "#0A0A0A";
    if (prior4 > 0) {
      momentumPct = Math.round(((recent4 - prior4) / prior4) * 100);
      momentumLabel = `${momentumPct > 0 ? "↑" : momentumPct < 0 ? "↓" : "→"} ${Math.abs(momentumPct)}%`;
      momentumColor = momentumPct > 0 ? "#16A34A" : momentumPct < 0 ? "#DC2626" : "#0A0A0A";
    } else if (recent4 > 0) {
      momentumLabel = "↑ New";
      momentumColor = "#16A34A";
    }
    const lastSeenDays = C.last_seen ? Math.max(0, Math.floor((Date.now() - new Date(C.last_seen).getTime()) / 86400000)) : null;
    const concentrated = top && Number(top.share_pct) >= 40 ? top : null;
    const spike = peakWeek && wkMedian > 0 && (peakWeek.permit_count || 0) >= wkMedian * 3 ? peakWeek : null;
    const dropCity = tc.find((c: any) => c.trend === "down" && Number(c.prior_30) >= 5 && Number(c.recent_30) < Number(c.prior_30) * 0.5) || null;
    const nf = (n: any) => Number(n || 0).toLocaleString();
    const dShort = (d: any) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const dFull = (d: any) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    const NM = C.display_name || C.canonical_name || "This contractor";
    let insight = "";
    if (C.status === "dormant") {
      insight = `${NM} has gone quiet${lastSeenDays != null ? `, no new permits in ${lastSeenDays} days` : ""}. ${nf(C.total_permits)} permits on record` + (concentrated ? `, heavily concentrated in ${concentrated.city} (${concentrated.share_pct}% of all work).` : ` across ${nf(C.cities_count)} cities.`);
    } else if (nm.length >= 2) {
      insight = `${NM} is pushing into new territory: ${nm.length} new cities entered in the last 60 days` + (top ? `, while ${top.city} stays their stronghold at ${top.share_pct}% of volume.` : ".");
    } else if (momentumPct != null && momentumPct >= 15) {
      insight = `${NM} is accelerating, up ${momentumPct}% over the last month` + (top ? `, led by ${top.city}.` : ".");
    } else if (momentumPct != null && momentumPct <= -15) {
      insight = `${NM} is slowing, down ${Math.abs(momentumPct)}% over the last month` + (top ? `. ${top.city} is still their largest market at ${top.share_pct}%.` : ".");
    } else {
      insight = `${NM} is holding steady, ${nf(C.last_30_count)} permits in the last 30 days across ${nf(C.cities_count)} cities` + (concentrated ? `, concentrated in ${concentrated.city} (${concentrated.share_pct}%).` : ".");
    }
    const flags: any[] = [];
    if (spike) flags.push({ dot: "#FF8800", title: "Volume spike", body: `Week of ${dShort(spike.week_start)} saw ${spike.permit_count} permits, well above their typical ${wkMedian} per week.` });
    if (C.status === "dormant" && lastSeenDays != null) flags.push({ dot: "#DC2626", title: "Gone dormant", body: `No new permits in ${lastSeenDays} days. Last filed ${dFull(C.last_seen)}.` });
    if (nm.length >= 3) flags.push({ dot: "#16A34A", title: "Territory push", body: `Entered ${nm.length} new cities in 60 days: ${nm.slice(0, 3).map((m: any) => m.city).join(", ")}${nm.length > 3 ? ` and ${nm.length - 3} more` : ""}.` });
    if (dropCity) flags.push({ dot: "#DC2626", title: "Cooling market", body: `${dropCity.city} fell to ${dropCity.recent_30} permits in 30 days, down from ${dropCity.prior_30} prior.` });
    return (
      <div style={{ flex: 1, overflow: "auto", background: "#fff" }}>
        {/* Back bar */}
        <div style={{ padding: "16px 28px", borderBottom: `1px solid ${T.border}`, background: "#fff", position: "sticky", top: 0, zIndex: 10 }}>
          <button onClick={() => { setSelectedContractor(null); setView("roofers"); }} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "#0A0A0A", fontSize: 13, fontWeight: 700 }}>
            <ArrowLeft size={14} /> Back to contractors
          </button>
        </div>

        {/* HEADER */}
        <div style={{ padding: "28px 28px 24px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: "#FF8800", flexShrink: 0 }} />
            <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "#FF8800" }}>Contractor intelligence</span>
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 34, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.02em", lineHeight: 1.1, marginBottom: 10 }}>
                {C.display_name}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10.5, fontWeight: 800, padding: "4px 10px", borderRadius: 5, background: statusStyle.bg, color: statusStyle.text, border: `1px solid ${statusStyle.border}`, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {statusStyle.label}
                </span>
                {C.last_seen && (
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: "#0A0A0A" }}>
                    Last permit {dFull(C.last_seen)}{lastSeenDays != null ? ` · ${lastSeenDays}d ago` : ""}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* INSIGHT BANNER */}
          <div style={{ marginTop: 18, padding: "14px 16px", background: "#FFF7ED", border: "1px solid #FDBA74", borderRadius: 12, borderLeft: "3px solid #FF8800" }}>
            <div style={{ fontSize: 14.5, fontWeight: 600, color: "#0A0A0A", lineHeight: 1.5 }}>{insight}</div>
          </div>

          {/* SIGNAL STAT ROW */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))", gap: 10, marginTop: 18 }}>
            {[
              { label: "Total permits", val: nf(C.total_permits), color: "#0A0A0A" },
              { label: "Cities", val: nf(C.cities_count), color: "#0A0A0A" },
              { label: "States", val: nf(C.states_count), color: "#0A0A0A" },
              { label: "Last 30 days", val: nf(C.last_30_count), color: "#0A0A0A" },
              { label: "30d momentum", val: momentumLabel, color: momentumColor },
            ].map((s: any) => (
              <div key={s.label} style={{ padding: "12px 14px", background: "#FAFAF6", border: `1px solid ${T.border}`, borderRadius: 10 }}>
                <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 20, fontWeight: 900, color: s.color, lineHeight: 1, letterSpacing: "-0.01em" }}>{s.val}</div>
                <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9.5, fontWeight: 700, color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 6 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* SPARKLINE */}
          {wk.length > 0 && (
            <div style={{ marginTop: 22 }}>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, fontWeight: 800, color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>
                Activity · last {wk.length} weeks
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 56, paddingBottom: 2 }}>
                {wk.map((w: any, i: number) => {
                  const h = Math.max(3, ((w.permit_count || 0) / sparkMax) * 52);
                  const isPeak = !!(spike && peakWeek && w.week_start === peakWeek.week_start);
                  return (
                    <div key={i} title={`Week of ${dShort(w.week_start)} · ${w.permit_count || 0} permits`}
                         style={{ flex: 1, height: h, background: isPeak ? "#FF5500" : "#FF8800", borderRadius: 2, opacity: isPeak ? 1 : 0.82, minWidth: 4, transition: "opacity 0.1s" }}
                         onMouseEnter={(e: any) => e.currentTarget.style.opacity = "1"}
                         onMouseLeave={(e: any) => e.currentTarget.style.opacity = isPeak ? "1" : "0.82"} />
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* UNUSUAL ACTIVITY */}
        {flags.length > 0 && (
          <div style={{ padding: "24px 28px 4px" }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.01em", marginBottom: 14 }}>Unusual activity</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {flags.map((f: any, i: number) => (
                <div key={i} style={{ display: "flex", gap: 12, padding: "12px 14px", border: `1px solid ${T.border}`, borderRadius: 10, borderLeft: `3px solid ${f.dot}`, background: "#fff" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10.5, fontWeight: 800, color: f.dot, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{f.title}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#0A0A0A", lineHeight: 1.45 }}>{f.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* WHERE THEY WORK */}
        {tc.length > 0 && (
          <div style={{ padding: "24px 28px 4px" }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.01em", marginBottom: 4 }}>Where they work</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#0A0A0A", marginBottom: 16 }}>Top {tc.length} cities by permit volume, with 30-day trend.</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {tc.map((c: any, i: number) => {
                const tColor = c.trend === "up" ? "#16A34A" : c.trend === "down" ? "#DC2626" : "#0A0A0A";
                const tGlyph = c.trend === "up" ? "↑" : c.trend === "down" ? "↓" : "→";
                return (
                  <div key={c.city + c.province + i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderRadius: 10, border: `1px solid ${T.border}`, background: "#fff" }}>
                    <div style={{ width: 26, height: 26, borderRadius: 6, background: "#F4F4EE", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, fontWeight: 800, color: "#0A0A0A" }}>{i + 1}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#0A0A0A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.city}{c.province ? `, ${c.province}` : ""}
                      </div>
                      <div style={{ fontSize: 11.5, fontWeight: 500, color: "#0A0A0A", marginTop: 2 }}>
                        {c.share_pct}% of portfolio · {c.recent_30} in last 30d vs {c.prior_30} prior
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                      <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 16, fontWeight: 900, color: "#0A0A0A" }}>{nf(c.permit_count)}</div>
                      <div style={{ fontSize: 11, fontWeight: 800, color: tColor, marginTop: 1 }}>{tGlyph} {c.trend}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* WHERE THEY ARE MOVING (new markets) */}
        {nm.length > 0 ? (
          <div style={{ padding: "24px 28px 4px" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.01em" }}>Where they are moving</div>
              <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 5, background: "#FFF7ED", color: "#FF8800", border: "1px solid #FDBA74", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Last 60 days
              </span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#0A0A0A", marginBottom: 16 }}>Cities where {C.display_name} filed a first-ever permit in the last 60 days.</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {nm.map((m: any, i: number) => (
                <div key={m.city + m.province + i} style={{ padding: "12px 14px", borderRadius: 10, border: "1.5px solid #FDBA74", background: "#FFF7ED", minWidth: 160 }}>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 800, color: "#0A0A0A" }}>
                    {m.city}{m.province ? `, ${m.province}` : ""}
                  </div>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: "#92400E", marginTop: 3 }}>
                    First filed {dShort(m.first_permit_date)}{m.permit_count_since > 1 ? ` · ${m.permit_count_since} since` : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : C.status !== "dormant" ? (
          <div style={{ padding: "24px 28px 4px" }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.01em", marginBottom: 4 }}>Where they are moving</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#0A0A0A" }}>No new territory in the last 60 days. They are filing in markets they already work.</div>
          </div>
        ) : null}

        {/* RECENT PERMITS */}
        {C.recent_permits && C.recent_permits.length > 0 && (
          <div style={{ padding: "24px 28px 40px" }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.01em", marginBottom: 4 }}>Recent permits</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#0A0A0A", marginBottom: 16 }}>Last 30 days, up to 20 most recent. Click any to open the lead.</div>
            <div style={{ borderRadius: 10, border: `1px solid ${T.border}`, overflow: "hidden" }}>
              {C.recent_permits.map((p: any, i: number) => {
                const stub = {
                  id: p.id, address: p.address, city: p.city, state: p.province || null, province: p.province || null,
                  permitDate: p.permit_date, permitValue: p.permit_value,
                  contractorName: C.display_name || C.canonical_name,
                };
                return (
                  <div key={p.id + i} onClick={() => openLeadById(p.id, stub)}
                    style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderBottom: i < C.recent_permits.length - 1 ? `1px solid ${T.border}` : "none", cursor: "pointer", transition: "background 0.1s", background: "#fff" }}
                    onMouseEnter={(e: any) => { e.currentTarget.style.background = "#FAFAF6"; }}
                    onMouseLeave={(e: any) => e.currentTarget.style.background = "#fff"}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0A0A0A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {cleanAddress(p.address)}
                      </div>
                      <div style={{ fontSize: 11.5, fontWeight: 500, color: "#0A0A0A", marginTop: 2 }}>
                        {p.city}{p.province ? `, ${p.province}` : ""} · {dShort(p.permit_date)}
                      </div>
                    </div>
                    {p.permit_value > 0 && (
                      <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 12.5, fontWeight: 800, color: "#16A34A" }}>{fmtVal(p.permit_value)}</div>
                    )}
                    <ChevronRight size={14} color="#0A0A0A" />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };
  // ── INFO TIP ───────────────────────────────────────────────────────────────
  // Small ⓘ icon with on-hover tooltip. Used to add context to metrics without
  // clutter. Click-to-toggle for touch; hover for desktop.
  const InfoTip = ({ text, side = "top" }: { text: string; side?: "top" | "bottom" }) => {
    const [show, setShow] = useState(false);
    return (
      <span style={{ position: "relative", display: "inline-flex", alignItems: "center", marginLeft: 4, lineHeight: 1 }}>
        <span
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
          onClick={(e: any) => { e.stopPropagation(); setShow((v) => !v); }}
          style={{ cursor: "help", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 13, height: 13, borderRadius: "50%" }}>
          <Info size={11} color="#A1A1AA" strokeWidth={2.25} />
        </span>
        {show && (
          <span style={{
            position: "absolute",
            bottom: side === "top" ? "calc(100% + 6px)" : "auto",
            top: side === "bottom" ? "calc(100% + 6px)" : "auto",
            left: "50%",
            transform: "translateX(-50%)",
            width: 220,
            padding: "8px 10px",
            background: "#0F0F1A",
            color: "#fff",
            fontSize: 11,
            lineHeight: 1.45,
            borderRadius: 6,
            zIndex: 300,
            pointerEvents: "none",
            whiteSpace: "normal",
            fontWeight: 500,
            textAlign: "left",
            letterSpacing: 0,
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
          }}>{text}</span>
        )}
      </span>
    );
  };

  // ── LEAD DRAWER ────────────────────────────────────────────────────────────
  // Right-side slide-in panel. Replaces the centered modal. Structure: fixed
  // hero (score + address + primary CTA) on top; left tab rail + scrollable
  // content below. Tabs: Overview / Neighborhood / Activity / Nearby.
  const Modal = () => {
    if (!modalLead) return null;
    const lead = modalLead;
    const score = getSmartScore(lead);
    const seg = SEGMENTS[getSegment(lead)];
    const segColor = seg?.color || "#FF8800";
    const segInk = segColor;
    const isUnlocked = unlocked[lead.id];
    const logs = callLog[lead.id] || [];
    const inPipe = pipeLeads.some((p: any) => p.id === lead.id);
    const pipeEntry = pipeLeads.find((p: any) => p.id === lead.id);
    const days = getDays(lead);
    const val = getVal(lead);
    const sysDetails = parsePermitDetails(lead);
    const ctx = modalMarketCtx;

    const scoreColor = scoreHex(score);
    const scoreLabel = scoreLabelFor(score); // HOT / WARM / COOL / New

    const close = () => { setModalLead(null); setModalMarketCtx(null); };

    const tabs = [
      { key: "overview", label: "Overview", icon: Home },
      { key: "neighborhood", label: "Neighborhood", icon: MapPin },
      { key: "activity", label: "Activity", icon: Radio },
      { key: "nearby", label: "Nearby", icon: Compass },
    ];

    // ── PRIMARY CTA ──
    // Contextual single action in the hero. Priority: unlock > add-to-pipeline > in-pipeline pill.
    const renderPrimaryCTA = () => {
      if (!isUnlocked) {
        return (
          <button
            onClick={() => {
              // First unlock requires the one-time compliance acknowledgment.
              // Admins are exempt. If not yet acknowledged, open the gate and
              // remember this lead so we can complete it after they accept.
              if (!user.isAdmin && !user.skiptraceAckAt) {
                setPendingUnlockLead(lead);
                setShowComplianceGate(true);
                return;
              }
              performUnlock(lead);
            }}
            style={{ padding: "11px 18px", background: unlocking === lead.id ? "#D97006" : "#FF8800", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'Outfit',sans-serif", boxShadow: "0 2px 10px rgba(255,136,0,0.28)", whiteSpace: "nowrap", transition: "background 0.15s" }}>
            {unlocking === lead.id ? <RefreshCw size={14} color="white" style={{ animation: "spin 0.8s linear infinite" }} /> : <Unlock size={14} color="white" />}
            {unlocking === lead.id ? "Unlocking…" : "Unlock contact · 1 credit"}
          </button>
        );
      }
      if (!inPipe) {
        return (
          <button
            onClick={() => { setPipeLeads([...pipeLeads, { ...lead, stage: "unlocked" }]); savePipelineToBackend(lead.id, "unlocked"); }}
            style={{ padding: "11px 18px", background: "#FF8800", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'Outfit',sans-serif", boxShadow: "0 2px 10px rgba(255,136,0,0.28)", whiteSpace: "nowrap" }}>
            <GitMerge size={14} color="white" />
            Add to focus
          </button>
        );
      }
      return (
        <div style={{ padding: "10px 14px", background: "#F0FDF4", borderRadius: 10, border: "1.5px solid #86EFAC", display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
          <Check size={14} color="#16A34A" strokeWidth={2.5} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#16A34A" }}>In focus</span>
        </div>
      );
    };

    // Owner contact block - door-card framing. Reuses the unlock + compliance flow.
    const renderOwnerContact = () => (
      <div style={{ borderRadius: 16, border: `1px solid ${segColor}40`, background: segColor + "0C", padding: "16px 16px 14px" }}>
        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 17, fontWeight: 900, letterSpacing: "-0.02em", color: "#0A0A0A", marginBottom: 12 }}>Owner contact</div>
        {!isUnlocked ? (
          <>
            <button
              onClick={() => {
                if (!user.isAdmin && !user.skiptraceAckAt) { setPendingUnlockLead(lead); setShowComplianceGate(true); return; }
                performUnlock(lead);
              }}
              style={{ width: "100%", padding: "15px 18px", background: segColor, border: "none", borderRadius: 13, color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 9, fontFamily: "'Outfit',sans-serif", opacity: unlocking === lead.id ? 0.8 : 1 }}>
              {unlocking === lead.id ? <RefreshCw size={15} color="white" style={{ animation: "spin 0.8s linear infinite" }} /> : <Unlock size={15} color="white" />}
              {unlocking === lead.id ? "Unlocking..." : "Unlock owner contact"}
            </button>
            <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11.5, color: "#1A1A1A", textAlign: "center", marginTop: 10 }}>
              {user.isAdmin ? "Admin · no credit charged · files to Verified" : "1 credit · files to Verified"}
            </div>
          </>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(unlocked[lead.id]?.owner_name) && <div style={{ fontSize: 18, fontWeight: 800, color: "#0A0A0A", fontFamily: "'Outfit',sans-serif" }}>{unlocked[lead.id].owner_name}</div>}
            {(unlocked[lead.id]?.owner_phone) && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "#fff", borderRadius: 10 }}>
                <Phone size={15} color={segInk} />
                <span style={{ fontSize: 15, fontWeight: 700, color: "#0A0A0A", letterSpacing: "0.3px" }}>{fmtPhone(unlocked[lead.id].owner_phone)}</span>
                {unlocked[lead.id]?.phone_type && <span style={{ fontSize: 11, color: "#1A1A1A", marginLeft: "auto" }}>{unlocked[lead.id].phone_type}</span>}
                {unlocked[lead.id]?.dnc && <span style={{ fontSize: 9, fontWeight: 800, color: "#EF4444", background: "#FEF2F2", padding: "2px 6px", borderRadius: 4 }}>DNC</span>}
              </div>
            )}
            {(unlocked[lead.id]?.owner_email) && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "#fff", borderRadius: 10 }}>
                <Mail size={15} color={segInk} />
                <span style={{ fontSize: 14, color: "#0A0A0A" }}>{unlocked[lead.id].owner_email}</span>
              </div>
            )}
            {(!unlocked[lead.id]?.owner_phone && !unlocked[lead.id]?.owner_email && !unlocked[lead.id]?.owner_name) && (
              <div style={{ fontSize: 13, color: "#1A1A1A", textAlign: "center", padding: "8px 0" }}>No contact information found for this address.</div>
            )}
          </div>
        )}
      </div>
    );

    // ── OVERVIEW TAB ──
    const renderOverview = () => (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Why now - segment play + the real intel rows (contractor footprint, nearby density). */}
        {(() => {
          const c = lead.intel?.contractor;
          const dn = lead.intel?.density;
          const notEntrenched = !!c && (c.cityPermits90d ?? 0) <= 1;
          const WHY: any = {
            installers: "Fresh install. The street is warm right now, prime for canvassing the block.",
            cleaning: "First service window. Systems this age are due for a first clean and tune-up.",
            battery: "Storage retrofit window. Past the early years and prime for a battery add-on.",
            takeover: "Service-partner window. Aging system, a candidate to take over from the original installer.",
            loanpayoff: "Loan-payoff window. The upgrade and refinance conversation opens around this age.",
            longterm: "Long-tenure system. A strong full-replacement candidate at this age.",
          };
          const why = (WHY[seg?.key] || WHY.cleaning) + (sysDetails?.battery ? " It runs a battery, so there is more system to service." : "");
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ background: segColor + "14", borderRadius: 14, padding: "14px 16px" }}>
                <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: segInk, marginBottom: 6 }}>Why now</div>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 700, lineHeight: 1.4, color: "#0A0A0A" }}>{why}</div>
              </div>
              {(lead.contractorName || (dn && dn.count90d > 0)) && (
                <div style={{ borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden" }}>
                  {lead.contractorName && (
                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: (dn && dn.count90d > 0) ? `1px solid ${T.border}` : "none" }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: segColor + "16", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Wrench size={15} color={segInk} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0A0A0A" }}>Filed by {lead.contractorName}</div>
                        <div style={{ fontSize: 12, color: "#1A1A1A", marginTop: 1 }}>
                          {c
                            ? (c.cityPermitsAllTime > 0
                                ? `${c.cityPermitsAllTime} permit${c.cityPermitsAllTime === 1 ? "" : "s"} in ${c.city}${(c.cityPermits90d ?? 0) > 0 ? `, ${c.cityPermits90d} in 90d` : ""}${notEntrenched ? ", not entrenched" : ""}`
                                : `New to ${c.city}, not entrenched here`)
                            : "Competitor on record"}
                        </div>
                      </div>
                    </div>
                  )}
                  {dn && dn.count90d > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px" }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: segColor + "16", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><MapPin size={15} color={segInk} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0A0A0A" }}>{dn.count90d} permit{dn.count90d === 1 ? "" : "s"} nearby</div>
                        <div style={{ fontSize: 12, color: "#1A1A1A", marginTop: 1 }}>In {dn.granularity === "zip" ? `ZIP ${dn.label}` : dn.label}, last 90 days</div>
                      </div>
                      <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontWeight: 800, fontSize: 15, color: segInk }}>+{dn.count90d}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Spec tiles - matches the map door card: system age / size / battery / installed. */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
          <div style={{ borderRadius: 13, background: segColor + "16", padding: "13px 14px" }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 22, letterSpacing: "-0.03em", color: segInk, lineHeight: 1 }}>{fmtAge(days)}</div>
            <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#0A0A0A", marginTop: 8 }}>System age</div>
          </div>
          {sysDetails?.kw && (
            <div style={{ borderRadius: 13, background: "#F1F1EF", padding: "13px 14px" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 22, letterSpacing: "-0.03em", color: "#0A0A0A", lineHeight: 1 }}>{sysDetails.kw} kW</div>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#0A0A0A", marginTop: 8 }}>System size</div>
            </div>
          )}
          {sysDetails?.battery && (
            <div style={{ borderRadius: 13, background: segColor, padding: "13px 14px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 16, color: "#fff", display: "flex", alignItems: "center", gap: 7 }}><Battery size={16} color="#fff" strokeWidth={2.4} />Storage</div>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#fff", marginTop: 8 }}>Battery on site</div>
            </div>
          )}
          <div style={{ borderRadius: 13, background: "#F1F1EF", padding: "13px 14px" }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 22, letterSpacing: "-0.03em", color: "#0A0A0A", lineHeight: 1 }}>{lead.permitDate ? new Date(lead.permitDate).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "n/a"}</div>
            <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#0A0A0A", marginTop: 8 }}>Installed</div>
          </div>
        </div>

        {/* Owner contact - placed high to drive skip tracing, matching the map card */}
        {renderOwnerContact()}

        {/* Filing contractor - mockup style, with Open profile */}
        {lead.contractorName ? (
          <div
            onClick={() => { setSelectedContractor(lead.contractorName); setView("competitors"); setModalLead(null); setModalMarketCtx(null); }}
            style={{ display: "flex", alignItems: "center", gap: 13, padding: "15px 16px", borderRadius: 14, border: `1px solid ${T.border}`, background: "#fff", cursor: "pointer" }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: segColor + "16", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Wrench size={17} color={segInk} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#1A1A1A" }}>Filing contractor</div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16, fontWeight: 800, color: "#0A0A0A", letterSpacing: "-0.01em", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lead.contractorName}</div>
              <div style={{ fontSize: 11.5, color: "#1A1A1A", marginTop: 2, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {lead.intel?.contractor
                  ? `${lead.intel.contractor.city}, ${lead.intel.contractor.cityPermits90d ?? 0} permit${(lead.intel.contractor.cityPermits90d ?? 0) === 1 ? "" : "s"} in 90d${(lead.intel.contractor.cityPermits90d ?? 0) <= 1 ? ", not entrenched" : ""}`
                  : `${lead.city || ""}${getState(lead) ? ", " + getState(lead) : ""}`}
              </div>
            </div>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12.5, fontWeight: 700, color: segInk, whiteSpace: "nowrap", flexShrink: 0 }}>Open profile <ChevronRight size={14} color={segInk} /></span>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 13, padding: "15px 16px", borderRadius: 14, border: `1px solid ${T.border}`, background: T.bgOff }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F1F1EF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Wrench size={17} color="#1A1A1A" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#1A1A1A" }}>Filing contractor</div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: "#1A1A1A", fontStyle: "italic", marginTop: 2 }}>Not listed on this permit</div>
            </div>
          </div>
        )}

        {/* Local solar activity - sparkline, only when there is real monthly data */}
        {lead.intel?.momentum?.monthly && lead.intel.momentum.monthly.length > 0 && (
          <div style={{ borderRadius: 14, border: `1px solid ${T.border}`, padding: "16px 18px 14px", background: "#fff" }}>
            <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: segInk, marginBottom: 10 }}>Neighborhood</div>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.02em" }}>Local solar activity</span>
              {lead.intel?.momentum?.label && (
                <span style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, color: "#1A1A1A" }}>{lead.intel.momentum.label}</span>
              )}
            </div>
            {(() => {
              const mm: number[] = lead.intel.momentum.monthly;
              const mx = Math.max(1, ...mm);
              const half = Math.ceil(mm.length / 2);
              return (
                <>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 64 }}>
                    {mm.map((c: number, i: number) => (
                      <div key={i} title={`${c}`} style={{ flex: 1, height: `${Math.max(6, Math.round((c / mx) * 100))}%`, borderRadius: "4px 4px 1px 1px", background: i >= mm.length - half ? segColor : "#E5E5E0" }} />
                    ))}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10.5, color: "#1A1A1A" }}>
                    <span>{lead.intel.momentum.startLabel || ""}</span>
                    <span>{lead.intel.momentum.endLabel || ""}</span>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* System & property */}
        {((sysDetails && (sysDetails.panels || sysDetails.systemType)) || (lead.property_data && (lead.property_data.assessed_value || lead.property_data.year_built || lead.property_data.neighborhood))) && (
          <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, overflow: "hidden" }}>
            {sysDetails && (sysDetails.panels || sysDetails.systemType) && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 14px", borderBottom: (lead.property_data && (lead.property_data.assessed_value || lead.property_data.year_built || lead.property_data.neighborhood)) ? `1px solid ${T.border}` : "none", flexWrap: "wrap" }}>
                <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, color: "#1A1A1A", textTransform: "uppercase", letterSpacing: "0.08em" }}>System</div>
                {sysDetails.panels && <span style={{ fontSize: 11.5, fontWeight: 700, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", color: "#1A1A1A", background: "#F1F1EF", padding: "4px 9px", borderRadius: 7 }}>{sysDetails.panels} panels</span>}
                {sysDetails.systemType && <span style={{ fontSize: 11.5, fontWeight: 700, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", color: "#1A1A1A", background: "#F1F1EF", padding: "4px 9px", borderRadius: 7 }}>{sysDetails.systemType}</span>}
              </div>
            )}
            {lead.property_data && (lead.property_data.assessed_value || lead.property_data.year_built || lead.property_data.neighborhood) && (
              <div style={{ padding: "11px 14px", background: "#fff" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: (lead.property_data.assessed_value || lead.property_data.year_built || lead.property_data.lot_sqft || lead.property_data.building_sqft) ? 8 : 0, flexWrap: "wrap" }}>
                  <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, color: "#1A1A1A", textTransform: "uppercase", letterSpacing: "0.08em" }}>Property</div>
                  {(lead.property_data.neighborhood || lead.property_data.property_class || lead.property_data.land_use) && (
                    <div style={{ fontSize: 11.5, color: "#1A1A1A", fontWeight: 600 }}>{[lead.property_data.neighborhood, lead.property_data.property_class, lead.property_data.land_use].filter(Boolean).join(", ")}</div>
                  )}
                </div>
                {(lead.property_data.assessed_value || lead.property_data.year_built || lead.property_data.lot_sqft || lead.property_data.building_sqft) && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {lead.property_data.assessed_value && <span style={{ fontSize: 11.5, fontWeight: 700, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", color: "#1A1A1A", background: "#F1F1EF", padding: "4px 9px", borderRadius: 7 }}>${lead.property_data.assessed_value >= 1000000 ? `${(lead.property_data.assessed_value / 1000000).toFixed(2)}M` : `${Math.round(lead.property_data.assessed_value / 1000)}K`} assessed</span>}
                    {lead.property_data.year_built && <span style={{ fontSize: 11.5, fontWeight: 700, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", color: "#1A1A1A", background: "#F1F1EF", padding: "4px 9px", borderRadius: 7 }}>Built {lead.property_data.year_built}</span>}
                    {lead.property_data.building_sqft && <span style={{ fontSize: 11.5, fontWeight: 700, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", color: "#1A1A1A", background: "#F1F1EF", padding: "4px 9px", borderRadius: 7 }}>{lead.property_data.building_sqft.toLocaleString()} sqft</span>}
                    {lead.property_data.lot_sqft && <span style={{ fontSize: 11.5, fontWeight: 700, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", color: "#1A1A1A", background: "#F1F1EF", padding: "4px 9px", borderRadius: 7 }}>{lead.property_data.lot_sqft.toLocaleString()} sqft lot</span>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    );

    // ── NEIGHBORHOOD TAB ──
    // Renders /api/market/context response. Backed by LBNL Tracking the Sun
    // installation data - independent from our permit feed.
    const renderNeighborhood = () => {
      if (modalMarketLoading || !ctx) return null;
      const total = ctx.total_installations || 0;
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: segInk, marginBottom: 7 }}>Neighborhood market</div>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 20, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.15, color: "#0A0A0A" }}>
              <span style={{ color: segInk }}>{total.toLocaleString()}</span> solar homes{ctx.zip ? ` in ZIP ${ctx.zip}` : (ctx.city || lead.city) ? ` in ${ctx.city || lead.city}` : ""}
            </div>
            {ctx.recent_2yr > 0 && (
              <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 12.5, color: "#1A1A1A", marginTop: 6, fontWeight: 600 }}>
                <span style={{ color: segInk, fontWeight: 800 }}>{ctx.recent_2yr.toLocaleString()}</span> installed in the last 2 years
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              { label: "Avg size", val: ctx.avg_system_kw != null ? `${ctx.avg_system_kw} kW` : "n/a", tip: "Average system size installed in this area. Benchmark for sizing your own quote." },
              { label: "Avg price", val: ctx.avg_price != null ? fmtCurrency(ctx.avg_price) : "n/a", tip: "Average total install price in this area. Paired with size to derive $/W." },
              { label: "Avg $/W", val: ctx.avg_ppw != null ? `$${ctx.avg_ppw.toFixed(2)}` : "n/a", tip: "Price per watt, the industry standard for comparing solar quotes. Lower is more competitive." },
              { label: "Storage", val: ctx.storage_pct != null ? `${ctx.storage_pct}%` : "n/a", tip: "Share of installs in this area that included battery storage. High % = hot battery market." },
            ].map((item) => (
              <div key={item.label} style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${T.border}`, background: "#fff" }}>
                <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, color: "#1A1A1A", textTransform: "uppercase", letterSpacing: "0.08em", display: "flex", alignItems: "center" }}>
                  {item.label}
                  <InfoTip text={item.tip} side="bottom" />
                </div>
                <div style={{ fontSize: 19, fontWeight: 900, color: "#0A0A0A", fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.02em", marginTop: 4 }}>{item.val}</div>
              </div>
            ))}
          </div>

          {ctx.top_installers && ctx.top_installers.length > 0 && (
            <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, overflow: "hidden" }}>
              <div style={{ padding: "10px 14px", background: T.bgOff, borderBottom: `1px solid ${T.border}`, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 9.5, fontWeight: 700, color: "#1A1A1A", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                Top installers here
              </div>
              {ctx.top_installers.map((inst: any, i: number) => (
                <div key={inst.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", borderBottom: i < ctx.top_installers.length - 1 ? `1px solid ${T.border}` : "none" }}>
                  <div style={{ width: 24, height: 24, borderRadius: 7, background: i === 0 ? segColor + "1F" : "#F1F1EF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 900, color: i === 0 ? segColor : "#1A1A1A", fontFamily: "'Outfit',sans-serif" }}>{i + 1}</span>
                  </div>
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: "#0A0A0A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inst.name}</div>
                  <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 12, color: "#1A1A1A", whiteSpace: "nowrap" }}>{inst.count} {inst.count === 1 ? "job" : "jobs"}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 10, color: "#1A1A1A", textAlign: "center", lineHeight: 1.5, padding: "2px 8px" }}>
            Source: Lawrence Berkeley National Lab · Tracking the Sun
          </div>
        </div>
      );
    };

    // ── ACTIVITY TAB ──
    const renderActivity = () => {
      const hasLogs = logs.length > 0;
      if (!hasLogs && !inPipe) return null;
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: segInk }}>Activity</div>
          {isUnlocked && unlocked[lead.id]?.owner_phone && (
            <button
              onClick={() => {
                const entry = { type: "Called", note: "", timestamp: new Date().toISOString(), user: user.email || "you" };
                setCallLog((prev: any) => ({ ...prev, [lead.id]: [...(prev[lead.id] || []), entry] }));
              }}
              style={{ padding: "11px 14px", background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 11, fontSize: 13, fontWeight: 700, color: "#0A0A0A", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "'Outfit',sans-serif" }}>
              <Phone size={13} color={segInk} />
              Log a call
            </button>
          )}

          {inPipe && pipeEntry && (
            <div style={{ display: "flex", gap: 11, padding: "12px 14px", borderRadius: 11, border: `1px solid ${T.border}`, background: T.bgOff }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: segColor, marginTop: 6, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0A0A0A" }}>
                  Added to focus{pipeEntry.stage ? `, ${pipeEntry.stage}` : ""}
                </div>
                {pipeEntry.pipeline_added_at && (
                  <div style={{ fontSize: 11, color: "#1A1A1A", marginTop: 2 }}>
                    {new Date(pipeEntry.pipeline_added_at).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          )}

          {logs.slice().reverse().map((log: any, i: number) => (
            <div key={i} style={{ display: "flex", gap: 11, padding: "12px 14px", borderRadius: 11, border: `1px solid ${T.border}` }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: segColor, marginTop: 6, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0A0A0A" }}>{log.type || "Called"}</div>
                {log.note && <div style={{ fontSize: 11, color: "#1A1A1A", marginTop: 3, lineHeight: 1.45 }}>{log.note}</div>}
                <div style={{ fontSize: 11, color: "#1A1A1A", marginTop: 3 }}>
                  {new Date(log.timestamp).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    };

    // ── NEARBY TAB ──
    // No-longer-gated on unlock: nearby permits are public data and are a
    // primary reason users unlock in the first place.
    const renderNearby = () => {
      const street = (lead.address || "").replace(/^\d+\s*/, "").trim().toUpperCase();
      const nearby = street.length > 3
        ? feedLeads.filter((l: any) => l.id !== lead.id && (l.address || "").toUpperCase().includes(street)).slice(0, 10)
        : [];

      if (nearby.length === 0) {
        return (
          <div style={{ padding: "40px 20px", textAlign: "center" }}>
            <Compass size={24} color={T.textSub} style={{ marginBottom: 12, opacity: 0.4 }} />
            <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.55, maxWidth: 260, margin: "0 auto" }}>
              No other permits detected on this street in your current feed.
            </div>
          </div>
        );
      }

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", color: "#FF8800", paddingLeft: 2, marginBottom: 2 }}>
            {nearby.length} more on this street
          </div>
          {nearby.map((n: any) => {
            const nDays = getDays(n);
            const nScore = getSmartScore(n);
            const nTier = scoreTier(nScore);
            const nHot = nTier === "hot";
            const nWarm = nTier === "warm";
            const nColor = nHot ? "#FF8800" : nWarm ? "#D97706" : T.textSub;
            return (
              <div
                key={n.id}
                onClick={() => { setModalLead(n); }}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: `1px solid ${T.border}`, cursor: "pointer", transition: "background 0.1s, border-color 0.1s", background: "#fff" }}
                onMouseEnter={(e: any) => { e.currentTarget.style.background = T.bgOff; e.currentTarget.style.borderColor = T.borderMid; }}
                onMouseLeave={(e: any) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = T.border; }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: nHot ? "#FFF3E5" : nWarm ? "#FEF3C7" : T.bgOff, border: `1.5px solid ${nColor}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ fontSize: nScore == null ? 9 : 12, fontWeight: 900, color: nColor, fontFamily: "'Outfit',sans-serif", lineHeight: 1 }}>{fmtScore(nScore)}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cleanAddress(n.address)}</div>
                  <div style={{ fontSize: 11, color: T.textSub, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {fmtAge(nDays)}{n.contractorName ? ` · ${n.contractorName}` : ""}
                  </div>
                </div>
                {unlocked[n.id]
                  ? <Check size={14} color="#16A34A" strokeWidth={2.5} />
                  : <ChevronRight size={14} color={T.textSub} />}
              </div>
            );
          })}
        </div>
      );
    };

    // ── DRAWER ROOT ──
    return (
      <>
        {/* Backdrop */}
        <div
          onClick={close}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(3px)", zIndex: 200, animation: "slDrawerFade 0.18s ease-out" }}
        />
        {/* Drawer panel */}
        <div
          onClick={(e: any) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: "min(580px, 100vw)",
            background: "#fff",
            zIndex: 201,
            display: "flex",
            flexDirection: "column",
            boxShadow: "-24px 0 64px rgba(0,0,0,0.22)",
            animation: "slDrawerSlide 0.24s cubic-bezier(0.22, 1, 0.36, 1)",
          }}>

          {/* HERO - door card header */}
          <div style={{ padding: "22px 22px 16px", flexShrink: 0, background: "#fff" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {seg && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 9, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: segInk, marginBottom: 12 }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: segColor }} />{seg.label}
                  </div>
                )}
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 26, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.04, color: "#0A0A0A" }}>{cleanAddress(lead.address)}</div>
                <div style={{ fontFamily: "ui-monospace, 'JetBrains Mono', monospace", fontSize: 13, color: "#1A1A1A", marginTop: 7 }}>{lead.city}{getState(lead) ? `, ${getState(lead)}` : ""}</div>
              </div>
              <button onClick={close} style={{ background: T.bgOff, border: "none", borderRadius: 999, width: 34, height: 34, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <X size={15} color={T.text} />
              </button>
            </div>
          </div>

          {/* BODY: single scroll, door-card layout */}
          <div style={{ flex: 1, overflowY: "auto", padding: "6px 22px 20px", display: "flex", flexDirection: "column", gap: 14, WebkitOverflowScrolling: "touch" }}>
            {renderOverview()}
            {renderNeighborhood()}
            {renderActivity()}
          </div>
          {/* FOOTER: Directions + Save to Focus */}
          <div style={{ flexShrink: 0, borderTop: `1px solid ${T.border}`, padding: "12px 22px", paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))", display: "flex", gap: 10, background: "#fff" }}>
            <button onClick={() => { const q = encodeURIComponent(`${cleanAddress(lead.address)}, ${lead.city || ""}${getState(lead) ? ", " + getState(lead) : ""}`); window.open(`https://www.google.com/maps/dir/?api=1&destination=${q}`, "_blank"); }}
              style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 16px", background: "#0A0A0A", color: "#fff", border: "none", borderRadius: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 15, cursor: "pointer" }}>
              <Navigation size={16} color="#fff" /> Directions
            </button>
            {inPipe ? (
              <div style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 16px", background: segColor + "16", color: segInk, borderRadius: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 15 }}>
                <Check size={16} color={segInk} strokeWidth={2.5} /> In focus
              </div>
            ) : (
              <button onClick={() => { setPipeLeads([...pipeLeads, { ...lead, stage: isUnlocked ? "unlocked" : "new" }]); savePipelineToBackend(lead.id, isUnlocked ? "unlocked" : "new"); }}
                style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 16px", background: segColor, color: "#fff", border: "none", borderRadius: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 15, cursor: "pointer" }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" strokeWidth="2.2"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" /></svg> Save to Focus
              </button>
            )}
          </div>
        </div>
      </>
    );
  };

  
  // ── MOBILE NAV ─────────────────────────────────────────────────────────────────
  const mobileNavJSX = (
    <div
      className="mobile-bottom-nav"
      id="mobile-nav"
      style={{
        height: 64,
        minHeight: 64,
        background: "#fff",
        borderTop: "1.5px solid #E5E5EA",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-around",
        flexShrink: 0,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      {NAV.filter((n) => ["dashboard", "explore", "zones", "roofers", "pipeline"].includes(n.key)).map((n) => {
        const active = view === n.key;
        return (
          <button
            key={n.key}
            onClick={() => setView(n.key)}
            style={{
              flex: 1,
              height: "100%",
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
            }}
          >
            <n.icon
              size={22}
              color={active ? T.primary : "#0A0A0A"}
              strokeWidth={active ? 2.4 : 2}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: active ? 800 : 600,
                color: active ? T.primary : "#0A0A0A",
              }}
            >
              {n.label}
            </span>
          </button>
        );
      })}
    </div>
  );

  // ── LOADING / AUTH GATE RENDERING ─────────────────────────────────────────────
  if (showLegal) {
    return <LegalPages onBack={() => { window.history.replaceState({}, "", window.location.pathname); window.location.reload(); }} initialPage={legalPage as "terms" | "privacy"} />;
  }

  if (showAuthGate) {
    if (!authChecked) return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", flexDirection: "column", gap: 14 }}>
        <SolarMark size={40} color="brand" />
        <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: 12, color: T.textSub, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase" }}>Loading</div>
      </div>
    );
    if (authView === "login") return <LoginPage onLogin={handleLogin} onGoSignup={() => setAuthView("signup")} onBack={() => setAuthView("splash")} onForgot={() => setAuthView("forgot-password")} />;
    if (authView === "onboarding") return <TrialOnboarding onComplete={handleTrialComplete} onGoLogin={() => setAuthView("login")} />;
    if (authView === "signup") return <SignupPage onSignupSuccess={handleLogin} onGoLogin={() => setAuthView("login")} onBack={() => setAuthView("splash")} />;
    if (authView === "forgot-password") return <ForgotPasswordPage onBack={() => setAuthView("login")} />;
    if (authView === "reset-password") return <ResetPasswordPage onDone={() => setAuthView("login")} />;
    if (authView === "why") return <WhySolarLeadPage onBack={() => setAuthView("splash")} onSignUp={() => setAuthView("signup")} />;
    return <SplashPage onEnterApp={() => setAuthView("login")} onSignIn={() => setAuthView("login")} onSignUp={() => setAuthView("signup")} onWhy={() => setAuthView("why")} />;
  }

  // ── PAYWALL GATE ─ authenticated but no active plan, not admin ───────────────
  if (showPaywall) {
    return <PaywallPage user={authedUser} onSignOut={handleLogout} />;
  }

  if (loading) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: T.bg,
          flexDirection: "column",
          gap: 14,
        }}
      >
        <SolarMark size={40} color="brand" />
        <div
          style={{
            fontFamily: "'Outfit',sans-serif",
            fontSize: 12,
            color: T.textSub,
            fontWeight: 700,
            letterSpacing: "1.5px",
            textTransform: "uppercase",
          }}
        >
          Loading
        </div>
      </div>
    );
  }

  // ── RENDER ─────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { overflow-x: clip; overflow-y: auto; width: 100%; height: 100%; }
        body { overflow-x: clip; width: 100%; height: 100%; font-family: 'DM Sans', sans-serif; background: ${T.bgOff}; -webkit-text-size-adjust: 100%; }
        #root { overflow: hidden; width: 100%; height: 100%; }
        @supports (height: 100dvh) { .app-shell { height: 100dvh !important; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes ask-bar { 0%, 100% { transform: scaleY(0.35); } 50% { transform: scaleY(1); } }
        @keyframes ask-pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
        @keyframes pulse-dot { 0% { opacity: 0.7; transform: scale(0.8); } 100% { opacity: 0; transform: scale(2.2); } }
        @keyframes battery-glow { 0% { box-shadow: 0 0 0 0 rgba(255,136,0,0.2); } 50% { box-shadow: 0 0 0 4px rgba(255,136,0,0.1); } 100% { box-shadow: 0 0 0 0 rgba(255,136,0,0.0); } }
        @keyframes slDrawerFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slDrawerSlide { from { transform: translateX(32px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .battery-pulse { animation: battery-glow 2.5s ease-in-out infinite; }
        @media (max-width: 768px) {
          .hide-mobile { display: none !important; }
          .show-mobile { display: flex !important; }
          #app-sidebar { display: none !important; }
          .mobile-sidebar-overlay { display: none !important; }
          .mobile-bottom-nav { display: flex !important; min-height: 64px !important; }
          .dash-hero { padding: 20px 16px 16px !important; }
          .dash-hero-row { flex-direction: column !important; align-items: flex-start !important; gap: 16px !important; }
          .dash-hero-stats { flex-direction: row !important; flex-wrap: wrap !important; gap: 12px !important; padding-bottom: 0 !important; }
          .dash-hero-num { font-size: 52px !important; letter-spacing: -3px !important; }
          .dash-section { margin-left: 16px !important; margin-right: 16px !important; }
          .dash-signals-grid { grid-template-columns: 1fr !important; }
          .dash-signals-cell { border-right: none !important; border-bottom: 1px solid #F0F0F4 !important; }
          .dash-metrics { grid-template-columns: repeat(3, 1fr) !important; }
          .dash-metrics span { font-size: 22px !important; }
          .dash-seg-grid { grid-template-columns: repeat(3, 1fr) !important; }
          .dash-bottom-grid { grid-template-columns: 1fr !important; }
          .dash-bottom-pad { padding: 0 16px 24px !important; }
          .dash-brief { margin: 0 16px 12px !important; }
          .dash-hotstrip { padding: 0 16px 12px !important; }
          .app-content-area { padding-bottom: 0 !important; max-width: 100% !important; overflow-x: hidden !important; }
          .feed-filter-bar { padding: 8px 16px !important; }
        }
        @media (min-width: 769px) {
          .show-mobile { display: none !important; }
          .mobile-bottom-nav { display: none !important; }
        }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 3px; }
        .feed-filter-bar::-webkit-scrollbar { display: none; }
        .feed-filter-bar { -ms-overflow-style: none; scrollbar-width: none; }
        select { -webkit-appearance: none; }
        button:focus, input:focus, textarea:focus { outline: none; }
      `}</style>

      <div className="app-shell" style={{ display: "flex", height: "100dvh", overflow: "hidden", width: "100%" }}>
        <div id="app-sidebar" className="hide-mobile">
          {sidebarJSX}
        </div>

        {mobileOpen && (
          <div className="mobile-sidebar-overlay">
            <div
              onClick={() => setMobileOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.45)",
                zIndex: 198,
              }}
            />
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: 68,
                height: "100vh",
                zIndex: 199,
              }}
            >
              {sidebarMobileJSX}
            </div>
          </div>
        )}

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          {topbarJSX}
          <div
            className="app-content-area"
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              overflowX: "hidden",
              minHeight: 0,
              WebkitOverflowScrolling: "touch",
            }}
          >
            {view === "dashboard" && Dashboard()}
            {view === "workspace" && (
              <NearbyMap
                segment={workspaceSegment}
                onBack={() => setView("dashboard")}
                onShowList={() => setView("explore")}
              />
            )}
            {view === "ask" && AskView()}
            {view === "hotspot" && HotspotDetail()}
            <div style={{ display: view === "insights" ? "flex" : "none", flex: 1, overflow: "hidden" }}>
              <SolarLeadInsights
                onAddToPipeline={(newLeads: any[]) => {
                  const stamped = newLeads.map((l: any) => ({ ...l, id: l.id || Date.now() + Math.random(), stage: l.stage || "new" }));
                  setPipeLeads((prev: any) => [...prev, ...stamped]);
                  // Persist each new entry - see PIPELINE STATE INVARIANT
                  stamped.forEach(l => savePipelineToBackend(l.id, l.stage));
                }}
                onViewPermits={(city: string, province: string) => { setOnbCity(city); setOnbRegion(province); setView("explore"); }}
                currentCity={onbCity}
                currentState={onbRegion}
              />
            </div>
            {view === "explore" && Explore()}
            {view === "roofers" && ContractorsView()}
            {view === "zones" && MarketsView()}
            {view === "competitors" && Competitors()}
            {view === "pipeline" && Focus()}
            {view === "calculator" && <InstallerROI />}
            {view === "route" && <RoutePlanner />}
            {view === "settings" && <SettingsPage />}
            {view === "terms" && <LegalPages onBack={() => setView("settings")} initialPage="terms" />}
            {view === "privacy" && <LegalPages onBack={() => setView("settings")} initialPage="privacy" />}
          </div>
          {mobileNavJSX}
        </div>
      </div>

      {Modal()}
      {/* Old WelcomeTour removed - replaced by dashboard onboarding */}

      {showFirstRun && (
        <FirstRunSetup
          isOpen={showFirstRun}
          firstName={user.firstName || user.accountOwner?.split(" ")[0] || ""}
          regions={FIRST_RUN_REGIONS}
          onPick={(name: string) => { selectTerritory(name); finishFirstRun(); }}
          onSkip={finishFirstRun}
        />
      )}

      {showTerritoryPicker && (
        <TerritoryPickerModal
          currentCity={onbCity}
          currentRegion={onbRegion}
          liveGroups={liveTerritoryGroups}
          onConfirm={(city, region, isAll) => {
            // Canonical name so currentTerritory + the briefing stay in lockstep
            // with the cards/feed. selectTerritory sets currentTerritory and
            // onbCity/onbRegion; the territory-change effect handles the refetch.
            const name = isAll
              ? "All Markets"
              : (city ? `${city}, ${region}` : (CODE_TO_TERRITORY_NAME[region] || region));
            selectTerritory(name);
            setOnbCountry(isAll ? null : (["AB","BC","ON","QC","MB","NS","SK"].includes(region) ? "CA" : "US"));
            setShowTerritoryPicker(false);
          }}
          onClose={() => setShowTerritoryPicker(false)}
        />
      )}

      {/* Floating assist bubble - Ask AI forward, support secondary. Logged-in only. */}
      <div className="hide-mobile"><SupportBubble authedUser={authedUser} onAskAI={() => setView("ask")} /></div>

      {/* Mobile: Ask AI as a floating button (it gave up its bottom-nav slot to Markets + Contractors). */}
      <button
        className="show-mobile"
        onClick={() => setView("ask")}
        aria-label="Ask AI"
        style={{
          position: "fixed",
          right: 16,
          bottom: "calc(64px + env(safe-area-inset-bottom, 0px) + 16px)",
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: view === "ask" ? "#E07700" : "#FF8800",
          border: "none",
          cursor: "pointer",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 10px 22px rgba(255,136,0,0.42), 0 2px 6px rgba(0,0,0,0.22)",
          zIndex: 150,
        }}
      >
        <Sparkles size={24} color="#FFFFFF" strokeWidth={2.1} />
      </button>

      {/* Mobile account menu (opened from the top-bar avatar) */}
      {showMobileMenu && (
        <>
          <div onClick={() => setShowMobileMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.35)" }} />
          <div style={{ position: "fixed", top: "calc(env(safe-area-inset-top, 0px) + 56px)", right: 12, width: 248, background: T.bg, borderRadius: 14, border: `1px solid ${T.border}`, boxShadow: "0 16px 40px rgba(0,0,0,0.22)", zIndex: 201, overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: "'Outfit',sans-serif", letterSpacing: "-0.01em" }}>{user.firstName} {user.lastName}</div>
              <div style={{ fontSize: 11.5, color: T.textSub, marginTop: 3, wordBreak: "break-all", fontFamily: "'JetBrains Mono', monospace" }}>{user.email}</div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, padding: "3px 7px", borderRadius: 4, background: T.primaryLight, color: T.primary, fontFamily: "'Outfit',sans-serif", fontWeight: 800, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}>{user.isAdmin ? "Admin" : "Pro"}</div>
            </div>
            {[
              { label: "Account & settings", icon: "⚙️", action: () => { setView("settings"); setShowMobileMenu(false); } },
              { label: "Change territory", icon: "📍", action: () => { setShowTerritoryPicker(true); setShowMobileMenu(false); } },
              { label: "Help & support", icon: "💬", action: () => { window.location.href = "mailto:support@solarlead.io?subject=" + encodeURIComponent("SolarLead support"); setShowMobileMenu(false); } },
            ].map((item: any) => (
              <button key={item.label} onClick={item.action} style={{ width: "100%", padding: "13px 16px", display: "flex", alignItems: "center", gap: 11, background: "none", border: "none", borderBottom: `1px solid ${T.border}`, cursor: "pointer", textAlign: "left" }}>
                <span style={{ fontSize: 14 }}>{item.icon}</span>
                <span style={{ fontSize: 14, color: T.text, fontWeight: 500 }}>{item.label}</span>
              </button>
            ))}
            <button onClick={() => { setShowMobileMenu(false); handleLogout(); }} style={{ width: "100%", padding: "13px 16px", display: "flex", alignItems: "center", gap: 11, background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
              <span style={{ fontSize: 14 }}>🚪</span>
              <span style={{ fontSize: 14, color: T.danger, fontWeight: 600 }}>Sign out</span>
            </button>
          </div>
        </>
      )}

      {/* Out-of-credits upsell modal */}
      <OutOfCreditsModal
        open={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        onPurchase={purchaseCreditPack}
        purchasing={creditsPurchasing}
      />

      {/* One-time skip-trace compliance gate */}
      <ComplianceGateModal
        open={showComplianceGate}
        onAccept={acknowledgeAndUnlock}
        onCancel={() => { setShowComplianceGate(false); setPendingUnlockLead(null); }}
        working={acknowledging}
      />
    </>
  );
}
